const { useState, useEffect } = React;

/* Theme: apply on load (before React). Default dark. Persist as ui_theme. */
(function applyThemeOnLoad() {
  var stored = window.localStorage.getItem('ui_theme');
  var theme = 'dark';
  if (stored === 'light' || stored === 'dark') {
    theme = stored;
  } else if (stored === 'auto') {
    theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  document.documentElement.setAttribute('data-theme', theme);
})();

/**
 * Single source of truth for API origin. Never use the static admin host for /api/*.
 * - Hosted admin (admin.*) -> api.* same registrable domain
 * - localhost / 127.0.0.1 -> same origin (API serves admin)
 * - Explicit window.API_BASE (e.g. Playwright) -> if it points at admin.*, rewrite to api.*
 */
(function resolveApiBase() {
  if (typeof window === 'undefined') return;
  var DEFAULT = 'https://api.nightcoders.com';
  var loc = window.location;
  var h = (loc && loc.hostname) || '';
  function hostIsAdmin(host) {
    return host.indexOf('admin.') === 0;
  }
  function adminToApiHost(host) {
    return host.replace(/^admin\./, 'api.');
  }
  var fromLocation = null;
  if (h && hostIsAdmin(h)) {
    fromLocation = loc.protocol + '//' + adminToApiHost(h);
  } else if (h === 'localhost' || h === '127.0.0.1') {
    fromLocation = loc.origin;
  }

  var existing = window.API_BASE;
  if (existing != null && String(existing).trim() !== '') {
    var s = String(existing).trim().replace(/\/$/, '');
    try {
      var u = new URL(s);
      if (hostIsAdmin(u.hostname)) {
        u.hostname = adminToApiHost(u.hostname);
        window.API_BASE = u.origin;
        return;
      }
      window.API_BASE = u.origin;
      return;
    } catch (e) {
      window.API_BASE = s;
      return;
    }
  }
  window.API_BASE = fromLocation || DEFAULT;
})();

const API_BASE =
  typeof window !== 'undefined' && window.API_BASE
    ? String(window.API_BASE).replace(/\/$/, '')
    : 'https://api.nightcoders.com';

var API_ERROR_TEXT = {
  invalid_credentials: 'That email and password do not match. Check both, or ask an admin to reset the password.',
  phone_required: 'A phone number is required.',
  no_tenant: 'Open a workspace before doing that.',
  use_tenant: 'Choose a tenant workspace first.',
  not_found: 'That record could not be found.',
  forbidden: 'You do not have permission to do that.',
  plan_blocked: 'Your plan does not include this.',
  file_required: 'Choose a file first.'
};

function textFromUnknown(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value === '[object Object]' ? '' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(textFromUnknown).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    var nested = textFromUnknown(value.message) || textFromUnknown(value.detail) || textFromUnknown(value.error) || textFromUnknown(value.reason);
    return nested;
  }
  return '';
}

function humanizeErrorCode(code) {
  var text = textFromUnknown(code);
  if (!text) return '';
  if (API_ERROR_TEXT[text]) return API_ERROR_TEXT[text];
  if (/^[a-z0-9_]+$/.test(text)) {
    var words = text.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1) + '.';
  }
  return text;
}

function friendlyApiError(data, statusText) {
  var message = humanizeErrorCode(data && data.message);
  var detail = humanizeErrorCode(data && data.detail);
  var error = humanizeErrorCode(data && data.error);
  if (message) return detail && message.indexOf(detail) < 0 ? message + ' — ' + detail : message;
  if (detail && error && error !== detail && error.indexOf(' ') < 0) return detail;
  if (detail) return detail;
  if (error) return error;
  if (statusText && !/^(bad request|internal server error|error|request failed|not found)$/i.test(statusText)) return statusText;
  return 'Something went wrong. Try again.';
}

function uiErrorMessage(err, fallback) {
  var fromData = friendlyApiError(err && err.data, '');
  if (fromData && fromData !== 'Something went wrong. Try again.') return fromData;
  var message = err && textFromUnknown(err.message);
  if (message && message !== 'Request failed') return humanizeErrorCode(message);
  return fallback || 'Something went wrong. Try again.';
}

function asCount(value) {
  var n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatMeasure(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return '—';
  var rounded = Math.round(n * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) return String(Math.round(rounded));
  return rounded.toFixed(1);
}

function roleLabel(role) {
  var map = {
    tenant_admin: 'Tenant admin',
    super_admin: 'Super admin',
    supervisor: 'Supervisor',
    agent: 'Agent',
    viewer: 'Viewer'
  };
  return map[role] || (role ? String(role).replace(/_/g, ' ') : '—');
}

function leadStatusLabel(lead) {
  if (!lead) return '—';
  if (lead.dnc_block_reason === 'suppression_list') return 'DNC (list)';
  if (lead.dnc_block_reason === 'suppression_list_and_lead') return 'DNC (list + lead)';
  if (lead.dnc_flag || lead.dnc_list_match || lead.status === 'dnc') return 'DNC';
  if (lead.status === 'not_interested') return 'Not interested';
  if (lead.status === 'interested') return 'Interested';
  if (!lead.status || lead.status === 'new') return 'New';
  return String(lead.status).replace(/_/g, ' ');
}

/** Quote-aware CSV so a value like "Acme, Inc" stays one column in the import preview. */
function parseCsvTable(text) {
  var src = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  var table = [];
  var row = [];
  var cell = '';
  var inQuotes = false;
  for (var i = 0; i < src.length; i++) {
    var ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { row.push(cell.trim()); cell = ''; continue; }
    if (ch === '\n') {
      row.push(cell.trim());
      cell = '';
      if (row.some(function (value) { return value.length; })) table.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  if (cell.length || row.length) {
    row.push(cell.trim());
    if (row.some(function (value) { return value.length; })) table.push(row);
  }
  return table;
}

function phoneColumnIndex(headers) {
  for (var i = 0; i < headers.length; i++) {
    if (headerLooksLikePhone(headers[i])) return i;
  }
  return -1;
}

function phoneLooksDialable(value) {
  var digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

import { LeadFinderView } from './lead-finder.js';
import { FollowUpsView } from './followups.js';
import { CampaignsView, AddToCampaignModal } from './campaigns.js';
import { QualityView } from './quality.js';
import { NotesPanel, ActivityTimeline } from './lead-notes.js';
import { AgentRoutingSettings, AgentStatusToggle } from './agent-routing-settings.js';
import { DispositionPicker, DispositionBadge, DispositionSettings } from './dispositions.js';
import { DncManager } from './dnc.js';
import { BusinessHoursSettings } from './business-hours.js';
import { VoicemailDropSettings } from './voicemail-settings.js';
import { NotificationSettings, LeadSmsThread } from './notifications-settings.js';
import { ConsentFormSettings } from './consent-form-settings.js';
import { ConsentPanel, BulkConsentModal, ImportConsentFields } from './consent-panel.js';
import { LeadImportHelp } from './lead-import-help.js';
import { LegalAcceptanceGate, LegalAcceptanceLog } from './legal-gate.js';
import { SetupWizard, FinishSetupCard, openSetupWizard, setupBlockMessage } from './setup-wizard.js';
import { ComplianceAdminView, ComplianceStatusBanner } from './compliance-admin.js';
import { DncComplianceSettings } from './dnc-compliance-settings.js';
import { CallerIdSetup } from './caller-id-setup.js';
import { UnrestrictedAccountToggle } from './account-override.js';
import {
  formatDateTime,
  formatRelative,
  formatDuration,
  zoneLabel,
  todayYmd,
  addDaysYmd,
  zonedYmd,
  parseInstant,
  setWorkspaceTimeZone,
  getWorkspaceTimeZone,
  leadLocalTimeLabel
} from './datetime.js';
import { SoftphonePanel } from './softphone-panel.js';
import { TenantUsersPanel, MyAccountPanel, ForgotPasswordForm, ResetPasswordForm, ForceChangePassword, ForcePhoneSetup, userMustChangePassword } from './account-editors.js';

// UI debug mode: ?debug=1 => window.DEBUG_API = true (log [ui] METHOD URL status ok bodyKeys error)
if (typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search || '')) {
  window.DEBUG_API = true;
}

/** Single API client: credentials, Content-Type, X-Debug-UI, safe 204/empty parse, returns { ok, status, data, errorText }. */
async function apiClientRequest(method, url, body) {
  var base = (typeof window !== 'undefined' && window.API_BASE) ? window.API_BASE : API_BASE;
  var fullUrl = (url.startsWith('http') ? url : base + url);
  var token = typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;
  var headers = {};
  if (body !== undefined && body !== null) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (typeof window !== 'undefined' && (window.DEBUG_API === true || window.location.search.indexOf('debug=1') !== -1)) {
    headers['X-Debug-UI'] = '1';
  }
  var res = await fetch(fullUrl, {
    method: method,
    headers: headers,
    body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
    credentials: 'include'
  });
  var text = await res.text();
  var data = null;
  if (text && text.trim()) {
    try { data = JSON.parse(text); } catch (_) { data = null; }
  }
  var errorText = null;
  if (!res.ok) {
    if (data && (data.error != null || data.message != null || data.detail != null)) {
      errorText = friendlyApiError(data, res.statusText || 'Request failed');
    } else if (res.status === 504) {
      errorText = 'Gateway timeout (504). The API did not respond in time — check server logs ([phone-join-debug]) and Twilio/BYO account alignment.';
    } else {
      var snippet = text && text.length < 400 && !/^\s*</.test(text) ? text : '';
      errorText = snippet || res.statusText || 'Request failed';
    }
  }
  if (typeof window !== 'undefined' && (window.DEBUG_API === true || window.location.search.indexOf('debug=1') !== -1)) {
    var bodyKeys = data && typeof data === 'object' ? Object.keys(data) : [];
    console.log('[ui]', method, fullUrl, res.status, res.ok, bodyKeys.join(','), errorText || '');
  }
  if (res.status === 401 && typeof window !== 'undefined') {
    window.localStorage.removeItem('authToken');
    if (typeof window.dispatchEvent === 'function') window.dispatchEvent(new CustomEvent('auth:expired'));
  }
  return { ok: res.ok, status: res.status, data: data, errorText: errorText };
}

/** Legacy API.json: uses apiClientRequest, throws on !ok so .catch() gets e.message, e.status, e.data. */
const API = {
  async json(method, url, body) {
    var r = await apiClientRequest(method, url, body);
    if (!r.ok) {
      var err = new Error(r.errorText || 'Request failed');
      err.status = r.status;
      err.data = r.data && typeof r.data === 'object' ? Object.assign({}, r.data) : r.data;
      if (err.data && err.data.error != null && typeof err.data.error !== 'string' && typeof err.data.error !== 'number') {
        err.data.error = r.errorText || 'Something went wrong. Try again.';
      }
      throw err;
    }
    return r.data;
  }
};

/** Absolute URL for Twilio recording proxy (/api/recordings/...) when admin UI is on a different host than the API. */
function absoluteRecordingUrl(url) {
  if (url == null || url === '') return '';
  var u = String(url);
  if (/^https?:\/\//i.test(u)) return u;
  var base = typeof window !== 'undefined' && window.API_BASE ? String(window.API_BASE).replace(/\/$/, '') : API_BASE;
  return base + (u.indexOf('/') === 0 ? u : '/' + u);
}

function headerLooksLikePhone(header) {
  var h = String(header || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (h === 'number' || h === 'tel') return true;
  var words = h.split(' ').filter(Boolean);
  return words.indexOf('phone') !== -1 || words.indexOf('mobile') !== -1 || words.indexOf('telephone') !== -1 || words.indexOf('cell') !== -1 || words.indexOf('tel') !== -1;
}

function shortCallSid(sid) {
  var s = String(sid || '');
  if (!s) return '';
  if (s.length <= 8) return s;
  return s.slice(0, 2) + '…' + s.slice(-4);
}

function authErrorText(err, fallback) {
  var data = err && err.data;
  if (data && data.message) return String(data.message);
  if (data && data.detail) return String(data.detail);
  return fallback;
}

function BuyerListNotice({ title, body, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: 20 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
      {body ? <div style={{ color: 'var(--fg-muted)', marginBottom: onRetry ? 8 : 0 }}>{body}</div> : null}
      {onRetry ? (
        <button type="button" className="button small outline" onClick={onRetry}>Try again</button>
      ) : null}
    </div>
  );
}

/**
 * Line-by-line transcript vs summary vs status (no fake transcript).
 * @param {{ call?: object, events?: object[], loadFailed?: boolean }} opts
 */
function CallTranscriptBlock(opts) {
  var call = opts && opts.call;
  var events = opts && opts.events;
  var loadFailed = opts && opts.loadFailed;
  if (loadFailed) {
    return React.createElement('div', { style: { color: 'var(--danger)', fontSize: '0.78rem' } }, 'Could not load transcript data. Refresh and try again.');
  }
  var list = Array.isArray(events) ? events.filter(function (e) { return e && String(e.content || '').trim() !== ''; }) : [];
  if (list.length > 0) {
    return list.map(function (e, i) {
      return React.createElement('div', { key: e.id != null ? e.id : i },
        React.createElement('strong', null, e.role || '—', ': '), e.content
      );
    });
  }
  var st = call && call.status != null ? String(call.status).toLowerCase().replace(/_/g, '-') : '';
  var inProgress = st === 'in-progress' || st === 'ringing' || st === 'queued' || st === 'initiated' || st === 'busy';
  if (inProgress) {
    return React.createElement('div', { style: { color: 'var(--fg-muted)', fontSize: '0.78rem' } }, 'Transcript is not available yet — the call may still be in progress.');
  }
  if (call && String(call.summary || '').trim() !== '') {
    return React.createElement(React.Fragment, null,
      React.createElement('div', { style: { color: 'var(--fg-muted)', fontSize: '0.78rem', marginBottom: 8 } }, 'No line-by-line transcript is stored for this call.'),
      React.createElement('div', { style: { fontSize: '0.78rem' } },
        React.createElement('strong', null, 'Call summary: '), call.summary
      )
    );
  }
  return React.createElement('div', { style: { color: 'var(--fg-muted)', fontSize: '0.78rem' } }, 'No line-by-line transcript was captured for this call.');
}

/** Session context for tenant/role checks. Call setSessionUser(user) from App when user loads. */
var _sessionUser = null;
function setSessionUser(user) { _sessionUser = user; }
function getSessionContext() {
  var u = _sessionUser;
  if (!u) return { role: null, tenant_id: null, is_impersonating: false, impersonated_tenant_id: null };
  var tenantId = u.tenant_id != null ? u.tenant_id : (u.tenantId != null ? u.tenantId : (u.tenant && u.tenant.id));
  var isImp = !!(u.role === 'super_admin' && tenantId != null);
  return { role: u.role || null, tenant_id: tenantId || null, is_impersonating: isImp, impersonated_tenant_id: isImp ? tenantId : null };
}

/** Central capabilities from backend. Use this instead of guessing role or plan. Returns { ok, plan_key, capabilities, integrations_reason } or null. */
async function getCapabilities() {
  try {
    var r = await apiClientRequest('GET', '/api/tenant/capabilities');
    if (r.ok && r.data) return r.data;
    return null;
  } catch (_) { return null; }
}

/** Show toast; type 'error' | 'success' | 'info'. */
function showToast(message, type) {
  type = type || 'info';
  message = textFromUnknown(message) || humanizeErrorCode(message) || 'Something went wrong. Try again.';
  if (typeof document === 'undefined' || !document.body) return;
  var id = 'toast-' + Date.now();
  var el = document.createElement('div');
  el.id = id;
  el.setAttribute('role', 'alert');
  el.className = 'admin-toast admin-toast-' + type;
  el.style.cssText = 'position:fixed;bottom:16px;right:16px;max-width:360px;padding:12px 16px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:10000;font-size:0.875rem;background:var(--card-bg, #1f2937);color:var(--color-fg, #f9fafb);border-left:4px solid ' + (type === 'error' ? '#ef4444' : type === 'success' ? '#22c55e' : '#3b82f6') + ';';
  el.textContent = message;
  var container = document.getElementById('admin-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'admin-toast-container';
    container.style.cssText = 'position:fixed;bottom:0;right:0;pointer-events:none;z-index:10000;';
    document.body.appendChild(container);
  }
  container.appendChild(el);
  setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 5000);
}

/** Normalize list responses: accept { items: [] } OR { ok: true, users: [] } etc. */
function normalizeUsersResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.users && Array.isArray(data.users)) return data.users;
  if (data.items && Array.isArray(data.items)) return data.items;
  if (typeof window !== 'undefined' && window.DEBUG_API) console.warn('[ui][shape-mismatch] users endpoint expected users/items array, got', Object.keys(data || {}));
  return [];
}
function normalizeFlowsResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.flows && Array.isArray(data.flows)) return data.flows;
  if (data.items && Array.isArray(data.items)) return data.items;
  if (typeof window !== 'undefined' && window.DEBUG_API) console.warn('[ui][shape-mismatch] flows endpoint expected flows/items array, got', Object.keys(data || {}));
  return [];
}
function normalizeCampaignsResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.campaigns && Array.isArray(data.campaigns)) return data.campaigns;
  if (data.items && Array.isArray(data.items)) return data.items;
  if (typeof window !== 'undefined' && window.DEBUG_API) console.warn('[ui][shape-mismatch] campaigns endpoint expected campaigns/items array, got', Object.keys(data || {}));
  return [];
}

/** Never show "Invalid Date". Display uses the workspace time zone unless options.timeZone is set. */
var EM_DASH = '\u2014';
function formatDisplayDate(value, options) {
  var opts = options || {};
  var style = opts.style || (opts.dateOnly ? 'date' : 'smart');
  return formatDateTime(value, { timeZone: opts.timeZone, style: style });
}

function parseTimestampMs(value) {
  var d = parseInstant(value);
  return d ? d.getTime() : 0;
}

function whenTitle(value) {
  var full = formatDateTime(value, { style: 'full' });
  if (!full || full === EM_DASH) return undefined;
  var rel = formatRelative(value);
  if (!rel || rel === EM_DASH || rel === full) return full;
  return full + ' · ' + rel;
}

function WhenText(props) {
  var opts = { style: props.dateOnly ? 'date' : (props.style || 'smart') };
  if (props.timeZone) opts.timeZone = props.timeZone;
  return <span title={whenTitle(props.value)}>{formatDisplayDate(props.value, opts)}</span>;
}

function TimesShown(props) {
  return <span className="pagination-meta">Times shown in {zoneLabel((props && props.timeZone) || getWorkspaceTimeZone())}.</span>;
}

function LeadLocalClock(props) {
  var tickState = useState(0);
  var tick = tickState[0];
  var setTick = tickState[1];
  useEffect(function () {
    var id = setInterval(function () { setTick(function (n) { return n + 1; }); }, 30000);
    return function () { clearInterval(id); };
  }, []);
  var label = leadLocalTimeLabel({
    phone: props.phone,
    state: props.state,
    local_timezone: props.localTimeZone,
    phone_e164: props.phone
  }, new Date(Date.now() + tick * 0));
  if (!label) return null;
  return <div className="field-help" style={{ marginTop: 4 }}>{label}</div>;
}

const JOIN_PHONE_ERRORS = {
  missing_phone:
    'Add your E.164 phone number in Settings → Your account. A tenant admin can also update it from Team.',
  not_assigned_agent:
    'You are not allowed to take this call on the phone under current handoff rules (e.g. only the lead’s assigned agent, or admins/supervisors may).',
  twilio_not_configured: 'Twilio is not configured — contact your admin.',
  call_not_found: 'Call no longer active or not found in Twilio (wrong account?).',
  already_taken: 'Another agent has already taken this call.',
  unauthorized: 'Session expired, please log in again.',
  twilio_error: 'Twilio rejected the phone join (account/caller ID/callback URL). Check server logs [phone-join-debug].',
  agent_join_phone_failed: 'Phone join failed. Please try again.',
  server_misconfig: 'Server is missing database access for phone join — contact your admin.',
  phone_join_failed: 'Phone join failed on the server. Check logs or try again.'
};

const DEBUG_LIVE = typeof window !== 'undefined' && (window.DEBUG_API === true || /[?&]debug=1/.test(window.location.search || ''));

function useSse(onEvent) {
  useEffect(() => {
    const token = window.localStorage.getItem('authToken');

const url = token
  ? `${API_BASE}/api/live/stream?token=${encodeURIComponent(token)}`
  : `${API_BASE}/api/live/stream`;

    const es = new EventSource(url);
    if (DEBUG_LIVE) {
      try {
        const payload = token ? (() => { try { return JSON.parse(atob(token.split('.')[1])); } catch (_) { return {}; } })() : {};
        console.log('[live] SSE connect url=%s tenantId=%s', url.replace(/token=[^&]+/, 'token=...'), payload.tenant_id ?? '(none)');
      } catch (_) {}
    }
    let eventCount = 0;
    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        eventCount++;
        if (DEBUG_LIVE && data.type === 'call') console.log('[live] SSE event #%s type=%s callSid=%s state=%s', eventCount, data.type, data.payload?.callSid, data.payload?.state);
        onEvent && onEvent(data);
      } catch (e) {
        console.error('SSE parse', e);
      }
    };
    es.onerror = () => { if (DEBUG_LIVE) console.log('[live] SSE disconnect eventCount=%s', eventCount); };
    return () => { es.close(); if (DEBUG_LIVE) console.log('[live] SSE close eventCount=%s', eventCount); };
  }, [onEvent]);
}

function useAgentEvents(onEvent, onConnectionChange, setPresenceState) {
  useEffect(() => {
    const token = window.localStorage.getItem('authToken');
    if (!token) {
      onConnectionChange && onConnectionChange(false);
      setPresenceState && setPresenceState('offline');
      return;
    }
    const url = `${API_BASE}/api/agent/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.onopen = function () {
      try {
        console.log('[agent-handoff-debug] sse_client_registered', { source: 'browser_tab' });
      } catch (_e) {
        /* ignore */
      }
      onConnectionChange && onConnectionChange(true);
    };
    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type === 'ready') {
          onConnectionChange && onConnectionChange(true);
          setPresenceState && setPresenceState(data.state || 'available');
        } else if (data.type === 'ping') {
          setPresenceState && setPresenceState(data.state || 'available');
        }
        onEvent && onEvent(data);
      } catch (e) {
        console.error('Agent SSE parse', e);
      }
    };
    es.onerror = function () {
      onConnectionChange && onConnectionChange(false);
      setPresenceState && setPresenceState('offline');
    };
    return () => {
      try {
        console.log('[agent-handoff-debug] sse_client_disconnected', { source: 'browser_tab_effect_cleanup' });
      } catch (_e) {
        /* ignore */
      }
      onConnectionChange && onConnectionChange(false);
      setPresenceState && setPresenceState('offline');
      es.close();
    };
  }, [onEvent, onConnectionChange, setPresenceState]);
}

function ThemeToggle() {
  var stored = window.localStorage.getItem('ui_theme');
  var initial = (stored === 'light' || stored === 'dark' || stored === 'auto') ? stored : 'dark';
  var _themeState = useState(initial);
  var theme = _themeState[0];
  var setTheme = _themeState[1];

  function resolveToDataTheme(val) {
    if (val === 'light' || val === 'dark') return val;
    if (val === 'auto' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  }

  function applyTheme(val) {
    window.localStorage.setItem('ui_theme', val);
    var resolved = resolveToDataTheme(val);
    document.documentElement.setAttribute('data-theme', resolved);
    setTheme(val);
  }

  useEffect(function() {
    if (theme !== 'auto' || !window.matchMedia) return;
    var mq = window.matchMedia('(prefers-color-scheme: light)');
    function handler() {
      var resolved = mq.matches ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', resolved);
    }
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return function() { mq.removeEventListener('change', handler); };
    }
    mq.addListener(handler);
    return function() { mq.removeListener(handler); };
  }, [theme]);

  return (
    <div className="theme-toggle">
      <button
        type="button"
        className={'theme-toggle-btn' + (theme === 'dark' ? ' active' : '')}
        onClick={function() { applyTheme('dark'); }}
        title="Dark theme"
      >
        Dark
      </button>
      <button
        type="button"
        className={'theme-toggle-btn' + (theme === 'light' ? ' active' : '')}
        onClick={function() { applyTheme('light'); }}
        title="Light theme"
      >
        Light
      </button>
      <button
        type="button"
        className={'theme-toggle-btn' + (theme === 'auto' ? ' active' : '')}
        onClick={function() { applyTheme('auto'); }}
        title="Follow system"
      >
        Auto
      </button>
    </div>
  );
}

/** True if user is super admin (role or flags). */
function isSuperAdminUser(user) {
  return !!(user && (user.role === 'super_admin'));
}

/** True if super admin is currently viewing as a tenant (impersonation). */
function isImpersonating(user) {
  return !!(user && user.role === 'super_admin' && user.tenant_id != null);
}

/** True when we should call tenant-scoped APIs (tenant user, or super admin impersonating). */
function isTenantContext(user) {
  if (!user) return false;
  if (user.role === 'super_admin' && user.tenant_id != null) return true;
  if (user.role === 'super_admin') return false;
  return true;
}

/* Role-based nav: agent/viewer see limited items; super_admin sees only admin items unless impersonating */
var CALL_START_ERRORS = {
  call_blocked_dnc: 'This number is on the Do Not Call list. Outbound calls are blocked.',
  call_blocked_opted_out: 'This lead has opted out. Outbound calls are blocked.',
  call_blocked_missing_phone: 'No phone number for this lead. Add a phone number to start a call.',
  lead_dnc_blocked: 'This lead is on DNC or has opted out. Outbound calls are blocked.',
  usage_limit_reached: 'Usage limit reached for this month. Upgrade your plan or try again next billing period.',
  billing_unavailable: 'Billing check is temporarily unavailable. Please try again later.',
  outbound_number_not_configured: 'This workspace has no outbound caller ID yet. Ask your platform admin to assign one.',
  twilio_not_configured: 'Phone calling is not connected yet. Finish setup, or ask a platform admin.',
  failed: 'The call did not start. Check the message and try again.',
  limit_reached: 'Concurrent call limit reached. Wait for an active call to end.',
  lead_not_found: 'Lead not found.',
  outside_calling_hours: "It is outside 8am-9pm where this lead is. Try again during the lead's local daytime.",
  tenant_suspended: 'This workspace is suspended. A platform admin has to restore it before calls can start.',
  plan_feature_required: 'Your plan does not include this. Upgrade to use it.',
  feature_not_in_plan: 'Your plan does not include premium calls. Upgrade to use them.'
};

var OUTBOUND_BLOCK_REASONS = {
  platform_paused: 'Outbound calling is paused for the platform right now.',
  tenant_suspended: 'Outbound calling is suspended for this workspace.',
  terms_not_accepted: 'A workspace admin has to accept the calling terms first.',
  opted_out: 'This number is on the Do Not Call list or asked not to be called.',
  outside_calling_hours: "It is outside legal calling hours where this lead is.",
  frequency_cap: 'This number was already called 3 times in the last 24 hours.',
  no_consent: 'The AI needs this person\u2019s consent on file. You can call them yourself instead.',
  not_business_landline: 'This is not a verified business landline, so the AI needs consent on file. You can call them yourself instead.',
  line_type_unknown: 'We have not verified this phone type yet, so the AI needs consent on file. You can call them yourself instead.',
  national_dnc: 'This number is on the National Do Not Call Registry. Only call it with the person\u2019s written consent on file.',
  state_dnc: 'This number is on a state Do Not Call list. Only call it with the person\u2019s written consent on file.',
  litigator: 'This number belongs to a known phone-law litigator, so it is blocked to protect you.',
  dnc_san_missing: 'Add your FTC Do Not Call account (SAN) in Settings before calling numbers without written consent.',
  dnc_quota_exceeded: 'This workspace has used its Do Not Call checks.',
  dnc_check_unavailable: 'We could not check the Do Not Call lists right now. Try again in a minute.',
  number_reassigned: 'This number changed owners after the consent was given, so that consent no longer counts.',
  reassigned_check_unavailable: 'We could not confirm this number still belongs to the person who gave consent. Try again in a minute.'
};

function callStartErrorMessage(err) {
  var data = err && err.data;
  var code = data && data.error;
  if (code === 'outbound_not_authorized') {
    var reasons = Array.isArray(data.reasons) ? data.reasons : [];
    var lines = reasons.map(function (r) { return OUTBOUND_BLOCK_REASONS[r]; }).filter(Boolean);
    return lines.length ? 'Call blocked: ' + lines.join(' ') : 'This call is not allowed by the calling rules.';
  }
  if (code && CALL_START_ERRORS[code]) return CALL_START_ERRORS[code];
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message;
  if (data && typeof data.detail === 'string' && data.detail.trim()) return data.detail;
  return (err && err.message) || 'Call failed';
}

async function outboundSetupBlock() {
  try {
    var status = await API.json('GET', '/api/onboarding/status');
    if (status && status.ready_for_outbound === false) return status;
  } catch (e) { /* the call itself will show the server error */ }
  return null;
}

function canAccessNav(role, pageId, user) {
  if (!role) return false;
  var r = role;
  if (r === 'super_admin') {
    if (user && user.tenant_id != null) {
      return ['dashboard', 'leads', 'lead-finder', 'calls', 'follow-ups', 'campaigns', 'monitor', 'integrations', 'flows', 'team', 'quality', 'insights', 'dnc', 'audit-logs', 'settings', 'go-live'].indexOf(pageId) >= 0;
    }
    return ['admin-dashboard', 'compliance-admin', 'oauth-providers', 'plans', 'billing', 'cost-usage', 'integrations', 'audit-logs', 'demo-controls', 'testlab', 'monitoring', 'settings'].indexOf(pageId) >= 0;
  }
  if (r === 'tenant_admin') {
    var tenantNav = ['dashboard', 'leads', 'lead-finder', 'calls', 'follow-ups', 'campaigns', 'monitor', 'flows', 'team', 'quality', 'insights', 'dnc', 'audit-logs', 'settings', 'go-live', 'integrations', 'billing'];
    return tenantNav.indexOf(pageId) >= 0;
  }
  if (r === 'supervisor') {
    return ['dashboard', 'leads', 'lead-finder', 'calls', 'follow-ups', 'campaigns', 'monitor', 'flows', 'team', 'quality', 'insights', 'dnc', 'audit-logs', 'settings', 'go-live', 'billing'].indexOf(pageId) >= 0;
  }
  if (r === 'agent' || r === 'viewer') {
    return ['dashboard', 'leads', 'calls', 'follow-ups', 'monitor', 'go-live'].indexOf(pageId) >= 0;
  }
  if (r === 'analyst') {
    return ['dashboard', 'leads', 'calls', 'campaigns', 'insights'].indexOf(pageId) >= 0;
  }
  return ['dashboard', 'leads', 'calls', 'follow-ups', 'monitor', 'go-live'].indexOf(pageId) >= 0;
}

function NotAuthorized() {
  return (
    <section className="card">
      <div className="card-body" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⛔</div>
        <div className="card-title">Not authorized</div>
        <div className="card-subtitle" style={{ marginTop: 8 }}>
          You do not have permission to access this page.
        </div>
      </div>
    </section>
  );
}

/** Bare super admin (no tenant / impersonation): explains platform vs tenant integration surfaces. */
function SuperAdminIntegrationsLanding({ onTenantsBilling, onAdminDashboard, onOAuthProviders }) {
  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Integrations hub is per-tenant</div>
          <div className="card-subtitle">
            OAuth connections, delivery logs, and manual REST/webhook integrations live on a <strong>tenant workspace</strong>. Platform-wide switches and OAuth app credentials are on Admin Dashboard and OAuth Providers.
          </div>
        </div>
      </div>
      <div className="card-body">
        <p className="muted" style={{ fontSize: '0.9rem', lineHeight: 1.55, marginTop: 0 }}>
          You are signed in as super admin without a selected tenant. To connect HubSpot, Outlook, custom webhooks, or manage API keys for a customer, open <strong>Tenants &amp; billing</strong> and use <strong>View as</strong> for that tenant.
        </p>
        <ul className="muted integration-ia-legend" style={{ fontSize: '0.85rem', lineHeight: 1.65, margin: '16px 0 0', paddingLeft: '1.25rem' }}>
          <li><strong>Admin Dashboard</strong> — Integration Provider Availability (globally enable/disable provider types for all tenants) plus a compact OAuth summary and integration health.</li>
          <li><strong>OAuth Providers</strong> — Full editor for shared OAuth apps (client ID, secret, redirect URIs). Tenants never see secrets; they only Connect/Disconnect in the hub.</li>
          <li><strong>Integrations hub</strong> (tenant context) — CRM/calendar tiles, event settings, and a link to <strong>Manual REST / Webhook</strong> for custom endpoints and keys.</li>
        </ul>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
          <button type="button" className="button primary" onClick={onTenantsBilling}>Tenants &amp; billing</button>
          <button type="button" className="button outline" onClick={onAdminDashboard}>Admin Dashboard</button>
          <button type="button" className="button outline" onClick={onOAuthProviders}>OAuth Providers</button>
        </div>
      </div>
    </section>
  );
}

/* Components */

function Sidebar({ active, onChange, user, mobileOpen }) {
  var role = user && user.role ? user.role : null;
  var isSuperAdmin = role === 'super_admin';
  var allItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '◆' },
    { id: 'leads', label: 'Leads', icon: '◎' },
    { id: 'lead-finder', label: 'Lead Finder', icon: '⌕' },
    { id: 'calls', label: 'Calls', icon: '☎' },
    { id: 'follow-ups', label: 'Follow-ups', icon: '☑' },
    { id: 'campaigns', label: 'Campaigns', icon: '▶' },
    { id: 'monitor', label: 'Live Monitor', icon: '▣' },
    { id: 'admin-dashboard', label: 'Admin Dashboard', icon: '★' },
    { id: 'compliance-admin', label: 'Calling compliance', icon: '⚖' },
    { id: 'oauth-providers', label: 'OAuth Providers', icon: '🔑' },
    { id: 'plans', label: 'Plans', icon: '📋' },
    { id: 'billing', label: 'Tenants & Billing', icon: '$' },
    { id: 'cost-usage', label: 'Cost & Usage', icon: '📊' },
    { id: 'integrations', label: 'Integrations hub', icon: '🔌' },
    { id: 'flows', label: 'Flows', icon: '⚡' },
    { id: 'team', label: 'Team', icon: '👥' },
    { id: 'quality', label: 'Quality', icon: '✓' },
    { id: 'insights', label: 'Insights', icon: '📈' },
    { id: 'dnc', label: 'Do not call', icon: '⛔' },
    { id: 'audit-logs', label: 'Audit logs', icon: '📋' },
    { id: 'demo-controls', label: 'Demo controls', icon: '🎪' },
    { id: 'testlab', label: 'Test & Diagnostics', icon: '🔬' },
    { id: 'monitoring', label: 'Monitoring', icon: '📡' },
    { id: 'go-live', label: 'Go Live', icon: '🚀' },
    { id: 'settings', label: 'Settings', icon: '⚙' }
  ];
  var items = allItems.filter(function (it) { return canAccessNav(role, it.id, user); });
  return (
    <aside className={'sidebar' + (mobileOpen ? ' sidebar--mobile-open' : '')}>
      <div className="logo-row">
        <div className="logo-orb">
          <div className="logo-orb-inner" />
        </div>
        <div className="logo-text">
          <div className="logo-title">AI Call Leads Pro</div>
          <div className="logo-subtitle">Nightcoders Workspace</div>
        </div>
      </div>
      <nav className="nav">
        {items.map((item) => {
          var navLabel = item.label;
          if (item.id === 'billing') {
            var globalTenantsBilling = role === 'super_admin' && user && user.tenant_id == null;
            if (!globalTenantsBilling) navLabel = 'Plan & billing';
          }
          return (
          <button
            key={item.id}
            className={'nav-button ' + (active === item.id ? 'active' : '')}
            onClick={() => onChange(item.id)}
          >
            <div className="nav-button-icon">{item.icon}</div>
            <span>{navLabel}</span>
          </button>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        Outbound discovery calls run directly from your browser.
      </div>
    </aside>
  );
}

function DashboardConfigButton({ onRefresh }) {
  var _useState = useState(false);
  var show = _useState[0];
  var setShow = _useState[1];
  var _useState2 = useState(null);
  var config = _useState2[0];
  var setConfig = _useState2[1];
  useEffect(function () {
    if (show) {
      API.json('GET', '/api/dashboard/config').then(setConfig).catch(function () { setConfig({ presets: [], widgets: [] }); });
    }
  }, [show]);
  return React.createElement(React.Fragment, null,
    React.createElement('button', { className: 'button outline small', onClick: function () { setShow(true); } }, 'Configure'),
    show && config ? React.createElement('div', { className: 'modal-overlay', onClick: function () { setShow(false); } },
      React.createElement('div', { className: 'modal', onClick: function (e) { e.stopPropagation(); } },
        React.createElement('div', { className: 'modal-header' },
          React.createElement('h3', null, 'Dashboard config'),
          React.createElement('button', { type: 'button', className: 'modal-close', onClick: function () { setShow(false); } }, '×')
        ),
        React.createElement('div', { className: 'modal-body' },
          React.createElement('label', { className: 'field' },
            React.createElement('span', { className: 'field-label' }, 'Industry preset'),
            React.createElement('select', {
              value: config.preset_id || config.preset || '',
              onChange: function (e) {
                var v = e.target.value;
                if (!v) return;
                API.json('POST', '/api/dashboard/config', { preset_id: v }).then(function (res) {
                  if (res && (res.preset || res.widgets)) setConfig(function (prev) { return prev ? { ...prev, preset_id: res.preset || v, preset: res.preset, widgets: res.widgets || prev.widgets } : prev; });
                  showToast('Dashboard config saved.', 'success');
                  onRefresh && onRefresh();
                  setShow(false);
                }).catch(function (err) {
                  var msg = (err.status === 400 && err.data && err.data.error === 'no_tenant') ? 'Select or impersonate a tenant to save dashboard config.' : (err.data && err.data.error) ? err.data.error : (err.message || 'Failed to save');
                  showToast(msg, 'error');
                });
              }
            },
              React.createElement('option', { value: '' }, 'None'),
              (config.presets || []).map(function (p) {
                return React.createElement('option', { key: p.id, value: p.id }, p.label);
              })
            )
          )
        )
      )
    ) : null
  );
}

function Dashboard({ stats, usage, metricsSummary, user, onRefresh }) {
  var dashState = useState(null);
  var dashStats = dashState[0];
  var setDashStats = dashState[1];
  useEffect(function () {
    if (!isTenantContext(user)) return undefined;
    var cancelled = false;
    API.json('GET', '/api/dashboard/stats').then(function (d) {
      if (!cancelled && d && !d.error) setDashStats(d);
    }).catch(function () {});
    return function () { cancelled = true; };
  }, [user, onRefresh]);
  var callsToday = (dashStats && dashStats.callsToday != null)
    ? dashStats.callsToday
    : ((metricsSummary && metricsSummary.calls_today != null) ? metricsSummary.calls_today : stats.callsToday);
  var avgDuration = (metricsSummary && metricsSummary.avg_duration_min != null) ? metricsSummary.avg_duration_min : stats.avgDuration;
  var escalations = (metricsSummary && metricsSummary.escalations != null) ? metricsSummary.escalations : 0;
  var outcomesTop5 = (metricsSummary && metricsSummary.outcomes_top5) ? metricsSummary.outcomes_top5 : [];
  var _q = useState({ queued: 0, running: 0, completed: 0 });
  var queueStatus = _q[0];
  var setQueueStatus = _q[1];
  var _pf = useState(null);
  var planFeatures = _pf[0];
  var setPlanFeatures = _pf[1];
  useEffect(function () {
    if (!isTenantContext(user)) return;
    API.json('GET', '/api/calls/queue/status').then(function (r) {
      if (r && typeof r.queued === 'number') setQueueStatus({ queued: r.queued, running: r.running || r.activeCount || 0, completed: r.completed || r.completedCount || 0 });
    }).catch(function () {});
  }, [user]);
  useEffect(function () {
    if (!isTenantContext(user)) return;
    API.json('GET', '/api/tenant/plan-features').then(function (r) {
      if (r && r.ok) setPlanFeatures(r);
    }).catch(function () { setPlanFeatures(null); });
  }, [user]);
  return (
    <React.Fragment>
    {user && (user.role === 'tenant_admin' || (user.role === 'super_admin' && user.tenant_id != null)) ? <FinishSetupCard api={API.json} user={user} /> : null}
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Pipeline overview</div>
          <div className="card-subtitle">
            High-level snapshot of your AI calls and lead outcomes.
          </div>
        </div>
        <div className="hud-status-pill">
          <span className="hud-status-dot" />
          <span>Systems nominal</span>
        </div>
        {(user && (user.role === 'tenant_admin' || user.role === 'super_admin')) ? React.createElement(DashboardConfigButton, { onRefresh: onRefresh }) : null}
      </div>
      <div className="card-body">
        <div className="stats-grid">
          <div className="stat-tile">
            <div className="stat-label">Total leads</div>
            <div className="stat-value">{stats.totalLeads}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Interested / hot</div>
            <div className="stat-value">{stats.interestedLeads}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Do-not-call</div>
            <div className="stat-value">{stats.dncLeads}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Calls today</div>
            <div className="stat-value">{callsToday}</div>
          </div>
          <div className="stat-tile stat-tile--duration">
            <div className="stat-label">Avg call duration</div>
            <div className="stat-value">
              <span className="hud-wave">
                <span />
                <span />
                <span />
                <span />
                <span />
              </span>
              <span>{avgDuration ? (typeof avgDuration === 'number' ? avgDuration.toFixed(1) : avgDuration) + ' min' : '—'}</span>
            </div>
          </div>
          <div className="stat-tile stat-tile--hotrate">
            <div className="stat-main">
              <div className="stat-label">Hot lead rate</div>
              <div className="stat-value">
        {stats.hotRate ? stats.hotRate.toFixed(0) + '%' : '—'}
              </div>
            </div>
            <div className="hud-radar">
              <div className="hud-radar-dot" />
            </div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Escalations / handoffs</div>
            <div className="stat-value">{escalations}</div>
          </div>
          <div className="stat-tile" style={{ gridColumn: 'span 1' }}>
            <div className="stat-label">Call queue</div>
            <div className="stat-value" style={{ fontSize: '0.9rem' }}>
              <span title="Queued">Q: {queueStatus.queued}</span>
              <span style={{ marginLeft: 8 }} title="Running">R: {queueStatus.running}</span>
              <span style={{ marginLeft: 8 }} title="Completed">C: {queueStatus.completed}</span>
            </div>
          </div>

        {usage && usage.scope === 'tenant' && (
          <div className="dashboard-usage-block">
            <div>
              <span className="usage-label">Minutes this month</span>
              <div className="usage-value">{formatMeasure(usage.minutesThisMonth)}</div>
            </div>
            {typeof usage.includedMinutes === 'number' && usage.includedMinutes > 0 && (
              <React.Fragment>
                <div>
                  <span className="usage-label">Included minutes</span>
                  <div className="usage-value">{usage.includedMinutes}</div>
                </div>
                <div>
                  <span className="usage-label">Minutes remaining</span>
                  <div className="usage-value">
                    {usage.remainingMinutes != null ? usage.remainingMinutes : '—'}
                  </div>
                </div>
                <div style={{ flexBasis: '100%', maxWidth: 320 }}>
                  <div className="usage-label" style={{ marginBottom: 2 }}>Usage</div>
                  <div className="dashboard-usage-bar-track">
                    <div
                      className="dashboard-usage-bar-fill"
                      style={{
                        transform: 'scaleX(' + Math.min(1, usage.includedMinutes ? (usage.minutesThisMonth / usage.includedMinutes) : 0).toFixed(2) + ')'
                      }}
                    />
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        )}
        </div>

        {planFeatures && planFeatures.ok && planFeatures.plan_key ? (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--color-bg-elevated)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Your Plan: {String(planFeatures.plan_key).charAt(0).toUpperCase() + String(planFeatures.plan_key).slice(1)}</div>
            <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>Includes:</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.85rem', color: 'var(--color-fg)' }}>
              {planFeatures.plan_includes && planFeatures.plan_includes.integration_list && planFeatures.plan_includes.integration_list.length ? planFeatures.plan_includes.integration_list.map(function (k) {
                var label = k.replace(/_/g, ' ');
                if (k === 'custom_webhook') label = 'Custom Webhooks';
                if (k === 'live_transfer') label = 'Live Agent Transfer';
                if (k === 'premium_calls') label = 'Premium Calls';
                if (k === 'call_recording') label = 'Call Recording';
                return <li key={k}>✓ {label}</li>;
              }) : <li>✓ Basic features</li>}
            </ul>
            {planFeatures.plan_includes && planFeatures.plan_includes.catalog_integration_list && planFeatures.plan_includes.integration_list &&
              planFeatures.plan_includes.catalog_integration_list.slice().sort().join(',') !== planFeatures.plan_includes.integration_list.slice().sort().join(',') ? (
              <div style={{ fontSize: '0.8rem', marginTop: 8, color: 'var(--color-muted)' }}>
                Plan SKU feature list (before per-tenant overrides): {planFeatures.plan_includes.catalog_integration_list.join(', ')}
              </div>
            ) : null}
            {planFeatures.plan_includes && (planFeatures.plan_includes.monthly_calls != null || planFeatures.plan_includes.agents != null) ? (
              <div style={{ fontSize: '0.85rem', marginTop: 8, color: 'var(--color-muted)' }}>
                Limit: {planFeatures.plan_includes.monthly_calls != null ? planFeatures.plan_includes.monthly_calls.toLocaleString() + ' calls/month' : ''}
                {planFeatures.plan_includes.monthly_calls != null && planFeatures.plan_includes.agents != null ? ' · ' : ''}
                {planFeatures.plan_includes.agents != null ? planFeatures.plan_includes.agents + ' agents' : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        {outcomesTop5.length > 0 ? (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div className="stat-label" style={{ marginBottom: 8 }}>Outcomes today (top 5)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {outcomesTop5.map(function (o) {
                return React.createElement('span', {
                  key: o.outcome_code,
                  className: 'badge',
                  style: { marginRight: 4 }
                }, o.outcome_code + ': ' + o.count);
              })}
            </div>
          </div>
        ) : null}

        <p className="dashboard-footer-text">
          Metrics from calls, outcomes, and handoff events. As you start running real
          traffic, this dashboard becomes your quick-glance health check.
          {' '}<TimesShown />
        </p>
      </div>
    </section>
    </React.Fragment>
  );
}

var DEFAULT_REDIRECT_BASE = 'https://api.nightcoders.com';
if (typeof window !== 'undefined' && window.API_BASE) DEFAULT_REDIRECT_BASE = (window.API_BASE || '').replace(/\/$/, '');
var OAUTH_PROVIDER_IDS = ['hubspot', 'outlook', 'salesforce', 'google'];
var OAUTH_PROVIDER_LABELS = { hubspot: 'HubSpot', outlook: 'Outlook', salesforce: 'Salesforce', google: 'Google Calendar' };

function OAuthProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_id: '', client_secret: '', redirect_uri: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState({});
  useEffect(function () {
    API.json('GET', '/api/admin/oauth-providers').then(function (r) {
      if (r && r.ok && r.providers) setProviders(r.providers);
    }).catch(function () { setProviders([]); }).finally(function () { setLoading(false); });
  }, []);
  function getRow(providerKey) {
    return (providers || []).find(function (r) { return (r.provider_key || r.provider) === providerKey; }) || null;
  }
  function redirectUriHelp(providerKey) {
    var base = DEFAULT_REDIRECT_BASE;
    if (base.indexOf('/api') === -1) base = base + '/api';
    return base + '/integrations/' + providerKey + '/callback';
  }
  function openEdit(providerKey) {
    var row = getRow(providerKey);
    var defaultUri = redirectUriHelp(providerKey);
    setEditing(providerKey);
    setForm({
      client_id: '',
      client_secret: '',
      redirect_uri: (row && row.redirect_uri) ? row.redirect_uri : defaultUri,
      enabled: row ? !!row.enabled : true
    });
  }
  function saveProvider(providerKey) {
    setSaving(true);
    var payload = {
      enabled: form.enabled,
      client_id: form.client_id && form.client_id.trim() ? form.client_id.trim() : undefined,
      client_secret: form.client_secret && form.client_secret.trim() ? form.client_secret.trim() : undefined,
      redirect_uri: form.redirect_uri && form.redirect_uri.trim() ? form.redirect_uri.trim() : undefined
    };
    var method = getRow(providerKey) ? 'PUT' : 'POST';
    var url = method === 'PUT' ? '/api/admin/oauth-providers/' + encodeURIComponent(providerKey) : '/api/admin/oauth-providers';
    if (method === 'POST') payload.provider = providerKey;
    API.json(method, url, payload).then(function () {
      setEditing(null);
      return API.json('GET', '/api/admin/oauth-providers');
    }).then(function (r) {
      if (r && r.ok && r.providers) setProviders(r.providers);
    }).catch(function (e) {
      alert((e.data && e.data.error) || e.data.detail || e.message || 'Save failed');
    }).finally(function () { setSaving(false); });
  }
  function runTest(providerKey) {
    API.json('GET', '/api/admin/oauth-providers/test?provider=' + encodeURIComponent(providerKey)).then(function (res) {
      setTestResult(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[providerKey] = res; return n; });
    }).catch(function (e) {
      setTestResult(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[providerKey] = { ok: false, error: (e.data && e.data.error) || e.message }; return n; });
    });
  }
  function openAuthUrl(providerKey) {
    API.json('GET', '/api/admin/oauth-providers/' + encodeURIComponent(providerKey) + '/auth-url').then(function (r) {
      if (r && r.ok && r.url) window.open(r.url, '_blank');
      else alert('No URL returned');
    }).catch(function (e) {
      var msg = (e.data && (e.data.detail || e.data.error)) || e.message || 'Failed to generate URL';
      if ((e.data && e.data.error) === 'oauth_not_configured') msg = 'OAuth app not configured. Use a real Developer App Client ID (not an email or placeholder).';
      alert(msg);
    });
  }
  if (loading) {
    return (
      <section className="card">
        <div className="card-header"><div className="card-title">OAuth Providers</div></div>
        <div className="card-body">Loading…</div>
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">OAuth Providers</div>
          <div className="card-subtitle">
            Super Admin only: create a Developer App in each provider (HubSpot, Outlook, etc.) and paste the real Client ID and Client Secret here. Tenants only use Connect/Disconnect in the per-tenant Integrations hub; they never see or enter secrets.
          </div>
        </div>
      </div>
      <div className="card-body">
        <p className="muted" style={{ marginBottom: 16, fontSize: '0.875rem' }}>
          Create a HubSpot Developer App at developers.hubspot.com, then paste the app&apos;s Client ID and Client Secret (not an email). Redirect URI must match exactly: <code style={{ background: 'var(--color-bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>{DEFAULT_REDIRECT_BASE}/api/integrations/hubspot/callback</code> (or <code>{DEFAULT_REDIRECT_BASE}/api/integrations/&lt;provider&gt;/callback</code> for others).
        </p>
        {OAUTH_PROVIDER_IDS.map(function (providerKey) {
          var row = getRow(providerKey);
          var configured = row && row.client_id_present && row.redirect_uri;
          var isEditing = editing === providerKey;
          var testRes = testResult[providerKey];
          return (
            <div key={providerKey} className="card" style={{ marginBottom: 16, background: 'var(--color-bg-elevated)', border: '1px solid var(--border)' }}>
              <div className="card-header" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{OAUTH_PROVIDER_LABELS[providerKey] || providerKey}</span>
                  <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                    Configured: {configured ? 'Yes' : 'No'}
                    {row && row.updated_at ? ' · Updated ' : ''}
                    {row && row.updated_at ? <WhenText value={row.updated_at} style="full" /> : null}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {row && row.redirect_uri ? (
                    <button type="button" className="button small outline" onClick={function () {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(row.redirect_uri).then(function () { showToast('Redirect URI copied'); }).catch(function () {});
                      }
                    }}>Copy redirect URI</button>
                  ) : null}
                  <button type="button" className="button small outline" onClick={function () { runTest(providerKey); }}>Test</button>
                  {(providerKey === 'hubspot' || providerKey === 'outlook') && configured ? (
                    <button type="button" className="button small outline" onClick={function () { openAuthUrl(providerKey); }}>Generate auth URL</button>
                  ) : null}
                  {!isEditing ? (
                    <button type="button" className="button small primary" onClick={function () { openEdit(providerKey); }}>{row ? 'Edit' : 'Configure'}</button>
                  ) : null}
                </div>
              </div>
              {isEditing ? (
                <div className="card-body" style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="field-label">Client ID</label>
                    <input type="text" className="input" value={form.client_id} onChange={function (e) { setForm(function (f) { return { ...f, client_id: e.target.value }; }); }} placeholder={row && row.client_id_present ? 'Leave blank to keep existing' : 'Paste from HubSpot Developer App (not an email)'} />
                  </div>
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="field-label">Client Secret</label>
                    <input type="password" className="input" value={form.client_secret} onChange={function (e) { setForm(function (f) { return { ...f, client_secret: e.target.value }; }); }} placeholder={row && row.client_secret_present ? '•••••• Replace secret' : 'Required for OAuth'} />
                    {row && row.client_secret_present ? <span className="muted" style={{ fontSize: '0.75rem', marginLeft: 8 }}>Currently set. Enter a new value to replace.</span> : null}
                  </div>
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="field-label">Redirect URI</label>
                    <input type="text" className="input" value={form.redirect_uri} onChange={function (e) { setForm(function (f) { return { ...f, redirect_uri: e.target.value }; }); }} placeholder={redirectUriHelp(providerKey)} />
                    <p className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>Use exactly: {redirectUriHelp(providerKey)}</p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.enabled} onChange={function (e) { setForm(function (f) { return { ...f, enabled: e.target.checked }; }); }} />
                    <span>Enabled (tenants can connect)</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="button primary small" disabled={saving} onClick={function () { saveProvider(providerKey); }}>{saving ? 'Saving…' : 'Save'}</button>
                    <button type="button" className="button outline small" onClick={function () { setEditing(null); }}>Cancel</button>
                  </div>
                </div>
              ) : null}
              {testRes && !isEditing ? (
                <div style={{ padding: '8px 16px 12px', fontSize: '0.8rem', color: testRes.ok ? 'var(--color-success)' : 'var(--color-error)' }}>
                  {testRes.ok ? 'Test OK' : (testRes.error || (testRes.missing && testRes.missing.join(', ')) || 'Test failed')}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AdminDashboard({ adminStats, loading }) {
  const [integrationHealth, setIntegrationHealth] = useState(null);
  const [providerAvailability, setProviderAvailability] = useState(null);
  const [providerSaving, setProviderSaving] = useState(false);
  const [auditSummary, setAuditSummary] = useState(null);
  const [oauthProviders, setOAuthProviders] = useState([]);
  const [oauthTestResult, setOAuthTestResult] = useState({});
  const [oauthEditing, setOAuthEditing] = useState(null);
  const [oauthForm, setOAuthForm] = useState({ client_id: '', client_secret: '', redirect_uri: '', enabled: true });
  const [oauthSaving, setOAuthSaving] = useState(false);
  useEffect(function () {
    if (!adminStats) return;
    API.json('GET', '/api/admin/integrations/health').then(setIntegrationHealth).catch(function () { setIntegrationHealth(null); });
  }, [adminStats]);
  useEffect(function () {
    API.json('GET', '/api/admin/system/audit-summary').then(function (r) {
      if (r && r.ok) setAuditSummary(r);
    }).catch(function () { setAuditSummary(null); });
  }, []);
  useEffect(function () {
    API.json('GET', '/api/admin/integrations/providers').then(function (r) {
      if (r && r.ok && r.providers) setProviderAvailability(r.providers);
    }).catch(function () { setProviderAvailability(null); });
  }, []);
  useEffect(function () {
    API.json('GET', '/api/admin/oauth-providers').then(function (r) {
      if (r && r.ok && r.providers) setOAuthProviders(r.providers);
    }).catch(function () { setOAuthProviders([]); });
  }, []);
  if (loading) {
    return (
      <section className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Super Admin Dashboard</div>
            <div className="card-subtitle">Loading platform-wide metrics…</div>
          </div>
        </div>
        <div className="card-body">Loading…</div>
      </section>
    );
  }

  if (!adminStats) {
    return (
      <section className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Super Admin Dashboard</div>
            <div className="card-subtitle">
              View high-level telemetry across all tenants, leads, and calls.
            </div>
          </div>
        </div>
        <div className="card-body">
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            No stats available yet. Once your tenants start making calls, you&apos;ll see aggregate
            metrics here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Super Admin Dashboard</div>
          <div className="card-subtitle">
            CRM-style overview of usage across all tenants in the workspace.
          </div>
        </div>
        <div className="hud-status-pill">
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              display: 'inline-block',
              marginRight: 6,
              background: '#4ade80',
              boxShadow: '0 0 10px rgba(74, 222, 128, 0.9)'
            }}
          />
          <span>Platform online</span>
        </div>
      </div>
      <div className="card-body">
        <div className="stats-grid">
          <div className="stat-tile">
            <div className="stat-label">Tenants</div>
            <div className="stat-value">{adminStats.tenantsCount}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Leads (all tenants)</div>
            <div className="stat-value">{adminStats.leadsCount}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Calls (all tenants)</div>
            <div className="stat-value">{adminStats.callsCount}</div>
          </div>
          <div className="stat-tile">
            <div className="stat-label">Minutes this month</div>
            <div className="stat-value">{formatMeasure(adminStats.minutesThisMonth)}</div>
          </div>
        </div>
        <div className="admin-dashboard-grid">
          <div className="card admin-dash-subcard">
            <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
              <div className="card-title" style={{ fontSize: '0.9rem' }}>Usage summary</div>
              <div className="card-subtitle" style={{ fontSize: '0.75rem' }}>
                High-level volumes for calls and minutes. For tenant-level detail, open Tenants &amp; Billing.
              </div>
            </div>
            <div className="card-body admin-dash-subcard-body" style={{ padding: 16 }}>
              <ul className="admin-dash-stat-list">
                <li>Total calls: {adminStats.callsCount}</li>
                <li>Total minutes billed: {formatMeasure(adminStats.minutesThisMonth)}</li>
                <li>Total leads under management: {adminStats.leadsCount}</li>
                <li>Active tenants: {adminStats.tenantsCount}</li>
              </ul>
            </div>
          </div>
          <div className="card admin-dash-subcard">
            <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
              <div className="card-title" style={{ fontSize: '0.9rem' }}>System status</div>
              <div className="card-subtitle" style={{ fontSize: '0.75rem' }}>
                Lightweight health indicators based on API reachability.
              </div>
            </div>
            <div className="card-body admin-dash-subcard-body" style={{ padding: 16, fontSize: '0.8rem' }}>
              <div>Auth API: <strong>OK</strong></div>
              <div>Leads API: <strong>OK</strong></div>
              <div>Calls API: <strong>OK</strong></div>
              <div>Twilio webhooks: <strong>Configured</strong></div>
              <p className="muted" style={{ marginTop: 8, fontSize: '0.8rem' }}>
                For deeper infrastructure metrics (CPU, memory, Twilio errors), wire this panel into
                your observability stack or extend the admin stats endpoint.
              </p>
            </div>
          </div>
        </div>
        {providerAvailability && (
          <div style={{ marginTop: 16 }}>
            <div className="card admin-dash-subcard">
              <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
                <div className="card-title" style={{ fontSize: '0.9rem' }}>Integration Provider Availability</div>
                <div className="card-subtitle" style={{ fontSize: '0.75rem' }}>
                  Platform-wide: turn provider <em>types</em> on or off for every tenant. This does not store OAuth secrets (use OAuth Providers) and does not connect accounts for a tenant (use Integrations hub while impersonating).
                </div>
              </div>
              <div className="card-body admin-dash-subcard-body" style={{ padding: 16, fontSize: '0.8rem' }}>
                {['hubspot', 'salesforce', 'outlook', 'google_calendar', 'webhook'].map(function (p) {
                  var label = p === 'hubspot' ? 'HubSpot' : p === 'salesforce' ? 'Salesforce' : p === 'outlook' ? 'Outlook' : p === 'google_calendar' ? 'Google Calendar' : 'Custom Webhook';
                  var enabled = providerAvailability[p] !== false;
                  return (
                    <div key={p} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                      <label style={{ flex: 1 }}>{label}</label>
                      <button
                        type="button"
                        className={'button small ' + (enabled ? 'primary' : 'outline')}
                        disabled={providerSaving}
                        onClick={function () {
                          var next = {};
                          for (var k in providerAvailability) next[k] = providerAvailability[k];
                          next[p] = !enabled;
                          setProviderSaving(true);
                          API.json('PATCH', '/api/admin/integrations/providers', { providers: next }).then(function () {
                            setProviderAvailability(next);
                          }).catch(function () { alert('Failed to save'); }).finally(function () { setProviderSaving(false); });
                        }}
                      >
                        {enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <div className="card admin-dash-subcard">
            <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
              <div className="card-title" style={{ fontSize: '0.9rem' }}>OAuth Providers (platform)</div>
              <div className="card-subtitle" style={{ fontSize: '0.75rem' }}>
                Shared developer apps: client ID, secret, redirect URIs. Tenants only use Connect/Disconnect in the per-tenant Integrations hub; they never see these secrets.
              </div>
            </div>
            <div className="card-body admin-dash-subcard-body" style={{ padding: 16, fontSize: '0.8rem' }}>
              {oauthProviders.length === 0 && <p className="muted">Loading OAuth providers…</p>}
              {oauthProviders.length > 0 && (
                <div className="table-wrap">
                <table className="table" style={{ fontSize: '0.75rem' }}>
                  <thead><tr><th>Provider</th><th>Client ID</th><th>Secret</th><th>Redirect URI</th><th>Enabled</th><th>Test</th><th>Edit</th></tr></thead>
                  <tbody>
                    {oauthProviders.map(function (r) {
                      var isEditing = oauthEditing === r.provider;
                      var testRes = oauthTestResult[r.provider];
                      return (
                        <tr key={r.provider}>
                          <td>{r.provider}</td>
                          <td>{r.client_id || <span className="muted">—</span>}</td>
                          <td>{r.has_client_secret ? '***' : <span className="muted">—</span>}</td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.redirect_uri || ''}>{r.redirect_uri || '—'}</td>
                          <td>{r.enabled ? 'Yes' : 'No'}</td>
                          <td>
                            <button type="button" className="button small outline" onClick={function () {
                              API.json('GET', '/api/admin/oauth-providers/test?provider=' + encodeURIComponent(r.provider)).then(function (res) {
                                setOAuthTestResult(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[r.provider] = res; return n; });
                              }).catch(function (e) { setOAuthTestResult(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[r.provider] = { ok: false, error: (e.data && e.data.error) || e.message }; return n; }); });
                            }}>Test</button>
                            {testRes && <span style={{ marginLeft: 4, color: testRes.ok ? '#4ade80' : '#f87171' }}>{testRes.ok ? 'OK' : (testRes.error || (testRes.missing && testRes.missing.join(', ')))}</span>}
                          </td>
                          <td>
                            <button type="button" className="button small outline" onClick={function () {
                              setOAuthEditing(r.provider);
                              setOAuthForm({ client_id: '', client_secret: '', redirect_uri: r.redirect_uri || '', enabled: r.enabled });
                            }}>Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
              {oauthEditing && (
                <div className="admin-dash-oauth-edit" style={{ marginTop: 16, padding: 16, borderRadius: 8 }}>
                  <div className="card-title" style={{ fontSize: '0.9rem', marginBottom: 8 }}>Edit OAuth: {oauthEditing}</div>
                  <label className="field"><span className="field-label">Client ID</span><input type="text" className="input" value={oauthForm.client_id} onChange={function (e) { setOAuthForm(function (f) { return { client_id: e.target.value, client_secret: f.client_secret, redirect_uri: f.redirect_uri, enabled: f.enabled }; }); }} placeholder="Leave blank to keep existing" /></label>
                  <label className="field"><span className="field-label">Client Secret</span><input type="password" className="input" value={oauthForm.client_secret} onChange={function (e) { setOAuthForm(function (f) { return { client_id: f.client_id, client_secret: e.target.value, redirect_uri: f.redirect_uri, enabled: f.enabled }; }); }} placeholder="Leave blank to keep existing" /></label>
                  <label className="field"><span className="field-label">Redirect URI</span><input type="text" className="input" value={oauthForm.redirect_uri} onChange={function (e) { setOAuthForm(function (f) { return { client_id: f.client_id, client_secret: f.client_secret, redirect_uri: e.target.value, enabled: f.enabled }; }); }} placeholder="e.g. https://your-domain.com/api/integrations/hubspot/callback" /></label>
                  <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={oauthForm.enabled} onChange={function (e) { setOAuthForm(function (f) { return { client_id: f.client_id, client_secret: f.client_secret, redirect_uri: f.redirect_uri, enabled: e.target.checked }; }); }} />Enabled (tenants can connect)</label>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <button type="button" className="button primary small" disabled={oauthSaving} onClick={function () {
                      setOAuthSaving(true);
                      API.json('POST', '/api/admin/oauth-providers', { provider: oauthEditing, client_id: oauthForm.client_id || undefined, client_secret: oauthForm.client_secret || undefined, redirect_uri: oauthForm.redirect_uri || undefined, enabled: oauthForm.enabled }).then(function () {
                        setOAuthEditing(null);
                        API.json('GET', '/api/admin/oauth-providers').then(function (r) { if (r && r.ok && r.providers) setOAuthProviders(r.providers); });
                      }).catch(function (e) { alert((e.data && e.data.error) || e.message || 'Save failed'); }).finally(function () { setOAuthSaving(false); });
                    }}>Save</button>
                    <button type="button" className="button outline small" onClick={function () { setOAuthEditing(null); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {integrationHealth && (
          <div style={{ marginTop: 16 }}>
            <div className="card admin-dash-subcard">
              <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
                <div className="card-title" style={{ fontSize: '0.9rem' }}>Integrations health</div>
                <div className="card-subtitle" style={{ fontSize: '0.75rem' }}>
                  Cross-tenant view of connection status and failed deliveries. Per-tenant Connect/Disconnect remains in Integrations hub.
                </div>
              </div>
              <div className="card-body admin-dash-subcard-body" style={{ padding: 16, fontSize: '0.8rem' }}>
                <div style={{ marginBottom: 8 }}>Pending events: <strong>{(integrationHealth && integrationHealth.pending_events_count != null) ? integrationHealth.pending_events_count : 0}</strong></div>
                {(integrationHealth.integrations || []).length > 0 && (
                  <div className="table-wrap">
                  <table className="table" style={{ fontSize: '0.75rem' }}>
                    <thead><tr><th>Tenant</th><th>Provider</th><th>Status</th><th>Last sync</th><th>Error</th></tr></thead>
                    <tbody>
                      {(integrationHealth.integrations || []).map(function (r, i) {
                        return (
                          <tr key={i}>
                            <td>{r.tenant_name || r.tenant_id}</td>
                            <td>{r.provider}</td>
                            <td>{r.status}</td>
                            <td><WhenText value={r.last_sync_at} /></td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.last_error || ''}>{r.last_error || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
                {(integrationHealth.failed_deliveries || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="card-subtitle" style={{ fontSize: '0.75rem', marginBottom: 4 }}>Failed deliveries (retry counts)</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {(integrationHealth.failed_deliveries || []).map(function (f, i) {
                        return <li key={i}>Tenant {f.tenant_id} / {f.provider}: {f.count} failed, max attempts {f.max_attempts}</li>;
                      })}
                    </ul>
                  </div>
                )}
                {(!integrationHealth.integrations || integrationHealth.integrations.length === 0) && (!integrationHealth.failed_deliveries || integrationHealth.failed_deliveries.length === 0) && (
                  <p className="muted">No integrations or failed events.</p>
                )}
                {auditSummary && auditSummary.missingWires && auditSummary.missingWires.length > 0 && (
                  <div className="card admin-dash-subcard" style={{ marginTop: 16 }}>
                    <div className="card-header" style={{ padding: '12px 16px 0 16px' }}>
                      <div className="card-title" style={{ fontSize: '0.9rem' }}>System audit: next wires to do</div>
                    </div>
                    <div className="card-body admin-dash-subcard-body" style={{ padding: 16 }}>
                      <ul className="admin-dash-stat-list admin-dash-stat-list--disc">
                        {auditSummary.missingWires.map(function (item, i) { return (<li key={i}>{item}</li>); })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


function LiveMonitor({ events, calls }) {
  const list = Array.isArray(calls) ? calls : [];

  return (
    <section className="card live-monitor-card">
      <div className="card-header live-monitor-header">
        <div>
          <div className="card-title">Live Calls</div>
          <div className="card-subtitle live-monitor-subtitle">
            Monitor and interject active AI calls in real-time. Join via Phone rings{' '}
            <strong>your</strong> profile number (not the tenant handoff line).
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <div style={{ padding: 16, color: "#9ca3af" }}>No active calls.</div>
      ) : (
        <div className="admin-table-scroll">
        <table className="table table--mobile-cards live-monitor-table">
          <thead>
            <tr>
              <th>Call SID</th>
              <th>Phone</th>
              <th>Lead</th>
              <th>Started</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.callSid}>
                <td data-label="Call SID" style={{ wordBreak: 'break-all', fontSize: '0.72rem' }}>{c.callSid}</td>
                <td data-label="Phone">{c.phone || '—'}</td>
                <td data-label="Lead">{c.leadName || '—'}</td>
                <td data-label="Started"><WhenText value={c.startedAt} /></td>
                <td className="mobile-actions-cell live-monitor-actions">
                  <button
                    type="button"
                    className="button small primary"
                    onClick={() => joinViaBrowser(c.callSid)}
                  >
                    🎤 Browser
                  </button>

                  <button
                    type="button"
                    className="button small outline"
                    onClick={() => joinViaPhone(c.callSid)}
                  >
                    📞 Phone
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '8px 16px 12px' }}><TimesShown /></div>
        </div>
      )}
    </section>
  );
}


function LeadsView({ leads, page, total, onPageChange, refresh, loadError, onLeadContext, openLeadId, onOpenLeadHandled, user, timezone }) {
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [addCampaignOpen, setAddCampaignOpen] = useState(false);

  useEffect(function () {
    if (!openLeadId) return undefined;
    var cancelled = false;
    var onPage = (Array.isArray(leads) ? leads : []).find(function (l) { return String(l.id) === String(openLeadId); });
    if (onPage) {
      setSelected(onPage);
      onLeadContext && onLeadContext(onPage);
      onOpenLeadHandled && onOpenLeadHandled();
      return undefined;
    }
    API.json('GET', '/api/leads/' + encodeURIComponent(openLeadId)).then(function (res) {
      if (cancelled || !res || !res.lead) return;
      setSelected(res.lead);
      onLeadContext && onLeadContext(res.lead);
    }).catch(function () {
      if (!cancelled) showToast('That lead could not be opened.', 'error');
    }).finally(function () {
      if (!cancelled && onOpenLeadHandled) onOpenLeadHandled();
    });
    return function () { cancelled = true; };
  }, [openLeadId]);
  const [queueing, setQueueing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [leadQuery, setLeadQuery] = useState('');
  const [bulkResearching, setBulkResearching] = useState(false);
  const [importMode, setImportMode] = useState(null); // 'leads' or 'dnc'
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importListName, setImportListName] = useState('');
  const [importError, setImportError] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [dncSourceType, setDncSourceType] = useState('internal_company_dnc');
  const [importConsent, setImportConsent] = useState(null);
  const [importSession, setImportSession] = useState(0);
  const [bulkConsentOpen, setBulkConsentOpen] = useState(false);
  const canAttestConsent = !!user && (user.role === 'tenant_admin' || user.role === 'super_admin');
  const [addLeadModal, setAddLeadModal] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ name: '', phone: '', email: '', company: '', industry: '', city: '', state: '', status: 'new', notes: '' });
  const [addLeadBusy, setAddLeadBusy] = useState(false);
  const [addLeadError, setAddLeadError] = useState('');

  const safeLeads = Array.isArray(leads) ? leads : [];
  const filteredLeads = safeLeads.filter((l) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'interested') return l.status === 'interested';
    if (statusFilter === 'not_interested') return l.status === 'not_interested';
    if (statusFilter === 'dnc') return !!l.dnc_flag || l.status === 'dnc' || !!l.dnc_list_match;
    if (statusFilter === 'new') {
      return !l.dnc_flag && !l.dnc_list_match && (l.status === 'new' || !l.status);
    }
    return true;
  }).filter((l) => {
    var q = leadQuery.trim().toLowerCase();
    if (!q) return true;
    var hay = [l.name, l.phone, l.email, l.company, l.industry, l.city, l.state].map(function (part) {
      return part == null ? '' : String(part);
    }).join(' ').toLowerCase();
    return hay.indexOf(q) >= 0;
  });


  async function handleAddLead(e) {
    e.preventDefault();
    if (!addLeadForm.phone || !addLeadForm.phone.trim()) {
      setAddLeadError('Phone is required.');
      return;
    }
    setAddLeadBusy(true);
    setAddLeadError('');
    try {
      const res = await API.json('POST', '/api/leads', addLeadForm);
      if (res && res.lead) {
        setAddLeadModal(false);
        setAddLeadForm({ name: '', phone: '', email: '', company: '', industry: '', city: '', state: '', status: 'new', notes: '' });
        refresh && refresh();
      }
    } catch (err) {
      setAddLeadError(uiErrorMessage(err, 'Could not add that lead. Check the phone number and try again.'));
    } finally {
      setAddLeadBusy(false);
    }
  }

  async function bulkResearch() {
    if (!window.confirm('Run AI research for all un-researched, non-DNC leads on this workspace?')) return;
    setBulkResearching(true);
    try {
      const res = await API.json('POST', '/api/leads/research-all', {});
      alert('Bulk research complete. Processed ' + res.processed + ' leads' + (res.failed ? ('; failed: ' + res.failed) : '') + '.');
      refresh && refresh();
    } catch (e) {
      alert('Bulk research failed: ' + e.message);
    } finally {
      setBulkResearching(false);
    }
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const eligibles = filteredLeads.filter((l) => !l.dnc_flag && !l.dnc_list_match).map((l) => l.id);
    if (selectedIds.size >= eligibles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibles));
    }
  }

  async function queueSelected() {
    const ids = Array.from(selectedIds).filter((id) => {
      const l = filteredLeads.find((le) => le.id === id);
      return l && !l.dnc_flag && !l.dnc_list_match;
    });
    if (!ids.length) {
      alert('Select at least one non-DNC lead to queue.');
      return;
    }
    setQueueing(true);
    try {
      const r = await API.json('POST', '/api/calls/batch', { leadIds: ids });
      var msg = 'Calling ' + (r.started != null ? r.started : (r.queued != null ? r.queued : 0)) + ' leads';
      if (r.skipped) msg += ' (' + r.skipped + ' skipped)';
      msg += '.';
      alert(msg);
      setSelectedIds(new Set());
      refresh && refresh();
    } catch (e) {
      alert('Failed to queue: ' + (e.message || 'Unknown error'));
    } finally {
      setQueueing(false);
    }
  }


  function openImport(mode) {
    setImportMode(mode);
    setImportFile(null);
    setImportPreview(null);
    setImportListName('');
    setImportError('');
    setImportBusy(false);
    setImportConsent(null);
    setImportSession(function (n) { return n + 1; });
    if (mode === 'dnc') setDncSourceType('internal_company_dnc');
  }

  function closeImport() {
    setImportMode(null);
    setImportFile(null);
    setImportPreview(null);
    setImportListName('');
    setImportError('');
    setImportBusy(false);
  }

  function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    setImportFile(file || null);
    setImportPreview(null);
    setImportError('');
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function (evt) {
      try {
        const text = String(evt.target.result || '');
        const lines = text.split(/\r?\n/).filter(function (line) {
          return line.trim().length > 0;
        });
        if (!lines.length) {
          setImportError('File appears to be empty.');
          return;
        }
        if (importMode === 'dnc' && /\.txt$/i.test(file.name)) {
          const phoneLines = lines.filter(function (ln) { return ln.trim() && ln.trim()[0] !== '#'; });
          setImportPreview({
            headers: ['value'],
            rows: phoneLines.slice(0, 5).map(function (ln) { return [ln.trim()]; }),
            count: phoneLines.length,
            leadsCount: phoneLines.length
          });
          if (!importListName) {
            var bn = file.name || '';
            var woe = bn.indexOf('.') > -1 ? bn.slice(0, bn.lastIndexOf('.')) : bn;
            setImportListName(woe);
          }
          return;
        }
        const table = parseCsvTable(text);
        if (!table.length) {
          setImportError('File appears to be empty.');
          return;
        }
        const headers = table[0].map(function (h) { return h.trim(); });
        const rows = table.slice(1).filter(function (parts) {
          return parts.some(function (p) { return String(p || '').trim(); });
        });
        const phoneIdx = phoneColumnIndex(headers);
        var skippedNoPhone = 0;
        var invalidPhones = 0;
        rows.forEach(function (parts) {
          var raw = phoneIdx >= 0 ? String(parts[phoneIdx] || '').trim() : '';
          if (!raw) skippedNoPhone++;
          else if (!phoneLooksDialable(raw)) invalidPhones++;
        });
        const previewRows = rows.slice(0, 5);
        setImportPreview({
          headers: headers,
          rows: previewRows,
          count: rows.length,
          leadsCount: rows.length,
          skippedNoPhone: skippedNoPhone,
          invalidPhones: invalidPhones
        });
        if (!importListName) {
          var base = file.name || '';
          var withoutExt = base.indexOf('.') > -1 ? base.slice(0, base.lastIndexOf('.')) : base;
          setImportListName(withoutExt);
        }
        if (importMode === 'leads' && !headers.some(headerLooksLikePhone)) {
          setImportError("We couldn't find a phone column. Name one column Phone.");
        }
      } catch (err) {
        console.error(err);
        setImportError('Failed to parse CSV.');
      }
    };
    reader.onerror = function () {
      setImportError('Failed to read file.');
    };
    reader.readAsText(file);
  }

  async function handleImportSubmit(e) {
    e.preventDefault();
    if (!importFile) {
      setImportError('Choose a file to import.');
      return;
    }
    if (importMode === 'leads' && importPreview && importPreview.headers && !importPreview.headers.some(headerLooksLikePhone)) {
      setImportError("We couldn't find a phone column. Name one column Phone.");
      return;
    }
    setImportBusy(true);
    setImportError('');
    try {
      var url = importMode === 'dnc'
        ? `${API_BASE}/api/dnc/import`
        : `${API_BASE}/api/leads/import`;
      var form = new FormData();
      form.append('file', importFile);
      if (importMode === 'leads' && importListName) {
        form.append('batchName', importListName);
      }
      if (importMode === 'leads' && importConsent) {
        form.append('consent_attestation', JSON.stringify(importConsent));
      }
      if (importMode === 'dnc') {
        form.append('source_type', dncSourceType);
        if (importListName) form.append('source_label', importListName);
        form.append('batchName', importListName || '');
      }
      var token = window.localStorage.getItem('authToken');
      var headers = {};
      if (token) headers['Authorization'] = 'Bearer ' + token;
      if (typeof window !== 'undefined' && (window.DEBUG_API === true || /[?&]debug=1/.test(window.location.search || ''))) headers['X-Debug-UI'] = '1';
      var res = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: form,
        credentials: 'include'
      });
      var data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok || data.error) {
        throw new Error(friendlyApiError(data, 'Import failed (' + res.status + ')'));
      }
      var n = data.imported != null ? data.imported : (data.count || 0);
      var noun = importMode === 'dnc' ? (n === 1 ? 'number' : 'numbers') : (n === 1 ? 'lead' : 'leads');
      var msg = 'Imported ' + n + ' ' + noun + '.';
      var rowsText = function (count) { return count + (count === 1 ? ' row was' : ' rows were'); };
      var skip = data.skip_reasons && typeof data.skip_reasons === 'object' ? data.skip_reasons : null;
      var invalidPhoneCount = skip ? asCount(skip.invalid_phone) : 0;
      var missingPhoneCount = skip ? asCount(skip.missing_phone) : 0;
      var invalidCount = skip
        ? invalidPhoneCount + missingPhoneCount
        : asCount(data.invalid_count != null ? data.invalid_count : (Array.isArray(data.invalid) ? data.invalid.length : 0));
      var duplicateCount = skip ? asCount(skip.duplicate) : asCount(data.duplicates_skipped);
      var dncCount = skip ? asCount(skip.dnc) : asCount(Array.isArray(data.dnc_blocked) ? data.dnc_blocked.length : data.dnc_blocked);
      if (skip && invalidPhoneCount) msg += ' ' + rowsText(invalidPhoneCount) + ' skipped because the phone number was not valid.';
      if (skip && missingPhoneCount) msg += ' ' + rowsText(missingPhoneCount) + ' skipped because the phone was blank.';
      if (!skip && invalidCount) msg += ' ' + rowsText(invalidCount) + ' skipped for a missing or invalid phone number.';
      if (duplicateCount) msg += ' ' + rowsText(duplicateCount) + ' skipped as duplicates of existing leads.';
      if (dncCount) msg += ' ' + rowsText(dncCount) + ' skipped because the number is on your do-not-call list.';
      if (data.compliance_note) msg += '\n\n' + data.compliance_note;
      if (data.is_test_sample) msg += '\n\nNote: test_sample is not official registry compliance data.';
      window.alert(msg);
      closeImport();
      if (refresh) refresh();
    } catch (err) {
      console.error(err);
      setImportError(err.message || 'Import failed.');
    } finally {
      setImportBusy(false);
    }
  }


  return (
    <div className="grid leads-workspace">
      <section className="card">
        {loadError ? (
          <BuyerListNotice
            title="Couldn't load leads"
            body="We couldn't retrieve your leads. Try again."
            onRetry={function () { refresh && refresh(); }}
          />
        ) : null}
        <div className="card-header leads-card-header">
          <div>
            <div className="card-title">Leads</div>
            <div className="card-subtitle leads-card-subtitle">
              Filter, research and dial imported leads. DNC entries are locked from dialing.
            </div>
          </div>
          <div className="leads-toolbar">
            <button
              className="button small primary"
              type="button"
              onClick={function () { setAddLeadModal(true); }}
            >
              Add Lead
            </button>
            <button
              className="button small outline"
              type="button"
              onClick={function () { openImport('leads'); }}
            >
              Import leads (CSV)
            </button>
            <button
              className="button small outline"
              type="button"
              onClick={function () { openImport('dnc'); }}
            >
              Import DNC list
            </button>
            <label className="leads-toolbar-filter">
              <span style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>Search</span>
              <input
                type="search"
                className="input"
                aria-label="Search leads on this page"
                placeholder="Name, phone, company"
                title="Searches the leads loaded on this page."
                value={leadQuery}
                onChange={(e) => setLeadQuery(e.target.value)}
              />
            </label>
            <label className="leads-toolbar-filter">
              <span style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>Status</span>
              <select
                className="select select-compact"
                aria-label="Filter leads by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="interested">Interested</option>
                <option value="not_interested">Not interested</option>
                <option value="new">New</option>
                <option value="dnc">DNC</option>
              </select>
            </label>
            <button className="button small outline" onClick={refresh}>
              ↻ Refresh
            </button>
            <button className="button small outline" onClick={bulkResearch} disabled={bulkResearching}>
              ⚡ Research all
            </button>
            <button className="button small primary" onClick={queueSelected} disabled={queueing || selectedIds.size === 0}>
              {queueing ? 'Queueing…' : 'Queue for calls (' + selectedIds.size + ')'}
            </button>
            <button className="button small outline" type="button" disabled={selectedIds.size === 0} onClick={function () { setAddCampaignOpen(true); }}>
              Add to campaign ({selectedIds.size})
            </button>
            {canAttestConsent ? (
              <button className="button small outline" type="button" disabled={selectedIds.size === 0} onClick={function () { setBulkConsentOpen(true); }}>
                Record consent ({selectedIds.size})
              </button>
            ) : null}
          </div>
        </div>
        <div className="admin-table-scroll">
        <table className="table table--mobile-cards">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" aria-label="Select all leads that are not on the do-not-call list" checked={filteredLeads.filter((l) => !l.dnc_flag && !l.dnc_list_match).length > 0 && selectedIds.size >= filteredLeads.filter((l) => !l.dnc_flag && !l.dnc_list_match).length} onChange={toggleSelectAll} title="Select all (non-DNC)" />
              </th>
              <th>Lead</th>
              <th>Company</th>
              <th>Industry</th>
              <th>Phone</th>
              <th>Status</th>
              <th>AI</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((l) => (
              <tr key={l.id}>
                <td className="mobile-td-skip-label">
                  {!l.dnc_flag && !l.dnc_list_match && (
                    <input type="checkbox" aria-label={'Select ' + (l.name || 'lead')} checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} />
                  )}
                </td>
                <td data-label="Lead">
                  <div>{l.name || '—'}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--fg-muted)' }}>{l.email}</div>
                </td>
                <td data-label="Company">{l.company || '—'}</td>
                <td data-label="Industry">{l.industry || '—'}</td>
                <td data-label="Phone">{l.phone}</td>
                <td data-label="Status">
                  <span
                    className={
                      'badge ' +
                      (l.dnc_flag || l.dnc_list_match
                        ? 'dnc'
                        : l.status === 'interested'
                        ? 'interested'
                        : l.status === 'not_interested'
                        ? 'not-interested'
                        : 'new')
                    }
                    title={l.dnc_block_reason === 'suppression_list' ? 'On tenant DNC suppression import' : l.dnc_block_reason === 'suppression_list_and_lead' ? 'Suppression list + lead DNC/opt-out' : l.dnc_list_match ? 'Suppression list' : ''}
                  >
                    {leadStatusLabel(l)}
                  </span>
                </td>
                <td data-label="AI research">
                  {l.research_summary ? (
                    <span className="badge interested">researched</span>
                  ) : (
                    <span className="badge new">pending</span>
                  )}
                </td>
                <td className="mobile-actions-cell">
                  <button className="button small outline" onClick={() => { setSelected(l); onLeadContext && onLeadContext(l); }}>
                    View
                  </button>
                  <button
                    className="button small danger"
                    style={{ marginLeft: 4 }}
                    onClick={function (e) {
                      e.stopPropagation();
                      if (!window.confirm('Delete this lead? This cannot be undone.')) return;
                      API.json('DELETE', '/api/leads/' + l.id)
                        .then(function () {
                          if (refresh) refresh();
                          if (selected && selected.id === l.id) {
                            setSelected(null);
                            onLeadContext && onLeadContext(null);
                          }
                        })
                        .catch(function (err) {
                          alert('Delete failed: ' + (err && err.message ? err.message : 'Unknown error'));
                        });
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!loadError && !filteredLeads.length && statusFilter !== 'all' && (safeLeads.length > 0 || total > 0) ? (
              <tr className="table-mobile-empty">
                <td colSpan={8} style={{ textAlign: 'center', padding: 20 }}>
                  No leads match the current filters.
                </td>
              </tr>
            ) : !loadError && !filteredLeads.length && statusFilter === 'all' && (!total || total === 0) ? (
              <tr className="table-mobile-empty">
                <td colSpan={8}>
                  <BuyerListNotice title="No leads yet" body="Leads will appear here after they're added or imported." />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        </div>
        <div className="pagination-row">
          <div>
            Page {page} of {Math.max(1, Math.ceil(total / 25))}
          <span className="pagination-meta">
            <TimesShown />
          </span>
          </div>
          <div>
            <button
              className="button small outline"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              ◀ Prev
            </button>
            <button
              className="button small outline"
              style={{ marginLeft: 4 }}
              disabled={page >= Math.ceil(total / 25)}
              onClick={() => onPageChange(page + 1)}
            >
              Next ▶
            </button>
          </div>
        </div>
      </section>

      <section className="card leads-detail-card">
        <div className="card-header">
          <div>
            <div className="card-title">Lead details</div>
            <div className="card-subtitle leads-detail-subtitle">
              Run pre‑call research, review AI script suggestions and start a call.
            </div>
          </div>
        </div>
        {selected ? (
          <LeadDetail lead={selected} user={user} timezone={timezone} onUpdated={refresh} onLeadUpdated={(updatedLead) => {
            if (updatedLead && selected && selected.id === updatedLead.id) {
              setSelected(updatedLead);
              onLeadContext && onLeadContext(updatedLead);
            }
          }} />
        ) : (
          <div style={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>Select a lead to see details.</div>
        )}

      </section>
    
      {importMode && (
        <div className="import-modal-backdrop">
          <div className="import-modal">
            <div className="import-modal-header">
              <div>
                <div className="import-modal-title">
                  {importMode === 'dnc' ? 'Import DNC list' : 'Import leads (CSV)'}
                </div>
                <div className="import-modal-subtitle">
                  {(
                    importMode === 'dnc'
                      ? "Upload numbers you never want dialed. We'll cross-check each call."
                      : "Upload a CSV. Only a phone column is required."
                  )}
                </div>
              </div>
              <button className="icon-button" type="button" onClick={closeImport}>
                ✕
              </button>
            </div>
            <form onSubmit={handleImportSubmit} className="stack gap-sm">
              {importMode === 'leads' && <LeadImportHelp />}
              {importMode === 'leads' && (
                <label className="field">
                  <span className="field-label">List name</span>
                  <input
                    type="text"
                    className="input"
                    value={importListName}
                    onChange={(e) => setImportListName(e.target.value)}
                    placeholder="e.g. December Realtors"
                  />
                  <span className="field-help">
                    This name will show up in Campaigns as a lead list.
                  </span>
                </label>
              )}
              {importMode === 'leads' && (
                <ImportConsentFields key={importSession} value={null} onChange={setImportConsent} user={user} timezone={timezone} />
              )}
              {importMode === 'dnc' && (
                <div className="field">
                  <span className="field-label">Suppression source type</span>
                  <select
                    className="input"
                    value={dncSourceType}
                    onChange={function (e) { setDncSourceType(e.target.value); }}
                    style={{ maxWidth: '100%' }}
                  >
                    <option value="internal_company_dnc">Your company list</option>
                    <option value="official_national_dnc">National registry file (from your registry account)</option>
                    <option value="state_dnc">State registry file</option>
                    <option value="test_sample">Test sample (not for compliance)</option>
                  </select>
                  <span className="field-help" style={{ color: dncSourceType === 'test_sample' ? '#b45309' : undefined }}>
                    {dncSourceType === 'test_sample'
                      ? 'Test data only. National DNC Registry compliance requires subscription files uploaded as official_national_dnc (add-on plan).'
                      : 'FTC public complaints APIs are not the suppression registry — only files from your authorized registry workflow count for compliance.'}
                  </span>
                  <a href="/samples/dnc-test-sample.csv" download className="button small outline" style={{ marginTop: 8, display: 'inline-block' }}>
                    Download sample test CSV
                  </a>
                </div>
              )}
              <label className="field">
                <span className="field-label">{importMode === 'dnc' ? 'CSV or TXT file' : 'CSV file'}</span>
                <input
                  type="file"
                  accept={importMode === 'dnc' ? '.csv,.txt,text/csv,text/plain' : '.csv,text/csv'}
                  onChange={handleFileChange}
                />
              </label>
              {importPreview && (
                <div className="import-preview">
                  <div className="import-preview-meta">
                    <span>
                      Detected columns: {importPreview.headers.join(', ')}
                    </span>
                    <span>
                      Estimated leads: {importPreview.leadsCount}
                    </span>
                    {importPreview.skippedNoPhone ? (
                      <span>
                        {importPreview.skippedNoPhone} {importPreview.skippedNoPhone === 1 ? 'row has' : 'rows have'} no phone and will be skipped.
                      </span>
                    ) : null}
                    {importPreview.invalidPhones ? (
                      <span>
                        {importPreview.invalidPhones} {importPreview.invalidPhones === 1 ? 'phone does' : 'phones do'} not look like a full number. Those rows will be skipped unless the number is fixed.
                      </span>
                    ) : null}
                  </div>
                  <div className="import-preview-table-wrapper">
                    <table className="table table-compact">
                      <thead>
                        <tr>
                          {importPreview.headers.map((h) => (
                            <th key={h}>{h || ' '}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.rows.map((row, idx) => (
                          <tr key={idx}>
                            {row.map((cell, i) => (
                              <td key={i}>{cell || ' '}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {importError && <div className="form-error">{importError}</div>}
              <div className="import-modal-footer">
                <button
                  type="button"
                  className="button small outline"
                  onClick={closeImport}
                  disabled={importBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button small primary"
                  disabled={importBusy}
                >
                  {importBusy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addLeadModal && (
        <div className="modal-backdrop" onClick={() => !addLeadBusy && setAddLeadModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Lead</div>
              <button type="button" className="button small ghost" aria-label="Close" onClick={() => !addLeadBusy && setAddLeadModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddLead} className="modal-body">
              <div className="field">
                <label htmlFor="add-lead-phone">Phone *</label>
                <input id="add-lead-phone" className="input" type="text" value={addLeadForm.phone} onChange={(e) => setAddLeadForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+15551234567" required />
              </div>
              <div className="field">
                <label htmlFor="add-lead-name">Name</label>
                <input id="add-lead-name" className="input" type="text" value={addLeadForm.name} onChange={(e) => setAddLeadForm((f) => ({ ...f, name: e.target.value }))} placeholder="John Doe" />
              </div>
              <div className="field">
                <label htmlFor="add-lead-email">Email</label>
                <input id="add-lead-email" className="input" type="email" value={addLeadForm.email} onChange={(e) => setAddLeadForm((f) => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="add-lead-company">Company</label>
                  <input id="add-lead-company" className="input" type="text" value={addLeadForm.company} onChange={(e) => setAddLeadForm((f) => ({ ...f, company: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="add-lead-industry">Industry</label>
                  <input id="add-lead-industry" className="input" type="text" value={addLeadForm.industry} onChange={(e) => setAddLeadForm((f) => ({ ...f, industry: e.target.value }))} />
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="add-lead-city">City</label>
                  <input id="add-lead-city" className="input" type="text" value={addLeadForm.city} onChange={(e) => setAddLeadForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="add-lead-state">State</label>
                  <input id="add-lead-state" className="input" type="text" value={addLeadForm.state} onChange={(e) => setAddLeadForm((f) => ({ ...f, state: e.target.value }))} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="add-lead-status">Status</label>
                <select id="add-lead-status" className="select" value={addLeadForm.status} onChange={(e) => setAddLeadForm((f) => ({ ...f, status: e.target.value }))}>
                  <option value="new">New</option>
                  <option value="interested">Interested</option>
                  <option value="not_interested">Not interested</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="add-lead-notes">Notes</label>
                <textarea id="add-lead-notes" className="input" value={addLeadForm.notes} onChange={(e) => setAddLeadForm((f) => ({ ...f, notes: e.target.value }))} rows={2} />
              </div>
              {addLeadError && <div className="form-error">{addLeadError}</div>}
              <div className="modal-footer">
                <button type="button" className="button ghost" onClick={() => !addLeadBusy && setAddLeadModal(false)} disabled={addLeadBusy}>Cancel</button>
                <button type="submit" className="button primary" disabled={addLeadBusy}>{addLeadBusy ? 'Adding…' : 'Add Lead'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {bulkConsentOpen ? (
        <BulkConsentModal
          api={API.json}
          toast={showToast}
          user={user}
          leadIds={Array.from(selectedIds)}
          onClose={function () { setBulkConsentOpen(false); }}
          onDone={function () { setBulkConsentOpen(false); refresh && refresh(); }}
        />
      ) : null}
      {addCampaignOpen ? (
        <AddToCampaignModal
          api={API.json}
          toast={showToast}
          leadIds={Array.from(selectedIds)}
          timezone={timezone || 'America/Los_Angeles'}
          onClose={function () { setAddCampaignOpen(false); }}
          onDone={function () { setAddCampaignOpen(false); setSelectedIds(new Set()); }}
        />
      ) : null}
</div>
  );
}



function LeadDetail({ lead, onUpdated, onLeadUpdated, user, timezone }) {
  const [local, setLocal] = useState(lead);
  const [saving, setSaving] = useState(false);
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [calling, setCalling] = useState(false);
  const [setupBlock, setSetupBlock] = useState(null);
  const [leadCalls, setLeadCalls] = useState([]);
  const [selectedCallSid, setSelectedCallSid] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [agents, setAgents] = useState([]);
  const [tenantHandoffMode, setTenantHandoffMode] = useState(null);

  useEffect(() => {
    setLocal(lead);
  }, [lead]);

  useEffect(function () {
    if (!lead || !lead.tenant_id) {
      setTenantHandoffMode(null);
      return;
    }
    API.json('GET', '/api/dashboard/config')
      .then(function (cfg) {
        setTenantHandoffMode(cfg && cfg.agentHandoffMode ? String(cfg.agentHandoffMode) : 'assigned_only');
      })
      .catch(function () {
        setTenantHandoffMode(null);
      });
  }, [lead && lead.tenant_id]);

  useEffect(() => {
    async function loadAgents() {
      if (!lead || !lead.id) return;
      try {
        const tid = lead.tenant_id;
        const url = tid ? '/api/users?tenant_id=' + encodeURIComponent(tid) : '/api/users';
        const r = await API.json('GET', url);
        const users = (r && r.users) || [];
        const agentRoles = ['agent', 'tenant_admin', 'supervisor'];
        setAgents(users.filter((u) => agentRoles.includes(String(u.role || ''))));
      } catch (_) {
        setAgents([]);
      }
    }
    loadAgents();
  }, [lead ? lead.id : undefined, lead ? lead.tenant_id : undefined]);

  useEffect(() => {
    async function loadCalls() {
      if (!lead || !lead.id) {
        setLeadCalls([]);
        setSelectedCallSid(null);
        setSelectedCall(null);
        return;
      }
      try {
        const res = await API.json('GET', `/api/leads/${lead.id}/calls`);
        setLeadCalls(res.calls || []);
      } catch (e) {
        console.error(e);
      }
    }
    loadCalls();
  }, [lead ? lead.id : undefined]);

  useEffect(() => {
    async function fetchCall() {
      if (!selectedCallSid) {
        setSelectedCall(null);
        return;
      }
      try {
        const data = await API.json('GET', `/api/calls/${selectedCallSid}`);
        setSelectedCall(data);
      } catch (e) {
        console.error(e);
      }
    }
    fetchCall();
  }, [selectedCallSid]);
  const updateField = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  async function save() {
    setSaving(true);
    try {
      const data = await API.json('PUT', `/api/leads/${local.id}`, local);
      if (data && data.lead) {
        setLocal(data.lead);
        onLeadUpdated && onLeadUpdated(data.lead);
      }
      onUpdated && onUpdated();
      alert('Lead saved.');
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function research() {
    setLoadingResearch(true);
    try {
      const data = await API.json('POST', `/api/leads/${local.id}/research`, {});
      if (data && data.lead) {
        setLocal(data.lead);
        onLeadUpdated && onLeadUpdated(data.lead);
      }
      onUpdated && onUpdated();
    } catch (e) {
      alert('Research failed: ' + e.message);
    } finally {
      setLoadingResearch(false);
    }
  }

  useEffect(function () {
    var role = user && user.role;
    if (role !== 'tenant_admin' && role !== 'super_admin') return undefined;
    var cancel = false;
    API.json('GET', '/api/onboarding/status').then(function (row) {
      if (!cancel) setSetupBlock(row && row.ready_for_outbound === false ? row : null);
    }).catch(function () {});
    return function () { cancel = true; };
  }, [user && user.id, user && user.tenant_id]);

  async function startCall() {
    if (setupBlock) {
      alert(setupBlockMessage(setupBlock));
      openSetupWizard();
      return;
    }
    setCalling(true);
    try {
      var blocked = await outboundSetupBlock();
      if (blocked) {
        setSetupBlock(blocked);
        alert(setupBlockMessage(blocked));
        openSetupWizard();
        return;
      }
      const data = await API.json('POST', '/api/call/start', { leadId: local.id });
      alert('Call started: ' + data.sid);
    } catch (e) {
      alert(callStartErrorMessage(e));
    } finally {
      setCalling(false);
    }
  }

  async function startPremiumCall() {
    if (!local || (local.id !== 0 && !local.id)) {
      alert('Please select a lead first.');
      return;
    }
    if (setupBlock) {
      alert(setupBlockMessage(setupBlock));
      openSetupWizard();
      return;
    }
    var blocked = await outboundSetupBlock();
    if (blocked) {
      setSetupBlock(blocked);
      alert(setupBlockMessage(blocked));
      openSetupWizard();
      return;
    }
    setCalling(true);
    try {
      const data = await API.json('POST', '/api/call/start-premium', { leadId: local.id });
      alert('Premium AI call started: ' + data.sid);
    } catch (e) {
      alert(callStartErrorMessage(e));
    } finally {
      setCalling(false);
    }
  }

  var dialBlocked = !!(local.dnc_flag || local.dnc_list_match || local.consent_status === 'opt_out');

  return (
    <div>
      {local.dnc_list_match ? (
        <div className="form-error" style={{ marginBottom: 10, fontSize: '0.8rem' }}>
          This number matches your workspace <strong>DNC suppression import</strong>. Outbound dialing is blocked until removed from the list.
        </div>
      ) : null}
      <div className="field-row">
        <div className="field">
          <label>Name</label>
          <input className="input" value={local.name || ''} onChange={(e) => updateField('name', e.target.value)} />
        </div>
        <div className="field">
          <label>Phone</label>
          <input className="input" value={local.phone || ''} onChange={(e) => updateField('phone', e.target.value)} />
          <LeadLocalClock phone={local.phone} state={local.state} localTimeZone={local.local_timezone} />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Company</label>
          <input
            className="input"
            value={local.company || ''}
            onChange={(e) => updateField('company', e.target.value)}
          />
        </div>
        <div className="field">
          <label>Industry</label>
          <input
            className="input"
            value={local.industry || ''}
            onChange={(e) => updateField('industry', e.target.value)}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>City</label>
          <input className="input" value={local.city || ''} onChange={(e) => updateField('city', e.target.value)} />
        </div>
        <div className="field">
          <label>State</label>
          <input className="input" value={local.state || ''} onChange={(e) => updateField('state', e.target.value)} />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Email</label>
          <input className="input" value={local.email || ''} onChange={(e) => updateField('email', e.target.value)} />
        </div>
        <div className="field">
          <label>Status</label>
          <select
            className="select"
            value={local.status || 'new'}
            onChange={(e) => updateField('status', e.target.value)}
          >
            <option value="new">new</option>
            <option value="interested">interested</option>
            <option value="not_interested">not_interested</option>
            <option value="dnc">dnc</option>
          </select>
        </div>
      </div>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>Consent / DNC</label>
        <div style={{ fontSize: '0.85rem', marginBottom: 4 }}>
          Status: {(local.consent_status || 'unknown')} {local.dnc_flag ? ' • DNC' : ''}
          {local.consent_source ? ' (source: ' + local.consent_source + ')' : ''}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="button small outline"
            onClick={async () => {
              try {
                const data = await API.json('PATCH', '/api/leads/' + local.id + '/consent', { action: 'opt_in', source: 'verbal' });
                if (data && data.lead) {
                  setLocal(data.lead);
                  onLeadUpdated && onLeadUpdated(data.lead);
                }
                onUpdated && onUpdated();
              } catch (e) { alert('Opt-in failed: ' + (e.message || (e.data && e.data.error) || '')); }
            }}
          >
            Mark Opt-in
          </button>
          <button
            type="button"
            className="button small outline"
            onClick={async () => {
              var note = window.prompt('Optional note/reason for opt-out:');
              try {
                const data = await API.json('PATCH', '/api/leads/' + local.id + '/consent', { action: 'opt_out', source: 'verbal', note: note || undefined });
                if (data && data.lead) {
                  setLocal(data.lead);
                  onLeadUpdated && onLeadUpdated(data.lead);
                }
                onUpdated && onUpdated();
              } catch (e) { alert('Opt-out failed: ' + (e.message || (e.data && e.data.error) || '')); }
            }}
          >
            Mark Opt-out
          </button>
        </div>
      </div>
      <div className="field">
        <label>Owner (assigned agent)</label>
        <select
          className="input"
          value={local && local.assigned_user_id != null && local.assigned_user_id !== '' ? String(local.assigned_user_id) : ''}
          onChange={async (e) => {
            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
            try {
              const data = await API.json('PATCH', `/api/leads/${local.id}/assign`, { assigned_user_id: val });
              if (data && data.lead) {
                setLocal(data.lead);
                onLeadUpdated && onLeadUpdated(data.lead);
              } else {
                setLocal((prev) => ({ ...prev, assigned_user_id: val }));
              }
              onUpdated && onUpdated();
            } catch (err) {
              var detail = (err.data && err.data.detail) ? err.data.detail : '';
              var code = (err.data && err.data.error) ? err.data.error : '';
              alert('Assign failed: ' + (code || err.message || 'Unknown error') + (detail ? ' — ' + detail : ''));
            }
          }}
        >
          <option value="">— None —</option>
          {agents.map((a) => (
            <option key={a.id} value={String(a.id)}>{a.name || a.email || 'User ' + a.id}</option>
          ))}
        </select>
        <p className="muted" style={{ fontSize: '0.75rem', marginTop: 6 }}>
          {tenantHandoffMode === 'tenant_any'
            ? 'Caller handoff: any eligible agent in the tenant may get the live request (whoever is online in admin).'
            : 'Caller handoff: only the assigned owner is notified for this lead; if they are offline, the request queues until they open admin or finish another call.'}
          {' '}
          {(() => {
            var aid = local && local.assigned_user_id;
            if (aid == null || aid === '') return <span>Current assignment: <strong>Unassigned</strong>.</span>;
            var m = agents.find(function (u) { return String(u.id) === String(aid); });
            return (
              <span>Current assignment: <strong>{m ? (m.name || m.email || 'User ' + m.id) : ('User #' + aid)}</strong>.</span>
            );
          })()}
        </p>
      </div>
      <ConsentPanel api={API.json} toast={showToast} user={user} leadId={local.id} timezone={timezone} />
      <NotesPanel api={API.json} toast={showToast} user={user} leadId={local.id} timezone={timezone} />
      {local.notes ? (
        <div className="field">
          <label>Imported notes</label>
          <div className="muted" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{local.notes}</div>
        </div>
      ) : null}
      <div className="field">
        <label>AI Research summary</label>
        <textarea
          className="input"
          value={local.research_summary || ''}
          onChange={(e) => updateField('research_summary', e.target.value)}
        />
      </div>
      <div className="field">
        <label>Opening line the caller hears first</label>
        <textarea
          className="input"
          value={local.script_suggestion || ''}
          onChange={(e) => updateField('script_suggestion', e.target.value)}
          placeholder="Hi, this is Alex from Acme Roofing. Do you have a minute?"
        />
        <p className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
          Premium calls speak this line first, then follow the workspace script. Leave it blank only if you want the assistant to open from the workspace script instead.
        </p>
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="button primary small" onClick={save} disabled={saving}>
          💾 Save
        </button>
        <button className="button small outline" onClick={research} disabled={loadingResearch}>
          ⚡ Run AI research
        </button>
        <button className="button small outline" onClick={startCall} disabled={calling || dialBlocked}>
          {setupBlock ? 'Finish setup to call' : '☎ Start call'}
        </button>
        {setupBlock ? (
          <div className="setup-call-block form-error">
            {setupBlockMessage(setupBlock)}{' '}
            <button type="button" className="button small outline" onClick={openSetupWizard}>Open setup</button>
          </div>
        ) : null}
          
          <button
    className="button small outline"
    onClick={startPremiumCall}
    disabled={calling || dialBlocked}
  >
    ⭐ Start premium AI call
  </button>

        <button
          className="button small outline"
          title="Mask PII (GDPR right to erasure). Keeps call metrics and audit."
          onClick={async () => {
            if (!window.confirm('Forget this lead? Name, phone, email, and company will be masked. Call history and metrics are kept. This cannot be undone.')) return;
            try {
              const data = await API.json('POST', `/api/leads/${local.id}/forget`);
              if (data && data.lead) {
                setLocal(data.lead);
                onLeadUpdated && onLeadUpdated(data.lead);
              }
              alert('Lead PII erased.');
              onUpdated && onUpdated();
            } catch (e) {
              alert((e.data && e.data.error === 'already_forgotten' ? 'Lead already forgotten.' : 'Forget failed: ') + (e.message || ''));
            }
          }}
          disabled={local.pii_masked_at}
        >
          {local.pii_masked_at ? '✓ Forgotten' : 'Forget (GDPR)'}
        </button>
        <button
          className="button small danger"
          onClick={async () => {
            if (!window.confirm('Delete this lead? The lead will be removed from the list.')) return;
            try {
              await API.json('DELETE', `/api/leads/${local.id}`);
              alert('Lead deleted.');
              onUpdated && onUpdated();
            } catch (e) {
              alert('Delete failed: ' + e.message);
            }
          }}
        >
          🗑 Delete
        </button>
      </div>
      <hr style={{ margin: '16px 0', borderColor: 'rgba(148,163,184,0.2)' }} />

      <div className="field">
        <label>Calls for this lead</label>
        {leadCalls.length ? (
          <div className="table-wrapper">
          <table className="table" style={{ fontSize: '0.78rem' }}>
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Status</th>
                <th>Interest</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {leadCalls.map((c) => (
                <tr key={c.call_sid}>
                  <td><WhenText value={c.created_at} /></td>
                  <td>{c.status}</td>
                  <td>{c.interest_level || '—'}</td>
                  <td>
                    <button
                      className="button small outline"
                      onClick={() => setSelectedCallSid(c.call_sid)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div style={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>No calls yet for this lead.</div>
        )}
      </div>

      <LeadSmsThread api={API.json} toast={showToast} leadId={local.id} timezone={timezone} />

      {selectedCall && (
        <div className="field" style={{ marginTop: 12 }}>
          <label>Call summary</label>
          <div style={{ fontSize: '0.78rem' }}>{selectedCall.call.summary || 'No summary yet.'}</div>

          {selectedCall.call.recording_url && (
            <div style={{ marginTop: 8 }}>
              <label>Recording</label>
              <audio
                controls
                src={absoluteRecordingUrl(selectedCall.call.recording_url)}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <div style={{ marginTop: 8 }}>
            <label>Transcript</label>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                background: 'var(--surface-sunken)',
                borderRadius: 4,
                padding: 8,
                maxHeight: 240,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              <CallTranscriptBlock call={selectedCall.call} events={selectedCall.events} loadFailed={false} />
            </div>
          </div>
          <NotesPanel api={API.json} toast={showToast} user={user} leadId={local.id} callSid={selectedCall.call.call_sid} timezone={timezone} compact />
        </div>
      )}

      <div className="field" style={{ marginTop: 16 }}>
        <label>Activity</label>
        <ActivityTimeline api={API.json} leadId={local.id} timezone={timezone} onOpenCall={function (sid) { setSelectedCallSid(sid); }} />
      </div>
    </div>
  );
}

function formatCallDuration(c) {
  if (c && c.duration_s != null && c.duration_s !== '' && !Number.isNaN(Number(c.duration_s))) {
    return formatDuration(Number(c.duration_s));
  }
  if (c && c.duration) return formatDuration(Number(c.duration) * 60);
  return EM_DASH;
}

var EMPTY_CALL_FILTERS = { q: '', direction: '', status: '', date_from: '', date_to: '', sort: '' };

var CALL_SORTS = {
  '': { label: 'Newest first' },
  oldest: { label: 'Oldest first', sort: 'created_at', order: 'asc' },
  longest: { label: 'Longest first', sort: 'duration', order: 'desc' }
};

function callFiltersActive(filters) {
  return !!(filters && (filters.q || filters.direction || filters.status || filters.date_from || filters.date_to));
}

function callsQueryString(page, filters) {
  var params = new URLSearchParams();
  params.set('page', String(page || 1));
  var f = filters || {};
  ['q', 'direction', 'status', 'date_from', 'date_to'].forEach(function (key) {
    var value = String(f[key] || '').trim();
    if (value) params.set(key, value);
  });
  var sort = CALL_SORTS[f.sort || ''];
  if (sort && sort.sort) {
    params.set('sort', sort.sort);
    params.set('order', sort.order);
  }
  return params.toString();
}

function CallsFilterBar({ filters, onChange }) {
  var f = filters || EMPTY_CALL_FILTERS;
  const [draftQ, setDraftQ] = useState(f.q || '');
  useEffect(function () { setDraftQ(f.q || ''); }, [f.q]);
  useEffect(function () {
    if ((draftQ || '') === (f.q || '')) return undefined;
    var t = setTimeout(function () { onChange(Object.assign({}, f, { q: draftQ })); }, 400);
    return function () { clearTimeout(t); };
  }, [draftQ]);
  function set(key, value) {
    onChange(Object.assign({}, f, { [key]: value }));
  }
  return (
    <div className="calls-filter-bar">
      <label className="leads-toolbar-filter">
        <span className="field-label">Search</span>
        <input type="search" className="input" aria-label="Search calls" placeholder="Name, phone, summary" value={draftQ} onChange={function (e) { setDraftQ(e.target.value); }} />
      </label>
      <label className="leads-toolbar-filter">
        <span className="field-label">Direction</span>
        <select className="select select-compact" aria-label="Filter by direction" value={f.direction} onChange={function (e) { set('direction', e.target.value); }}>
          <option value="">All</option>
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </select>
      </label>
      <label className="leads-toolbar-filter">
        <span className="field-label">Status</span>
        <select className="select select-compact" aria-label="Filter by status" value={f.status} onChange={function (e) { set('status', e.target.value); }}>
          <option value="">All</option>
          <option value="completed">Completed</option>
          <option value="no-answer">No answer</option>
          <option value="busy">Busy</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
          <option value="in-progress">In progress</option>
        </select>
      </label>
      <label className="leads-toolbar-filter">
        <span className="field-label">From</span>
        <input type="date" className="input" aria-label="Calls from date" value={f.date_from} onChange={function (e) { set('date_from', e.target.value); }} />
      </label>
      <label className="leads-toolbar-filter">
        <span className="field-label">To</span>
        <input type="date" className="input" aria-label="Calls to date" value={f.date_to} onChange={function (e) { set('date_to', e.target.value); }} />
      </label>
      <label className="leads-toolbar-filter">
        <span className="field-label">Sort</span>
        <select className="select select-compact" aria-label="Sort calls" value={f.sort || ''} onChange={function (e) { set('sort', e.target.value); }}>
          {Object.keys(CALL_SORTS).map(function (key) { return <option key={key || 'newest'} value={key}>{CALL_SORTS[key].label}</option>; })}
        </select>
      </label>
      {callFiltersActive(f) || f.sort ? (
        <button type="button" className="button small ghost" onClick={function () { setDraftQ(''); onChange(EMPTY_CALL_FILTERS); }}>Clear</button>
      ) : null}
    </div>
  );
}

var hubspotPortalCache = { id: null, loaded: false, pending: null };

function loadHubSpotPortalId() {
  if (hubspotPortalCache.loaded) return Promise.resolve(hubspotPortalCache.id);
  if (!hubspotPortalCache.pending) {
    hubspotPortalCache.pending = API.json('GET', '/api/integrations/hubspot/status').then(function (r) {
      hubspotPortalCache.id = r && r.portalId ? String(r.portalId) : null;
      hubspotPortalCache.loaded = true;
      return hubspotPortalCache.id;
    }).catch(function () {
      hubspotPortalCache.loaded = true;
      return null;
    });
  }
  return hubspotPortalCache.pending;
}

function hubspotCallRecordUrl(portalId, engagementId) {
  if (!portalId || !engagementId) return '';
  return 'https://app.hubspot.com/contacts/' + encodeURIComponent(String(portalId)) + '/record/0-48/' + encodeURIComponent(String(engagementId));
}

function HubSpotCallCell({ call }) {
  var crmStatus = (call && call.crm_sync_status) || '';
  var crmLabel = crmStatus === 'ok' ? 'OK' : (crmStatus === 'failed' ? 'Failed' : (crmStatus === 'partial' ? 'Partial' : (crmStatus === 'pending' ? 'Pending' : '—')));
  var engagementId = call && call.hubspot_engagement_id;
  var preset = (call && (call.hubspot_url || call.crm_remote_url)) || '';
  var [url, setUrl] = useState(preset);
  useEffect(function () {
    if (preset) {
      setUrl(preset);
      return undefined;
    }
    if (!engagementId || (crmStatus !== 'ok' && crmStatus !== 'partial')) return undefined;
    var cancelled = false;
    loadHubSpotPortalId().then(function (portalId) {
      if (cancelled) return;
      var next = hubspotCallRecordUrl(portalId, engagementId);
      if (next) setUrl(next);
    });
    return function () { cancelled = true; };
  }, [preset, engagementId, crmStatus]);
  var synced = (crmStatus === 'ok' || crmStatus === 'partial') && url;
  return (
    <span>
      {synced ? (
        <a href={url} target="_blank" rel="noreferrer" onClick={function (ev) { ev.stopPropagation(); }} style={{ fontSize: '0.75rem', color: 'var(--fg)' }}>Open in HubSpot</a>
      ) : (
        <span className={crmStatus === 'ok' ? 'success' : (crmStatus === 'failed' ? 'error' : 'muted')} style={{ fontSize: '0.75rem' }}>{crmLabel}</span>
      )}
      {(crmStatus === 'failed' || crmStatus === 'partial') && call && call.crm_sync_error ? (
        <span title={call.crm_sync_error} className="muted" style={{ marginLeft: 4, cursor: 'help', fontSize: '0.7rem' }}>{crmStatus === 'partial' ? 'Details' : 'View error'}</span>
      ) : null}
    </span>
  );
}

var CALL_COST_LINE_LABELS = [
  ['twilio_voice', 'Twilio call leg'],
  ['twilio_child_legs', 'Transfer and conference legs'],
  ['twilio_client', 'Browser agent leg'],
  ['twilio_recording', 'Recording'],
  ['twilio_amd', 'Answering machine detection'],
  ['ai_realtime', 'AI voice (realtime)'],
  ['ai_transcription_estimated', 'Caller transcription'],
  ['ai_summary', 'Call summary'],
  ['ai_other', 'Other AI']
];

function formatCostUsd(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return '$' + n.toFixed(n !== 0 && Math.abs(n) < 0.01 ? 4 : 2);
}

function CallCostBreakdown({ breakdown }) {
  var lines = breakdown.lines || {};
  var pending = breakdown.price_state === 'pending';
  var unavailable = breakdown.price_state === 'unavailable';
  var rows = CALL_COST_LINE_LABELS.filter(function (pair) {
    var line = lines[pair[0]];
    return line && (line.usd == null ? line.state !== 'final' : line.usd > 0);
  });
  return (
    <div className="field" style={{ marginTop: 12 }}>
      <label>Cost to us</label>
      <table className="call-cost-table">
        <tbody>
          {rows.map(function (pair) {
            var line = lines[pair[0]];
            var value = line.usd == null
              ? (line.state === 'pending' ? 'Pending' : 'Unavailable')
              : formatCostUsd(line.usd) + (line.estimated ? ' (est.)' : '');
            return (
              <tr key={pair[0]}>
                <td>{pair[1]}</td>
                <td style={{ textAlign: 'right' }}>{value}</td>
              </tr>
            );
          })}
          <tr className="call-cost-total">
            <td>Total</td>
            <td style={{ textAlign: 'right' }}>
              {formatCostUsd(breakdown.total_usd)}
              {pending ? ' + Twilio pending' : (unavailable ? ' (Twilio price unavailable)' : '')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CallsView({ calls, page, total, onPageChange, filters, onFiltersChange, timezone, callsError, onRetryCalls, callbacks, callbacksError, onMarkCallbackDone, callsFiltered, user, openCallSid, onOpenCallHandled }) {
  const [selectedSid, setSelectedSid] = useState(null);
  useEffect(function () {
    if (!openCallSid) return;
    setSelectedSid(openCallSid);
    if (onOpenCallHandled) onOpenCallHandled();
  }, [openCallSid]);
  const [selectedRowCall, setSelectedRowCall] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetailLoading, setCallDetailLoading] = useState(false);
  const [callDetailError, setCallDetailError] = useState(null);
  const [recordingPlaybackError, setRecordingPlaybackError] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);

  function openCall(call) {
    var sid = call && call.call_sid ? call.call_sid : null;
    setSelectedRowCall(call || null);
    setSelectedCall(null);
    setCallDetailError(null);
    setRecordingPlaybackError(null);
    setCallDetailLoading(!!sid);
    setSelectedSid(sid);
  }

  function closeCall() {
    setSelectedSid(null);
    setSelectedRowCall(null);
    setSelectedCall(null);
    setCallDetailError(null);
    setRecordingPlaybackError(null);
    setCallDetailLoading(false);
  }

  useEffect(function () {
    if (!selectedSid) {
      setSelectedCall(null);
      setCallDetailError(null);
      setRecordingPlaybackError(null);
      return;
    }
    var cancelled = false;
    setSelectedCall(null);
    setCallDetailLoading(true);
    setCallDetailError(null);
    setRecordingPlaybackError(null);
    API.json('GET', '/api/calls/' + selectedSid)
      .then(function (data) {
        if (!cancelled) {
          setSelectedCall(data);
          setCallDetailLoading(false);
        }
      })
      .catch(function (e) {
        console.error(e);
        if (!cancelled) {
          setSelectedCall(null);
          setCallDetailError('Failed to load call details.');
          setCallDetailLoading(false);
        }
      });
    return function () { cancelled = true; };
  }, [selectedSid]);

  async function loadQueueStatus() {
    setQueueLoading(true);
    try {
      const r = await API.json('GET', '/api/calls/queue/status');
      setQueueStatus(r);
    } catch (e) {
      setQueueStatus(null);
    } finally {
      setQueueLoading(false);
    }
  }

  useEffect(() => {
    loadQueueStatus();
    const t = setInterval(loadQueueStatus, 10000);
    return () => clearInterval(t);
  }, []);

  var rowForSid = selectedRowCall && String(selectedRowCall.call_sid) === String(selectedSid) ? selectedRowCall : null;
  var recordingUrl = (selectedCall && selectedCall.call && selectedCall.call.recording_url)
    || (rowForSid && rowForSid.recording_url)
    || '';

  return (
    <div>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Callback requests</div>
            <div className="card-subtitle">
              Numbers a caller asked a person to dial. Saving a request does not place the call.
            </div>
          </div>
        </div>
        {callbacksError ? (
          <div className="card-body" style={{ color: 'var(--danger)' }}>{callbacksError}</div>
        ) : null}
        <div className="admin-table-scroll">
          <table className="table table--mobile-cards">
            <thead>
              <tr>
                <th>Phone</th>
                <th>Name</th>
                <th>Note</th>
                <th>Lead</th>
                <th>When</th>
                <th>Call</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(callbacks || []).map(function (row) {
                return (
                  <tr key={row.id}>
                    <td data-label="Phone">{row.phone_e164 || '—'}</td>
                    <td data-label="Name">{row.caller_name || '—'}</td>
                    <td data-label="Note">{row.note || '—'}</td>
                    <td data-label="Lead">{row.lead_name || '—'}</td>
                    <td data-label="When"><WhenText value={row.created_at} timeZone={timezone} /></td>
                    <td data-label="Call">
                      {row.call_sid ? (
                        <button
                          type="button"
                          title={row.call_sid}
                          onClick={function () { openCall({ call_sid: row.call_sid }); }}
                          style={{ fontFamily: 'monospace', background: 'none', border: 'none', padding: 0, color: 'var(--color-primary, #2563eb)', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {shortCallSid(row.call_sid)}
                        </button>
                      ) : '—'}
                    </td>
                    <td className="mobile-actions-cell">
                      <button type="button" className="button small outline" onClick={function () { onMarkCallbackDone && onMarkCallbackDone(row.id); }}>
                        Mark done
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!(callbacks || []).length && !callbacksError ? (
                <tr className="table-mobile-empty">
                  <td colSpan={7} style={{ textAlign: 'center', padding: 16, color: 'var(--fg-muted)' }}>
                    No callback requests yet. If a caller leaves a number during a call, it shows up here for someone to dial.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    <div className="calls-page-layout">
      <section className="card calls-table-card">
        <div className="card-header calls-list-header">
          <div>
            <div className="card-title">Calls</div>
            <div className="card-subtitle calls-list-subtitle">
              Review summaries, interest classification and recordings. Click a row to open details.
            </div>
            {onFiltersChange ? <CallsFilterBar filters={filters} onChange={onFiltersChange} /> : null}
          </div>
        </div>
        <div className="admin-table-scroll">
          <table className="table table--mobile-cards">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Phone</th>
                <th>Direction</th>
                <th>Status</th>
                <th>Date/Time</th>
                <th>Interest</th>
                <th>Outcome</th>
                <th>Duration</th>
                <th>HubSpot</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {calls.map(function (c) {
                return (
                  <tr
                    key={c.call_sid}
                    className="table-row-clickable"
                    onClick={function () { openCall(c); }}
                  >
                    <td data-label="Lead">{c.lead_name || (c.direction === 'inbound' ? 'Inbound caller' : '—')}</td>
                    <td data-label="Phone">{c.lead_phone || c.from_e164 || c.to_e164 || '—'}</td>
                    <td data-label="Direction">{c.direction || '—'}</td>
                    <td data-label="Status">
                      {c.status}
                      {c.recording_url ? <span className="badge new" style={{ marginLeft: 6 }}>Recording</span> : null}
                    </td>
                    <td data-label="Date"><WhenText value={c.created_at} timeZone={timezone} /></td>
                    <td data-label="Interest">{c.interest_level || '—'}</td>
                    <td data-label="Outcome">
                      {c.disposition_code ? <DispositionBadge code={c.disposition_code} label={c.disposition_label} category={c.disposition_category} /> : '—'}
                    </td>
                    <td data-label="Duration">{formatCallDuration(c)}</td>
                    <td data-label="CRM">
                      <HubSpotCallCell call={c} />
                    </td>
                    <td className="mobile-actions-cell" onClick={function (ev) { ev.stopPropagation(); }}>
                      <button type="button" className="button small outline" onClick={function () { openCall(c); }}>
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
              {callsError ? (
                <tr className="table-mobile-empty">
                  <td colSpan={9}>
                    <BuyerListNotice title="Couldn't load calls" body="We couldn't retrieve your call history. Try again." onRetry={onRetryCalls} />
                  </td>
                </tr>
              ) : !calls.length && callsFiltered ? (
                <tr className="table-mobile-empty">
                  <td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>No calls match the current filters.</td>
                </tr>
              ) : !calls.length && (!total || total === 0) ? (
                <tr className="table-mobile-empty">
                  <td colSpan={9}>
                    <BuyerListNotice title="No calls yet" body="Inbound and outbound calls will appear here once calls are made or received." />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination-row">
          <div>
            Page {page} of {Math.max(1, Math.ceil(total / 25))}
          <span className="pagination-meta">
            <TimesShown timeZone={timezone} />
          </span>
          </div>
          <div>
            <button
              type="button"
              className="button small outline"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              ◀ Prev
            </button>
            <button
              type="button"
              className="button small outline"
              style={{ marginLeft: 4 }}
              disabled={page >= Math.ceil(total / 25)}
              onClick={() => onPageChange(page + 1)}
            >
              Next ▶
            </button>
          </div>
        </div>
      </section>

      <aside className="card calls-queue-card">
        <div className="card-header calls-queue-header">
          <div>
            <div className="card-title">Call queue</div>
            <div className="card-subtitle calls-queue-subtitle">Queued, active, and completed batch calls.</div>
          </div>
          <button type="button" className="button small outline" onClick={loadQueueStatus} disabled={queueLoading}>↻ Refresh</button>
        </div>
        <div className="card-body">
          {queueStatus ? (
            <div className="calls-queue-stats">
              <div><span style={{ color: 'var(--fg-muted)' }}>Queued:</span> <strong>{(queueStatus.queuedCount != null ? queueStatus.queuedCount : (queueStatus.queued != null ? queueStatus.queued : 0))}</strong></div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Active calls:</span> <strong>{(queueStatus.activeCount != null ? queueStatus.activeCount : (queueStatus.running != null ? queueStatus.running : 0))}</strong>{queueStatus.concurrencyLimit != null ? <span style={{ color: 'var(--fg-muted)', fontSize: '0.85em' }}> / {queueStatus.concurrencyLimit}</span> : null}</div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Completed:</span> <strong>{(queueStatus.completedCount != null ? queueStatus.completedCount : (queueStatus.completed != null ? queueStatus.completed : 0))}</strong></div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Failed:</span> <strong>{(queueStatus.failedCount != null ? queueStatus.failedCount : (queueStatus.failed != null ? queueStatus.failed : 0))}</strong></div>
            </div>
          ) : (
            <div className="muted">Queue status not available (super admin has no tenant queue).</div>
          )}
        </div>
      </aside>

      {selectedSid && (
        <div className="card call-detail-modal" style={{ marginTop: 16, gridColumn: '1 / -1', position: 'static', maxWidth: 720 }}>
            <div className="modal-header">
              <div className="modal-title">Call details</div>
              <button type="button" className="icon-button" onClick={closeCall} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {callDetailLoading && !selectedCall && !recordingUrl ? (
                <div style={{ color: 'var(--fg-muted)' }}>Loading call details…</div>
              ) : callDetailError && !selectedCall ? (
                <div style={{ color: 'var(--danger)' }}>{callDetailError}</div>
              ) : (
                <div className="call-detail-body">
                  <div className="field">
                    <label>Summary</label>
                    <div style={{ fontSize: '0.78rem' }}>{(selectedCall && selectedCall.call && selectedCall.call.summary) || 'No summary yet.'}</div>
                  </div>
                  {selectedCall && selectedCall.call ? (
                    <DispositionPicker
                      api={API.json}
                      toast={showToast}
                      callSid={selectedSid}
                      timezone={timezone}
                      onSaved={function () { if (onRetryCalls) onRetryCalls(); }}
                    />
                  ) : null}
                  {selectedCall && selectedCall.call ? (
                    <NotesPanel
                      api={API.json}
                      toast={showToast}
                      user={user}
                      leadId={selectedCall.call.lead_id}
                      callSid={selectedSid}
                      timezone={timezone}
                      compact
                    />
                  ) : null}
                  {selectedCall && selectedCall.call ? (
                    <div className="field" style={{ marginTop: 12 }}>
                      <label>When</label>
                      <div style={{ fontSize: '0.78rem' }}><WhenText value={selectedCall.call.created_at} style="full" /></div>
                      <LeadLocalClock
                        phone={(selectedCall.call.lead && selectedCall.call.lead.phone) || selectedCall.call.to_e164 || selectedCall.call.from_e164 || (rowForSid && (rowForSid.lead_phone || rowForSid.to_e164 || rowForSid.from_e164))}
                        state={selectedCall.call.lead && selectedCall.call.lead.state}
                        localTimeZone={selectedCall.call.lead && selectedCall.call.lead.local_timezone}
                      />
                    </div>
                  ) : null}
                  {selectedCall && selectedCall.call && selectedCall.call.crm_sync_status ? (
                    <div className="field" style={{ marginTop: 12 }}>
                      <label>Synced to HubSpot</label>
                      <div style={{ fontSize: '0.78rem' }}>
                        <span className={selectedCall.call.crm_sync_status === 'ok' ? 'success' : (selectedCall.call.crm_sync_status === 'failed' ? 'error' : 'muted')}>
                          {selectedCall.call.crm_sync_status === 'ok' ? 'OK' : (selectedCall.call.crm_sync_status === 'failed' ? 'Failed' : (selectedCall.call.crm_sync_status === 'partial' ? 'Partial' : 'Pending'))}
                        </span>
                        {(selectedCall.call.crm_sync_status === 'failed' || selectedCall.call.crm_sync_status === 'partial') && selectedCall.call.crm_sync_error ? (
                          <div className="error" style={{ marginTop: 4, fontSize: '0.7rem', wordBreak: 'break-word' }}>{selectedCall.call.crm_sync_error}</div>
                        ) : null}
                        {selectedCall.call.hubspot_engagement_id ? (
                          <div style={{ marginTop: 6 }}><HubSpotCallCell call={selectedCall.call} /></div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  <div className="field" style={{ marginTop: 12 }}>
                    <label>Recording</label>
                    {recordingUrl ? (
                      <div>
                        <audio
                          controls
                          src={absoluteRecordingUrl(recordingUrl)}
                          style={{ width: '100%' }}
                          onError={function () { setRecordingPlaybackError('Playback failed. The recording may be missing, expired, or unreachable.'); }}
                          onLoadedData={function () { setRecordingPlaybackError(null); }}
                        />
                        {recordingPlaybackError ? (
                          <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 6 }}>{recordingPlaybackError}</div>
                        ) : null}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.78rem', color: 'var(--fg-muted)' }}>No recording is stored for this call.</div>
                    )}
                  </div>
                  {selectedCall && selectedCall.call && selectedCall.call.cost_breakdown ? (
                    <CallCostBreakdown breakdown={selectedCall.call.cost_breakdown} />
                  ) : null}
                  <div className="field" style={{ marginTop: 12 }}>
                    <label>Transcript</label>
                    <div className="call-detail-transcript">
                      {selectedCall ? (
                        <CallTranscriptBlock call={selectedCall.call} events={selectedCall.events} loadFailed={false} />
                      ) : (
                        <div style={{ color: 'var(--fg-muted)' }}>Loading call details…</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
        </div>
      )}
    </div>
    </div>
  );
}

function TeamView({ user }) {
  var _useState4 = useState(false);
  var showCreate = _useState4[0];
  var setShowCreate = _useState4[1];
  var _useState5 = useState(0);
  var teamVersion = _useState5[0];
  var setTeamVersion = _useState5[1];

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Team</div>
          <div className="card-subtitle">
            Edit a teammate’s email, phone, and role, or reset their password.
          </div>
        </div>
        <button className="button primary" onClick={function () { setShowCreate(true); }}>
          Add user
        </button>
      </div>
      <div className="card-body">
        <TenantUsersPanel
          key={teamVersion}
          tenantId={user && user.tenant_id}
          api={API.json}
          toast={showToast}
          scope="tenant"
          actorId={user && user.id}
        />
      </div>
      {showCreate ? <TeamCreateModal onClose={function () { setShowCreate(false); }} onSaved={function () { setShowCreate(false); setTeamVersion(function (n) { return n + 1; }); }} /> : null}
    </section>
  );
}

function normalizePhoneInput(val) {
  if (!val || typeof val !== 'string') return '';
  var p = val.trim().replace(/[\s().-]/g, '');
  if (!p) return '';
  if (p[0] !== '+') {
    if (p[0] === '1' && p.length === 11) p = '+' + p;
    else p = '+1' + p;
  }
  return p;
}


function TeamCreateModal({ onClose, onSaved }) {
  var _useState = useState('');
  var name = _useState[0];
  var setName = _useState[1];
  var _useState2 = useState('');
  var email = _useState2[0];
  var setEmail = _useState2[1];
  var _useState3 = useState('agent');
  var role = _useState3[0];
  var setRole = _useState3[1];
  var _useState4 = useState('');
  var password = _useState4[0];
  var setPassword = _useState4[1];
  var _useState5 = useState('');
  var phone = _useState5[0];
  var setPhone = _useState5[1];
  var _useState6 = useState(false);
  var saving = _useState6[0];
  var setSaving = _useState6[1];
  var _useState7 = useState('');
  var error = _useState7[0];
  var setError = _useState7[1];

  var mfaRequiredRoles = ['agent', 'tenant_admin'];
  var needsPhone = mfaRequiredRoles.indexOf(role) >= 0;

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Email and password required.');
      return;
    }
    var phoneE164 = phone.trim() ? normalizePhoneInput(phone) : '';
    if (needsPhone && !phoneE164) {
      setError('Phone number is required for Agent and Tenant Admin roles.');
      return;
    }
    if (phoneE164 && !/^\+[1-9]\d{9,14}$/.test(phoneE164)) {
      setError('Invalid phone. Use E.164 format (e.g. +14155551234).');
      return;
    }
    setSaving(true);
    var payload = { name: name.trim() || null, email: email.trim(), role: role, password: password };
    if (phoneE164) payload.phone_e164 = phoneE164;
    API.json('POST', '/api/users', payload)
      .then(function () {
        showToast('Team member added.', 'success');
        onSaved();
      })
      .catch(function (e) {
        var msg = (e.status === 403) ? 'You don\'t have permission: users.manage. Impersonate a tenant or check your role.' : (e.data && (e.data.error || e.data.detail)) ? (e.data.error || e.data.detail) : (e.message || 'Failed to create user.');
        setError(msg);
        showToast(msg, 'error');
      })
      .finally(function () { setSaving(false); });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={function (e) { e.stopPropagation(); }}>
        <div className="modal-header">
          <h3>Add team member</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error ? <div className="form-error">{error}</div> : null}
            <div className="form-group">
              <label>Name</label>
              <input type="text" value={name} onChange={function (e) { setName(e.target.value); }} placeholder="Jane Doe" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} placeholder="jane@example.com" required />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={role} onChange={function (e) { setRole(e.target.value); }}>
                <option value="agent">Agent</option>
                <option value="supervisor">Supervisor</option>
                <option value="tenant_admin">Tenant Admin</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="form-group">
              <label>Phone (E.164 — login SMS, Join via Phone){needsPhone ? ' *' : ''}</label>
              <input type="tel" value={phone} onChange={function (e) { setPhone(e.target.value); }} placeholder="+14155552671" required={needsPhone} />
            </div>
            <div className="form-group">
              <label>Temp password</label>
              <input type="password" value={password} onChange={function (e) { setPassword(e.target.value); }} placeholder="User will change on first login" required />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="button outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="button primary" disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function hubspotStateLine(live) {
  var state = live && live.state;
  if (state === 'connected' && live.portalId) return 'Connected to HubSpot portal ' + live.portalId;
  if (state === 'connected') return 'Connected to HubSpot';
  if (state === 'needs_reconnect') return 'Needs reconnect — click Reconnect';
  return 'Not set up: paste a private app token or ask your platform admin to add HubSpot app credentials';
}

function HubSpotIntegrationPanel({ live, activity, onConnect, onDisconnect, connecting, disconnecting, onReload }) {
  var [token, setToken] = useState('');
  var [leadId, setLeadId] = useState('');
  var [busy, setBusy] = useState('');
  var [notice, setNotice] = useState('');
  var [testUrl, setTestUrl] = useState('');
  var state = live && live.state;
  var connected = state === 'connected';
  var needsReconnect = state === 'needs_reconnect';

  function saveToken(e) {
    e.preventDefault();
    setBusy('token');
    setNotice('');
    API.json('POST', '/api/integrations/hubspot/private-token', { access_token: token }).then(function (r) {
      setToken('');
      setNotice(r && r.portalId ? ('Connected to HubSpot portal ' + r.portalId) : 'HubSpot token saved.');
      if (onReload) onReload();
    }).catch(function (err) {
      setNotice((err.data && (err.data.message || err.data.error)) || err.message || 'Could not save the token.');
    }).finally(function () { setBusy(''); });
  }

  function sendTest() {
    setBusy('test');
    setNotice('');
    setTestUrl('');
    var body = {};
    if (String(leadId).trim()) body.leadId = Number(leadId);
    API.json('POST', '/api/integrations/hubspot/test', body).then(function (r) {
      setTestUrl(r && r.remote_url ? r.remote_url : '');
      setNotice(r && r.ok ? 'Test contact synced.' : ((r && r.error) || 'Test sync did not reach HubSpot.'));
      if (onReload) onReload();
    }).catch(function (err) {
      setNotice((err.data && (err.data.message || err.data.error)) || err.message || 'Test failed.');
    }).finally(function () { setBusy(''); });
  }

  function retryFailed() {
    setBusy('retry');
    setNotice('');
    API.json('POST', '/api/integrations/hubspot/retry-failed', {}).then(function (r) {
      setNotice('Retried ' + ((r && r.retried) || 0) + ' failed syncs.');
      if (onReload) onReload();
    }).catch(function (err) {
      setNotice((err.data && (err.data.message || err.data.error)) || err.message || 'Retry failed.');
    }).finally(function () { setBusy(''); });
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className={connected ? 'success' : (needsReconnect ? 'error' : 'muted')} style={{ fontSize: '0.875rem', marginBottom: 8 }}>{hubspotStateLine(live)}</div>
      {live && live.lastSyncAt ? <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 8 }}>Last sync: <WhenText value={live.lastSyncAt} /></div> : null}
      {live && live.tokenHint ? <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 8 }}>Private app token {live.tokenHint}</div> : null}
      {live && live.counts ? (
        <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 8 }}>
          Contacts {live.counts.contacts || 0} · Calls {live.counts.calls || 0} · Failed in 24h {live.counts.failed24h || 0}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {live && live.oauthAvailable && !connected ? (
          <button type="button" className="button small primary" disabled={connecting} onClick={onConnect}>{connecting ? 'Redirecting…' : (needsReconnect ? 'Reconnect' : 'Connect')}</button>
        ) : null}
        {(connected || needsReconnect) ? (
          <button type="button" className="button small outline" disabled={disconnecting} onClick={onDisconnect}>{disconnecting ? 'Disconnecting…' : 'Disconnect'}</button>
        ) : null}
      </div>
      {!connected ? (
        <form onSubmit={saveToken} style={{ marginBottom: 12 }}>
          <label className="field">
            <span className="muted" style={{ fontSize: '0.75rem' }}>Private app token</span>
            <input type="password" className="input" autoComplete="off" value={token} onChange={function (e) { setToken(e.target.value); }} placeholder="Paste private app token" style={{ width: '100%', marginTop: 4 }} />
          </label>
          <p className="muted" style={{ fontSize: '0.75rem', margin: '6px 0' }}>Scopes: crm.objects.contacts.read, crm.objects.contacts.write, crm.objects.calls.read, crm.objects.calls.write.</p>
          <button type="submit" className="button small primary" disabled={busy === 'token' || !String(token).trim()}>{busy === 'token' ? 'Checking…' : (needsReconnect ? 'Reconnect' : 'Connect with token')}</button>
        </form>
      ) : null}
      {notice ? <div className="muted" style={{ fontSize: '0.8rem', marginBottom: 8 }}>{notice}</div> : null}
      {testUrl ? <div style={{ marginBottom: 8 }}><a href={testUrl} target="_blank" rel="noreferrer">Open in HubSpot</a></div> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <input type="number" className="input" value={leadId} onChange={function (e) { setLeadId(e.target.value); }} placeholder="Lead id (optional)" style={{ width: 140 }} />
        <button type="button" className="button small outline" disabled={busy === 'test'} onClick={sendTest}>{busy === 'test' ? 'Sending…' : 'Send a test lead'}</button>
        <button type="button" className="button small outline" disabled={busy === 'retry'} onClick={retryFailed}>{busy === 'retry' ? 'Retrying…' : 'Retry failed'}</button>
      </div>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', margin: '8px 0' }}>Recent activity</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th>Time</th>
              <th>What</th>
              <th>Action</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(activity || []).map(function (row) {
              var what = (row.object_type || 'record') + (row.local_id ? (' · ' + (row.local_type || 'record') + ' ' + row.local_id) : '');
              return (
                <tr key={row.id}>
                  <td><WhenText value={row.created_at} /></td>
                  <td>{what}</td>
                  <td className={row.action === 'failed' ? 'error' : (row.action === 'created' || row.action === 'updated' ? 'success' : 'muted')}>{row.action}</td>
                  <td>{row.remote_url ? <a href={row.remote_url} target="_blank" rel="noreferrer">Open in HubSpot</a> : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!(activity || []).length ? <div className="muted" style={{ fontSize: '0.8rem' }}>No HubSpot syncs yet. Saved contacts and calls show up here with a link.</div> : null}
      </div>
    </div>
  );
}

function IntegrationsHubView({ onNavigateToManual, user }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [showEventSettings, setShowEventSettings] = useState(false);
  const [eventSettings, setEventSettings] = useState({ event_types: [], settings: {} });
  const [eventSettingsLoading, setEventSettingsLoading] = useState(false);
  const [eventSettingsSaving, setEventSettingsSaving] = useState(false);
  const [eventSettingsSavedToast, setEventSettingsSavedToast] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [showHubSpotSyncSettings, setShowHubSpotSyncSettings] = useState(false);
  const [showHubSpotImport, setShowHubSpotImport] = useState(false);
  const [hubspotSyncSettings, setHubspotSyncSettings] = useState({ integrations_hubspot_enabled: 1, hubspot_auto_create_contacts: 1, hubspot_sync_transcripts: 1, hubspot_sync_recordings: 1 });
  const [hubspotSyncSettingsSaving, setHubspotSyncSettingsSaving] = useState(false);
  const [hubspotLists, setHubspotLists] = useState([]);
  const [hubspotImportListId, setHubspotImportListId] = useState('');
  const [hubspotImportMode, setHubspotImportMode] = useState('create_only');
  const [hubspotImportTag, setHubspotImportTag] = useState('HubSpot');
  const [hubspotImporting, setHubspotImporting] = useState(false);
  const [hubspotImportResult, setHubspotImportResult] = useState(null);
  const [hubspotLive, setHubspotLive] = useState(null);
  const [hubspotActivity, setHubspotActivity] = useState([]);
  const [showDeliveryLogs, setShowDeliveryLogs] = useState(false);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [deliveryLogsLoading, setDeliveryLogsLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const isSuperAdmin = user && user.role === 'super_admin';
  const isSuperAdminNoTenant = isSuperAdmin && (user.tenant_id == null);

  async function load() {
    if (isSuperAdminNoTenant) {
      setLoading(false);
      setProviders([]);
      setStatus({});
      setCapabilities(null);
      setPlanFeatures(null);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    setLoadFailed(false);
    try {
      var capsPath = '/api/tenant/capabilities';
      if (typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search || '')) capsPath += '?debug=1';
      const [hubRes, statusRes, capsRes, planRes] = await Promise.all([
        API.json('GET', '/api/integrations/hub'),
        API.json('GET', '/api/integrations/status').catch(function () { return { ok: false, providers: {} }; }),
        API.json('GET', capsPath).catch(function () { return null; }),
        API.json('GET', '/api/tenant/plan-features').catch(function () { return null; })
      ]);
      setProviders(hubRes.providers || []);
      if (statusRes && statusRes.ok && statusRes.providers) setStatus(statusRes.providers);
      API.json('GET', '/api/integrations/hubspot/status').then(function (hubspotStatus) {
        setHubspotLive(hubspotStatus || null);
        return API.json('GET', '/api/integrations/hubspot/activity?limit=50');
      }).then(function (activity) {
        if (activity) setHubspotActivity(activity.rows || []);
      }).catch(function () { /* HubSpot panel stays on the last good load */ });
      if (capsRes && capsRes.ok) setCapabilities(capsRes);
      if (planRes && planRes.ok) setPlanFeatures(planRes);
    } catch (e) {
      setLoadFailed(true);
      var errCode = e.data && e.data.error;
      if (errCode === 'forbidden') {
        setError('You don\'t have permission to manage integrations. Your role may lack integrations.manage, or you may need a tenant context. Check "Plan includes" below for your plan.');
      }
      try {
        var capsRetry = '/api/tenant/capabilities';
        if (typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search || '')) capsRetry += '?debug=1';
        const capsRes = await API.json('GET', capsRetry);
        if (capsRes && capsRes.ok) setCapabilities(capsRes);
        const planRes = await API.json('GET', '/api/tenant/plan-features');
        if (planRes && planRes.ok) setPlanFeatures(planRes);
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  }

  const [status, setStatus] = useState({});
  useEffect(function () { load(); }, [isSuperAdminNoTenant]);
  useEffect(function () {
    if (!showDeliveryLogs) return;
    setDeliveryLogsLoading(true);
    API.json('GET', '/api/integrations/delivery-logs?limit=50').then(function (r) {
      setDeliveryLogs(r.logs || []);
    }).catch(function () { setDeliveryLogs([]); }).finally(function () { setDeliveryLogsLoading(false); });
  }, [showDeliveryLogs]);

  function getProviderState(providerId) {
    var key = providerId === 'custom_webhook' ? 'webhook' : providerId === 'google' ? 'google_calendar' : providerId;
    var s = status[key] || status[providerId] || {};
    return {
      status: s.status || null,
      enabled_by_plan: s.enabled_by_plan !== false,
      enabled_globally: s.enabled_globally !== false,
      configured: s.configured === true,
      provider_disabled: s.provider_disabled === true,
      connected: s.connected === true,
      coming_soon: s.coming_soon === true,
      can_connect: s.canConnect === true,
      hint: s.hint || null,
      error: s.error,
      status_error: s.status_error
    };
  }

  /** Status label: exact reason from canonical status. Tile states: Disabled by platform admin | Platform OAuth app not configured | Not in your plan | Connected. */
  function getIntegrationStatus(p, state) {
    if (p && p.id === 'custom_webhook') {
      var wh = state.status || (state.connected ? 'connected' : null);
      if (wh === 'plan_blocked') return 'Not in your plan';
      if (wh === 'rbac_blocked') return "You don't have permission";
      if (wh === 'connected' || state.connected) return 'Connected';
      return 'Not configured yet';
    }
    var status = state.status || (state.connected ? 'connected' : null);
    if (status === 'coming_soon') return 'Coming soon';
    if (status === 'rbac_blocked') return "You don't have permission";
    if (status === 'plan_blocked') return 'Not in your plan';
    if (status === 'oauth_not_configured') return 'Platform OAuth app not configured';
    if (status === 'provider_disabled' || state.provider_disabled || state.enabled_globally === false) return 'Disabled by platform admin';
    if (!state.configured) return 'Platform OAuth app not configured';
    if (status === 'connected' || state.connected) return 'Connected';
    return 'Disconnected';
  }

  var showIntegrationsDebug = typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search || '');

  async function handleConnect(provider) {
    if (!['hubspot', 'outlook'].includes(provider)) return;
    const state = getProviderState(provider);
    var statusLoaded = Object.keys(status).length > 0;
    if (state.coming_soon) return;
    if (statusLoaded) {
      if (!state.can_connect) return;
    } else {
      var caps0 = capabilities && capabilities.capabilities;
      var ent0 = provider === 'hubspot' ? caps0 && caps0.hubspot : caps0 && caps0.outlook;
      if (!caps0 || !caps0.integrations || !ent0) return;
    }
    setConnecting(provider);
    setError('');
    try {
      const r = await API.json('GET', '/api/integrations/' + provider + '/start');
      if (r.url) {
        window.location.href = r.url;
        return;
      }
      setError('No connect URL returned.');
    } catch (e) {
      var err = e.data && e.data.error;
      var errMsg = err || e.message || 'Connect failed.';
      var statusCode = e.status;
      if (err === 'coming_soon' || statusCode === 501) {
        setError('Coming soon — OAuth connect is not fully available for this provider yet.');
      } else {
      setError(errMsg === 'plan_blocked' || errMsg === 'feature_not_in_plan' ? 'Your plan does not include this integration. Upgrade or ask admin.' : errMsg === 'provider_disabled' ? 'Disabled by platform admin. Ask admin to enable in OAuth Providers.' : errMsg === 'oauth_not_configured' ? 'Platform OAuth app not configured. Ask platform admin to set Client ID and Redirect URI in OAuth Providers.' : errMsg);
      }
    } finally {
      setConnecting(null);
    }
  }

  async function handleDisconnect(provider) {
    if (!confirm('Disconnect ' + provider + '? You can reconnect later.')) return;
    setDisconnecting(provider);
    setError('');
    try {
      await API.json('POST', '/api/integrations/' + encodeURIComponent(provider) + '/disconnect');
      load();
    } catch (e) {
      setError((e.data ? e.data.error : undefined) || e.message || 'Disconnect failed.');
    } finally {
      setDisconnecting(null);
    }
  }

  function openEventSettings() {
    setShowEventSettings(true);
    setEventSettingsLoading(true);
    API.json('GET', '/api/integrations/event-settings').then(function (r) {
      setEventSettings({ event_types: r.event_types || [], settings: r.settings || {} });
    }).catch(function () {
      setEventSettings({ event_types: [], settings: {} });
    }).finally(function () { setEventSettingsLoading(false); });
  }

  function setProviderEvents(provider, enabledEvents) {
    setEventSettings(function (prev) {
      var next = { event_types: prev.event_types, settings: {} };
      for (var k in prev.settings) {
        next.settings[k] = prev.settings[k] ? { enabled_events: prev.settings[k].enabled_events || [], field_mapping: prev.settings[k].field_mapping || {} } : { enabled_events: [], field_mapping: {} };
      }
      var existing = prev.settings[provider];
      next.settings[provider] = { enabled_events: enabledEvents, field_mapping: (existing && existing.field_mapping) || {} };
      return next;
    });
  }

  function saveEventSettings() {
    setEventSettingsSaving(true);
    var providers = ['hubspot', 'salesforce', 'outlook', 'custom_webhook'];
    var promises = providers.map(function (p) {
      var s = eventSettings.settings[p];
      if (!s || !Array.isArray(s.enabled_events)) return Promise.resolve();
      return API.json('PATCH', '/api/integrations/event-settings', { provider: p, enabled_events: s.enabled_events });
    });
    Promise.all(promises).then(function () {
      setEventSettingsSavedToast(true);
      setShowEventSettings(false);
      setTimeout(function () { setEventSettingsSavedToast(false); }, 2500);
    }).catch(function (e) {
      showToast('Save failed: ' + ((e.data && e.data.error) || e.message || 'Unknown error'), 'error');
    }).finally(function () { setEventSettingsSaving(false); });
  }

  function getDebugErrorCode(providerId) {
    var state = getProviderState(providerId);
    if (state.status_error) return state.status_error;
    if (state.provider_disabled) return 'provider_disabled';
    if (!state.configured) return 'oauth_not_configured';
    if (!state.enabled_by_plan) return 'plan_blocked';
    return null;
  }
  function getDebugHint(code) {
    if (code === 'provider_disabled') return 'Admin has disabled this provider. Enable it in OAuth Providers.';
    if (code === 'oauth_not_configured') return 'OAuth app not configured. Super admin: set client_id, redirect_uri in OAuth Providers.';
    if (code === 'plan_blocked' || code === 'feature_not_in_plan') return 'Your plan does not include this integration. Upgrade to enable.';
    return '';
  }

  async function recheckStatus() {
    setRechecking(true);
    setError('');
    try {
      var statusRes = await API.json('GET', '/api/integrations/status').catch(function () { return { ok: false, providers: {} }; });
      if (statusRes && statusRes.ok && statusRes.providers) setStatus(statusRes.providers);
    } finally {
      setRechecking(false);
    }
  }

  var capBlock = capabilities && capabilities.capabilities;
  var anyHubTileEntitled = !!(capBlock && (capBlock.hubspot || capBlock.salesforce || capBlock.outlook || capBlock.google_calendar || capBlock.custom_webhook));
  var pfIntList = planFeatures && planFeatures.plan_includes && planFeatures.plan_includes.integration_list;
  var hubKeys = ['hubspot', 'salesforce', 'outlook', 'google_calendar', 'custom_webhook'];
  var planListShowsHubIntegrations = Array.isArray(pfIntList) && pfIntList.some(function (k) { return hubKeys.indexOf(k) >= 0; });
  var suppressFalsePlanBanner = anyHubTileEntitled || planListShowsHubIntegrations;

  return (
    <section className="card integrations-card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="card-title">Integrations Hub</div>
          <div className="card-subtitle">
            OAuth connects supported CRMs and calendars where available. Custom webhooks are manual REST—you configure URLs and keys separately.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="button small outline" disabled={rechecking} onClick={recheckStatus}>{rechecking ? 'Checking…' : 'Re-check status'}</button>
          <button type="button" className="button small outline" onClick={openEventSettings}>Event settings</button>
          {isSuperAdmin ? (
            <a href="#oauth-providers" className="button small outline" style={{ textDecoration: 'none' }}>Configure OAuth (Admin)</a>
          ) : null}
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
        {eventSettingsSavedToast ? (
          <div className="success" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'rgba(34, 197, 94, 0.15)', fontSize: '0.875rem' }}>Event settings saved.</div>
        ) : null}
        {isSuperAdminNoTenant ? (
          <div className="form-error" style={{ marginBottom: 12, padding: 12, borderRadius: 8 }}>
            Super admin: open <strong>Tenants &amp; billing</strong> → <strong>View as</strong> for a tenant, then return here to connect CRMs or webhooks. Platform OAuth apps stay under <strong>OAuth Providers</strong>.
          </div>
        ) : capabilities && capabilities.integrations_reason === 'plan' && !suppressFalsePlanBanner ? (
          <div style={{ marginBottom: 12, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, fontSize: '0.9rem' }}>
            <p style={{ margin: '0 0 8px 0' }}>Your current plan (<strong>{(capabilities && capabilities.plan_key) || (planFeatures && planFeatures.plan_key) || '—'}</strong>) does not include any CRM, calendar, or webhook integrations (HubSpot, Salesforce, Outlook, Google Calendar, or custom webhook).</p>
            <a href="#billing" className="button primary small" style={{ textDecoration: 'none' }}>Upgrade Plan</a>
          </div>
        ) : capabilities && capabilities.integrations_reason === 'plan' && suppressFalsePlanBanner ? (
          <div style={{ marginBottom: 12, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, fontSize: '0.9rem' }}>
            <p style={{ margin: 0 }}>Your plan includes integration access (see below). If tiles still show disconnected after HubSpot, refresh this page or click Re-check status—the UI may be stale right after OAuth redirect.</p>
          </div>
        ) : capabilities && capabilities.integrations_reason === 'rbac' ? (
          <div style={{ marginBottom: 12, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, fontSize: '0.9rem' }}>
            Your role does not have <code>integrations.manage</code> permission.
          </div>
        ) : capabilities && capabilities.integrations_reason == null ? (
          <div style={{ marginBottom: 12, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, fontSize: '0.9rem' }}>
            <p style={{ margin: 0 }}>Your plan includes these integration entitlements where listed below. Whether you can connect still depends on platform OAuth setup and whether that provider supports full tenant connect today.</p>
          </div>
        ) : null}
        {(capabilities || planFeatures) && !isSuperAdminNoTenant ? (
          <div className="card-subtitle" style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--color-bg-elevated)', borderRadius: 8, fontSize: '0.8rem' }}>
            <strong>Plan:</strong> {(capabilities && capabilities.plan_key) || (planFeatures && planFeatures.plan_key) || '—'}
            {planFeatures && planFeatures.plan_includes && planFeatures.plan_includes.integration_list && planFeatures.plan_includes.integration_list.length ? (
              <span style={{ marginLeft: 8 }}> • <strong>Your integration access:</strong> {planFeatures.plan_includes.integration_list.join(', ')}</span>
            ) : null}
            {planFeatures && planFeatures.plan_includes && planFeatures.plan_includes.catalog_integration_list && planFeatures.plan_includes.catalog_integration_list.length &&
              planFeatures.plan_includes.integration_list && planFeatures.plan_includes.catalog_integration_list.slice().sort().join(',') !== planFeatures.plan_includes.integration_list.slice().sort().join(',') ? (
              <span style={{ marginLeft: 8 }} className="muted"> (plan SKU: {planFeatures.plan_includes.catalog_integration_list.join(', ')})</span>
            ) : null}
          </div>
        ) : null}
        {loading ? (
          <div className="muted">Loading…</div>
        ) : loadFailed ? (
          <BuyerListNotice title="Couldn't load integrations" body="We couldn't retrieve your integration settings. Try again." onRetry={load} />
        ) : providers.length === 0 && !isSuperAdminNoTenant ? (
          <BuyerListNotice title="No integrations connected" body="Connect an available integration to start using it with this workspace." />
        ) : (
          <div className="integrations-marketplace" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {providers.map(function (p) {
              var state = getProviderState(p.id);
              var statusText = getIntegrationStatus(p, state);
              var statusLoaded = Object.keys(status).length > 0;
              var connected = state.status != null && state.status !== ''
                ? (state.status === 'connected' || state.connected)
                : (p.status === 'connected' || state.connected);
              if (p.id === 'hubspot' && hubspotLive && hubspotLive.state === 'connected') connected = true;
              var caps = capabilities && capabilities.capabilities;
              var entitledOAuth = p.id === 'hubspot' ? caps && caps.hubspot : p.id === 'outlook' ? caps && caps.outlook : false;
              var oauthFallback = !!(caps && caps.integrations && entitledOAuth) && !connected;
              var canConnect = p.oauth && !connected;
              if (p.id === 'salesforce' || p.id === 'google') {
                canConnect = false;
              } else if (statusLoaded) {
                canConnect = state.can_connect === true && p.oauth;
              } else if (p.id === 'hubspot' || p.id === 'outlook') {
                canConnect = oauthFallback;
              } else {
                canConnect = false;
              }
              var canDisconnect = connected && p.id !== 'google';
              var blockedStatus = state.status && ['provider_disabled', 'oauth_not_configured', 'plan_blocked', 'rbac_blocked'].indexOf(state.status) >= 0;
              if (p.id === 'custom_webhook') {
                return (
                  <div key={p.id} className="integration-card" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.name} <span className="muted" style={{ fontWeight: 400, fontSize: '0.75rem' }}>(manual REST)</span></div>
                    <div className="muted" style={{ fontSize: '0.875rem', marginBottom: 8 }}>Not OAuth—add your REST endpoint, headers, and webhook/API keys on the manual integrations page.</div>
                    <div className="muted" style={{ fontSize: '0.875rem', marginBottom: 12 }}>{p.description}</div>
                    <div style={{ marginBottom: 12 }}>
                      <span className={connected ? 'success' : 'muted'}>{statusText}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <a href="#integrations/manual" className="button primary small">Open manual setup</a>
                    </div>
                  </div>
                );
              }
              var singleAction = null;
              if (canConnect) {
                singleAction = (
                  <button
                    className="button primary small"
                    disabled={connecting !== null}
                    onClick={function () { handleConnect(p.id); }}
                  >
                    {connecting === p.id ? 'Redirecting…' : 'Connect'}
                  </button>
                );
              } else if (canDisconnect) {
                singleAction = (
                  <button
                    className="button small outline"
                    disabled={disconnecting !== null}
                    onClick={function () { handleDisconnect(p.id); }}
                  >
                    {disconnecting === p.id ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                );
              } else if (state.coming_soon || state.status === 'coming_soon') {
                var soonMsg = p.id === 'salesforce'
                  ? 'Tenant connect is not fully available yet—the OAuth callback is not implemented, so Connect is disabled.'
                  : p.id === 'google'
                  ? 'Google Calendar tenant OAuth is not available in this product yet—there is no tenant connect route.'
                  : 'This integration is not fully available yet.';
                singleAction = <span className="muted" style={{ fontSize: '0.875rem' }}>{soonMsg}</span>;
              } else if (blockedStatus) {
                var fixMsg = state.status === 'provider_disabled' || state.status === 'oauth_not_configured'
                  ? 'Ask platform admin to configure OAuth Providers'
                  : state.status === 'plan_blocked'
                  ? 'Upgrade plan / ask admin'
                  : state.status === 'rbac_blocked'
                  ? 'Ask tenant admin for permission'
                  : null;
                singleAction = (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {isSuperAdmin && (state.status === 'provider_disabled' || state.status === 'oauth_not_configured') ? (
                      <a href="#oauth-providers" className="button small primary" style={{ textDecoration: 'none' }}>Fix it: OAuth Providers</a>
                    ) : fixMsg ? (
                      <span className="muted" style={{ fontSize: '0.875rem' }}>{fixMsg}</span>
                    ) : null}
                  </div>
                );
              } else if (statusText === 'Disabled by plan') {
                singleAction = <span className="muted" style={{ fontSize: '0.875rem' }}>Upgrade</span>;
              }
              var hubspotExtra = p.id === 'hubspot' && connected ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="button small outline" onClick={function () {
                    API.json('GET', '/api/tenant/settings').then(function (r) {
                      setHubspotSyncSettings({
                        integrations_hubspot_enabled: r.integrations_hubspot_enabled !== 0 ? 1 : 0,
                        hubspot_auto_create_contacts: r.hubspot_auto_create_contacts !== 0 ? 1 : 0,
                        hubspot_sync_transcripts: r.hubspot_sync_transcripts !== 0 ? 1 : 0,
                        hubspot_sync_recordings: r.hubspot_sync_recordings !== 0 ? 1 : 0
                      });
                      setShowHubSpotSyncSettings(true);
                    }).catch(function () { setShowHubSpotSyncSettings(true); });
                  }}>Sync settings</button>
                  <button type="button" className="button small outline" onClick={function () {
                    setHubspotImportResult(null);
                    API.json('GET', '/api/integrations/hubspot/lists').then(function (r) {
                      setHubspotLists(r.lists || []);
                      setHubspotImportListId((r.lists && r.lists[0]) ? r.lists[0].listId : '');
                      setShowHubSpotImport(true);
                    }).catch(function (e) { setHubspotLists([]); setShowHubSpotImport(true); });
                  }}>Import Leads</button>
                </div>
              ) : null;
              return (
                <div key={p.id} className="integration-card" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                  <div className="muted" style={{ fontSize: '0.875rem', marginBottom: 12 }}>{p.description}</div>
                  {state.coming_soon ? (
                    <p className="muted" style={{ fontSize: '0.8rem', marginBottom: 10, fontStyle: 'italic' }}>
                      {p.id === 'salesforce'
                        ? 'Partial: plan may allow Salesforce, but tenant OAuth is incomplete in this release.'
                        : p.id === 'google'
                        ? 'Partial: Google Calendar is shown for roadmap visibility only—no working tenant OAuth connect yet.'
                        : 'Coming soon — connect is not available for this provider yet.'}
                    </p>
                  ) : null}
                  {p.id === 'hubspot' ? (
                    <HubSpotIntegrationPanel
                      live={hubspotLive}
                      activity={hubspotActivity}
                      onConnect={function () { handleConnect('hubspot'); }}
                      onDisconnect={function () { handleDisconnect('hubspot'); }}
                      connecting={connecting === 'hubspot'}
                      disconnecting={disconnecting === 'hubspot'}
                      onReload={load}
                    />
                  ) : (
                    <div style={{ marginBottom: 12 }}>
                      <span className={connected ? 'success' : 'muted'}>{statusText}</span>
                      {p.last_sync_at && connected && <span className="muted" style={{ marginLeft: 8, fontSize: '0.75rem' }}>Last sync: <WhenText value={p.last_sync_at} /></span>}
                      {p.last_error && <div className="error" style={{ fontSize: '0.75rem', marginTop: 4 }}>{p.last_error}</div>}
                    </div>
                  )}
                  {p.id !== 'hubspot' && singleAction && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {singleAction}
                    </div>
                  )}
                  {hubspotExtra}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 24, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <a href="#integrations/manual" className="muted" style={{ fontSize: '0.875rem' }}>Configure REST / webhook integrations →</a>
          <button type="button" className="button small outline" onClick={function () { setShowDeliveryLogs(!showDeliveryLogs); }}>
            {showDeliveryLogs ? 'Hide delivery logs' : 'View delivery logs'}
          </button>
        </div>
        {showDeliveryLogs && (
          <div className="card-body" style={{ borderTop: '1px solid var(--border)', marginTop: 12, overflowX: 'auto' }}>
            {deliveryLogsLoading ? (
              <div className="muted">Loading…</div>
            ) : (
              <table className="table" style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>id</th>
                    <th>provider</th>
                    <th>event_type</th>
                    <th>status</th>
                    <th>attempt_count</th>
                    <th>last_error</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryLogs.map(function (row) {
                    return (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.provider}</td>
                        <td>{row.event_type}</td>
                        <td className={row.status === 'success' ? 'success' : (row.status === 'failed' ? 'error' : '')}>{row.status}</td>
                        <td>{row.attempt_count}</td>
                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.last_error}>{row.last_error || '—'}</td>
                        <td><WhenText value={row.created_at} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!deliveryLogsLoading && deliveryLogs.length === 0 && <div className="muted" style={{ padding: 8 }}>No delivery logs.</div>}
            <div style={{ marginTop: 8 }}><TimesShown /></div>
          </div>
        )}
        {showIntegrationsDebug ? (
          <div className="integrations-debug-panel" style={{ marginTop: 20, padding: 12, background: 'var(--color-bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.8rem' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Debug (?debug=1)</div>
            <div style={{ marginBottom: 12 }}>
              <div><strong>plan_key:</strong> {(capabilities && capabilities.plan_key) || (planFeatures && planFeatures.plan_key) || '—'}</div>
              <div><strong>features (effective):</strong> [{((planFeatures && planFeatures.plan_includes && planFeatures.plan_includes.integration_list) || []).join(', ')}]</div>
              <div><strong>features (plan SKU):</strong> [{((planFeatures && planFeatures.plan_includes && planFeatures.plan_includes.catalog_integration_list) || []).join(', ')}]</div>
              <div><strong>integrations_reason:</strong> {(capabilities && capabilities.integrations_reason) || 'null'}</div>
              {capabilities && capabilities.debug ? (
                <div><strong>capabilities.debug:</strong> <code style={{ wordBreak: 'break-all' }}>{JSON.stringify(capabilities.debug)}</code></div>
              ) : null}
            </div>
            <p className="muted" style={{ marginBottom: 8, fontSize: '0.75rem' }}>Per-provider hints (from last status load):</p>
            {['hubspot', 'outlook', 'salesforce', 'google'].map(function (providerId) {
              var code = getDebugErrorCode(providerId);
              var hint = getDebugHint(code);
              var provState = getProviderState(providerId);
              var reason = provState.status || code || 'ok';
              return (
                <div key={providerId} style={{ marginBottom: 8 }}>
                  <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{providerId}</span>
                  <span>: <code style={{ background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>{reason}</code>
                    {hint ? <span className="muted" style={{ marginLeft: 8 }}>— {hint}</span> : null}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {showEventSettings ? (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function (e) { if (e.target === e.currentTarget) setShowEventSettings(false); }}>
          <div className="card" style={{ maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }} onClick={function (e) { e.stopPropagation(); }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="card-title">Event settings</div>
              <button type="button" className="button small outline" onClick={function () { setShowEventSettings(false); }}>Close</button>
            </div>
            <div className="card-body">
              {eventSettingsLoading ? (
                <div className="muted">Loading…</div>
              ) : (
                <div>
                  <p className="muted" style={{ marginBottom: 16, fontSize: '0.875rem' }}>Choose which events to send to each integration (e.g. lead_created, call_completed).</p>
                  {['hubspot', 'salesforce', 'outlook', 'custom_webhook'].map(function (provider) {
                    var s = eventSettings.settings[provider];
                    var events = (s && Array.isArray(s.enabled_events)) ? s.enabled_events : (eventSettings.event_types || []);
                    var provState = getProviderState(provider === 'custom_webhook' ? 'custom_webhook' : provider);
                    var isConnected = provState.connected;
                    return (
                      <div key={provider} style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, textTransform: 'capitalize' }}>{provider.replace('_', ' ')}</div>
                        {!isConnected && provider !== 'custom_webhook' ? <p className="muted" style={{ fontSize: '0.75rem', marginBottom: 8 }}>Will apply after connect.</p> : null}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {(eventSettings.event_types || []).map(function (ev) {
                            var checked = events.indexOf(ev) !== -1;
                            return (
                              <label key={ev} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
                                <input type="checkbox" checked={checked} onChange={function (e) {
                                  var next = e.target.checked ? events.concat(ev) : events.filter(function (x) { return x !== ev; });
                                  setProviderEvents(provider, next);
                                }} />
                                <span>{ev}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button type="button" className="button primary" disabled={eventSettingsSaving} onClick={saveEventSettings}>{eventSettingsSaving ? 'Saving…' : 'Save'}</button>
                    <button type="button" className="button outline" onClick={function () { setShowEventSettings(false); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {showHubSpotSyncSettings ? (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function (e) { if (e.target === e.currentTarget) setShowHubSpotSyncSettings(false); }}>
          <div className="card" style={{ maxWidth: 420 }} onClick={function (e) { e.stopPropagation(); }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="card-title">HubSpot sync settings</div>
              <button type="button" className="button small outline" onClick={function () { setShowHubSpotSyncSettings(false); }}>Close</button>
            </div>
            <div className="card-body">
              <p className="muted" style={{ fontSize: '0.875rem', marginBottom: 16 }}>Control what gets synced to HubSpot after each call.</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={hubspotSyncSettings.integrations_hubspot_enabled === 1} onChange={function (e) { setHubspotSyncSettings(function (s) { return { ...s, integrations_hubspot_enabled: e.target.checked ? 1 : 0 }; }); }} />
                <span>Sync calls to HubSpot</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={hubspotSyncSettings.hubspot_auto_create_contacts === 1} onChange={function (e) { setHubspotSyncSettings(function (s) { return { ...s, hubspot_auto_create_contacts: e.target.checked ? 1 : 0 }; }); }} />
                <span>Auto-create contacts</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={hubspotSyncSettings.hubspot_sync_transcripts === 1} onChange={function (e) { setHubspotSyncSettings(function (s) { return { ...s, hubspot_sync_transcripts: e.target.checked ? 1 : 0 }; }); }} />
                <span>Sync transcript</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={hubspotSyncSettings.hubspot_sync_recordings === 1} onChange={function (e) { setHubspotSyncSettings(function (s) { return { ...s, hubspot_sync_recordings: e.target.checked ? 1 : 0 }; }); }} />
                <span>Sync recording link</span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="button primary" disabled={hubspotSyncSettingsSaving} onClick={function () {
                  setHubspotSyncSettingsSaving(true);
                  API.json('PATCH', '/api/tenant/settings', hubspotSyncSettings).then(function () { showToast('HubSpot sync settings saved.'); setShowHubSpotSyncSettings(false); }).catch(function (err) { showToast((err.data && err.data.error) || err.message || 'Save failed', 'error'); }).finally(function () { setHubspotSyncSettingsSaving(false); });
                }}>{hubspotSyncSettingsSaving ? 'Saving…' : 'Save'}</button>
                <button type="button" className="button outline" onClick={function () { setShowHubSpotSyncSettings(false); }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showHubSpotImport ? (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function (e) { if (e.target === e.currentTarget) setShowHubSpotImport(false); }}>
          <div className="card" style={{ maxWidth: 480 }} onClick={function (e) { e.stopPropagation(); }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="card-title">Import leads from HubSpot</div>
              <button type="button" className="button small outline" onClick={function () { setShowHubSpotImport(false); }}>Close</button>
            </div>
            <div className="card-body">
              <div className="field" style={{ marginBottom: 12 }}>
                <label>List</label>
                <select className="input" value={hubspotImportListId} onChange={function (e) { setHubspotImportListId(e.target.value); }} style={{ width: '100%' }}>
                  {hubspotLists.map(function (l) { return <option key={l.listId} value={l.listId}>{l.name || l.listId}</option>; })}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label>Mode</label>
                <select className="input" value={hubspotImportMode} onChange={function (e) { setHubspotImportMode(e.target.value); }} style={{ width: '100%' }}>
                  <option value="create_only">Create only (skip existing)</option>
                  <option value="upsert">Upsert (update existing)</option>
                </select>
              </div>
              <div className="field" style={{ marginBottom: 16 }}>
                <label>Tag (source label)</label>
                <input type="text" className="input" value={hubspotImportTag} onChange={function (e) { setHubspotImportTag(e.target.value); }} placeholder="HubSpot" style={{ width: '100%' }} />
              </div>
              {hubspotImportResult ? (
                <div className="success" style={{ marginBottom: 16, padding: 10, borderRadius: 6, fontSize: '0.875rem' }}>
                  Imported: {hubspotImportResult.imported}, Skipped: {hubspotImportResult.skipped}, Updated: {hubspotImportResult.updated}
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="button primary" disabled={hubspotImporting || !hubspotImportListId} onClick={function () {
                  setHubspotImporting(true);
                  setHubspotImportResult(null);
                  API.json('POST', '/api/integrations/hubspot/import', { listId: hubspotImportListId, mode: hubspotImportMode, tag: hubspotImportTag || 'HubSpot' }).then(function (r) {
                    setHubspotImportResult({ imported: r.imported || 0, skipped: r.skipped || 0, updated: r.updated || 0 });
                  }).catch(function (e) { setHubspotImportResult({ imported: 0, skipped: 0, updated: 0 }); alert((e.data && e.data.detail) || e.message || 'Import failed'); }).finally(function () { setHubspotImporting(false); });
                }}>{hubspotImporting ? 'Importing…' : 'Import'}</button>
                <button type="button" className="button outline" onClick={function () { setShowHubSpotImport(false); }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ExternalIntegrationsView({ onBackToHub }) {
  const [items, setItems] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testId, setTestId] = useState(null);
  const [showKeysFor, setShowKeysFor] = useState(null);
  const [showCreateKey, setShowCreateKey] = useState(null);

  async function load() {
    setLoading(true);
    setError('');
    setLoadFailed(false);
    try {
      const r = await API.json('GET', '/api/external-integrations');
      setItems(r.items || []);
    } catch (e) {
      setLoadFailed(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadKeys() {
    try {
      const r = await API.json('GET', '/api/external-integrations/keys');
      setKeys(r.items || []);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { if (showKeysFor !== null) loadKeys(); }, [showKeysFor]);

  return (
    <section className="card integrations-card">
      <div className="card-header integrations-manual-header">
        <div>
          {onBackToHub && (
            <button type="button" className="button small outline" style={{ marginBottom: 8 }} onClick={onBackToHub}>
              ← Back to External Integrations
            </button>
          )}
          <div className="card-title">Manual REST / Webhook integrations</div>
          <div className="card-subtitle integrations-manual-subtitle">
            Configure REST integrations for external data. Create, test, and manage API keys.
          </div>
        </div>
        <button type="button" className="button primary integrations-create-btn" onClick={() => { setShowCreate(true); setError(''); }}>
          Create integration
        </button>
      </div>
      <div className="card-body integrations-card-body">
        {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
        {loading ? (
          <div className="muted">Loading integrations…</div>
        ) : loadFailed ? (
          <BuyerListNotice title="Couldn't load integrations" body="We couldn't retrieve your integration settings. Try again." onRetry={load} />
        ) : items.length === 0 ? (
          <BuyerListNotice title="No integrations connected" body="Connect an available integration to start using it with this workspace." />
        ) : (
          <div className="admin-table-scroll">
            <table className="table table--mobile-cards">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Base URL</th>
                  <th>RPM</th>
                  <th>Daily</th>
                  <th>Private IPs</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                <tr key={i.id}>
                  <td data-label="Name">{i.name}</td>
                  <td data-label="Kind">{i.kind || 'rest'}</td>
                  <td data-label="Base URL" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }} title={i.base_url}>{i.base_url || '—'}</td>
                  <td data-label="RPM">{(i.rpm_limit === null || i.rpm_limit === undefined) ? '—' : i.rpm_limit}</td>
                  <td data-label="Daily">{(i.daily_quota === null || i.daily_quota === undefined) ? '—' : i.daily_quota}</td>
                  <td data-label="Private IPs">{i.allow_private_ips ? 'Yes' : 'No'}</td>
                  <td data-label="Created"><WhenText value={i.created_at} dateOnly /></td>
                  <td className="mobile-actions-cell external-int-actions">
                    <button type="button" className="button small outline" onClick={() => setEditingId(i.id)}>Edit</button>
                    <button type="button" className="button small outline" style={{ marginLeft: 4 }} onClick={() => setTestId(i.id)}>Test</button>
                    <button type="button" className="button small outline" style={{ marginLeft: 4 }} onClick={() => { setShowKeysFor(i.id); setShowCreateKey(null); }}>Keys</button>
                    <button type="button" className="button small outline" style={{ marginLeft: 4 }} onClick={async () => {
                      if (!confirm('Delete this integration? This cannot be undone.')) return;
                      try {
                        await API.json('DELETE', '/api/external-integrations/' + i.id);
                        load();
                      } catch (e) {
                        setError('Failed to delete.');
                      }
                    }}>Delete</button>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showCreate && <IntegrationModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editingId && <IntegrationEditModal id={editingId} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />}
      {testId && <IntegrationTestModal id={testId} onClose={() => setTestId(null)} />}
      {showKeysFor && <IntegrationKeysModal integrationId={showKeysFor} keys={keys} onClose={() => { setShowKeysFor(null); setShowCreateKey(null); }} onCreateKey={() => setShowCreateKey(showKeysFor)} onRefresh={loadKeys} />}
      {showCreateKey && <CreateKeyModal integrationId={showCreateKey} onClose={() => setShowCreateKey(null)} onSaved={() => { setShowCreateKey(null); loadKeys(); }} />}
    </section>
  );
}

function IntegrationModal({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('rest');
  const [baseUrl, setBaseUrl] = useState('');
  const [allowedHosts, setAllowedHosts] = useState('');
  const [allowedPathPrefixes, setAllowedPathPrefixes] = useState('/');
  const [authType, setAuthType] = useState('none');
  const [authToken, setAuthToken] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authHeaderName, setAuthHeaderName] = useState('X-API-Key');
  const [authHeaderValue, setAuthHeaderValue] = useState('');
  const [rpmLimit, setRpmLimit] = useState(60);
  const [dailyQuota, setDailyQuota] = useState(1000);
  const [allowPrivateIps, setAllowPrivateIps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function getAuthConfig() {
    if (authType === 'bearer_static') return { token: authToken };
    if (authType === 'basic') return { username: authUsername, password: authPassword };
    if (authType === 'api_key_header') return { headerName: authHeaderName, apiKey: authHeaderValue };
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!name.trim() || !baseUrl.trim()) {
      setError('Name and Base URL are required.');
      return;
    }
    setSaving(true);
    try {
      const hosts = allowedHosts.split(/[\n,]/).map((h) => h.trim()).filter(Boolean);
      const prefixes = allowedPathPrefixes.split(/[\n,]/).map((p) => p.trim()).filter(Boolean);
      const payload = {
        name: name.trim(),
        kind: kind,
        base_url: baseUrl.trim(),
        allowed_hosts: hosts.length ? hosts : undefined,
        allowed_path_prefixes: prefixes.length ? prefixes : undefined,
        auth_type: authType,
        auth_config: getAuthConfig(),
        rpm_limit: parseInt(rpmLimit, 10) || 60,
        daily_quota: parseInt(dailyQuota, 10) || 1000,
        allow_private_ips: allowPrivateIps
      };
      await API.json('POST', '/api/external-integrations', payload);
      setName(''); setBaseUrl(''); setAuthToken(''); setAuthUsername(''); setAuthPassword(''); setAuthHeaderValue('');
      onSaved();
    } catch (e) {
      const msg = (e && e.message) || String(e);
      setError(msg.includes('ssrf') || msg.includes('blocked') ? 'Request blocked (SSRF protection).' : msg.includes('429') || msg.includes('rate') ? 'Rate limit exceeded.' : 'Failed to create integration.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>Create integration</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body stack gap-sm">
          <label className="field"><span className="field-label">Name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="My API" required /></label>
          <label className="field"><span className="field-label">Kind</span><input type="text" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="rest" /></label>
          <label className="field"><span className="field-label">Base URL</span><input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com" required /></label>
          <label className="field"><span className="field-label">Allowed hosts (one per line or comma)</span><textarea value={allowedHosts} onChange={(e) => setAllowedHosts(e.target.value)} placeholder="api.example.com" rows={2} /></label>
          <label className="field"><span className="field-label">Allowed path prefixes</span><input type="text" value={allowedPathPrefixes} onChange={(e) => setAllowedPathPrefixes(e.target.value)} placeholder="/" /></label>
          <label className="field">
            <span className="field-label">Auth type</span>
            <select value={authType} onChange={(e) => setAuthType(e.target.value)}>
              <option value="none">None</option>
              <option value="bearer_static">Bearer (static token)</option>
              <option value="basic">Basic</option>
              <option value="api_key_header">API Key (header)</option>
            </select>
          </label>
          {authType === 'bearer_static' && <label className="field"><span className="field-label">Token</span><input type="password" value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></label>}
          {authType === 'basic' && (<React.Fragment><label className="field"><span className="field-label">Username</span><input type="text" value={authUsername} onChange={(e) => setAuthUsername(e.target.value)} /></label><label className="field"><span className="field-label">Password</span><input type="password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} autoComplete="new-password" /></label></React.Fragment>)}
          {authType === 'api_key_header' && (<React.Fragment><label className="field"><span className="field-label">Header name</span><input type="text" value={authHeaderName} onChange={(e) => setAuthHeaderName(e.target.value)} placeholder="X-API-Key" /></label><label className="field"><span className="field-label">Header value</span><input type="password" value={authHeaderValue} onChange={(e) => setAuthHeaderValue(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></label></React.Fragment>)}
          <label className="field"><span className="field-label">RPM limit</span><input type="number" min={1} max={300} value={rpmLimit} onChange={(e) => setRpmLimit(e.target.value)} /></label>
          <label className="field"><span className="field-label">Daily quota</span><input type="number" min={1} max={10000} value={dailyQuota} onChange={(e) => setDailyQuota(e.target.value)} /></label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={allowPrivateIps} onChange={(e) => setAllowPrivateIps(e.target.checked)} />
            <span><strong>Allow private IPs</strong> <span style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>(Advanced / Dangerous – only enable if you understand the risks)</span></span>
          </label>
          {error && <div className="form-error">{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="submit" className="button primary" disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
            <button type="button" className="button outline" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function IntegrationEditModal({ id, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [allowedHosts, setAllowedHosts] = useState('');
  const [allowedPathPrefixes, setAllowedPathPrefixes] = useState('');
  const [authType, setAuthType] = useState('none');
  const [authToken, setAuthToken] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [authHeaderValue, setAuthHeaderValue] = useState('');
  const [rpmLimit, setRpmLimit] = useState(60);
  const [dailyQuota, setDailyQuota] = useState(1000);
  const [allowPrivateIps, setAllowPrivateIps] = useState(false);
  const [replaceSecret, setReplaceSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await API.json('GET', '/api/external-integrations/' + id);
        setData(r);
        setName(r.name || '');
        setBaseUrl(r.base_url || '');
        const hosts = Array.isArray(r.allowed_hosts) ? r.allowed_hosts : (typeof r.allowed_hosts === 'string' ? (r.allowed_hosts.startsWith('[') ? JSON.parse(r.allowed_hosts) : r.allowed_hosts.split(',')) : []);
        const prefixes = Array.isArray(r.allowed_path_prefixes) ? r.allowed_path_prefixes : (typeof r.allowed_path_prefixes === 'string' ? (r.allowed_path_prefixes.startsWith('[') ? JSON.parse(r.allowed_path_prefixes) : r.allowed_path_prefixes) : ['/']);
        setAllowedHosts(Array.isArray(hosts) ? hosts.join('\n') : String(hosts || ''));
        setAllowedPathPrefixes(Array.isArray(prefixes) ? prefixes.join('\n') : (prefixes || '/'));
        setAuthType(r.auth_type || 'none');
        setRpmLimit((r.rpm_limit === null || r.rpm_limit === undefined) ? 60 : r.rpm_limit);
        setDailyQuota((r.daily_quota === null || r.daily_quota === undefined) ? 1000 : r.daily_quota);
        setAllowPrivateIps(!!r.allow_private_ips);
      } catch (e) {
        setError('Failed to load.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  function getAuthConfig() {
    if (authType === 'none') return null;
    if (authType === 'bearer_static') return replaceSecret && authToken ? { token: authToken } : null;
    if (authType === 'basic') return replaceSecret ? { username: authUsername, password: authPassword } : null;
    if (authType === 'api_key_header') return replaceSecret && authHeaderValue ? { headerName: authHeaderName || 'X-API-Key', apiKey: authHeaderValue } : null;
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const hosts = allowedHosts.split(/[\n,]/).map((h) => h.trim()).filter(Boolean);
      const prefixes = allowedPathPrefixes.split(/[\n,]/).map((p) => p.trim()).filter(Boolean);
      const payload = {
        name: name.trim(),
        base_url: baseUrl.trim(),
        allowed_hosts: hosts.length ? hosts : undefined,
        allowed_path_prefixes: prefixes.length ? prefixes : undefined,
        auth_type: authType,
        rpm_limit: parseInt(rpmLimit, 10) || 60,
        daily_quota: parseInt(dailyQuota, 10) || 1000,
        allow_private_ips: allowPrivateIps
      };
      const ac = getAuthConfig();
      if (ac) payload.auth_config = ac;
      await API.json('PATCH', '/api/external-integrations/' + id, payload);
      onSaved();
    } catch (e) {
      const msg = (e && e.message) || String(e);
      setError(msg.includes('ssrf') || msg.includes('blocked') ? 'Request blocked (SSRF protection).' : msg.includes('429') || msg.includes('rate') ? 'Rate limit exceeded.' : 'Failed to update.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="modal-overlay" onClick={onClose}><div className="modal" onClick={(e) => e.stopPropagation()}><div className="modal-body">Loading…</div></div></div>;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header"><h3>Edit integration</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit} className="modal-body stack gap-sm">
          <label className="field"><span className="field-label">Name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} required /></label>
          <label className="field"><span className="field-label">Base URL</span><input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required /></label>
          <label className="field"><span className="field-label">Allowed hosts</span><textarea value={allowedHosts} onChange={(e) => setAllowedHosts(e.target.value)} rows={2} /></label>
          <label className="field"><span className="field-label">Allowed path prefixes</span><input type="text" value={allowedPathPrefixes} onChange={(e) => setAllowedPathPrefixes(e.target.value)} /></label>
          <label className="field"><span className="field-label">Auth type</span><select value={authType} onChange={(e) => setAuthType(e.target.value)}><option value="none">None</option><option value="bearer_static">Bearer</option><option value="basic">Basic</option><option value="api_key_header">API Key</option></select></label>
          {authType === 'bearer_static' && (
            <div className="field">
              <span className="field-label">Token</span>
              <input type="password" value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder="•••••••• (leave blank to keep existing)" autoComplete="new-password" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <input type="checkbox" checked={replaceSecret} onChange={(e) => setReplaceSecret(e.target.checked)} />
                <span>Replace secret</span>
              </div>
            </div>
          )}
          {authType === 'basic' && (
            <React.Fragment>
              <div className="field">
                <span className="field-label">Username</span>
                <input type="text" value={authUsername} onChange={(e) => setAuthUsername(e.target.value)} placeholder="(leave blank to keep)" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <input type="checkbox" checked={replaceSecret} onChange={(e) => setReplaceSecret(e.target.checked)} />
                  <span>Replace secret</span>
                </div>
              </div>
              <label className="field"><span className="field-label">Password</span><input type="password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></label>
            </React.Fragment>
          )}
          {authType === 'api_key_header' && (
            <React.Fragment>
              <label className="field"><span className="field-label">Header name</span><input type="text" value={authHeaderName} onChange={(e) => setAuthHeaderName(e.target.value)} placeholder="X-API-Key" /></label>
              <div className="field">
                <span className="field-label">Header value</span>
                <input type="password" value={authHeaderValue} onChange={(e) => setAuthHeaderValue(e.target.value)} placeholder="•••••••• (leave blank to keep)" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <input type="checkbox" checked={replaceSecret} onChange={(e) => setReplaceSecret(e.target.checked)} />
                  <span>Replace secret</span>
                </div>
              </div>
            </React.Fragment>
          )}
          <label className="field"><span className="field-label">RPM limit</span><input type="number" min={1} max={300} value={rpmLimit} onChange={(e) => setRpmLimit(e.target.value)} /></label>
          <label className="field"><span className="field-label">Daily quota</span><input type="number" min={1} max={10000} value={dailyQuota} onChange={(e) => setDailyQuota(e.target.value)} /></label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={allowPrivateIps} onChange={(e) => setAllowPrivateIps(e.target.checked)} /><span><strong>Allow private IPs</strong> <span style={{ color: 'var(--danger)' }}>(Advanced / Dangerous)</span></span></label>
          {error && <div className="form-error">{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button type="submit" className="button primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button><button type="button" className="button outline" onClick={onClose}>Cancel</button></div>
        </form>
      </div>
    </div>
  );
}

function IntegrationTestModal({ id, onClose }) {
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function runTest() {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const payload = { method: method || 'GET', path: path || '/', body: body ? (body.trim().startsWith('{') ? JSON.parse(body.trim()) : body) : undefined };
      const r = await API.json('POST', '/api/external-integrations/' + id + '/test', payload);
      setResult(r);
    } catch (e) {
      const msg = (e && e.message) || String(e);
      setError(msg.includes('ssrf') || msg.includes('blocked') ? 'SSRF blocked.' : msg.includes('429') || msg.includes('rate') ? 'Rate limited.' : (msg || 'Test failed.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header"><h3>Test integration</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body stack gap-sm">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label className="field"><span className="field-label">Method</span><select value={method} onChange={(e) => setMethod(e.target.value)}><option value="GET">GET</option><option value="POST">POST</option><option value="PUT">PUT</option><option value="PATCH">PATCH</option></select></label>
            <label className="field"><span className="field-label">Path</span><input type="text" value={path} onChange={(e) => setPath(e.target.value)} placeholder="/" style={{ minWidth: 120 }} /></label>
          </div>
          <label className="field"><span className="field-label">Body (optional JSON)</span><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder='{"key": "value"}' /></label>
          {error && <div className="form-error">{error}</div>}
          <button className="button primary" onClick={runTest} disabled={loading}>{loading ? 'Testing…' : 'Run test'}</button>
          {result && (
            <div style={{ marginTop: 12, padding: 12, background: 'var(--surface-sunken)', borderRadius: 8, fontSize: '0.8rem' }}>
              <div><strong>Status:</strong> {result.status}{result.status >= 400 && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>(Error{result.status === 403 ? ' – SSRF blocked' : result.status === 429 ? ' – Rate limited' : ''})</span>}</div>
              {result.latencyMs != null && <div><strong>Latency:</strong> {result.latencyMs} ms</div>}
              <div><strong>Response:</strong></div>
              <pre style={{ marginTop: 4, overflow: 'auto', maxHeight: 200 }}>{typeof result.body === 'string' ? result.body : (result.body != null ? JSON.stringify(result.body) : result.detail || '—')}</pre>
            </div>
          )}
          <button className="button outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function IntegrationKeysModal({ integrationId, keys, onClose, onCreateKey, onRefresh }) {
  const filtered = keys.filter((k) => k.integration_id === integrationId);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header"><h3>API Keys</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <button className="button primary" onClick={onCreateKey} style={{ marginBottom: 12 }}>Create key</button>
          <table className="table"><thead><tr><th>Prefix</th><th>Created</th><th>Status</th><th></th></tr></thead><tbody>
            {filtered.map((k) => (
              <tr key={k.id}>
                <td><code>{k.key_prefix}…</code></td>
                <td><WhenText value={k.created_at} /></td>
                <td>{k.revoked_at ? 'Revoked' : 'Active'}</td>
                <td>{!k.revoked_at && <button className="button small outline" onClick={async () => { if (confirm('Revoke this key? It will stop working immediately.')) { try { await API.json('POST', '/api/external-integrations/keys/' + k.id + '/revoke'); onRefresh(); } catch (e) { alert('Failed'); } } }}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody></table>
          {filtered.length === 0 && <div className="muted">No keys yet.</div>}
          <button className="button outline" onClick={onClose} style={{ marginTop: 12 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function CreateKeyModal({ integrationId, onClose, onSaved }) {
  const [createdKey, setCreatedKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function create() {
    setLoading(true);
    setError('');
    try {
      const r = await API.json('POST', '/api/external-integrations/keys', { integration_id: integrationId });
      setCreatedKey(r);
    } catch (e) {
      setError('Failed to create key.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { create(); }, []);

  function copyKey() {
    if (createdKey && createdKey.api_key) {
      navigator.clipboard.writeText(createdKey.api_key);
    }
  }

  if (loading) return <div className="modal-overlay" onClick={onClose}><div className="modal" onClick={(e) => e.stopPropagation()}><div className="modal-body">Creating…</div></div></div>;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header"><h3>New API key</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          {createdKey && (
            <React.Fragment>
              <p style={{ color: 'var(--danger)', fontWeight: 600 }}>Store this key now. You will not see it again.</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <code style={{ flex: 1, padding: 8, background: 'var(--surface-sunken)', borderRadius: 4, wordBreak: 'break-all' }}>{createdKey.api_key}</code>
                <button className="button primary" onClick={copyKey}>Copy</button>
              </div>
            </React.Fragment>
          )}
          <button className="button outline" onClick={onSaved} style={{ marginTop: 12 }}>Done</button>
        </div>
      </div>
    </div>
  );
}

function NumbersModal({ tenantId, tenantName, onClose, onSaved }) {
  const [data, setData] = React.useState({ inbound: [], outbound: { outbound_number: null }, profiles: [] });
  const [loading, setLoading] = React.useState(true);
  const [addE164, setAddE164] = React.useState('');
  const [addRole, setAddRole] = React.useState('inbound');
  const [addProfileId, setAddProfileId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [outboundVal, setOutboundVal] = React.useState('');

  React.useEffect(function () {
    var cancelled = false;
    (async function () {
      try {
        var r = await API.json('GET', '/api/admin/tenants/' + tenantId + '/numbers');
        if (!cancelled) {
          setData(r);
          setOutboundVal(r.outbound && r.outbound.outbound_number ? r.outbound.outbound_number : '');
        }
      } catch (e) { if (!cancelled) setData({ inbound: [], outbound: { outbound_number: null }, profiles: [] }); }
      if (!cancelled) setLoading(false);
    })();
    return function () { cancelled = true; };
  }, [tenantId]);

  async function saveOutbound() {
    setSaving(true);
    try {
      await API.json('PATCH', '/api/admin/tenants/' + tenantId + '/outbound-number', { outbound_number: outboundVal });
      setData(function (prev) { return { ...prev, outbound: { outbound_number: outboundVal } }; });
      onSaved && onSaved();
    } catch (e) { alert('Failed: ' + (e && e.message)); }
    setSaving(false);
  }

  async function addNumber() {
    var e164 = addE164.trim();
    if (!e164) return;
    setSaving(true);
    try {
      var r = await API.json('POST', '/api/admin/tenants/' + tenantId + '/numbers', {
        e164: e164,
        role: addRole,
        profile_id: addProfileId || undefined,
        is_primary: false
      });
      setData(r);
      setAddE164('');
      onSaved && onSaved();
    } catch (e) { alert('Failed: ' + (e && e.message)); }
    setSaving(false);
  }

  async function removeNumber(phoneId) {
    if (!confirm('Remove this number?')) return;
    setSaving(true);
    try {
      await API.json('DELETE', '/api/admin/tenants/' + tenantId + '/numbers/' + phoneId);
      var r = await API.json('GET', '/api/admin/tenants/' + tenantId + '/numbers');
      setData(r);
      onSaved && onSaved();
    } catch (e) { alert('Failed: ' + (e && e.message)); }
    setSaving(false);
  }

  async function updateNumberProfile(phoneId, profileId) {
    setSaving(true);
    try {
      var r = await API.json('PUT', '/api/admin/tenants/' + tenantId + '/numbers/' + phoneId, { profile_id: profileId || null });
      setData(r);
      onSaved && onSaved();
    } catch (e) { alert('Failed: ' + (e && e.message)); }
    setSaving(false);
  }

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--surface-raised)', color: 'var(--fg)', borderRadius: 8, padding: 24, border: '1px solid var(--border-strong)' }}>Loading...</div>
    </div>
  );

  var profiles = data.profiles || [];
  var inbound = data.inbound || [];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: 'var(--surface-raised)', color: 'var(--fg)', borderRadius: 8, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20, border: '1px solid var(--border-strong)' }}
        onClick={function (e) { e.stopPropagation(); }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Numbers — {tenantName}</h3>
          <button type="button" className="button small" onClick={onClose}>Close</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.85em', marginBottom: 4 }}>Outbound number</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={outboundVal}
              onChange={function (e) { setOutboundVal(e.target.value); }}
              placeholder="+1..."
              style={{ flex: 1, padding: 8, boxSizing: 'border-box' }}
            />
            <button type="button" className="button primary small" disabled={saving} onClick={saveOutbound}>Save</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.85em', marginBottom: 4 }}>Inbound numbers</label>
          {inbound.length === 0 ? (
            <div style={{ fontSize: '0.8em', color: 'var(--fg-muted)', marginBottom: 8 }}>No inbound numbers</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px 0' }}>
              {inbound.map(function (n) {
                return (
                  <li key={n.phone_id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1, fontFamily: 'monospace' }}>{n.e164}</span>
                    <select
                      value={n.profile_id || ''}
                      onChange={function (e) { updateNumberProfile(n.phone_id, e.target.value || null); }}
                      disabled={saving}
                      style={{ minWidth: 120, padding: 4 }}
                    >
                      <option value="">(default)</option>
                      {profiles.map(function (p) {
                        return <option key={p.profile_id} value={p.profile_id}>{p.display_name || p.profile_id}</option>;
                      })}
                    </select>
                    <button type="button" className="button small" disabled={saving} onClick={function () { removeNumber(n.phone_id); }}>Remove</button>
                  </li>
                );
              })}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={addE164}
              onChange={function (e) { setAddE164(e.target.value); }}
              placeholder="+1..."
              style={{ width: 140, padding: 8, boxSizing: 'border-box' }}
            />
            <select value={addRole} onChange={function (e) { setAddRole(e.target.value); }} style={{ padding: 8 }}>
              <option value="inbound">Inbound</option>
              <option value="both">Both</option>
            </select>
            <select value={addProfileId} onChange={function (e) { setAddProfileId(e.target.value); }} style={{ padding: 8, minWidth: 100 }}>
              <option value="">(default)</option>
              {profiles.map(function (p) {
                return <option key={p.profile_id} value={p.profile_id}>{p.display_name || p.profile_id}</option>;
              })}
            </select>
            <button type="button" className="button primary small" disabled={saving} onClick={addNumber}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CostUsageView() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costError, setCostError] = useState(false);
  const [seenCostRows, setSeenCostRows] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [dateFrom, setDateFrom] = useState(function () { return addDaysYmd(todayYmd(), -30); });
  const [dateTo, setDateTo] = useState(function () { return todayYmd(); });
  const [tenantFilter, setTenantFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [costPage, setCostPage] = useState(1);
  const [costTab, setCostTab] = useState('calls');
  const [recon, setRecon] = useState(null);
  const [reconError, setReconError] = useState(false);
  const pageSize = 50;

  function moneyOrState(amount, state, digits) {
    if (state === 'pending') return <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Pending</span>;
    if (state === 'unavailable' && !(Number(amount) > 0)) return <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Unavailable</span>;
    var n = Number(amount);
    var text = '$' + (Number.isFinite(n) ? n.toFixed(digits == null ? 4 : digits) : '0.0000');
    if (state === 'unavailable') return <span style={{ color: 'var(--danger)' }}>{text}</span>;
    return text;
  }

  function lineLabel(line) {
    if (!line) return '';
    if (line.state === 'pending') return 'Pending';
    if (line.state === 'unavailable' && !(Number(line.usd) > 0)) return 'Unavailable';
    var money = line.usd == null ? '' : '$' + Number(line.usd).toFixed(4);
    if (line.state === 'unavailable') return money + ' Unavailable';
    if (line.estimated) return money + ' est.';
    return money;
  }

  function renderCostLines(lines) {
    if (!lines) return null;
    var labels = {
      twilio_voice: 'Voice',
      twilio_child_legs: 'Child legs',
      twilio_recording: 'Recording',
      twilio_amd: 'AMD',
      twilio_client: 'Client',
      ai_realtime: 'AI realtime',
      ai_transcription_estimated: 'AI transcription',
      ai_summary: 'AI summary',
      ai_other: 'AI other'
    };
    var parts = [];
    Object.keys(labels).forEach(function (key) {
      var line = lines[key];
      if (!line) return;
      var visible = line.state !== 'final' || line.estimated || (line.usd != null && Number(line.usd) !== 0);
      if (!visible) return;
      parts.push(labels[key] + ' ' + lineLabel(line));
    });
    if (!parts.length) return <span style={{ color: 'var(--muted)' }}>No priced lines yet</span>;
    return <span style={{ color: 'var(--fg)', fontSize: '0.78rem' }}>{parts.join(' · ')}</span>;
  }

  async function loadData() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (tenantFilter) params.set('tenant_id', tenantFilter);
      if (directionFilter) params.set('direction', directionFilter);
      params.set('tz', getWorkspaceTimeZone());
      params.set('limit', String(pageSize));
      params.set('offset', String((costPage - 1) * pageSize));
      const [usageRes, summaryRes, tenantsRes] = await Promise.all([
        API.json('GET', '/api/admin/cost/usage?' + params.toString()),
        API.json('GET', '/api/admin/cost/summary?' + params.toString()),
        API.json('GET', '/api/admin/tenants')
      ]);
      var nextItems = usageRes.items || [];
      setItems(nextItems);
      setSummary(summaryRes);
      setTenants(tenantsRes.items || []);
      setCostError(false);
      if (nextItems.length > 0 || (summaryRes && summaryRes.calls_count > 0)) setSeenCostRows(true);
    } catch (e) {
      console.error('Cost usage load failed', e);
      setCostError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(function () { loadData(); }, [dateFrom, dateTo, tenantFilter, directionFilter, costPage]);

  async function loadRecon() {
    setReconError(false);
    try {
      var params = new URLSearchParams();
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      var report = await API.json('GET', '/api/admin/cost/reconciliation?' + params.toString());
      setRecon(report);
    } catch (e) {
      console.error('Cost reconciliation load failed', e);
      setReconError(true);
    }
  }

  useEffect(function () {
    if (costTab === 'reconciliation') loadRecon();
  }, [costTab, dateFrom, dateTo]);

  async function handleSyncTwilio() {
    setSyncing(true);
    try {
      await API.json('POST', '/api/admin/cost/sync-twilio', { limit: 100 });
      loadData();
    } catch (e) {
      alert(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  function handleExportCsv() {
    const headers = ['call_sid', 'tenant_name', 'direction', 'minutes', 'twilio_cost', 'ai_cost', 'recording_cost', 'total_cost', 'created_at', 'created_at_local'];
    const rows = items.map(function (r) {
      var pending = r.price_state && r.price_state !== 'final';
      var cell = function (amount) { return pending ? (r.price_state || 'pending') : Number(amount || 0).toFixed(4); };
      var created = parseInstant(r.created_at);
      return [r.call_sid || '', r.tenant_name || '', r.direction || '', (r.minutes || 0).toFixed(2), cell(r.twilio_cost), (r.ai_cost || 0).toFixed(4), cell(r.recording_cost), cell(r.total_cost), created ? created.toISOString() : '', created ? (zonedYmd(created) + ' ' + formatDateTime(created, { style: 'time' })) : ''];
    });
    const csv = [headers.join(','), ...rows.map(function (r) { return r.map(function (c) { return '"' + String(c).replace(/"/g, '""') + '"'; }).join(','); })].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'cost-usage-' + todayYmd() + '.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="card cost-usage-page" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <div className="card-header cost-usage-header" style={{ flexShrink: 0 }}>
        <div>
          <div className="card-title">Cost &amp; Usage</div>
          <div className="card-subtitle cost-usage-subtitle">Per-call lines and account reconciliation. A Twilio price still settling shows Pending, not $0.00. <TimesShown /></div>
        </div>
        <div className="cost-usage-header-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className={costTab === 'calls' ? 'button small' : 'button outline small'} onClick={function () { setCostTab('calls'); }}>Calls</button>
          <button type="button" className={costTab === 'reconciliation' ? 'button small' : 'button outline small'} onClick={function () { setCostTab('reconciliation'); }}>Reconciliation</button>
          <button type="button" className="button outline small" disabled={syncing} onClick={handleSyncTwilio}>
            {syncing ? 'Syncing…' : 'Sync Twilio costs'}
          </button>
          <button type="button" className="button outline small" onClick={handleExportCsv}>Export CSV</button>
        </div>
      </div>
      <div className="card-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <label className="field" style={{ flex: '0 0 auto' }}>
            <span className="field-label">From</span>
            <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} />
          </label>
          <label className="field" style={{ flex: '0 0 auto' }}>
            <span className="field-label">To</span>
            <input type="date" value={dateTo} onChange={function (e) { setDateTo(e.target.value); }} />
          </label>
          <label className="field" style={{ flex: '0 0 auto' }}>
            <span className="field-label">Tenant</span>
            <select value={tenantFilter} onChange={function (e) { setTenantFilter(e.target.value); }}>
              <option value="">All</option>
              {tenants.map(function (t) { return <option key={t.id} value={t.id}>{t.name || 'Tenant ' + t.id}</option>; })}
            </select>
          </label>
          <label className="field" style={{ flex: '0 0 auto' }}>
            <span className="field-label">Direction</span>
            <select value={directionFilter} onChange={function (e) { setDirectionFilter(e.target.value); }}>
              <option value="">All</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </label>
        </div>
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="stat-tile">
              <div className="stat-label">Total cost</div>
              <div className="stat-value">${(summary.total_cost_usd || 0).toFixed(2)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Twilio</div>
              <div className="stat-value">${(summary.total_twilio_usd || 0).toFixed(2)}</div>
              {summary.pending_calls ? <div style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{summary.pending_calls} pending</div> : null}
            </div>
            <div className="stat-tile">
              <div className="stat-label">Number rental</div>
              <div className="stat-value">${(summary.number_rental_usd || 0).toFixed(2)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">AI</div>
              <div className="stat-value">${(summary.total_ai_usd || 0).toFixed(2)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Cost/min</div>
              <div className="stat-value">${(summary.cost_per_minute || 0).toFixed(3)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Cost/call</div>
              <div className="stat-value">${(summary.cost_per_call || 0).toFixed(3)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Calls</div>
              <div className="stat-value">{summary.calls_count || 0}</div>
            </div>
          </div>
        )}
        {summary && ((summary.tenant_totals && summary.tenant_totals.length > 0) || (summary.top_tenants && summary.top_tenants.length > 0)) && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 8, fontSize: '0.9rem', color: 'var(--fg)' }}>Per-tenant totals</h4>
            <div className="admin-table-scroll">
            <table className="table table--mobile-cards" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr><th>Tenant</th><th>Calls</th><th>Twilio</th><th>AI</th><th>Number rental</th><th>Pending</th><th>Total</th></tr>
              </thead>
              <tbody>
                {(summary.tenant_totals && summary.tenant_totals.length ? summary.tenant_totals : summary.top_tenants).map(function (t) {
                  return (
                    <tr key={t.tenant_id}>
                      <td data-label="Tenant">{t.tenant_name}</td>
                      <td data-label="Calls">{t.calls || 0}</td>
                      <td data-label="Twilio">${(t.twilio || 0).toFixed(2)}</td>
                      <td data-label="AI">${(t.ai || 0).toFixed(2)}</td>
                      <td data-label="Number rental">${(t.number_rental || 0).toFixed(2)}</td>
                      <td data-label="Pending">{t.pending ? <span style={{ color: 'var(--muted)' }}>{t.pending}</span> : '0'}</td>
                      <td data-label="Total">${(t.total || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
        <p className="muted" style={{ fontSize: '0.8rem', marginBottom: 8, color: 'var(--muted)' }}>
          Recording, AMD, and client legs are included in Twilio. Total = final Twilio + AI + allocated number rental. Pending Twilio prices are not shown as $0.00.
        </p>
        {costTab === 'reconciliation' ? (
          <div>
            {reconError ? (
              <BuyerListNotice title="Couldn't load reconciliation" body="We couldn't retrieve Twilio usage versus captured call cost. Try again." onRetry={loadRecon} />
            ) : !recon ? (
              <div style={{ color: 'var(--muted)' }}>Loading…</div>
            ) : (
              <div className="admin-table-scroll">
                <table className="table table--mobile-cards" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Allocation</th>
                      <th>Twilio billed</th>
                      <th>Captured</th>
                      <th>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recon.days || []).reduce(function (acc, day) {
                      (day.categories || []).forEach(function (row) { acc.push(row); });
                      return acc;
                    }, []).map(function (row, idx) {
                      var diff = row.difference_usd;
                      var hot = diff != null && Math.abs(Number(diff)) >= 0.0001;
                      var allocLabel = row.allocation === 'platform_overhead' ? 'Platform overhead' : row.allocation === 'number_rental' ? 'Number rental' : row.allocation === 'rollup' ? 'Rollup' : 'Per call';
                      var allocated = (row.allocated || []).map(function (part) {
                        return (part.tenant_name || ('Tenant ' + part.tenant_id)) + ' $' + Number(part.usd || 0).toFixed(2);
                      }).join(', ');
                      return (
                        <tr key={row.date + '-' + row.category + '-' + idx}>
                          <td data-label="Date">{row.date}</td>
                          <td data-label="Category">{row.category}</td>
                          <td data-label="Allocation" style={{ color: 'var(--fg)' }}>{allocLabel}{allocated ? <div style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{allocated}</div> : null}</td>
                          <td data-label="Twilio billed">${Number(row.twilio_billed_usd || 0).toFixed(4)}</td>
                          <td data-label="Captured">{row.captured_usd == null ? <span style={{ color: 'var(--muted)' }}>—</span> : '$' + Number(row.captured_usd).toFixed(4)}</td>
                          <td data-label="Difference" style={{ color: hot ? 'var(--danger)' : 'var(--fg)', fontWeight: hot ? 600 : 400 }}>{diff == null ? <span style={{ color: 'var(--muted)' }}>—</span> : '$' + Number(diff).toFixed(4)}</td>
                        </tr>
                      );
                    })}
                    {(recon.days || []).length === 0 ? (
                      <tr className="table-mobile-empty"><td colSpan={6} style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>No Twilio usage records in this range.</td></tr>
                    ) : null}
                  </tbody>
                </table>
                {recon.platform_overhead_usd ? (
                  <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 8 }}>Platform overhead (MFA SMS / Verify) ${Number(recon.platform_overhead_usd).toFixed(4)} is not allocated to tenants.</p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
        {costTab === 'calls' ? (
        <div>
        {loading ? (
          <div className="muted">Loading…</div>
        ) : (
          <div className="admin-table-scroll">
            <table className="table table--mobile-cards cost-usage-detail-table">
              <thead>
                <tr>
                  <th>Call SID</th>
                  <th>Tenant</th>
                  <th>Dir</th>
                  <th>Min</th>
                  <th>Twilio</th>
                  <th>AI</th>
                  <th>Recording (included in Twilio)</th>
                  <th>Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {costError ? (
                  <tr className="table-mobile-empty"><td colSpan={9}><BuyerListNotice title="Couldn't load billing" body="We couldn't retrieve your billing information. Try again." onRetry={loadData} /></td></tr>
                ) : items.length === 0 && seenCostRows ? (
                  <tr className="table-mobile-empty"><td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>No billing records match the current filters.</td></tr>
                ) : items.length === 0 ? (
                  <tr className="table-mobile-empty"><td colSpan={9}><BuyerListNotice title="No billing activity yet" body="Usage and invoices will appear here after billable activity occurs." /></td></tr>
                ) : items.map(function (r) {
                  var state = r.price_state || 'final';
                  return (
                    <React.Fragment key={r.call_sid}>
                    <tr>
                      <td data-label="Call SID" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{r.call_sid}</td>
                      <td data-label="Tenant">{r.tenant_name}</td>
                      <td data-label="Dir">{r.direction || '—'}</td>
                      <td data-label="Min">{(r.minutes || 0).toFixed(2)}</td>
                      <td data-label="Twilio">{moneyOrState(r.twilio_cost, state)}</td>
                      <td data-label="AI">${(r.ai_cost || 0).toFixed(4)}</td>
                      <td data-label="Recording (included in Twilio)">{moneyOrState(r.recording_cost, state)}</td>
                      <td data-label="Total">{state === 'final' ? <strong>{moneyOrState(r.total_cost, 'final')}</strong> : moneyOrState(r.total_cost, state)}</td>
                      <td data-label="Created" style={{ fontSize: '0.8rem' }}><WhenText value={r.created_at} /></td>
                    </tr>
                    <tr>
                      <td colSpan={9} data-label="Lines" style={{ color: 'var(--fg)' }}>{renderCostLines(r.lines)}{state !== 'final' ? <span style={{ marginLeft: 8 }}>{moneyOrState(null, state)}</span> : null}</td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span className="muted">Page {costPage} · {items.length} rows</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="button small outline" disabled={costPage <= 1} onClick={() => setCostPage((p) => Math.max(1, p - 1))}>◀ Prev</button>
              <button type="button" className="button small outline" disabled={items.length < pageSize} onClick={() => setCostPage((p) => p + 1)}>Next ▶</button>
            </div>
          </div>
        )}
        </div>
        ) : null}
      </div>
    </section>
  );
}

function DBPlansEditor() {
  var _s = React.useState([]);
  var dbPlans = _s[0];
  var setDbPlans = _s[1];
  var _s2 = React.useState(false);
  var loading = _s2[0];
  var setLoading = _s2[1];
  React.useEffect(function () {
    setLoading(true);
    API.json('GET', '/api/admin/plans').then(function (r) {
      if (r && r.plans) setDbPlans(r.plans);
    }).catch(function () {}).then(function () { setLoading(false); });
  }, []);
  var deactivate = function (id) {
    if (!window.confirm('Soft-deactivate this plan?')) return;
    API.json('DELETE', '/api/admin/plans/' + id).then(function () {
      return API.json('GET', '/api/admin/plans');
    }).then(function (r) { if (r && r.plans) setDbPlans(r.plans); });
  };
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 8 }}>Key-based plans (DB)</div>
      {loading ? <div className="muted">Loading…</div> : (
        <table className="table" style={{ fontSize: '0.78rem' }}>
          <thead><tr><th>Key</th><th>Display name</th><th>Price/mo</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {(dbPlans || []).map(function (p) {
              return (
                <tr key={p.id}>
                  <td>{p.key || '—'}</td>
                  <td>{p.display_name || '—'}</td>
                  <td>${(p.price_monthly != null ? p.price_monthly : 0).toFixed(2)}</td>
                  <td>{p.is_active ? 'Yes' : 'No'}</td>
                  <td>{p.is_active ? <button type="button" className="button small outline" onClick={function () { deactivate(p.id); }}>Deactivate</button> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AdminPlansView() {
  var PLAN_FEATURE_TOGGLES = [
    { key: 'integrations', label: 'Integrations (Hub)' },
    { key: 'hubspot', label: 'HubSpot' },
    { key: 'outlook', label: 'Outlook' },
    { key: 'salesforce', label: 'Salesforce' },
    { key: 'custom_webhook', label: 'Custom Webhooks' },
    { key: 'live_transfer', label: 'Live Agent Transfer' },
    { key: 'premium_calls', label: 'Premium Calling' },
    { key: 'call_recording', label: 'Call Recording' }
  ];
  var PLAN_LIMIT_KEYS = [
    { key: 'monthly_calls', label: 'Monthly Calls Limit', default: 2000 },
    { key: 'agents', label: 'Agents Limit', default: 3 },
    { key: 'workflows', label: 'Workflows Limit', default: 10 }
  ];
  function parsePlanFeatures(plan) {
    var features = plan && plan.features_json;
    if (typeof features === 'string') { try { features = JSON.parse(features); } catch (_) { features = {}; } }
    features = features || {};
    return { integrations: (features.integrations && typeof features.integrations === 'object') ? features.integrations : {} };
  }
  function parsePlanLimits(plan) {
    var limits = plan && plan.limits_json;
    if (typeof limits === 'string') { try { limits = JSON.parse(limits); } catch (_) { limits = {}; } }
    limits = limits || {};
    var monthly = limits.outbound_calls_per_month != null ? limits.outbound_calls_per_month : (limits.inbound_ai_minutes_per_month != null ? limits.inbound_ai_minutes_per_month : limits.monthly_calls);
    var agents = limits.agents_max != null ? limits.agents_max : limits.agents;
    var workflows = limits.workflows_max != null ? limits.workflows_max : limits.workflows;
    return { monthly_calls: monthly != null ? Number(monthly) : 2000, agents: agents != null ? Number(agents) : 3, workflows: workflows != null ? Number(workflows) : 10 };
  }

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editFeatures, setEditFeatures] = useState({});
  const [editLimits, setEditLimits] = useState({ monthly_calls: 2000, agents: 3, workflows: 10 });
  const [editIsActive, setEditIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createKey, setCreateKey] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createPriceMonthly, setCreatePriceMonthly] = useState('');
  const [createLimitsJson, setCreateLimitsJson] = useState('{}');

  function load() {
    setLoading(true);
    API.json('GET', '/api/admin/plans').then(function (r) {
      var list = (r && r.items && r.items.length >= 0) ? r.items : (r && r.plans) ? r.plans : [];
      setPlans(list);
    }).catch(function () {}).finally(function () { setLoading(false); });
  }
  useEffect(function () { load(); }, []);

  function openEdit(plan) {
    setEditingPlan(plan);
    setEditDisplayName(plan.display_name || plan.key || '');
    setEditFeatures(parsePlanFeatures(plan).integrations);
    setEditLimits(parsePlanLimits(plan));
    setEditIsActive(plan.is_active !== 0 && plan.is_active !== false);
  }
  function setEditFeature(key, value) {
    setEditFeatures(function (prev) { var next = { ...prev }; next[key] = !!value; return next; });
  }
  function setEditLimit(key, value) {
    var n = parseInt(value, 10);
    setEditLimits(function (prev) { return { ...prev, [key]: isNaN(n) ? prev[key] : Math.max(0, n) }; });
  }

  function savePlan() {
    if (!editingPlan) return;
    setSaving(true);
    var features_json = { integrations: { ...editFeatures }, limits: {} };
    var limits_json = {
      outbound_calls_per_month: editLimits.monthly_calls,
      inbound_ai_minutes_per_month: editLimits.monthly_calls,
      agents_max: editLimits.agents,
      workflows_max: editLimits.workflows
    };
    API.json('PUT', '/api/admin/plans/' + editingPlan.id, {
      display_name: editDisplayName,
      features_json: features_json,
      limits_json: limits_json,
      is_active: editIsActive
    }).then(function () {
      setEditingPlan(null);
      load();
    }).catch(function (e) { alert((e.data && e.data.error) || e.message || 'Save failed'); }).finally(function () { setSaving(false); });
  }

  function createPlan() {
    var key = (createKey && createKey.trim()) ? createKey.trim().toLowerCase().replace(/\s+/g, '_') : '';
    if (!key) { alert('Key is required'); return; }
    setSaving(true);
    var limitsObj = {};
    try { if (createLimitsJson.trim()) limitsObj = JSON.parse(createLimitsJson); } catch (e) { alert('Invalid limits_json: ' + e.message); setSaving(false); return; }
    API.json('POST', '/api/admin/plans', {
      key: key,
      display_name: (createDisplayName && createDisplayName.trim()) ? createDisplayName.trim() : key,
      price_monthly: createPriceMonthly !== '' ? Number(createPriceMonthly) : 0,
      limits_json: limitsObj
    }).then(function () {
      setCreating(false);
      setCreateKey('');
      setCreateDisplayName('');
      setCreatePriceMonthly('');
      setCreateLimitsJson('{}');
      load();
    }).catch(function (e) { alert((e.data && e.data.error) || e.message || 'Create failed'); }).finally(function () { setSaving(false); });
  }

  return (
    <section className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="card-title">Plans &amp; Features</div>
          <div className="card-subtitle">Edit plan name, limits, and feature toggles. Tenants reference plan_key; changes apply immediately.</div>
        </div>
        <button type="button" className="button primary" onClick={function () { setCreating(true); }}>Create plan</button>
      </div>
      <div className="card-body">
        {loading ? <div className="muted">Loading…</div> : (
          <table className="table" style={{ fontSize: '0.85rem' }}>
            <thead><tr><th>Key</th><th>Display name</th><th>Price/mo</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {(plans || []).map(function (p) {
                return (
                  <tr key={p.id}>
                    <td><code>{p.key || '—'}</code></td>
                    <td>{p.display_name || '—'}</td>
                    <td>${(p.price_monthly != null ? p.price_monthly : 0).toFixed(2)}</td>
                    <td>{p.is_active ? 'Yes' : 'No'}</td>
                    <td><button type="button" className="button small outline" onClick={function () { openEdit(p); }}>Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {editingPlan ? (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function (e) { if (e.target === e.currentTarget) setEditingPlan(null); }}>
            <div className="card" style={{ maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }} onClick={function (e) { e.stopPropagation(); }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="card-title">Edit plan: {editingPlan.key}</div>
                <button type="button" className="button small outline" onClick={function () { setEditingPlan(null); }}>Close</button>
              </div>
              <div className="card-body">
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Display name</label>
                  <input type="text" value={editDisplayName} onChange={function (e) { setEditDisplayName(e.target.value); }} style={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Features</div>
                  {PLAN_FEATURE_TOGGLES.map(function (f) {
                    var checked = editFeatures[f.key] === true;
                    return (
                      <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <input type="checkbox" checked={checked} onChange={function (e) { setEditFeature(f.key, e.target.checked); }} />
                        <span>{checked ? '✓' : ''} {f.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Limits</div>
                  {PLAN_LIMIT_KEYS.map(function (l) {
                    var val = editLimits[l.key] != null ? editLimits[l.key] : l.default;
                    return (
                      <div key={l.key} className="field" style={{ marginBottom: 8 }}>
                        <label>{l.label}</label>
                        <input type="number" min={0} value={val} onChange={function (e) { setEditLimit(l.key, e.target.value); }} style={{ width: '100%' }} />
                      </div>
                    );
                  })}
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={editIsActive} onChange={function (e) { setEditIsActive(e.target.checked); }} />
                    Active
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="button primary" disabled={saving} onClick={savePlan}>{saving ? 'Saving…' : 'Save'}</button>
                  <button type="button" className="button outline" onClick={function () { setEditingPlan(null); }}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {creating ? (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function (e) { if (e.target === e.currentTarget) setCreating(false); }}>
            <div className="card" style={{ maxWidth: 480 }} onClick={function (e) { e.stopPropagation(); }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="card-title">Create plan</div>
                <button type="button" className="button small outline" onClick={function () { setCreating(false); }}>Close</button>
              </div>
              <div className="card-body">
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Key (e.g. starter, growth, pro, enterprise)</label>
                  <input type="text" value={createKey} onChange={function (e) { setCreateKey(e.target.value); }} placeholder="enterprise" style={{ width: '100%' }} />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Display name</label>
                  <input type="text" value={createDisplayName} onChange={function (e) { setCreateDisplayName(e.target.value); }} style={{ width: '100%' }} />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Price monthly</label>
                  <input type="number" value={createPriceMonthly} onChange={function (e) { setCreatePriceMonthly(e.target.value); }} style={{ width: '100%' }} />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>limits_json (e.g. inbound_ai_minutes_per_month)</label>
                  <textarea value={createLimitsJson} onChange={function (e) { setCreateLimitsJson(e.target.value); }} rows={4} style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem' }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="button primary" disabled={saving} onClick={createPlan}>{saving ? 'Creating…' : 'Create'}</button>
                  <button type="button" className="button outline" onClick={function () { setCreating(false); }}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function GoLiveView(props) {
  var onNavigate = props && props.onNavigate;
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(function () {
    var cancelled = false;
    API.json('GET', '/api/tenant/go-live-status').then(function (r) {
      if (!cancelled && r && r.ok) setData({ steps: r.steps, percent_complete: r.percent_complete });
    }).catch(function () { if (!cancelled) setData(null); }).finally(function () { if (!cancelled) setLoading(false); });
    return function () { cancelled = true; };
  }, []);
  if (loading) return <section className="card"><div className="card-body">Loading…</div></section>;
  var steps = (data && data.steps) ? data.steps : {};
  var pct = (data && typeof data.percent_complete === 'number') ? data.percent_complete : 0;
  var complete = pct >= 100;
  var stepLabels = [
    { key: 'telephony_connected', label: 'Telephony connected (Twilio or BYO)', actionPage: 'settings' },
    { key: 'ai_profile_configured', label: 'AI / outbound profile configured', actionPage: 'settings' },
    { key: 'lead_uploaded', label: 'At least one lead uploaded', actionPage: 'leads' },
    { key: 'compliance_configured', label: 'Compliance (recording preference) set', actionPage: 'settings' },
    { key: 'billing_configured', label: 'Billing / plan configured', actionPage: 'billing' }
  ];
  var onGoLiveAction = onNavigate || (function (page) { window.location.hash = '#/' + (page || 'settings'); });
  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Go Live</div>
          <div className="card-subtitle">Complete these steps to be ready to place outbound calls.</div>
        </div>
        {complete ? <span className="main-title-pill" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#16a34a', fontWeight: 600 }}>READY TO CALL</span> : null}
      </div>
      <div className="card-body">
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>Progress</div>
          <div style={{ height: 12, background: 'var(--color-bg-elevated)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: pct + '%', background: complete ? '#16a34a' : 'var(--color-primary)', transition: 'width 0.2s' }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginTop: 4 }}>{Math.round(pct)}% complete</div>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {stepLabels.map(function (s) {
            var done = steps[s.key] === true;
            return (
              <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: done ? '#16a34a' : 'var(--color-bg-elevated)', color: done ? '#fff' : 'var(--color-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600 }}>{done ? '✓' : '—'}</span>
                <span style={{ color: done ? 'inherit' : 'var(--color-muted)', flex: 1 }}>{s.label}</span>
                {s.actionPage ? <button type="button" className="button small outline" onClick={function () { onGoLiveAction(s.actionPage); }}>{done ? 'View' : 'Configure'} →</button> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

var BILLING_PLAN_FALLBACK = [
  { key: 'starter', name: 'Starter', monthly_price_usd: 249, included_minutes: 400, overage_per_minute_usd: 0.69, custom: false },
  { key: 'growth', name: 'Growth', monthly_price_usd: 599, included_minutes: 1000, overage_per_minute_usd: 0.65, custom: false },
  { key: 'pro', name: 'Pro', monthly_price_usd: 1199, included_minutes: 2200, overage_per_minute_usd: 0.59, custom: false },
  { key: 'enterprise', name: 'Enterprise', monthly_price_usd: null, included_minutes: null, overage_per_minute_usd: null, custom: true }
];

function billingMoney(amount) {
  var n = Number(amount);
  if (!isFinite(n)) return '';
  var whole = Math.round(n * 100) % 100 === 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

function billingMoneyExact(amount) {
  var n = Number(amount);
  if (!isFinite(n)) return '';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function billingMinutes(amount) {
  var n = Number(amount);
  if (!isFinite(n)) return '';
  var rounded = Math.abs(n - Math.round(n)) < 0.05 ? Math.round(n) : Math.round(n * 10) / 10;
  return rounded.toLocaleString('en-US');
}

function billingUtcDay(value) {
  if (value == null || value === '') return null;
  var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  var parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function billingFormatDate(value) {
  var day = billingUtcDay(value);
  if (day == null) return '';
  return new Date(day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function billingTodayUtc() {
  var now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

function billingStatusLabel(status) {
  var key = String(status || '').toLowerCase();
  if (key === 'active') return 'Active';
  if (key === 'past_due') return 'Past due';
  if (key === 'canceled' || key === 'cancelled') return 'Canceled';
  if (key === 'trialing') return 'Trial';
  if (key === 'paused') return 'Paused';
  return 'Not active';
}

function billingStatusClass(status) {
  var key = String(status || '').toLowerCase();
  if (key === 'active' || key === 'trialing') return 'bill-status bill-status-active';
  if (key === 'past_due' || key === 'paused') return 'bill-status bill-status-past_due';
  if (key === 'canceled' || key === 'cancelled') return 'bill-status bill-status-canceled';
  return 'bill-status bill-status-inactive';
}

function billingInvoiceStatusLabel(status) {
  var key = String(status || '').toLowerCase();
  if (key === 'paid') return 'Paid';
  if (key === 'issued' || key === 'open') return 'Due';
  if (key === 'draft') return 'Not sent yet';
  if (key === 'void') return 'Voided';
  if (key === 'uncollectible' || key === 'past_due') return 'Past due';
  if (!key) return 'Unknown';
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

function billingInvoiceHref(inv) {
  if (!inv) return '';
  var candidates = [inv.hosted_invoice_url, inv.invoice_pdf, inv.pdf_url, inv.invoice_pdf_url];
  for (var i = 0; i < candidates.length; i++) {
    var url = candidates[i] ? String(candidates[i]).trim() : '';
    if (/^https:\/\//i.test(url)) return url;
  }
  return '';
}

function billingInvoiceRows(invoices, summary) {
  if (Array.isArray(invoices) && invoices.length) return invoices;
  if (summary && Array.isArray(summary.invoices) && summary.invoices.length) return summary.invoices;
  if (Array.isArray(invoices)) return invoices;
  if (summary && Array.isArray(summary.invoices)) return summary.invoices;
  return [];
}

function billingMergePlans(serverPlans) {
  var byKey = {};
  BILLING_PLAN_FALLBACK.forEach(function (plan) {
    byKey[plan.key] = {
      key: plan.key,
      name: plan.name,
      monthly_price_usd: plan.monthly_price_usd,
      included_minutes: plan.included_minutes,
      overage_per_minute_usd: plan.overage_per_minute_usd,
      custom: plan.custom
    };
  });
  if (Array.isArray(serverPlans) && serverPlans.length) {
    serverPlans.forEach(function (plan) {
      if (!plan) return;
      var key = String(plan.key || plan.name || '').toLowerCase().trim();
      if (!key) return;
      var base = byKey[key] || { key: key, name: key, monthly_price_usd: null, included_minutes: null, overage_per_minute_usd: null, custom: false };
      if (plan.name) base.name = String(plan.name);
      if (plan.display_name) base.name = String(plan.display_name);
      if (plan.monthly_price_usd != null && isFinite(Number(plan.monthly_price_usd))) base.monthly_price_usd = Number(plan.monthly_price_usd);
      if (plan.included_minutes != null && isFinite(Number(plan.included_minutes))) base.included_minutes = Number(plan.included_minutes);
      if (plan.overage_per_minute_usd != null && isFinite(Number(plan.overage_per_minute_usd))) base.overage_per_minute_usd = Number(plan.overage_per_minute_usd);
      if (plan.custom === true || plan.custom === false) base.custom = !!plan.custom;
      if (key === 'enterprise' && !(Number(base.monthly_price_usd) > 0)) {
        base.custom = true;
        base.monthly_price_usd = null;
        base.included_minutes = null;
        base.overage_per_minute_usd = null;
      } else if (Number(base.monthly_price_usd) > 0) {
        base.custom = false;
      }
      byKey[key] = base;
    });
  }
  var order = ['starter', 'growth', 'pro', 'enterprise'];
  var list = [];
  order.forEach(function (key) {
    if (byKey[key]) list.push(byKey[key]);
  });
  Object.keys(byKey).forEach(function (key) {
    if (order.indexOf(key) < 0) list.push(byKey[key]);
  });
  return list;
}

function billingVoiceRateFromSummary(summary) {
  var overages = summary && summary.overages;
  if (!Array.isArray(overages)) return null;
  var rate = null;
  overages.forEach(function (row) {
    if (!row) return;
    var type = String(row.usage_type || '');
    if (type !== 'voice_minute' && type !== 'voice_minutes') return;
    var n = Number(row.unit_rate_usd);
    if (isFinite(n) && n >= 0) rate = n;
  });
  return rate;
}

function billingResolvePlan(summary, catalog, catalogLoading) {
  var serverPlan = summary && summary.plan ? summary.plan : null;
  var key = '';
  if (serverPlan && serverPlan.name) key = String(serverPlan.name).toLowerCase();
  else if (catalog && catalog.plan_key) key = String(catalog.plan_key).toLowerCase();
  var plans = billingMergePlans(catalog && catalog.plans);
  var match = null;
  plans.forEach(function (plan) { if (plan.key === key) match = plan; });
  if (!serverPlan && !key && catalogLoading) {
    return { key: '', name: '', price: null, included: null, rate: null, custom: false, pending: true, unknown: false };
  }
  if (!serverPlan && !key) {
    return { key: '', name: '', price: null, included: null, rate: null, custom: false, pending: false, unknown: true };
  }
  var price = null;
  if (serverPlan && serverPlan.monthly_price_usd != null && isFinite(Number(serverPlan.monthly_price_usd))) price = Number(serverPlan.monthly_price_usd);
  else if (match && match.monthly_price_usd != null) price = match.monthly_price_usd;
  var included = null;
  if (serverPlan && serverPlan.included_minutes != null && isFinite(Number(serverPlan.included_minutes))) included = Number(serverPlan.included_minutes);
  else if (match && match.included_minutes != null) included = match.included_minutes;
  var rate = billingVoiceRateFromSummary(summary);
  if (rate == null && match && match.overage_per_minute_usd != null) rate = match.overage_per_minute_usd;
  var name = serverPlan && serverPlan.display_name ? serverPlan.display_name : (match && match.name ? match.name : (key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Your plan'));
  var custom = (key === 'enterprise' && !(price > 0)) || (!!(match && match.custom) && !(price > 0));
  return { key: key, name: name, price: custom ? null : price, included: included, rate: rate, custom: custom, pending: false, unknown: false };
}

function billingCycleProjection(summary, catalog, used, included, rate) {
  var start = billingUtcDay((summary && summary.billing_cycle_start) || (catalog && catalog.cycle_start));
  var end = billingUtcDay((summary && (summary.billing_cycle_end || summary.next_billing_date)) || (catalog && (catalog.cycle_end || catalog.renewal_date)));
  if (start == null || end == null || end < start) return null;
  var today = billingTodayUtc();
  var dayMs = 86400000;
  var totalDays = Math.max(1, Math.round((end - start) / dayMs) + 1);
  var elapsed = Math.round((today - start) / dayMs) + 1;
  if (today < start) elapsed = 0;
  if (elapsed > totalDays) elapsed = totalDays;
  var daysLeft = Math.max(0, Math.round((end - today) / dayMs));
  var usedNum = Number(used) || 0;
  var includedNum = included != null && isFinite(Number(included)) ? Number(included) : null;
  var projected = null;
  var extra = null;
  var projectedUsd = null;
  if (elapsed > 0) {
    projected = (usedNum / elapsed) * totalDays;
    if (includedNum != null) extra = Math.max(0, projected - includedNum);
    if (extra != null && rate != null && isFinite(Number(rate))) projectedUsd = Math.round(extra * Number(rate) * 100) / 100;
  }
  var periodEndLabel = billingFormatDate((summary && (summary.billing_cycle_end || summary.next_billing_date)) || (catalog && catalog.cycle_end));
  var renewalLabel = catalog && catalog.renewal_date ? billingFormatDate(catalog.renewal_date) : '';
  return {
    totalDays: totalDays,
    elapsed: elapsed,
    daysLeft: daysLeft,
    endsToday: today === end,
    afterEnd: today > end,
    projectedUsd: projectedUsd,
    periodEndLabel: periodEndLabel,
    renewalLabel: renewalLabel && renewalLabel !== periodEndLabel ? renewalLabel : ''
  };
}

function billingProjectionCopy(projection, used, included, actualOverageUsd) {
  if (!projection) return 'Billing period dates appear here once a plan is billed.';
  var parts = [];
  if (projection.afterEnd) parts.push('This billing period has ended.');
  else if (projection.elapsed === 0) parts.push('This billing period has not started yet.');
  else if (projection.endsToday) parts.push('This period ends today.');
  else if (projection.daysLeft === 1) parts.push('1 day left in this period.');
  else parts.push(projection.daysLeft + ' days left in this period.');
  if (projection.elapsed > 0 && projection.projectedUsd != null) {
    if (projection.projectedUsd > 0) parts.push('If this pace continues, extra minutes would cost about ' + billingMoneyExact(projection.projectedUsd) + '.');
    else parts.push('At this pace, you stay within the minutes included this period.');
  }
  if (Number(actualOverageUsd) > 0) parts.push('Extra minutes so far: ' + billingMoneyExact(actualOverageUsd) + '.');
  else if (included != null && Number(used) > Number(included)) parts.push('You are past the minutes included this period.');
  return parts.join(' ');
}

function billingPriceLabel(plan) {
  if (!plan || plan.custom || plan.monthly_price_usd == null || !isFinite(Number(plan.monthly_price_usd))) return 'Custom';
  return billingMoney(plan.monthly_price_usd) + '/mo';
}

function billingIncludedLabel(plan) {
  if (plan && plan.included_minutes != null && isFinite(Number(plan.included_minutes)) && Number(plan.included_minutes) > 0) {
    return billingMinutes(plan.included_minutes) + ' min';
  }
  if (plan && plan.custom) return 'Custom';
  return '—';
}

function billingRateLabel(rate, custom) {
  if (rate != null && isFinite(Number(rate))) return billingMoney(rate) + '/min';
  if (custom) return 'Custom';
  return '—';
}

function billingChangeKind(current, target) {
  if (!target) return 'contact';
  if (current && current.key && current.key === target.key) return 'current';
  if (target.custom || target.monthly_price_usd == null || !isFinite(Number(target.monthly_price_usd))) return 'contact';
  if (current && current.custom) return 'switch';
  if (!current || !current.key || current.price == null || !isFinite(Number(current.price))) return 'upgrade';
  if (Number(target.monthly_price_usd) > Number(current.price)) return 'upgrade';
  if (Number(target.monthly_price_usd) < Number(current.price)) return 'downgrade';
  return 'switch';
}

function billingReturnUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.href;
}

function BillingCurrentPlan(props) {
  var plan = props.plan;
  var status = props.status;
  var projection = props.projection;
  if (plan && plan.pending) {
    return (
      <section className="bill-panel" aria-labelledby="bill-plan-heading" aria-busy="true">
        <h3 id="bill-plan-heading" className="bill-kicker">Current plan</h3>
        <p className="bill-lead">Loading your plan…</p>
      </section>
    );
  }
  if (plan && plan.unknown) {
    return (
      <section className="bill-panel" aria-labelledby="bill-plan-heading">
        <h3 id="bill-plan-heading" className="bill-kicker">Current plan</h3>
        <p className="bill-lead">No plan is assigned yet. Choose one below, or contact us and we will set it up.</p>
      </section>
    );
  }
  var renew = props.renewal || (projection && (projection.renewalLabel || projection.periodEndLabel)) || '';
  return (
    <section className="bill-panel" aria-labelledby="bill-plan-heading">
      <h3 id="bill-plan-heading" className="bill-kicker">Current plan</h3>
      <p className="bill-plan-name">{plan.name}</p>
      <p className="bill-price">{plan.custom ? 'Custom pricing' : (billingMoney(plan.price) ? billingMoney(plan.price) + ' per month' : 'Price not listed')}</p>
      <ul className="bill-facts">
        <li>
          <span className="bill-fact-label">Minutes included</span>
          <span className="bill-fact-value">{plan.included != null && isFinite(Number(plan.included)) ? billingMinutes(plan.included) + ' each month' : (plan.custom ? 'Custom' : '—')}</span>
        </li>
        <li>
          <span className="bill-fact-label">Extra minutes</span>
          <span className="bill-fact-value">{billingRateLabel(plan.rate, plan.custom)}</span>
        </li>
        <li>
          <span className="bill-fact-label">Renews</span>
          <span className="bill-fact-value">{renew || 'Not scheduled'}</span>
        </li>
      </ul>
      {status ? <p className={billingStatusClass(status)}>{billingStatusLabel(status)}</p> : <p className="bill-lead">Checking account status…</p>}
    </section>
  );
}

function BillingUsagePanel(props) {
  var used = Number(props.used) || 0;
  var included = props.included != null && isFinite(Number(props.included)) ? Number(props.included) : null;
  var projection = props.projection;
  var copy = props.copy;
  var pct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
  var fillClass = 'bill-meter-fill';
  if (included > 0 && used > included) fillClass += ' is-over';
  else if (pct >= 90) fillClass += ' is-warn';
  var usedLabel = billingMinutes(used) || '0';
  var includedLabel = included != null ? billingMinutes(included) : '';
  var meterNow = included > 0 ? Math.min(used, included) : 0;
  return (
    <section className="bill-panel" aria-labelledby="bill-usage-heading">
      <h3 id="bill-usage-heading" className="bill-kicker">Usage this period</h3>
      {included > 0 ? (
        <div
          className="bill-meter"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={included}
          aria-valuenow={meterNow}
          aria-label={usedLabel + ' of ' + includedLabel + ' included minutes used'}
        >
          <div className={fillClass} style={{ width: pct + '%' }} />
        </div>
      ) : (
        <p className="bill-lead">{props.custom ? 'Included minutes are part of your custom agreement.' : 'Included minutes are not set for this plan yet.'}</p>
      )}
      <div className="bill-usage-line">
        <span>{included != null ? usedLabel + ' of ' + (includedLabel || '0') + ' minutes used' : usedLabel + ' minutes used'}</span>
        {projection && projection.periodEndLabel ? <span>Period ends {projection.periodEndLabel}</span> : null}
      </div>
      <p className="bill-usage-meta">{copy}</p>
    </section>
  );
}

function BillingPlanCompare(props) {
  var plans = props.plans || [];
  var current = props.current;
  var busyKey = props.busyKey;
  var onAction = props.onAction;
  if (props.loading) {
    return (
      <section className="bill-panel" aria-labelledby="bill-compare-heading" aria-busy="true">
        <h3 id="bill-compare-heading" className="bill-kicker">Compare plans</h3>
        <p className="bill-lead">Loading plans…</p>
      </section>
    );
  }
  return (
    <section className="bill-panel" aria-labelledby="bill-compare-heading">
      <div className="bill-table-wrap">
        <table className="table bill-table">
          <caption id="bill-compare-heading">Compare plans</caption>
          <thead>
            <tr>
              <th scope="col">Plan</th>
              <th scope="col">Monthly price</th>
              <th scope="col">Minutes included</th>
              <th scope="col">Extra minute</th>
              <th scope="col"><span className="bill-sr">Change plan</span></th>
            </tr>
          </thead>
          <tbody>
            {plans.map(function (plan) {
              var kind = billingChangeKind(current, plan);
              var isCurrent = kind === 'current';
              var label = kind === 'upgrade' ? 'Upgrade' : kind === 'downgrade' ? 'Downgrade' : kind === 'switch' ? 'Switch' : 'Contact us';
              var aria = kind === 'contact'
                ? 'Contact us about ' + plan.name
                : label + ' to ' + plan.name;
              return (
                <tr key={plan.key} className={isCurrent ? 'bill-row-current' : undefined}>
                  <th scope="row">
                    {plan.name}
                    {isCurrent ? <span className="bill-badge">Current</span> : null}
                  </th>
                  <td>{billingPriceLabel(plan)}</td>
                  <td>{billingIncludedLabel(plan)}</td>
                  <td>{billingRateLabel(plan.overage_per_minute_usd, plan.custom)}</td>
                  <td>
                    {isCurrent ? <span className="bill-sr">Current plan</span> : (
                      <button
                        type="button"
                        className={kind === 'upgrade' ? 'button small primary' : 'button small outline'}
                        disabled={!!busyKey}
                        aria-label={aria}
                        onClick={function () { onAction(plan, kind); }}
                      >
                        {busyKey === plan.key ? 'Opening…' : label}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BillingInvoiceList(props) {
  var rows = props.rows || [];
  return (
    <section className="bill-panel" aria-labelledby="bill-invoices-heading">
      <h3 id="bill-invoices-heading" className="bill-kicker">Invoices</h3>
      {rows.length === 0 ? (
        <BuyerListNotice title="No invoices yet" body="Invoices show up here after a billing period is billed. Minutes used above still update during the period." />
      ) : (
        <div className="bill-table-wrap">
          <table className="table bill-table">
            <caption className="bill-sr">Invoices</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Amount</th>
                <th scope="col">Status</th>
                <th scope="col">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(function (inv) {
                var href = billingInvoiceHref(inv);
                var when = billingFormatDate(inv.created_at || inv.period_end || inv.period_start);
                var pdf = href && /pdf/i.test(href);
                return (
                  <tr key={inv.id || inv.invoice_number || when}>
                    <td>{when || '—'}</td>
                    <td>{billingMoneyExact(inv.total_usd != null ? inv.total_usd : 0)}</td>
                    <td>{billingInvoiceStatusLabel(inv.status)}</td>
                    <td>
                      {href ? (
                        <a className="bill-link" href={href} target="_blank" rel="noopener noreferrer">
                          {pdf ? 'Download PDF' : 'View invoice'}
                          <span className="bill-sr"> (opens in a new tab)</span>
                        </a>
                      ) : (
                        <span className="bill-note">Receipt link not available</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BillingPaymentPanel(props) {
  var portalAvailable = !!props.portalAvailable;
  var copy = !props.known
    ? 'Checking how this workspace pays…'
    : portalAvailable
      ? 'Your card is saved with your billing account. Manage billing opens a secure page where you can update the card, download receipts, or cancel.'
      : 'No billing account is on file yet. Choose a plan to add a payment method, or contact us and we will set one up.';
  return (
    <section className="bill-panel" aria-labelledby="bill-pay-heading">
      <h3 id="bill-pay-heading" className="bill-kicker">Payment method</h3>
      <p className="bill-usage-meta">{copy}</p>
      <div className="bill-actions">
        <button type="button" className="button primary" onClick={props.onManage} disabled={!!props.busyKey} aria-label="Manage billing">
          {props.busyKey === 'portal' ? 'Opening…' : 'Manage billing'}
        </button>
      </div>
    </section>
  );
}

function TenantBillingView({ summary, invoices, onRefresh }) {
  const [loading, setLoading] = React.useState(false);
  const [catalogState, setCatalogState] = React.useState({ loading: true, error: false, data: null });
  const [busyKey, setBusyKey] = React.useState('');
  const [actionError, setActionError] = React.useState('');
  const [contactNote, setContactNote] = React.useState('');
  var summaryToken = !summary
    ? 'pending'
    : String((summary.plan && (summary.plan.id || summary.plan.name)) || '') + '|' + String(summary.message || '') + '|' + String(summary.billing_cycle_end || '');
  React.useEffect(function () {
    var cancelled = false;
    setCatalogState(function (prev) { return { loading: true, error: false, data: prev.data }; });
    API.json('GET', '/api/billing/catalog').then(function (data) {
      if (!cancelled) setCatalogState({ loading: false, error: false, data: data || null });
    }).catch(function () {
      if (!cancelled) setCatalogState(function (prev) { return { loading: false, error: true, data: prev.data }; });
    });
    return function () { cancelled = true; };
  }, [summaryToken]);
  async function refreshAll() {
    setLoading(true);
    setActionError('');
    try {
      if (typeof onRefresh === 'function') await onRefresh();
      var data = await API.json('GET', '/api/billing/catalog');
      setCatalogState({ loading: false, error: false, data: data || null });
    } catch (err) {
      setCatalogState(function (prev) { return { loading: false, error: true, data: prev.data }; });
    } finally {
      setLoading(false);
    }
  }
  async function openPortal() {
    setBusyKey('portal');
    setActionError('');
    try {
      var res = await API.json('POST', '/api/billing/portal', { return_url: billingReturnUrl() });
      if (res && res.url && /^https?:\/\//i.test(String(res.url))) {
        window.location.assign(res.url);
        return;
      }
      setContactNote('Manage billing is not available yet. Contact us and we will update the card on file.');
    } catch (err) {
      var code = err && err.data && typeof err.data.error === 'string' ? err.data.error : '';
      if (code === 'no_stripe_customer') {
        setContactNote('Manage billing opens after the first payment. Choose a plan above, or contact us and we will set it up.');
      } else if (code === 'stripe_not_configured' || err.status === 501) {
        setContactNote('Online billing is not turned on yet. Contact us to update your payment method.');
      } else {
        setActionError('We could not open billing management. Try again in a moment.');
      }
    } finally {
      setBusyKey('');
    }
  }
  async function startCheckout(planKey) {
    setBusyKey(planKey);
    setActionError('');
    setContactNote('');
    try {
      var res = await API.json('POST', '/api/billing/create-checkout-session', {
        plan_key: planKey,
        success_url: billingReturnUrl(),
        cancel_url: billingReturnUrl()
      });
      if (res && res.url && /^https?:\/\//i.test(String(res.url))) {
        window.location.assign(res.url);
        return;
      }
      setContactNote('That plan change could not be started online. Contact us and we will update it.');
    } catch (err) {
      var code = err && err.data && typeof err.data.error === 'string' ? err.data.error : '';
      if (code === 'stripe_not_configured' || err.status === 501) {
        var portalOn = catalogState.data && catalogState.data.portal_available;
        if (portalOn) {
          await openPortal();
          return;
        }
        setContactNote('Online plan changes are not turned on yet. Contact us and we will update the plan.');
      } else if (code === 'plan_not_billable') {
        setContactNote('That plan is not available for online checkout. Contact us and we will set it up.');
      } else {
        setActionError('We could not start that plan change. Try again, or use Manage billing.');
      }
    } finally {
      setBusyKey('');
    }
  }
  function onPlanAction(plan, kind) {
    if (kind === 'contact') {
      setActionError('');
      setContactNote(plan && (plan.custom || plan.key === 'enterprise')
        ? 'Enterprise is a custom plan. Contact us and we will set the price and minutes with you.'
        : 'Contact us and we will change this plan for you.');
      return;
    }
    if (kind === 'upgrade' || kind === 'downgrade' || kind === 'switch') startCheckout(plan.key);
  }
  if (!summary) {
    return (
      <section className="card bill-page" aria-busy="true" aria-live="polite">
        <div className="card-body">
          <p className="bill-lead">Loading your plan and billing…</p>
        </div>
      </section>
    );
  }
  if (summary.message === 'billing_load_failed' || (summary.error && !summary.plan && summary.message !== 'billing_not_configured')) {
    return (
      <section className="card bill-page" aria-live="polite">
        <div className="card-body">
          <BuyerListNotice
            title="Couldn't load billing"
            body="We couldn't retrieve your billing information. Try again."
            onRetry={refreshAll}
          />
        </div>
      </section>
    );
  }
  var catalog = catalogState.data;
  var current = billingResolvePlan(summary, catalog, catalogState.loading && !catalog);
  var plans = billingMergePlans(catalog && catalog.plans);
  var usage = summary.usage || {};
  var usedMins = Number(usage.voice_minutes) || 0;
  var projection = billingCycleProjection(summary, catalog, usedMins, current.included, current.rate);
  var projectionCopy = billingProjectionCopy(projection, usedMins, current.included, summary.estimated_overage_usd);
  var status = catalog ? (catalog.subscription_status || 'inactive') : '';
  var rows = billingInvoiceRows(invoices, summary);
  return (
    <section className="card bill-page" aria-labelledby="bill-heading">
      <div className="card-header">
        <div>
          <h2 className="card-title" id="bill-heading">Plan and billing</h2>
          <p className="card-subtitle">Your plan, minutes used this period, and invoices.</p>
        </div>
        <button type="button" className="button small outline" onClick={refreshAll} disabled={loading} aria-label="Refresh plan and billing">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div className="card-body bill-stack">
        {summary.message === 'billing_not_configured' ? (
          <p className="bill-lead">Online billing is not turned on for this workspace yet. The plan below is the one assigned to you.</p>
        ) : null}
        {catalogState.error ? (
          <p className="bill-note">We could not refresh the plan list. Comparison uses the standard prices.</p>
        ) : null}
        {actionError ? <p className="bill-alert" role="alert">{actionError}</p> : null}
        {contactNote ? <p className="bill-note" id="bill-contact-note" role="status">{contactNote}</p> : null}
        <div className="bill-grid">
          <BillingCurrentPlan plan={current} status={status} projection={projection} renewal={(projection && (projection.renewalLabel || projection.periodEndLabel)) || billingFormatDate(catalog && catalog.renewal_date) || ''} />
          <BillingUsagePanel used={usedMins} included={current.included} custom={current.custom} projection={projection} copy={projectionCopy} />
        </div>
        <BillingPlanCompare plans={plans} current={current} busyKey={busyKey} loading={catalogState.loading && !catalog} onAction={onPlanAction} />
        <BillingInvoiceList rows={rows} />
        <BillingPaymentPanel known={!!catalog || catalogState.error} portalAvailable={catalog && catalog.portal_available} busyKey={busyKey} onManage={openPortal} />
      </div>
    </section>
  );
}

function BillingView({ tenants, superAdminUserId, refreshTenants, user }) {
  const [overviewTenants, setOverviewTenants] = React.useState([]);
  const [overviewPlans, setOverviewPlans] = React.useState([]);
  const [adminPlans, setAdminPlans] = React.useState([]);
  const [plansExpanded, setPlansExpanded] = React.useState(false);
  const [generateInvoiceLoading, setGenerateInvoiceLoading] = React.useState(false);
  const [name, setName] = React.useState('');
  const [adminEmail, setAdminEmail] = React.useState('');
  const [adminPassword, setAdminPassword] = React.useState('');
  const [timezone, setTimezone] = React.useState('America/Los_Angeles');
  const [planKey, setPlanKey] = React.useState('starter');
  const [includedMinutes, setIncludedMinutes] = React.useState(2000);
  const [overageRate, setOverageRate] = React.useState(5);
  const [outbound_number, setOutboundNumber] = React.useState('');
  const [adminMfaPhone, setAdminMfaPhone] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [okMessage, setOkMessage] = React.useState('');
  const [inboundEditTenantId, setInboundEditTenantId] = React.useState(null);
  const [outboundEditTenantId, setOutboundEditTenantId] = React.useState(null);
  const [outboundEditValue, setOutboundEditValue] = React.useState('');
  const [numbersModalTenantId, setNumbersModalTenantId] = React.useState(null);
  const [tenantEditId, setTenantEditId] = React.useState(null);
  const [tenantEditPlanList, setTenantEditPlanList] = React.useState([]);
  const [tenantEditPlanKey, setTenantEditPlanKey] = React.useState('');
  const [tenantEditFeaturesOverride, setTenantEditFeaturesOverride] = React.useState('');
  const [tenantEditOverrideIntegrations, setTenantEditOverrideIntegrations] = React.useState({});
  const [tenantEditSaving, setTenantEditSaving] = React.useState(false);
  const [tenantEditName, setTenantEditName] = React.useState('');
  const [tenantEditCompany, setTenantEditCompany] = React.useState('');
  const [tenantEditBillingEmail, setTenantEditBillingEmail] = React.useState('');
  const [tenantEditBillingPhone, setTenantEditBillingPhone] = React.useState('');
  const [tenantEditInboundVoice, setTenantEditInboundVoice] = React.useState('');
  const [tenantEditInboundVoiceEffective, setTenantEditInboundVoiceEffective] = React.useState('');
  var TENANT_OVERRIDE_FEATURE_KEYS = [
    { key: 'hubspot', label: 'HubSpot' },
    { key: 'outlook', label: 'Outlook' },
    { key: 'salesforce', label: 'Salesforce' },
    { key: 'custom_webhook', label: 'Custom Webhooks' },
    { key: 'live_transfer', label: 'Live Agent Transfer' },
    { key: 'premium_calls', label: 'Premium Calling' },
    { key: 'call_recording', label: 'Call Recording' }
  ];
  const [billingExpanded, setBillingExpanded] = React.useState(false);
  const [billingCompanyName, setBillingCompanyName] = React.useState('');
  const [billingContactName, setBillingContactName] = React.useState('');
  const [billingEmail, setBillingEmail] = React.useState('');
  const [billingPhone, setBillingPhone] = React.useState('');
  const [billingAddressLine1, setBillingAddressLine1] = React.useState('');
  const [billingAddressLine2, setBillingAddressLine2] = React.useState('');
  const [billingCity, setBillingCity] = React.useState('');
  const [billingState, setBillingState] = React.useState('');
  const [billingPostalCode, setBillingPostalCode] = React.useState('');
  const [billingCountry, setBillingCountry] = React.useState('');
  const [billingTaxId, setBillingTaxId] = React.useState('');
  const [industry, setIndustry] = React.useState('');
  const [expandedTenantId, setExpandedTenantId] = React.useState(null);
  const inboundModalRef = React.useRef(null);

  async function loadOverview() {
    try {
      const r = await API.json('GET', '/api/admin/tenants/overview');
      if (r && r.ok) {
        setOverviewTenants(r.tenants || []);
        setOverviewPlans(r.plans || []);
      }
    } catch (e) {}
  }

  React.useEffect(function () {
    (async function () {
      try {
        await loadOverview();
        const ap = await API.json('GET', '/api/admin/plans');
        if (ap && ap.ok && ap.plans && ap.plans.length) {
          setAdminPlans(ap.plans);
          var first = ap.plans[0];
          var key = first.key || 'starter';
          setPlanKey(key);
          setIncludedMinutes(first.included_minutes_default != null ? first.included_minutes_default : 2000);
          setOverageRate(first.overage_rate_default != null ? first.overage_rate_default : 5);
        }
      } catch (e) {}
    })();
  }, []);

  React.useEffect(() => {
    if (!inboundEditTenantId || !inboundModalRef.current || !window.mountInboundInstructionsUI) return;
    const el = inboundModalRef.current;
    el.innerHTML = '';
    window.mountInboundInstructionsUI({
      tenantId: String(inboundEditTenantId),
      container: el,
      apiBase: API_BASE,
      getAuthToken: function () { return window.localStorage.getItem('authToken'); },
      useAdminApi: true
    });
    return () => { el.innerHTML = ''; };
  }, [inboundEditTenantId]);

  function onPlanKeyChange(key) {
    setPlanKey(key);
    var p = adminPlans.find(function (x) { return (x.key || '').toLowerCase() === (key || '').toLowerCase(); });
    if (p) {
      setIncludedMinutes(p.included_minutes_default != null ? p.included_minutes_default : 2000);
      setOverageRate(p.overage_rate_default != null ? p.overage_rate_default : 5);
    }
  }

  async function handleCreateTenant(e) {
    e.preventDefault();
    setError('');
    setOkMessage('');
    if (!name || !adminEmail || !adminPassword) {
      setError('Name, admin email, and admin password are required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name,
        timezone,
        plan_key: planKey,
        included_minutes: Number(includedMinutes) || 0,
        overage_rate: Number(overageRate) || 0,
        admin_email: adminEmail,
        admin_name: name,
        admin_password: adminPassword,
        outbound_caller_id: outbound_number || undefined,
        mfa_phone: adminMfaPhone || undefined
      };
      if (billingCompanyName || billingContactName || billingEmail || billingPhone || billingAddressLine1 || billingAddressLine2 || billingCity || billingState || billingPostalCode || billingCountry || billingTaxId || industry) {
        body.billing_company_name = billingCompanyName || undefined;
        body.billing_contact_name = billingContactName || undefined;
        body.billing_email = billingEmail || undefined;
        body.billing_phone = billingPhone || undefined;
        body.billing_address_line1 = billingAddressLine1 || undefined;
        body.billing_address_line2 = billingAddressLine2 || undefined;
        body.billing_city = billingCity || undefined;
        body.billing_state = billingState || undefined;
        body.billing_postal_code = billingPostalCode || undefined;
        body.billing_country = billingCountry || undefined;
        body.billing_tax_id = billingTaxId || undefined;
        body.industry = industry || undefined;
      }
      await API.json('POST', '/api/admin/tenants', body);
      setName('');
      setAdminEmail('');
      setAdminPassword('');
      setAdminMfaPhone('');
      setBillingCompanyName('');
      setBillingContactName('');
      setBillingEmail('');
      setBillingPhone('');
      setBillingAddressLine1('');
      setBillingAddressLine2('');
      setBillingCity('');
      setBillingState('');
      setBillingPostalCode('');
      setBillingCountry('');
      setBillingTaxId('');
      setIndustry('');
      setOkMessage('Tenant created.');
      if (refreshTenants) refreshTenants();
      loadOverview();
    } catch (err) {
      console.error(err);
      var status = err.status;
      var data = err.data;
      var errCode = data && data.error ? data.error : '';
      if (status === 409 && errCode === 'email_exists') {
        setError((data && data.message) ? data.message : 'Email already exists.');
      } else if (status === 409 && errCode === 'tenant_name_exists') {
        setError((data && data.message) ? data.message : 'Tenant name already exists.');
      } else if (status === 400 && errCode === 'invalid_plan') {
        setError((data && data.message) ? data.message : 'Invalid plan.');
      } else if ((status === 400 && (errCode === 'validation_failed' || errCode === 'validation_error')) && data && data.fields) {
        var firstKey = Object.keys(data.fields)[0];
        setError(firstKey ? (data.fields[firstKey] || (data.message || 'Validation failed')) : (data.message || 'Validation failed'));
      } else if (status === 500) {
        var detail = (data && data.detail) ? data.detail : (data && data.message) ? data.message : 'Failed to create tenant.';
        var detailId = data && data.detail_id ? ' (Ref: ' + data.detail_id + ')' : '';
        setError(detail + detailId);
      } else {
        setError((data && data.message) ? data.message : (data && data.detail) ? data.detail : (err.message || 'Failed to create tenant.'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Tenants &amp; Billing</div>
          <div className="card-subtitle">
            Overview of workspace usage and plans. Super admin user id: {superAdminUserId || 'n/a'}.
          </div>
        </div>
        <button
          type="button"
          className="button small"
          disabled={generateInvoiceLoading}
          onClick={async function () {
            setGenerateInvoiceLoading(true);
            try {
              var res = await API.json('POST', '/api/admin/billing/invoices/generate', {});
              alert('Invoices: ' + (res.invoices_created || 0) + ' created for ' + (res.tenants_processed || 0) + ' tenants');
              loadOverview();
            } catch (e) { alert('Failed: ' + (e && e.message)); }
            finally { setGenerateInvoiceLoading(false); }
          }}
        >
          {generateInvoiceLoading ? 'Generating…' : 'Generate monthly invoices'}
        </button>
      </div>
      <div className="card-body">
        <div style={{ marginBottom: 16 }}>
          <button type="button" className="button small outline" onClick={function () { setPlansExpanded(!plansExpanded); }}>
            {plansExpanded ? '▼' : '▶'} Plans ({overviewPlans.length})
          </button>
          {plansExpanded && (
            <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
              <table className="table" style={{ fontSize: '0.78rem' }}>
                <thead><tr><th>Plan</th><th>Price/mo</th><th>Included min</th></tr></thead>
                <tbody>
                  {overviewPlans.map(function (p, idx) {
                    return (
                      <tr key={p.key || idx}>
                        <td>{p.label || p.key}</td>
                        <td>${(p.price_usd || 0).toFixed(2)}</td>
                        <td>{p.included_minutes != null ? p.included_minutes : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <DBPlansEditor />
            </div>
          )}
        </div>
        <form
          className="create-tenant-form theme-aware-form"
          onSubmit={handleCreateTenant}
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--card)'
          }}
        >
          <div className="create-tenant-form-title" style={{ fontSize: '0.8rem', marginBottom: 8, fontWeight: 500, color: 'var(--fg)' }}>
            Create new tenant
          </div>
          <div className="field-row">
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-name">Tenant name</label>
              <input
                id="create-tenant-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Skyline Apps"
              />
            </div>
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-timezone">Timezone</label>
              <input
                id="create-tenant-timezone"
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="America/Los_Angeles"
              />
            </div>
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-plan">Plan</label>
              <select
                id="create-tenant-plan"
                className="select"
                value={planKey}
                onChange={(e) => onPlanKeyChange(e.target.value)}
              >
                {adminPlans.length ? adminPlans.map(function (p) {
                  return <option key={p.id || p.key} value={p.key || ''}>{p.label || p.display_name || p.key}</option>;
                }) : (
                  <option value="starter">Starter</option>
                )}
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-minutes">Included minutes / month</label>
              <input
                id="create-tenant-minutes"
                type="number"
                value={includedMinutes}
                onChange={(e) => setIncludedMinutes(e.target.value)}
              />
            </div>
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-overage">Overage rate ($ / min)</label>
              <input
                id="create-tenant-overage"
                type="number"
                step="0.01"
                value={overageRate}
                onChange={(e) => setOverageRate(e.target.value)}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-email">Admin email</label>
              <input
                id="create-tenant-email"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="founder@customer.com"
              />
            </div>
            <div className="field">
              <label className="theme-aware-label" htmlFor="create-tenant-password">Admin password</label>
              <input
                id="create-tenant-password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
              />
            </div>
          <div className="field">
            <label className="theme-aware-label" htmlFor="create-tenant-caller-id">Outbound caller ID number (Twilio)</label>
            <input
              id="create-tenant-caller-id"
              value={outbound_number}
              onChange={(e) => setOutboundNumber(e.target.value)}
              placeholder="+1..."
            />
          </div>
          <div className="field">
            <label className="theme-aware-label" htmlFor="create-tenant-mfa">Primary Phone for MFA</label>
            <input
              id="create-tenant-mfa"
              value={adminMfaPhone}
              onChange={(e) => setAdminMfaPhone(e.target.value)}
              placeholder="+1..."
            />
          </div>
          </div>
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <button type="button" className="button small outline" onClick={function () { setBillingExpanded(!billingExpanded); }}>
              {billingExpanded ? '▼' : '▶'} Billing (optional)
            </button>
            {billingExpanded ? (
              <div className="field-row" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-company">Company name</label><input id="bill-company" type="text" value={billingCompanyName} onChange={function (e) { setBillingCompanyName(e.target.value); }} placeholder="Acme Inc." /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-contact">Contact name</label><input id="bill-contact" type="text" value={billingContactName} onChange={function (e) { setBillingContactName(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-email">Billing email</label><input id="bill-email" type="email" value={billingEmail} onChange={function (e) { setBillingEmail(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-phone">Billing phone</label><input id="bill-phone" type="text" value={billingPhone} onChange={function (e) { setBillingPhone(e.target.value); }} /></div>
                <div className="field" style={{ width: '100%' }}><label className="theme-aware-label" htmlFor="bill-addr1">Address line 1</label><input id="bill-addr1" type="text" value={billingAddressLine1} onChange={function (e) { setBillingAddressLine1(e.target.value); }} /></div>
                <div className="field" style={{ width: '100%' }}><label className="theme-aware-label" htmlFor="bill-addr2">Address line 2</label><input id="bill-addr2" type="text" value={billingAddressLine2} onChange={function (e) { setBillingAddressLine2(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-city">City</label><input id="bill-city" type="text" value={billingCity} onChange={function (e) { setBillingCity(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-state">State</label><input id="bill-state" type="text" value={billingState} onChange={function (e) { setBillingState(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-postal">Postal code</label><input id="bill-postal" type="text" value={billingPostalCode} onChange={function (e) { setBillingPostalCode(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-country">Country</label><input id="bill-country" type="text" value={billingCountry} onChange={function (e) { setBillingCountry(e.target.value); }} /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-tax">Tax ID</label><input id="bill-tax" type="text" value={billingTaxId} onChange={function (e) { setBillingTaxId(e.target.value); }} placeholder="Optional" /></div>
                <div className="field"><label className="theme-aware-label" htmlFor="bill-industry">Industry</label><input id="bill-industry" type="text" value={industry} onChange={function (e) { setIndustry(e.target.value); }} placeholder="e.g. sales" /></div>
              </div>
            ) : null}
          </div>
          {error ? (
            <div className="form-error" style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: 4 }}>{error}</div>
          ) : null}
          {okMessage ? (
            <div className="form-ok" style={{ color: 'var(--success)', fontSize: '0.78rem', marginTop: 4 }}>{okMessage}</div>
          ) : null}
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="submit" className="button primary small" disabled={saving}>
              {saving ? 'Creating…' : 'Create tenant'}
            </button>
          </div>
        </form>

        {overviewTenants.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
            No tenants found yet. A default tenant is created automatically.
          </div>
        ) : (
          <div className="tenants-list-scroll" style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid var(--color-border)' }}>
            <table className="table" style={{ fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th style={{ color: 'var(--color-fg)' }}>Tenant</th>
                  <th style={{ color: 'var(--color-fg)' }}>Plan</th>
                  <th style={{ color: 'var(--color-fg)' }}>Revenue (month)</th>
                  <th style={{ color: 'var(--color-fg)' }}>Provider cost</th>
                  <th style={{ color: 'var(--color-fg)' }}>Margin</th>
                  <th style={{ color: 'var(--color-fg)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {overviewTenants.map(function (t) {
                  var usage = t.usage || {};
                  var revenue = t.revenue || {};
                  var costs = t.costs || {};
                  var marginUsd = t.margin_usd != null ? t.margin_usd : ((revenue.total_usd || 0) - (costs.provider_cost_usd || 0));
                  var isExpanded = expandedTenantId === t.tenant_id;
                  return (
                    <React.Fragment key={t.tenant_id}>
                      <tr style={marginUsd < -50 ? { background: 'rgba(239,68,68,0.1)' } : {}}>
                        <td>
                          <button type="button" className="button small outline" style={{ marginRight: 6, padding: '2px 6px', minWidth: 24 }} onClick={function () { setExpandedTenantId(isExpanded ? null : t.tenant_id); }} title="Show billing details">
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          {t.name || 'Tenant ' + t.tenant_id}
                        </td>
                        <td>
                          <select
                            className="select"
                            aria-label={'Plan for ' + (t.name || ('tenant ' + t.tenant_id))}
                            style={{ fontSize: '0.75rem', padding: '2px 6px', color: 'var(--color-fg)', background: 'var(--color-bg)' }}
                            value={t.plan_key || ''}
                            onChange={async function (e) {
                              var newKey = e.target.value;
                              if (!newKey) return;
                              try {
                                await API.json('PATCH', '/api/admin/tenants/' + t.tenant_id, { plan_key: newKey });
                                loadOverview();
                                if (refreshTenants) refreshTenants();
                              } catch (err) { alert('Failed: ' + (err && err.message)); }
                            }}
                          >
                            {overviewPlans.map(function (p) {
                              return <option key={p.key || p.label} value={p.key || ''}>{p.label || p.key}</option>;
                            })}
                          </select>
                        </td>
                        <td>${(revenue.total_usd != null ? revenue.total_usd : 0).toFixed(2)}</td>
                        <td>${(costs.provider_cost_usd != null ? costs.provider_cost_usd : 0).toFixed(2)}</td>
                        <td>${Number(marginUsd).toFixed(2)}</td>
                        <td>
                          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" className="button small outline" onClick={function () {
                              setTenantEditId(t.tenant_id);
                              setTenantEditInboundVoice('');
                              setTenantEditInboundVoiceEffective('');
                              setTenantEditPlanKey(t.plan_key || '');
                              setTenantEditFeaturesOverride('{}');
                              setTenantEditOverrideIntegrations({});
                              API.json('GET', '/api/admin/plans').then(function (r) { if (r && r.plans) setTenantEditPlanList(r.plans); }).catch(function () {});
                              API.json('GET', '/api/admin/tenants').then(function (r) {
                                var item = (r.items || []).find(function (x) { return x.id === t.tenant_id; });
                                if (item) {
                                  var raw = item.features_override_json;
                                  if (typeof raw === 'string') { try { raw = JSON.parse(raw || '{}'); } catch (_) { raw = {}; } }
                                  raw = raw || {};
                                  var integrations = (raw.integrations && typeof raw.integrations === 'object') ? raw.integrations : {};
                                  setTenantEditFeaturesOverride(JSON.stringify(raw, null, 2));
                                  setTenantEditOverrideIntegrations(integrations);
                                  setTenantEditName(item.name || t.name || '');
                                  setTenantEditCompany(item.billing_company_name || item.name || '');
                                  setTenantEditBillingEmail(item.billing_contact_email || item.billing_email || '');
                                  setTenantEditBillingPhone(item.billing_contact_phone || item.billing_phone || '');
                                  setTenantEditInboundVoice(item.inbound_voice_tier === 'premium' || item.inbound_voice_tier === 'standard' ? item.inbound_voice_tier : '');
                                  setTenantEditInboundVoiceEffective(item.inbound_voice_effective === 'premium' ? 'premium' : 'standard');
                                }
                              }).catch(function () {});
                            }}>
                              Edit
                            </button>
                            <button type="button" className="button small primary" onClick={async function () {
                              try {
                                var res = await API.json('POST', '/api/admin/tenants/' + t.tenant_id + '/impersonate', {});
                                if (res && res.ok && res.token) {
                                  var orig = window.localStorage.getItem('authToken');
                                  if (orig) window.localStorage.setItem('superAdminToken', orig);
                                  window.localStorage.setItem('authToken', res.token);
                                  window.location.reload();
                                }
                              } catch (e) { alert('Failed to impersonate: ' + (e && e.message)); }
                            }}>
                              Impersonate
                            </button>
                            <button type="button" className="button small" onClick={function () { setOutboundEditTenantId(t.tenant_id); setOutboundEditValue(t.outbound_number || ''); }}>
                              Edit Outbound
                            </button>
                            <button type="button" className="button small" onClick={function () { setNumbersModalTenantId(t.tenant_id); }}>
                              Numbers
                            </button>
                            <button type="button" className="button small" onClick={function () { setInboundEditTenantId(t.tenant_id); }}>
                              Inbound Instructions
                            </button>
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ background: 'var(--color-bg-elevated)' }}>
                          <td colSpan={6} style={{ fontSize: '0.72rem', padding: '8px 12px', color: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)' }}>
                            <div>Plan price: ${(revenue.plan_price_usd != null ? revenue.plan_price_usd : 0).toFixed(2)} · Overage: ${(revenue.overage_usd != null ? revenue.overage_usd : 0).toFixed(2)} · Twilio: ${(costs.twilio_usd != null ? costs.twilio_usd : 0).toFixed(2)} · OpenAI: ${(costs.openai_usd != null ? costs.openai_usd : 0).toFixed(2)}</div>
                            <div>Minutes used: {usage.minutes_used != null ? usage.minutes_used : 0} · Calls: {usage.calls_count != null ? usage.calls_count : 0} · Admin: {t.admin_email || '—'} · Timezone: {t.timezone || '—'}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 8 }}>
          Usage is estimated from call duration (voice minutes). For production billing, plug this into your actual payment processor.
        </p>

        {outboundEditTenantId && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
            onClick={(e) => e.target === e.currentTarget && setOutboundEditTenantId(null)}
          >
            <div
              style={{
                background: 'var(--surface-raised)', color: 'var(--fg)',
                borderRadius: 8,
                maxWidth: 400,
                width: '100%',
                padding: 20,
                border: '1px solid var(--border-strong)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Edit Outbound Number</h3>
              <input
                type="text"
                aria-label="Outbound number"
                value={outboundEditValue}
                onChange={(e) => setOutboundEditValue(e.target.value)}
                placeholder="+1..."
                style={{ width: '100%', padding: 8, marginBottom: 12, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="button small" onClick={() => setOutboundEditTenantId(null)}>Cancel</button>
                <button
                  type="button"
                  className="button primary small"
                  onClick={async () => {
                    try {
                      await API.json('PATCH', '/api/admin/tenants/' + outboundEditTenantId + '/outbound-number', { outbound_number: outboundEditValue });
                      if (refreshTenants) refreshTenants();
                      loadOverview();
                      setOutboundEditTenantId(null);
                    } catch (err) { alert('Failed: ' + (err && err.message)); }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {numbersModalTenantId && (
          <NumbersModal
            tenantId={numbersModalTenantId}
            tenantName={(overviewTenants.find(function (x) { return x.tenant_id === numbersModalTenantId; }) || tenants.find(function (x) { return x.id === numbersModalTenantId; }) || {}).name || 'Tenant'}
            onClose={function () { setNumbersModalTenantId(null); }}
            onSaved={function () { if (refreshTenants) refreshTenants(); loadOverview(); }}
          />
        )}

        {inboundEditTenantId && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
            onClick={(e) => e.target === e.currentTarget && setInboundEditTenantId(null)}
          >
            <div
              style={{
                background: 'var(--surface-raised)', color: 'var(--fg)',
                borderRadius: 8,
                maxWidth: 640,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                padding: 20,
                border: '1px solid var(--border-strong)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>
                  Inbound Instructions — {((t) => (t && t.name) || inboundEditTenantId)(tenants.find((x) => x.id === inboundEditTenantId))}
                </h3>
                <button
                  type="button"
                  className="button small"
                  onClick={() => setInboundEditTenantId(null)}
                >
                  Close
                </button>
              </div>
              <div ref={inboundModalRef} />
            </div>
          </div>
        )}

        {tenantEditId && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
            onClick={function (e) { if (e.target === e.currentTarget) setTenantEditId(null); }}
          >
            <div
              style={{
                background: 'var(--surface-raised)', color: 'var(--fg)',
                borderRadius: 8,
                maxWidth: 760,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                padding: 20,
                border: '1px solid var(--border-strong)'
              }}
              onClick={function (e) { e.stopPropagation(); }}
            >
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Edit tenant</h3>
              <div className="acct-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                <div className="acct-form">
                  <label className="acct-label">Company name
                    <input className="acct-input" value={tenantEditName} onChange={function (e) { setTenantEditName(e.target.value); setTenantEditCompany(e.target.value); }} />
                  </label>
                  <label className="acct-label">Billing email
                    <input className="acct-input" type="email" value={tenantEditBillingEmail} onChange={function (e) { setTenantEditBillingEmail(e.target.value); }} />
                  </label>
                  <label className="acct-label">Billing phone
                    <input className="acct-input" type="tel" value={tenantEditBillingPhone} onChange={function (e) { setTenantEditBillingPhone(e.target.value); }} />
                  </label>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', marginBottom: 12, padding: 8, background: 'var(--color-bg-elevated)', borderRadius: 6 }}>
                <strong>Plan:</strong> {tenantEditPlanKey || '—'}
                {Object.keys(tenantEditOverrideIntegrations).length > 0 ? (
                  <span style={{ marginLeft: 8, color: 'var(--color-muted)' }}>
                    <strong>Overrides:</strong> {TENANT_OVERRIDE_FEATURE_KEYS.filter(function (f) { return tenantEditOverrideIntegrations[f.key] !== undefined; }).map(function (f) { return f.label + ' ' + (tenantEditOverrideIntegrations[f.key] ? 'enabled' : 'disabled') + ' (override)'; }).join(', ')}
                  </span>
                ) : (
                  <span style={{ marginLeft: 8, color: 'var(--color-muted)' }}>No overrides</span>
                )}
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label htmlFor="tenant-edit-plan">Plan</label>
                <select
                  id="tenant-edit-plan"
                  className="select"
                  value={tenantEditPlanKey}
                  onChange={function (e) { setTenantEditPlanKey(e.target.value); }}
                  style={{ width: '100%', padding: 8 }}
                >
                  <option value="">—</option>
                  {(tenantEditPlanList || []).map(function (p) { return <option key={p.id} value={p.key || ''}>{p.key || ''} ({p.display_name || ''})</option>; })}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label htmlFor="tenant-edit-inbound-voice">Inbound voice</label>
                <select
                  id="tenant-edit-inbound-voice"
                  className="select"
                  value={tenantEditInboundVoice}
                  onChange={function (e) { setTenantEditInboundVoice(e.target.value); }}
                  style={{ width: '100%', padding: 8 }}
                >
                  <option value="">{'Automatic (callers hear ' + (tenantEditInboundVoiceEffective === 'premium' ? 'Premium voice' : 'Standard voice (lower cost)') + ')'}</option>
                  <option value="premium">Premium voice</option>
                  <option value="standard">Standard voice (lower cost)</option>
                </select>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginTop: 6 }}>
                  {tenantEditInboundVoice
                    ? 'Saved choice is ' + (tenantEditInboundVoice === 'premium' ? 'Premium voice' : 'Standard voice (lower cost)') + '. Callers hear ' + (tenantEditInboundVoiceEffective === 'premium' ? 'Premium voice' : 'Standard voice (lower cost)') + '.'
                    : 'Automatic. Callers hear ' + (tenantEditInboundVoiceEffective === 'premium' ? 'Premium voice' : 'Standard voice (lower cost)') + '.'}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600 }}>Capabilities Override</label>
                  <button type="button" className="button small outline" onClick={function () { setTenantEditOverrideIntegrations({}); }}>Reset to plan defaults</button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginBottom: 8 }}>Override features for this tenant. Leave as &quot;Plan default&quot; to use plan value.</div>
                {TENANT_OVERRIDE_FEATURE_KEYS.map(function (f) {
                  var val = tenantEditOverrideIntegrations[f.key];
                  return (
                    <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ width: 140 }}>{f.label}</span>
                      <select
                        aria-label={f.label + ' override'}
                        value={val === true ? 'on' : val === false ? 'off' : 'default'}
                        onChange={function (e) {
                          var v = e.target.value;
                          setTenantEditOverrideIntegrations(function (prev) {
                            var next = { ...prev };
                            if (v === 'default') delete next[f.key]; else next[f.key] = v === 'on';
                            return next;
                          });
                        }}
                        style={{ padding: 4, minWidth: 120 }}
                      >
                        <option value="default">Plan default</option>
                        <option value="on">Enable (override)</option>
                        <option value="off">Disable (override)</option>
                      </select>
                    </div>
                  );
                })}
              </div>
              <UnrestrictedAccountToggle api={API.json} toast={showToast} tenant={{ id: tenantEditId, name: tenantEditName }} />
              <TenantUsersPanel tenantId={tenantEditId} api={API.json} toast={showToast} scope="platform" actorId={user && user.id} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button type="button" className="button small" onClick={function () { setTenantEditId(null); }}>Cancel</button>
                <button
                  type="button"
                  className="button primary small"
                  disabled={tenantEditSaving}
                  onClick={async function () {
                    var overrideObj = Object.keys(tenantEditOverrideIntegrations).length > 0 ? { integrations: tenantEditOverrideIntegrations } : null;
                    setTenantEditSaving(true);
                    try {
                      await API.json('PUT', '/api/admin/tenants/' + tenantEditId, {
                        plan_key: tenantEditPlanKey || null,
                        features_override_json: overrideObj,
                        name: tenantEditName,
                        billing_company_name: tenantEditCompany || tenantEditName,
                        billing_email: tenantEditBillingEmail,
                        billing_phone: tenantEditBillingPhone,
                        inbound_voice_tier: tenantEditInboundVoice || null
                      });
                      if (refreshTenants) refreshTenants();
                      loadOverview();
                      setTenantEditId(null);
                    } catch (err) { alert((err && err.message) || 'Save failed'); } finally { setTenantEditSaving(false); }
                  }}
                >
                  {tenantEditSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function InsightsView({ user, tenants, tenantMe }) {
  const [insights, setInsights] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [groupBy, setGroupBy] = useState('lead_source');
  const [tenantId, setTenantId] = useState(null);
  const isSuperAdminNoTenant = user && user.role === 'super_admin' && (user.tenant_id == null);
  const effectiveTenantId = isSuperAdminNoTenant ? (tenantId || (tenants && tenants[0] ? tenants[0].id : null)) : (user && user.tenant_id);

  useEffect(function () {
    if (!effectiveTenantId) { setLoading(false); setInsights(null); return; }
    setLoading(true);
    var zone = (tenantMe && tenantMe.timezone) || getWorkspaceTimeZone();
    var dateTo = todayYmd(zone);
    var dateFrom = addDaysYmd(dateTo, -(dateRange || 30));
    var qs = '?date_from=' + encodeURIComponent(dateFrom) + '&date_to=' + encodeURIComponent(dateTo);
    if (isSuperAdminNoTenant && effectiveTenantId) qs += '&tenant_id=' + effectiveTenantId;
    Promise.all([
      API.json('GET', '/api/insights' + qs),
      API.json('GET', '/api/insights/breakdown' + qs + '&group_by=' + encodeURIComponent(groupBy)).catch(function () { return { breakdown: [] }; })
    ]).then(function (results) {
      setInsights(results[0] && results[0].insights ? results[0].insights : null);
      setBreakdown(results[1] && results[1].breakdown ? results[1].breakdown : []);
    }).catch(function () { setInsights(null); setBreakdown([]); }).finally(function () { setLoading(false); });
  }, [effectiveTenantId, dateRange, groupBy, tenantId, isSuperAdminNoTenant, tenantMe && tenantMe.timezone]);

  function exportCsv() {
    if (!breakdown || breakdown.length === 0) { alert('No data to export'); return; }
    var headers = ['segment', 'calls', 'connected', 'avg_duration_seconds'];
    var rows = [headers.join(',')].concat(breakdown.map(function (r) {
      return [r.segment, r.calls, r.connected, r.avg_duration_seconds].map(function (v) {
        var s = String(v != null ? v : '');
        return s.indexOf(',') >= 0 ? '"' + s.replace(/"/g, '""') + '"' : s;
      }).join(',');
    }));
    var blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'insights-breakdown.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  var v = insights && insights.volume ? insights.volume : {};
  var f = insights && insights.funnel ? insights.funnel : {};
  var ai = insights && insights.ai_performance ? insights.ai_performance : {};
  var fin = insights && insights.financial ? insights.financial : null;
  var groupLabel = groupBy === 'agent_id' ? 'Assigned agent' : 'Lead source';

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">AI Insights</div>
          <div className="card-subtitle">Executive metrics, funnel, and cost for the selected period. <TimesShown timeZone={tenantMe && tenantMe.timezone} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {isSuperAdminNoTenant && tenants && tenants.length > 0 ? (
            <select value={tenantId || ''} onChange={function (e) { setTenantId(e.target.value ? parseInt(e.target.value, 10) : null); }} style={{ padding: '6px 10px', borderRadius: 6 }}>
              <option value="">Select tenant…</option>
              {tenants.map(function (t) { return <option key={t.id} value={t.id}>{t.name || ('Tenant ' + t.id)}</option>; })}
            </select>
          ) : null}
          <select className="select" aria-label="Insights date range" value={dateRange} onChange={function (e) { setDateRange(parseInt(e.target.value, 10) || 30); }} style={{ padding: '6px 10px', borderRadius: 6, width: 'auto' }}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button type="button" className="button small outline" onClick={exportCsv} disabled={!breakdown || breakdown.length === 0}>Export CSV</button>
        </div>
      </div>
      <div className="card-body">
        {loading ? <div className="muted">Loading…</div> : !insights ? (
          <div className="muted">No insights for this tenant. Select a tenant (Super Admin) or ensure you have call data.</div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
              <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Total calls</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{v.total_calls || 0}</div></div>
              <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Connected</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{v.connected_calls || 0}</div></div>
              <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Booked</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{f.appointments_booked || 0}</div></div>
              <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Answer rate</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{v.answer_rate || 0}%</div></div>
              {fin ? <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Provider cost</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>${(fin.total_cost_usd || 0).toFixed(2)}</div></div> : null}
              {fin ? <div className="card" style={{ padding: 12, background: 'var(--color-bg-elevated)' }}><div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Margin</div><div style={{ fontSize: '1.25rem', fontWeight: 600 }}>${(fin.margin_usd || 0).toFixed(2)}</div></div> : null}
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>Breakdown by</div>
                <select className="select" aria-label="Breakdown grouping" value={groupBy} onChange={function (e) { setGroupBy(e.target.value); }} style={{ width: 'auto', padding: '4px 8px' }}>
                  <option value="lead_source">Lead source</option>
                  <option value="agent_id">Assigned agent</option>
                </select>
              </div>
              <table className="table" style={{ fontSize: '0.85rem' }}>
                <thead><tr><th>{groupLabel}</th><th>Calls</th><th>Connected</th><th>Avg duration (s)</th></tr></thead>
                <tbody>
                  {(breakdown && breakdown.length > 0) ? breakdown.map(function (r, i) { return <tr key={i}><td>{r.segment}</td><td>{r.calls}</td><td>{r.connected}</td><td>{r.avg_duration_seconds}</td></tr>; }) : <tr><td colSpan={4} className="muted">No breakdown data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function getStoredTheme() {
  var stored = window.localStorage.getItem('adminTheme');
  return (stored === 'dark' || stored === 'light') ? stored : 'light';
}

function applyTheme(theme) {
  window.localStorage.setItem('adminTheme', theme);
  document.documentElement.setAttribute('data-theme', theme);
}

var PLATFORM_SALES_PREVIEW = {
  primaryProduct: 'custom mobile apps and simple software',
  focusDescription: 'We build affordable, done-for-you mobile apps and simple software for small businesses.',
  targetProfile: 'Owners and operators of small businesses who might benefit from a mobile app or automation.',
  painPoints: 'Manual processes, missed opportunities on mobile, clunky systems, inability to move fast.',
  callObjective: 'Qualify interest and, when appropriate, book a follow-up call to scope a project.',
  objectionStyle: 'Handle objections calmly and helpfully. Acknowledge concerns, ask clarifying questions, and never be pushy.',
  closingStyle: 'Soft, collaborative close. Suggest next steps instead of hard closing, and always respect their time.'
};
var NEUTRAL_SALES_PREVIEW = {
  primaryProduct: 'your core offering',
  focusDescription: '',
  targetProfile: 'Prospects and businesses that may be a fit for the product or service described above.',
  painPoints: 'Business challenges and opportunities relevant to their situation—avoid assuming a specific product category.',
  callObjective: 'Understand their needs, qualify fit, and agree on appropriate next steps.',
  objectionStyle: 'Handle objections calmly and helpfully; acknowledge concerns and never be pushy.',
  closingStyle: 'Use a soft, collaborative close; suggest next steps and respect their time.'
};

function spokenSalesLine(settings, key) {
  var sales = (settings && settings.sales) || {};
  var stored = String(sales[key] || '').trim();
  if (stored) return stored;
  var company = String((settings && settings.companyName) || '').trim();
  var started = !!(company || String(sales.primaryProduct || '').trim() || String(sales.focusDescription || '').trim());
  var table = started ? NEUTRAL_SALES_PREVIEW : PLATFORM_SALES_PREVIEW;
  return table[key] || '';
}

function CallerHearsLine({ text }) {
  var line = String(text || '').trim();
  return <div className="field-help" style={{ marginTop: 4 }}>Caller hears: {line || '(no extra line)'}</div>;
}

function InboundScriptEditor({ tenantId }) {
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState('default');
  const [script, setScript] = useState('');
  const [preview, setPreview] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const scriptDirtyRef = React.useRef(false);

  useEffect(function () {
    if (!tenantId) return;
    var cancelled = false;
    API.json('GET', '/api/tenant/inbound/profiles').then(function (json) {
      if (cancelled) return;
      var list = (json && json.profiles && json.profiles.length) ? json.profiles : [{ profile_id: 'default', display_name: 'Default', script: '' }];
      setProfiles(list);
      setProfileId(function (cur) {
        var ids = list.map(function (p) { return p.profile_id || 'default'; });
        return ids.indexOf(cur) >= 0 ? cur : (list[0].profile_id || 'default');
      });
    }).catch(function () {
      if (!cancelled) setProfiles([{ profile_id: 'default', display_name: 'Default', script: '' }]);
    });
    return function () { cancelled = true; };
  }, [tenantId]);

  useEffect(function () {
    if (!tenantId) return;
    var cancelled = false;
    var profile = null;
    for (var i = 0; i < profiles.length; i++) {
      if ((profiles[i].profile_id || 'default') === profileId) profile = profiles[i];
    }
    if (!scriptDirtyRef.current) {
      setScript(profile && typeof profile.script === 'string' ? profile.script : '');
    }
    setPreview('');
    API.json('GET', '/api/inbound/instructions/' + encodeURIComponent(tenantId) + '?profile_id=' + encodeURIComponent(profileId))
      .then(function (data) {
        if (!cancelled) setPreview(data && typeof data.instructions === 'string' ? data.instructions : '');
      })
      .catch(function () { if (!cancelled) setPreview(''); });
    return function () { cancelled = true; };
  }, [tenantId, profileId, profiles]);

  async function saveScript() {
    setSaving(true);
    setStatus('');
    try {
      await API.json('POST', '/api/inbound/instructions/' + encodeURIComponent(tenantId) + '?profile_id=' + encodeURIComponent(profileId), { instructions: script });
      try {
        await API.json('PUT', '/api/tenant/inbound/profiles/' + encodeURIComponent(profileId), { script: script });
      } catch (_profileErr) {}
      scriptDirtyRef.current = false;
      setProfiles(function (list) {
        return list.map(function (p) {
          if ((p.profile_id || 'default') !== profileId) return p;
          return Object.assign({}, p, { script: script });
        });
      });
      setStatus('Saved.');
    } catch (e) {
      setStatus(authErrorText(e, 'Could not save instructions.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <label className="field">
        <span className="field-label">Profile</span>
        <select className="select" value={profileId} onChange={function (e) { scriptDirtyRef.current = false; setProfileId(e.target.value); }}>
          {profiles.map(function (p) {
            var id = p.profile_id || 'default';
            return <option key={id} value={id}>{p.display_name || id}</option>;
          })}
        </select>
      </label>
      <label className="field" style={{ marginTop: 8 }}>
        <span className="field-label">Profile script</span>
        <textarea className="input" rows={8} value={script} onChange={function (e) { scriptDirtyRef.current = true; setScript(e.target.value); }} />
        <span className="field-help">This box is only the saved profile script. Leave it blank when nothing is stored.</span>
      </label>
      <button type="button" className="button primary small" style={{ marginTop: 8 }} disabled={saving} onClick={saveScript}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      {status ? <div className="field-help" style={{ marginTop: 8 }}>{status}</div> : null}
      <div className="field" style={{ marginTop: 12 }}>
        <div className="field-label">Caller will hear</div>
        <div className="field-help" style={{ whiteSpace: 'pre-wrap' }}>{preview || '(no extra line)'}</div>
      </div>
    </div>
  );
}

function SettingsView({ settings, refresh, tenantId, user, openLead }) {
  const [voice, setVoice] = useState(settings.pollyVoice || 'Matthew');
  const [agentName, setAgentName] = useState(settings.agentName || '');
  const [companyName, setCompanyName] = useState(settings.companyName || '');
  const [timezone, setTimezone] = useState(settings.timezone || 'America/Los_Angeles');
  const [theme, setTheme] = useState(getStoredTheme);
  const [salesPrimaryProduct, setSalesPrimaryProduct] = useState('');
  const [salesFocusDescription, setSalesFocusDescription] = useState('');
  const [salesTargetProfile, setSalesTargetProfile] = useState('');
  const [salesPainPoints, setSalesPainPoints] = useState('');
  const [salesCallObjective, setSalesCallObjective] = useState('');
  const [salesObjectionStyle, setSalesObjectionStyle] = useState('');
  const [salesClosingStyle, setSalesClosingStyle] = useState('');
  const [handoffNumber, setHandoffNumber] = useState('');
  const [industry, setIndustry] = useState('sales');
  const [metricsSelected, setMetricsSelected] = useState([]);
  const [recordCalls, setRecordCalls] = useState(1);
  const [agentHandoffMode, setAgentHandoffMode] = useState('assigned_only');
  const [recordingPreference, setRecordingPreference] = useState('');
  const [inboundVoiceTier, setInboundVoiceTier] = useState('');
  const [inboundVoiceEffective, setInboundVoiceEffective] = useState('');
  const [inboundVoiceSaving, setInboundVoiceSaving] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [voices, setVoices] = useState([]);
  const isImpersonating = !!(user && user.is_impersonation);
  /** Bare super admin (no impersonation JWT): no tenant row on user — workspace APIs need impersonate. */
  const superAdminNoTenantContext = !!(user && user.role === 'super_admin' && (user.tenant_id == null || user.tenant_id === '') && !isImpersonating);

  const METRIC_OPTIONS = [
    { id: 'calls_placed', label: 'Calls placed' },
    { id: 'calls_answered', label: 'Calls answered' },
    { id: 'connect_rate', label: 'Connect rate' },
    { id: 'avg_handle_time', label: 'Avg handle time' },
    { id: 'transfers_requested', label: 'Transfers requested' },
    { id: 'transfers_completed', label: 'Transfers completed' },
    { id: 'dnc_rate', label: 'DNC rate' },
    { id: 'interested_rate', label: 'Interested rate' }
  ];

  useEffect(() => {
    setVoice(settings.pollyVoice || 'Matthew');
    setAgentName(settings.agentName || '');
    setCompanyName(settings.companyName || '');
    setTimezone(settings.timezone || 'America/Los_Angeles');

    if (settings.sales) {
      setSalesPrimaryProduct(settings.sales.primaryProduct || '');
      setSalesFocusDescription(settings.sales.focusDescription || '');
      setSalesTargetProfile(settings.sales.targetProfile || '');
      setSalesPainPoints(settings.sales.painPoints || '');
      setSalesCallObjective(settings.sales.callObjective || '');
      setSalesObjectionStyle(settings.sales.objectionStyle || '');
      setSalesClosingStyle(settings.sales.closingStyle || '');
    }
  }, [settings]);

  useEffect(() => {
    async function loadVoices() {
      try {
        if (user && user.role === 'super_admin' && (user.tenant_id == null || user.tenant_id === '') && !user.is_impersonation) return;
        const res = await API.json('GET', '/api/settings/voices');
        setVoices(res.voices || []);
      } catch (e) {
        console.error(e);
      }
    }
    loadVoices();
  }, [user]);

  useEffect(function () {
    if (!tenantId) return;
    API.json('GET', '/api/tenant/handoff').then(function (r) {
      setHandoffNumber(r.handoff_number || '');
    }).catch(function () {});
  }, [tenantId]);

  useEffect(function () {
    if (!tenantId) return;
    API.json('GET', '/api/tenant/settings').then(function (r) {
      if (r && r.ok) {
        setIndustry(r.industry || 'sales');
        setMetricsSelected(Array.isArray(r.metrics_json) ? r.metrics_json : []);
        setRecordCalls(r.record_calls === 0 ? 0 : 1);
        setAgentHandoffMode(r.agent_handoff_mode === 'tenant_any' ? 'tenant_any' : 'assigned_only');
        setRecordingPreference(r.recording_preference != null ? String(r.recording_preference) : '');
        setInboundVoiceTier(r.inbound_voice_tier === 'premium' || r.inbound_voice_tier === 'standard' ? r.inbound_voice_tier : '');
        setInboundVoiceEffective(r.inbound_voice_effective === 'premium' ? 'premium' : 'standard');
      }
    }).catch(function () {});
  }, [tenantId]);

  function inboundVoiceLabel(tier) {
    return tier === 'premium' ? 'Premium voice' : 'Standard voice (lower cost)';
  }

  async function saveInboundVoice() {
    if (!tenantId) {
      showToast('Impersonate a tenant to edit tenant settings (handoff, industry, etc.).', 'error');
      return;
    }
    try {
      setInboundVoiceSaving(true);
      await API.json('PATCH', '/api/tenant/settings', { inbound_voice_tier: inboundVoiceTier || null });
      var ts = await API.json('GET', '/api/tenant/settings');
      if (ts && ts.ok) {
        setInboundVoiceTier(ts.inbound_voice_tier === 'premium' || ts.inbound_voice_tier === 'standard' ? ts.inbound_voice_tier : '');
        setInboundVoiceEffective(ts.inbound_voice_effective === 'premium' ? 'premium' : 'standard');
      }
      showToast('Inbound voice saved.', 'success');
    } catch (e) {
      showToast(uiErrorMessage(e, 'Could not save inbound voice.'), 'error');
    } finally {
      setInboundVoiceSaving(false);
    }
  }

  async function save() {
    if (!tenantId) {
      showToast('Impersonate a tenant to edit tenant settings (handoff, industry, etc.).', 'error');
      return;
    }
    try {
      setSettingsSaving(true);
      var promises = [
        API.json('POST', '/api/settings/voice', { voice }),
        API.json('POST', '/api/settings/brand', { agentName, companyName }),
        API.json('POST', '/api/settings/timezone', { timezone }),
        API.json('POST', '/api/settings/sales', {
          primaryProduct: salesPrimaryProduct,
          focusDescription: salesFocusDescription,
          targetProfile: salesTargetProfile,
          painPoints: salesPainPoints,
          callObjective: salesCallObjective,
          objectionStyle: salesObjectionStyle,
          closingStyle: salesClosingStyle
        })
      ];
      promises.push(API.json('PATCH', '/api/tenant/handoff', { handoff_number: handoffNumber || null }));
      promises.push(API.json('PATCH', '/api/tenant/settings', { industry, metrics_json: metricsSelected, record_calls: recordCalls, agent_handoff_mode: agentHandoffMode, recording_preference: recordingPreference || null }));
      await Promise.all(promises);
      try {
        const [h, ts] = await Promise.all([
          API.json('GET', '/api/tenant/handoff'),
          API.json('GET', '/api/tenant/settings')
        ]);
        if (h && h.ok) setHandoffNumber(h.handoff_number != null && h.handoff_number !== '' ? String(h.handoff_number) : '');
        if (ts && ts.ok) {
          setIndustry(ts.industry || 'sales');
          setMetricsSelected(Array.isArray(ts.metrics_json) ? ts.metrics_json : []);
          setRecordCalls(ts.record_calls === 0 ? 0 : 1);
          setAgentHandoffMode(ts.agent_handoff_mode === 'tenant_any' ? 'tenant_any' : 'assigned_only');
          setRecordingPreference(ts.recording_preference != null ? String(ts.recording_preference) : '');
        }
      } catch (e) {
        console.error(e);
      }
      setWorkspaceTimeZone(timezone);
      showToast('Settings saved.', 'success');
      refresh && refresh();
    } catch (e) {
      var msg = (e.status === 403) ? 'You don\'t have permission to change these settings.' : uiErrorMessage(e, 'Could not save settings.');
      showToast(msg, 'error');
    } finally {
      setSettingsSaving(false);
    }
  }

  return (
    <section className="card settings-card">
      <div className="card-header settings-view-header">
        <div>
          <div className="card-title">Settings</div>
          <div className="card-subtitle settings-view-subtitle">
            {superAdminNoTenantContext
              ? 'Your platform account and admin theme. Impersonate a tenant to edit workspace voice, brand, and sales defaults.'
              : 'Configure caller identity, AWS Polly voice and workspace behavior.'}
          </div>
        </div>
      </div>
      <div className="card-body settings-card-body">
      {user && !isImpersonating ? (
        <MyAccountPanel user={user} api={API.json} toast={showToast} />
      ) : null}
      {tenantId ? (
        <React.Fragment>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="settings-industry">Industry (for dashboard presets)</label>
          <select id="settings-industry" className="select" value={industry} onChange={(e) => setIndustry(e.target.value)}>
            <option value="sales">Sales</option>
            <option value="utilities">Utilities</option>
            <option value="healthcare">Healthcare</option>
            <option value="government">Government</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Dashboard metrics (KPIs to show)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {METRIC_OPTIONS.map((m) => (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={metricsSelected.includes(m.id)} onChange={(e) => {
                  setMetricsSelected((prev) => e.target.checked ? [...prev, m.id] : prev.filter((x) => x !== m.id));
                }} />
                <span>{m.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={recordCalls === 1} onChange={(e) => setRecordCalls(e.target.checked ? 1 : 0)} />
            <span>Record calls</span>
          </label>
          <div className="field-help" style={{ marginTop: 4, color: 'var(--muted)' }}>When disabled, Twilio will not record calls for this tenant. Tenant is responsible for consent requirements.</div>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="settings-handoff-mode">Agent handoff mode</label>
          <select id="settings-handoff-mode" className="select" value={agentHandoffMode} onChange={function (e) { setAgentHandoffMode(e.target.value); }}>
            <option value="assigned_only">Only assigned agent</option>
            <option value="tenant_any">Any available agent</option>
          </select>
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
            When the <strong>caller</strong> asks for a human (in the voice session): either notify only the lead’s{' '}
            <strong>assigned owner</strong> (must be online in admin for the pop-up), or broadcast to any eligible agent in the tenant. Does{' '}
            <strong>not</strong> change Live Monitor — staff “Join via Phone” always uses the logged-in user’s profile number.
          </p>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="settings-recording-pref">Recording preference (compliance / Go Live)</label>
          <select id="settings-recording-pref" className="select" value={recordingPreference} onChange={function (e) { setRecordingPreference(e.target.value); }}>
            <option value="">Not set</option>
            <option value="record_all">Record all calls</option>
            <option value="record_with_consent">Record with consent</option>
            <option value="no_recording">Do not record</option>
          </select>
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>Required for Go Live. Ensures compliance is configured.</p>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="settings-handoff-number">PSTN handoff / fallback number (voice relay)</label>
          <input
            id="settings-handoff-number"
            className="input"
            type="text"
            value={handoffNumber}
            onChange={function (e) { setHandoffNumber(e.target.value); }}
            placeholder="+15551234567"
            aria-label="PSTN handoff number"
          />
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
            Used by the <strong>voice relay</strong> when no in-app agent can be reached (e.g. nobody online / no eligible agent).
            Think front desk or backup hunt group — <strong>not</strong> used when you click “Join via Phone” in admin (that rings <strong>your</strong> profile phone).
          </p>
        </div>
        <AgentRoutingSettings api={API.json} toast={showToast} tenantId={tenantId} />
        </React.Fragment>
      ) : null}
      <div className="field" style={{ marginBottom: 12 }}>
        <label>Theme (affects all admin screens)</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            className={'button small ' + (theme === 'light' ? 'primary' : 'outline')}
            onClick={function () { setTheme('light'); applyTheme('light'); }}
          >
            Light
          </button>
          <button
            type="button"
            className={'button small ' + (theme === 'dark' ? 'primary' : 'outline')}
            onClick={function () { setTheme('dark'); applyTheme('dark'); }}
          >
            Dark
          </button>
        </div>
      </div>
      {tenantId ? (
      <React.Fragment>
      <div className="field-row">
        <div className="field">
          <label htmlFor="settings-caller-name">Caller name (you)</label>
          <input
            id="settings-caller-name"
            className="input"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="Alex"
          />
          <div className="field-help" style={{ marginTop: 4 }}>
            The name the assistant uses for itself on the call.
          </div>
          <CallerHearsLine text={String((settings && settings.agentName) || '').trim() || 'your assistant'} />
        </div>
        <div className="field">
          <label htmlFor="settings-company-name">Company name</label>
          <input
            id="settings-company-name"
            className="input"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Your company name"
          />
          <div className="field-help" style={{ marginTop: 4 }}>
            Leave this blank until you save a company name. The preview is not written back into the field.
          </div>
          <CallerHearsLine text={String((settings && settings.companyName) || '').trim() || 'your company'} />
        </div>
      </div>
      <div className="field" style={{ marginTop: 8 }}>
        <label htmlFor="settings-polly-voice">Polly neural voice</label>
        {voices.length ? (
          <select
            id="settings-polly-voice"
            className="select"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} · {v.language} · {v.gender}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="input"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            placeholder="Matthew"
          />
        )}
        <div className="field-help" style={{ marginTop: 4 }}>
          Premium AI calls speak with Marin. This Polly voice is only for the older non-premium phone line.
        </div>
      </div>
      <div className="field" style={{ marginTop: 8 }}>
        <label htmlFor="settings-timezone">Display timezone (calls)</label>
        <select
          id="settings-timezone"
          className="select"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
        >
          <option value="America/Los_Angeles">Pacific (PST/PDT)</option>
          <option value="America/Denver">Mountain</option>
          <option value="America/Chicago">Central</option>
          <option value="America/New_York">Eastern</option>
        </select>
        <div style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', marginTop: 3 }}>
          Used for displaying all call timestamps.
        </div>
      </div>
      <hr style={{ margin: '16px 0', borderColor: '#1f2933' }} />
      <div className="card-subtitle" style={{ marginBottom: 8 }}>
        What the caller hears
      </div>
      <p className="field-help" style={{ marginBottom: 8 }}>
        Opening lines are edited on the lead; when a lead is selected, the last saved line is previewed here.
      </p>
      {openLead ? (
        <CallerHearsLine text={String(openLead.script_suggestion || '').trim() || '(no opening line saved on this lead)'} />
      ) : null}

      <div className="field">
        <label htmlFor="settings-product">Primary Product or Service</label>
        <input
          id="settings-product"
          className="input"
          value={salesPrimaryProduct}
          onChange={(e) => setSalesPrimaryProduct(e.target.value)}
          placeholder="What you sell"
        />
        <CallerHearsLine text={spokenSalesLine(settings, 'primaryProduct')} />
      </div>

      <div className="field" style={{ marginTop: 8 }}>
        <label htmlFor="settings-focus">Sales Focus Description</label>
        <textarea
          id="settings-focus"
          className="input"
          rows={3}
          value={salesFocusDescription}
          onChange={(e) => setSalesFocusDescription(e.target.value)}
          placeholder="What your company builds and why it matters"
        />
        <CallerHearsLine text={spokenSalesLine(settings, 'focusDescription')} />
      </div>

      <div className="field-row" style={{ marginTop: 8 }}>
        <div className="field">
          <label htmlFor="settings-target">Target Customer Profile</label>
          <textarea
            id="settings-target"
            className="input"
            rows={3}
            value={salesTargetProfile}
            onChange={(e) => setSalesTargetProfile(e.target.value)}
            placeholder="Who you call"
          />
          <CallerHearsLine text={spokenSalesLine(settings, 'targetProfile')} />
        </div>
        <div className="field">
          <label htmlFor="settings-pain">Key Pain Points</label>
          <textarea
            id="settings-pain"
            className="input"
            rows={3}
            value={salesPainPoints}
            onChange={(e) => setSalesPainPoints(e.target.value)}
            placeholder="Problems you help with"
          />
          <CallerHearsLine text={spokenSalesLine(settings, 'painPoints')} />
        </div>
      </div>

      <div className="field-row" style={{ marginTop: 8 }}>
        <div className="field">
          <label htmlFor="settings-objective">Call Objective</label>
          <textarea
            id="settings-objective"
            className="input"
            rows={2}
            value={salesCallObjective}
            onChange={(e) => setSalesCallObjective(e.target.value)}
            placeholder="What this call should accomplish"
          />
          <CallerHearsLine text={spokenSalesLine(settings, 'callObjective')} />
        </div>
        <div className="field">
          <label htmlFor="settings-objection">Objection Handling Style</label>
          <textarea
            id="settings-objection"
            className="input"
            rows={2}
            value={salesObjectionStyle}
            onChange={(e) => setSalesObjectionStyle(e.target.value)}
            placeholder="Friendly, calm, helpful — never pushy"
          />
          <CallerHearsLine text={spokenSalesLine(settings, 'objectionStyle')} />
        </div>
      </div>

      <div className="field" style={{ marginTop: 8 }}>
        <label htmlFor="settings-closing">Closing Style / CTA</label>
        <textarea
          id="settings-closing"
          className="input"
          rows={2}
          value={salesClosingStyle}
          onChange={(e) => setSalesClosingStyle(e.target.value)}
          placeholder="Soft, collaborative next step suggestion"
        />
        <CallerHearsLine text={spokenSalesLine(settings, 'closingStyle')} />
      </div>

      <div style={{ marginTop: 8 }}>
        <button className="button primary small" onClick={save} disabled={settingsSaving}>
          {settingsSaving ? 'Saving…' : '💾 Save'}
        </button>
      </div>

      <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
      <div className="inbound-instructions-section">
        <h3 className="section-heading">Inbound AI Instructions</h3>
        <div className="field" style={{ marginBottom: 12 }}>
          <label className="field-label" htmlFor="inbound-voice-tier">Inbound voice</label>
          <select
            id="inbound-voice-tier"
            className="select"
            value={inboundVoiceTier}
            onChange={function (e) { setInboundVoiceTier(e.target.value); }}
          >
            <option value="">{'Automatic (callers hear ' + inboundVoiceLabel(inboundVoiceEffective || 'standard') + ')'}</option>
            <option value="premium">Premium voice</option>
            <option value="standard">Standard voice (lower cost)</option>
          </select>
          <div className="field-help">
            {inboundVoiceTier
              ? ('Saved choice is ' + inboundVoiceLabel(inboundVoiceTier) + '. Callers hear ' + inboundVoiceLabel(inboundVoiceEffective || inboundVoiceTier) + '.')
              : ('Automatic. Callers hear ' + inboundVoiceLabel(inboundVoiceEffective || 'standard') + '.')}
            {' '}Automatic uses Premium voice when the plan includes premium calls, and Standard voice otherwise.
          </div>
          <button type="button" className="button small" style={{ marginTop: 8 }} disabled={inboundVoiceSaving} onClick={saveInboundVoice}>
            {inboundVoiceSaving ? 'Saving…' : 'Save inbound voice'}
          </button>
        </div>
        <p className="field-help" style={{ marginBottom: 12 }}>
          Used for inbound calls routed to this profile. The caller-will-hear preview is not saved.
        </p>
        <InboundScriptEditor tenantId={tenantId} />
      </div>

      {!superAdminNoTenantContext && user && ['tenant_admin', 'supervisor', 'super_admin'].indexOf(user.role) >= 0 ? (
        <React.Fragment>
          <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
          <BusinessHoursSettings api={API.json} toast={showToast} timezone={settings.timezone || 'America/Los_Angeles'} />
          {user.role !== 'supervisor' ? (
            <React.Fragment>
              <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
              <VoicemailDropSettings api={API.json} toast={showToast} />
            </React.Fragment>
          ) : null}
          <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
          <DispositionSettings api={API.json} toast={showToast} />
          <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
          <NotificationSettings api={API.json} toast={showToast} />
          {user.role !== 'supervisor' ? (
            <React.Fragment>
              <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
              <ConsentFormSettings api={API.json} toast={showToast} />
              <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
              <DncComplianceSettings api={API.json} toast={showToast} />
              <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
              <CallerIdSetup api={API.json} toast={showToast} />
              <hr style={{ margin: '24px 0', borderColor: 'rgba(148,163,184,0.2)' }} />
              <LegalAcceptanceLog api={API.json} user={user} timezone={settings.timezone || 'America/Los_Angeles'} />
            </React.Fragment>
          ) : null}
        </React.Fragment>
      ) : null}
      </React.Fragment>
      ) : superAdminNoTenantContext ? (
        <p className="muted" style={{ fontSize: '0.875rem', lineHeight: 1.5, marginTop: 8 }}>
          Workspace settings (caller identity, Polly voice, call timezone, sales prompts, recording/handoff, inbound instructions) are stored per tenant. Open <strong>Tenants &amp; billing</strong> and use <strong>Impersonate</strong> on a tenant, then return to this page to edit them.
        </p>
      ) : null}
    </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// SOFTPHONE_DEBUG: toggleable via URL ?SOFTPHONE_DEBUG=1 or localStorage
// ---------------------------------------------------------------------
function getSOFTPHONE_DEBUG() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('SOFTPHONE_DEBUG') === '1') return true;
    return localStorage.getItem('SOFTPHONE_DEBUG') === '1';
  } catch (e) {
    return false;
  }
}

// ---------------------------------------------------------------------
// Softphone / Browser Agent join helpers
// ---------------------------------------------------------------------

function isSoftphoneDebug() {
  try {
    if (typeof URLSearchParams !== 'undefined' && window.location && window.location.search) {
      if (new URLSearchParams(window.location.search).get('SOFTPHONE_DEBUG') === '1') return true;
    }
    return (typeof localStorage !== 'undefined' && localStorage.getItem('SOFTPHONE_DEBUG') === '1');
  } catch (e) {
    return false;
  }
}

async function joinViaPhone(callSid) {
  try {
    // Same handler as POST /api/call/agent-join-phone; use agent path so nginx/routing matches popup "Phone Answer".
    const res = await API.json('POST', '/api/agent/calls/' + encodeURIComponent(callSid) + '/join-phone');
    if (isSoftphoneDebug()) {
      console.log('[joinViaPhone] response', res);
    }
    if (res && res.ok) {
      alert("Calling your profile phone number now — answer when it rings. (This is not the tenant handoff line.)");
    } else {
      const msg = JOIN_PHONE_ERRORS[res && res.error] || (res && res.error) || "Phone join failed.";
      alert(msg);
    }
  } catch (err) {
    if (err && err.status === 401) {
      alert("Session expired, please log in again.");
      return;
    }
    if (isSoftphoneDebug() && err && err.data) {
      console.log('[joinViaPhone] error response', err.data);
    }
    const msg = (err && err.data && err.data.error) ? (JOIN_PHONE_ERRORS[err.data.error] || err.data.error) : (err && err.message ? err.message : "Network error");
    alert("Phone join failed: " + msg);
  }
}

// Request relay info, but DO NOT connect or start mic yet.
// The actual WebSocket + microphone start happens only when the
// user clicks actions in the softphone UI.
async function joinViaBrowser(callSid) {
  try {
    let voiceReady = false;
    try {
      const tok = await API.json('GET', '/api/agent/voice-token');
      voiceReady = !!(tok && tok.token);
    } catch (err) {
      if (err && err.status === 401) {
        alert('Session expired, please log in again.');
        return;
      }
      voiceReady = false;
    }
    let relayUrl = null;
    let token = null;
    try {
      const res = await API.json("POST", "/api/call/agent-join-browser", { callSid });
      if (res && !res.error && res.relayUrl && res.token) {
        relayUrl = res.relayUrl;
        token = res.token;
      }
    } catch (err) {
      if (!voiceReady) {
        console.error(err);
        alert("Browser join failed: " + (err && err.message ? err.message : "Invalid server response"));
        return;
      }
    }
    if (!voiceReady && (!relayUrl || !token)) {
      alert("Browser join failed: Invalid server response");
      return;
    }
    // Overlay stays idle until Join. Listen still uses the relay websocket.
    SoftphoneController.openForCall({
      callSid: callSid,
      relayUrl: relayUrl,
      token: token,
      transport: voiceReady ? 'voice-sdk' : null
    });
  } catch (err) {
    console.error(err);
    alert("Browser join failed: " + err.message);
  }
}

// Debug stats for SOFTPHONE_DEBUG (rate-limited by caller)
var softphoneDebugStats = { micRms: 0, outgoingLevel: 0, jitterFillMs: 0, underrunCount: 0 };

// Start microphone capture and stream audio frames over WebSocket.
// Mic is only started from an explicit user action (Unmute Mic) after
// human_takeover has been sent.
async function startMicStream(ws, audioContext, isMutedFn) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1
    }
  });

  let ctx = audioContext;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) {
      throw new Error('Web Audio API not supported in this browser');
    }
    try {
      ctx = new AC({ sampleRate: 8000 });
    } catch (e) {
      ctx = new AC();
    }
  }

  if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
    try {
      await ctx.resume();
    } catch (e) {
      console.warn('AudioContext resume failed', e);
    }
  }

  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(1024, 1, 1);

  source.connect(processor);

  // ScriptProcessor must be connected in the graph to run.
  // We connect it to a muted sink to avoid local echo.
  const _txSink = ctx.createGain();
  _txSink.gain.value = 0;
  processor.connect(_txSink);
  _txSink.connect(ctx.destination);

  // Target telephony sample rate (G.711 μ-law expects 8 kHz)
  const TARGET_SAMPLE_RATE = 8000;
  const inputSampleRate = ctx.sampleRate || TARGET_SAMPLE_RATE;
  const sampleRatio = inputSampleRate / TARGET_SAMPLE_RATE;

  // Carry-over for simple but stable linear downsampling
  let prevInputTail = null; // Float32Array of length 1
  // Buffer of 8 kHz mono samples waiting to be framed
  let pending8k = [];

  function downsampleTo8k(input) {
    if (!input || input.length === 0) return new Float32Array(0);

    // Prepend tail from previous buffer (for linear interpolation continuity)
    let full;
    if (prevInputTail && prevInputTail.length) {
      full = new Float32Array(prevInputTail.length + input.length);
      full.set(prevInputTail, 0);
      full.set(input, prevInputTail.length);
    } else {
      full = input;
    }

    const inLen = full.length;
    if (inLen === 0) {
      prevInputTail = null;
      return new Float32Array(0);
    }

    // Keep last sample as tail for next block
    prevInputTail = full.subarray(inLen - 1);

    if (inputSampleRate === TARGET_SAMPLE_RATE) {
      // No resampling needed; drop the duplicated tail sample
      return full.subarray(0, inLen - 1);
    }

    const outLen = Math.max(0, Math.floor((inLen - 1) / sampleRatio));
    const output = new Float32Array(outLen);

    for (let i = 0; i < outLen; i++) {
      const index = i * sampleRatio;
      const idx0 = Math.floor(index);
      const idx1 = idx0 + 1;
      const frac = index - idx0;
      const s0 = full[idx0];
      const s1 = full[idx1] != null ? full[idx1] : full[idx0];
      output[i] = s0 + (s1 - s0) * frac;
    }

    return output;
  }

  // Limiter: speech peaks near full scale without clipping (replaces fixed 1.2 gain).
  // Gentle compression above threshold, hard-clip protection. Single gain stage.
  function applyLimiter(samples) {
    const threshold = 0.85;
    const out = new Float32Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      let s = samples[i];
      if (Math.abs(s) <= threshold) {
        out[i] = s;
      } else {
        const excess = Math.abs(s) - threshold;
        const compressed = threshold + (1 - threshold) * Math.tanh(excess / (1 - threshold));
        out[i] = Math.sign(s) * compressed;
      }
      if (out[i] > 1) out[i] = 1;
      if (out[i] < -1) out[i] = -1;
    }
    return out;
  }

  processor.onaudioprocess = function (e) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (typeof isMutedFn === 'function' && isMutedFn()) return;

    const input = e.inputBuffer.getChannelData(0);
    const ds = downsampleTo8k(input);
    if (!ds.length) return;

    // SOFTPHONE_DEBUG: compute mic RMS (short-term)
    if (getSOFTPHONE_DEBUG() && ds.length > 0) {
      let sumSq = 0;
      for (let i = 0; i < ds.length; i++) sumSq += ds[i] * ds[i];
      softphoneDebugStats.micRms = Math.sqrt(sumSq / ds.length);
    }

    // Append to pending 8 kHz buffer
    for (let i = 0; i < ds.length; i++) {
      pending8k.push(ds[i]);
    }

    // 20 ms frames @ 8 kHz => 160 samples per frame
    const FRAME_SAMPLES = 160;

    while (pending8k.length >= FRAME_SAMPLES) {
      const frameSamples = pending8k.slice(0, FRAME_SAMPLES);
      pending8k = pending8k.slice(FRAME_SAMPLES);

      const limited = applyLimiter(frameSamples);

      // SOFTPHONE_DEBUG: outgoing level after limiter
      if (getSOFTPHONE_DEBUG() && limited.length > 0) {
        let sumSq = 0;
        for (let i = 0; i < limited.length; i++) sumSq += limited[i] * limited[i];
        softphoneDebugStats.outgoingLevel = Math.sqrt(sumSq / limited.length);
      }

      const ulawFrame = floatToUlaw(limited);
      if (ulawFrame && ulawFrame.length === FRAME_SAMPLES) {
        try {
          // Send raw binary μ-law frame over WebSocket
          ws.send(ulawFrame);
        } catch (err) {
          console.error('WS send error', err);
          // On send failure, stop processing further frames this tick
          break;
        }
      }
    }
  };

  return {
    stream: stream,
    audioContext: ctx,
    source: source,
    processor: processor
  };
}

// Proper G.711 μ-law encoder
// - Input: Array-like of Float32 samples in [-1.0, 1.0] at 8 kHz
// - Output: Uint8Array μ-law bytes
function floatToUlaw(samples) {
  const len = samples.length || 0;
  const out = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    // Clamp to [-1, 1]
    let s = samples[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;

    // Convert to 16-bit linear PCM
    let pcm = s * 32768;
    if (pcm > 32767) pcm = 32767;
    else if (pcm < -32768) pcm = -32768;
    out[i] = linearPCM16ToMuLaw(pcm | 0);
  }

  return out;
}

// Helper: 16-bit linear PCM to μ-law byte (8-bit), ITU-T G.711
function linearPCM16ToMuLaw(sample) {
  const BIAS = 0x84;
  const CLIP = 32635;

  let sign = (sample < 0) ? 0x80 : 0x00;
  if (sample < 0) {
    sample = -sample;
    if (sample < 0) sample = 0; // handle -32768 safely
  }

  if (sample > CLIP) {
    sample = CLIP;
  }

  sample = sample + BIAS;

  // Determine segment (exponent)
  let segment = 0;
  let temp = sample >> 7; // 9-bit value
  while (temp) {
    segment++;
    temp >>= 1;
  }

  // Mantissa is the 4 bits after the segment bit
  const mantissa = (sample >> (segment + 3)) & 0x0F;

  // Assemble sign, segment, and mantissa and then invert
  const ulawByte = ~(sign | (segment << 4) | mantissa) & 0xFF;
  return ulawByte;
}

// Decode 8‑bit μ‑law bytes to a Float32Array in [-1, 1] for playback
// Decode 8‑bit G.711 μ‑law bytes to Float32Array in [-1, 1] (standard)
function ulawToFloat32(ulawBytes) {
  const len = ulawBytes.length;
  const pcm = new Float32Array(len);

  // G.711 μ-law constants
  const BIAS = 0x84; // 132
  for (let i = 0; i < len; i++) {
    let u = ulawBytes[i] ^ 0xFF;

    const sign = u & 0x80;
    const exponent = (u >> 4) & 0x07;
    const mantissa = u & 0x0F;

    // Reconstruct magnitude (see ITU-T G.711)
    let magnitude = ((mantissa << 3) + BIAS) << exponent;
    magnitude -= BIAS;

    let sample16 = sign ? -magnitude : magnitude;

    // Clamp and scale to [-1, 1]
    if (sample16 > 32767) sample16 = 32767;
    if (sample16 < -32768) sample16 = -32768;
    pcm[i] = sample16 / 32768;
  }

  return pcm;
}

// Linear resampler (8k → AudioContext rate). For PSTN band-limited speech,
// linear interpolation is sufficient and stable.
function resampleLinear(input, inRate, outRate) {
  if (inRate === outRate) return input;

  const ratio = outRate / inRate;
  const outLen = Math.max(1, Math.round(input.length * ratio));
  const out = new Float32Array(outLen);

  const step = inRate / outRate; // input samples per output sample
  let pos = 0;

  for (let i = 0; i < outLen; i++) {
    const idx = Math.floor(pos);
    const frac = pos - idx;

    const s0 = input[idx] || 0;
    const s1 = input[idx + 1] || s0;

    out[i] = s0 + (s1 - s0) * frac;
    pos += step;
  }

  return out;
}

import { createVoiceDevice } from './voice-device.js';

// ---------------------------------------------------------------------
// SoftphoneController: central browser softphone state + media control
// ---------------------------------------------------------------------
var SoftphoneController = (function () {
  var state = {
    active: false,
    callSid: null,
    relayUrl: null,
    token: null,
    connectionState: 'disconnected', // 'disconnected' | 'connecting' | 'connected'
    // Microphone starts muted / disabled. Agent must explicitly unmute.
    isMuted: true,          // mic mute flag used by startMicStream
    isListening: false,     // listen-only mode (remote audio via WebSocket)
    micEnabled: false,      // whether mic stream has been initialized
    speakerMuted: false,    // UI flag for speaker mute
    volume: 1,              // playback gain (remote audio only)
    // Whether we've sent human_takeover and have agent control
    isJoined: false,
    // Live transcript accumulated from relay text messages
    transcript: [],
    // 'voice-sdk' uses Twilio WebRTC; 'websocket' is the relay μ-law path. Listen stays on websocket.
    transport: null,
    voice: null
  };

  var internals = {
    ws: null,
    audioContext: null,
    micStream: null,
    micSource: null,
    micProcessor: null,
    remoteGain: null,
    // Remember last non-zero volume so "Mute Speaker" can restore it
    lastVolume: 1,
    // Track how many remote audio frames we've played for debug logging
    remoteFrames: 0,
    // Mode requested while WebSocket is still connecting
    pendingListenOnOpen: false,
    pendingJoinOnOpen: false,
    softphoneDebugInterval: null,
    voiceDevice: null,
    voiceUnsub: null,
    voiceJoinInFlight: false
  };

  var listeners = [];

  function notify() {
    listeners.forEach(function (fn) {
      try {
        fn(state);
      } catch (e) {
        console.error(e);
      }
    });
  }

  function setState(patch) {
    state = Object.assign({}, state, patch);
    notify();
  }

  function getState() {
    return state;
  }

  function subscribe(fn) {
    if (listeners.indexOf(fn) === -1) {
      listeners.push(fn);
    }
    return function unsubscribe() {
      var idx = listeners.indexOf(fn);
      if (idx !== -1) {
        listeners.splice(idx, 1);
      }
    };
  }

  // Stop only microphone-related resources (keep speaker pipeline alive)
  function cleanupMic() {
    if (internals.softphoneDebugInterval) {
      clearInterval(internals.softphoneDebugInterval);
      internals.softphoneDebugInterval = null;
    }
    try {
      if (internals.micProcessor) {
        internals.micProcessor.disconnect();
        internals.micProcessor.onaudioprocess = null;
      }
    } catch (e) {}
    try {
      if (internals.micSource) {
        internals.micSource.disconnect();
      }
    } catch (e) {}
    if (internals.micStream) {
      try {
        internals.micStream.getTracks().forEach(function (t) {
          t.stop();
        });
      } catch (e) {}
    }
    internals.micProcessor = null;
    internals.micSource = null;
    internals.micStream = null;
  }

  // Stop microphone and speaker pipelines, and close AudioContext
  function cleanupAllMedia() {
    cleanupMic();
    if (internals.remoteGain) {
      try {
        internals.remoteGain.disconnect();
      } catch (e) {}
    }
    if (internals.audioContext) {
      try {
        internals.audioContext.close();
      } catch (e) {}
    }
    internals.remoteGain = null;
    internals.audioContext = null;
  }

  function sendControlMessage(payload) {
    if (!internals.ws || internals.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      internals.ws.send(JSON.stringify(payload));
      if (payload && payload.type && payload.callSid) {
        console.log('[Softphone] Sent ' + payload.type + ' for call ' + payload.callSid);
      }
      return true;
    } catch (e) {
      console.error('[Softphone] Failed to send ' + (payload && payload.type) + ' control frame', e);
      return false;
    }
  }

  function sendListenStart() {
    if (!state.callSid) return;
    if (state.isListening) return;
    var ok = sendControlMessage({ type: 'listen_start', callSid: state.callSid });
    if (ok) {
      setState({ isListening: true });
    }
  }

  function sendListenStop() {
    if (!state.callSid) return;
    if (!state.isListening) return;
    var ok = sendControlMessage({ type: 'listen_stop', callSid: state.callSid });
    if (ok) {
      setState({ isListening: false });
    }
  }

  function sendHumanTakeover() {
    if (!state.callSid) return;
    if (state.isJoined) return;
    var ok = sendControlMessage({ type: 'human_takeover', callSid: state.callSid });
    if (ok) {
      setState({ isJoined: true });
    }
  }

  function sendAgentDisconnect() {
    if (!state.callSid) return;
    if (!state.isJoined) return;
    var ok = sendControlMessage({ type: 'agent_disconnect', callSid: state.callSid });
    if (ok) {
      setState({ isJoined: false });
    }
  }

  function closeWs() {
    if (internals.ws) {
      try {
        internals.ws.onopen = null;
        internals.ws.onclose = null;
        internals.ws.onerror = null;
        internals.ws.onmessage = null;
        if (
          internals.ws.readyState === WebSocket.OPEN ||
          internals.ws.readyState === WebSocket.CONNECTING
        ) {
          internals.ws.close();
        }
      } catch (e) {}
    }
    internals.ws = null;
    internals.pendingListenOnOpen = false;
    internals.pendingJoinOnOpen = false;
  }

  // Called from joinViaBrowser after we receive relay credentials.
  function openForCall(opts) {
    closeWs();
    cleanupAllMedia();
    if (internals.voiceDevice) {
      try { internals.voiceDevice.hangup(); } catch (e) {}
    }
    internals.remoteFrames = 0;
    setState({
      active: true,
      callSid: opts.callSid || null,
      relayUrl: opts.relayUrl || null,
      token: opts.token || null,
      connectionState: 'disconnected',
      isMuted: true,
      isListening: false,
      micEnabled: false,
      speakerMuted: false,
      volume: 1,
      isJoined: false,
      transcript: [],
      transport: opts.transport || null,
      reason: opts.reason || null,
      voice: internals.voiceDevice ? internals.voiceDevice.getState() : null
    });
  }

  function ensureAudioContext() {
    if (internals.audioContext) return internals.audioContext;

    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) {
      console.error('Web Audio API unsupported');
      return null;
    }
    var ctx;
    try {
      ctx = new AC({ sampleRate: 8000 });
    } catch (e) {
      ctx = new AC();
    }

    internals.audioContext = ctx;

    if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
      ctx.resume().catch(function (err) {
        console.warn('AudioContext resume failed', err);
      });
    }

    internals.remoteGain = ctx.createGain();
    internals.remoteGain.gain.value = typeof state.volume === 'number' ? state.volume : 1;
    internals.remoteGain.connect(ctx.destination);

    return ctx;
  }

  // -------------------------------------------------------------
  // Remote (phone → browser) audio playout
  //
  // Industry standard approach: jitter buffer + continuous playout
  // via AudioWorklet (Web Audio API). Starting a new AudioBufferSource
  // per 20ms chunk is prone to jitter/underruns and sounds "robotic".
  // -------------------------------------------------------------

  async function ensureRemotePlayer(ctx) {
    if (internals.remotePlayerNode || internals.remotePlayerFallback) return;

    try {
      const url = new URL('./softphone-worklet.js', window.location.href).toString();
      await ctx.audioWorklet.addModule(url);
      internals.remotePlayerNode = new AudioWorkletNode(ctx, 'softphone-player', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1]
      });
      internals.remotePlayerNode.connect(internals.remoteGain);
      if (getSOFTPHONE_DEBUG()) {
        internals.remotePlayerNode.port.postMessage({ type: 'setDebug', enabled: true });
        internals.remotePlayerNode.port.onmessage = function (e) {
          const d = e.data;
          if (d && d.type === 'stats') {
            softphoneDebugStats.jitterFillMs = d.fillMs != null ? d.fillMs : 0;
            softphoneDebugStats.underrunCount = d.underrunCount != null ? d.underrunCount : 0;
          }
        };
      }
    } catch (e) {
      console.warn('[Softphone] Remote player worklet unavailable; using legacy playout', e);
      internals.remotePlayerFallback = true;
    }
  }

  function pushRemotePcmFrame(pcm8k) {
    const ctx = internals.audioContext;
    if (!ctx || !pcm8k || !pcm8k.length) return;

    // Start loading the worklet on first inbound audio (fire-and-forget).
    if (!internals.remotePlayerNode && !internals.remotePlayerFallback && !internals.remotePlayerLoading) {
      internals.remotePlayerLoading = true;
      ensureRemotePlayer(ctx).finally(() => {
        internals.remotePlayerLoading = false;
      });
    }

    if (internals.remotePlayerNode && internals.remotePlayerNode.port) {
      // Resample to AudioContext sampleRate (typically 48k) before queueing.
      const pcm = resampleLinear(pcm8k, 8000, ctx.sampleRate);
      internals.remotePlayerNode.port.postMessage({ type: 'push', pcm });
      return;
    }

    // Legacy fallback: per-chunk AudioBufferSource (works, but lower quality).
    try {
      const buffer = ctx.createBuffer(1, pcm8k.length, 8000);
      buffer.getChannelData(0).set(pcm8k);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(internals.remoteGain);
      source.start();
    } catch (e) {
      console.error('[Softphone] Remote audio fallback playback failed', e);
    }
  }


  function appendTranscriptLine(line) {
    var role = line.role || line.speaker || 'unknown';
    var content = line.content || line.text || '';
    if (!content) return;

    var entry = {
      id:
        line.id ||
        String((state.transcript && state.transcript.length) || 0) +
          '_' +
          String(Date.now()),
      role: role,
      content: content,
      timestamp: line.timestamp || line.ts || Date.now()
    };

    setState({ transcript: (state.transcript || []).concat([entry]) });
  }

  function handleRemoteAudio(arrayBuffer) {
    if (!arrayBuffer || !internals.audioContext || !internals.remoteGain) {
      return;
    }
    try {
      internals.remoteFrames = (internals.remoteFrames || 0) + 1;
      if (internals.remoteFrames % 200 === 0) {
        console.log('[Softphone] Received remote audio frames:', internals.remoteFrames);
      }

      var bytes = new Uint8Array(arrayBuffer);
      if (!bytes.length) return;

      var pcm8k = ulawToFloat32(bytes);
      pushRemotePcmFrame(pcm8k);
    } catch (e) {
      console.error('Remote audio decode error', e);
    }
  }


  function applySdkSpeaker(volume, speakerMuted) {
    if (state.transport !== 'voice-sdk' || typeof document === 'undefined') return;
    var nodes = document.querySelectorAll('audio');
    var muted = !!speakerMuted || volume === 0;
    var level = volume > 1 ? 1 : (volume < 0 ? 0 : volume);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].srcObject) continue;
      nodes[i].muted = muted;
      if (!muted) nodes[i].volume = level;
    }
  }

  function applyVoiceState(voiceState) {
    var patch = { voice: voiceState };
    if (state.transport === 'voice-sdk') {
      if (voiceState.status === 'registering' || voiceState.status === 'connecting') {
        patch.connectionState = 'connecting';
      } else if (voiceState.status === 'connected') {
        patch.connectionState = 'connected';
        patch.isJoined = true;
        patch.micEnabled = true;
        patch.isMuted = !!voiceState.muted;
      } else if (voiceState.status === 'ended') {
        patch.connectionState = 'disconnected';
        patch.isJoined = false;
        patch.micEnabled = false;
        patch.isMuted = true;
        patch.isListening = false;
      } else if (voiceState.status === 'error') {
        patch.connectionState = 'disconnected';
        patch.isJoined = false;
      } else if (state.isJoined) {
        patch.isMuted = !!voiceState.muted;
      }
    }
    setState(patch);
  }

  function ensureVoiceDevice(initialToken) {
    if (internals.voiceDevice) return internals.voiceDevice;
    var cached = initialToken || null;
    internals.voiceDevice = createVoiceDevice({
      fetchToken: async function () {
        if (cached) {
          var first = cached;
          cached = null;
          return first;
        }
        return API.json('GET', '/api/agent/voice-token');
      }
    });
    internals.voiceUnsub = internals.voiceDevice.subscribe(applyVoiceState);
    return internals.voiceDevice;
  }

  function destroyVoiceDevice() {
    var device = internals.voiceDevice;
    internals.voiceDevice = null;
    if (internals.voiceUnsub) {
      try { internals.voiceUnsub(); } catch (e) {}
      internals.voiceUnsub = null;
    }
    if (device) {
      try { device.destroy(); } catch (e) {}
    }
  }

  function voiceJoinMessage(err) {
    var data = err && err.data;
    var code = data && data.error;
    if (code === 'plan_feature_required') return 'Live transfer is not included in this plan.';
    if (code === 'tenant_suspended') return 'This workspace is suspended.';
    if (code && JOIN_PHONE_ERRORS[code]) return JOIN_PHONE_ERRORS[code];
    if (data && typeof data.detail === 'string' && data.detail) return data.detail;
    if (code) return String(code);
    return (err && err.message) || 'Browser join failed';
  }

  async function startVoiceSdkJoin(initialToken) {
    if (!state.callSid) {
      alert('Softphone not ready for this call.');
      return false;
    }
    if (internals.voiceJoinInFlight) return true;
    internals.voiceJoinInFlight = true;
    try {
      setState({ transport: 'voice-sdk', connectionState: 'connecting', isMuted: true });
      var device = ensureVoiceDevice(initialToken);
      try {
        await device.register();
      } catch (err) {
        console.error('[Softphone] voice device register failed', err);
        setState({ transport: null, connectionState: 'disconnected' });
        return false;
      }
      device.expectCall(state.callSid);
      try {
        var joinRes = await API.json('POST', '/api/agent/calls/' + encodeURIComponent(state.callSid) + '/join-browser');
        if (!joinRes || joinRes.ok === false) {
          throw new Error((joinRes && joinRes.error) || 'join_browser_failed');
        }
        return true;
      } catch (err) {
        console.error('[Softphone] join-browser failed', err);
        try { device.hangup(); } catch (e) {}
        alert('Browser join failed: ' + voiceJoinMessage(err));
        setState({ connectionState: 'disconnected', isJoined: false });
        return true;
      }
    } finally {
      internals.voiceJoinInFlight = false;
    }
  }

  async function acceptInBrowser(callSid, extra) {
    var tok = null;
    try {
      tok = await API.json('GET', '/api/agent/voice-token');
    } catch (err) {
      if (err && err.status === 401) {
        alert('Session expired, please log in again.');
        return true;
      }
      return false;
    }
    if (!tok || !tok.token) return false;
    openForCall({ callSid: callSid, transport: 'voice-sdk', reason: extra && extra.reason });
    var started = await startVoiceSdkJoin(tok);
    if (started === false) {
      close();
      return false;
    }
    return true;
  }

  // Human click-to-call: the server rings this browser first, then dials the lead once we answer.
  async function callLeadInBrowser(leadId, extra) {
    var tok = null;
    try {
      tok = await API.json('GET', '/api/agent/voice-token');
    } catch (err) {
      return { ok: false, error: err };
    }
    if (!tok || !tok.token) return { ok: false, error: { data: { error: 'voice_sdk_not_configured' } } };
    var pendingId = 'c2c-' + leadId + '-' + Date.now();
    openForCall({ callSid: pendingId, transport: 'voice-sdk', reason: extra && extra.reason });
    setState({ connectionState: 'connecting', isMuted: false });
    var device = ensureVoiceDevice(tok);
    try {
      await device.register();
    } catch (err) {
      close();
      return { ok: false, error: err };
    }
    device.expectCall(pendingId);
    device.setMuted(false);
    try {
      var placed = await API.json('POST', '/api/agent/leads/' + encodeURIComponent(leadId) + '/call-browser');
      if (placed && placed.agentCallSid) setState({ callSid: placed.agentCallSid });
      return { ok: true, to: placed && placed.to };
    } catch (err) {
      try { device.hangup(); } catch (e) {}
      close();
      return { ok: false, error: err };
    }
  }

  // joinOptions: { listenOnly?: boolean }
  // Listen-only stays on the relay websocket. A full join uses the Voice SDK when a token is available.
  async function join(joinOptions) {
    var listenOnly = !!(joinOptions && joinOptions.listenOnly);
    if (!state.callSid) {
      alert('Softphone not ready for this call.');
      return;
    }
    if (listenOnly && state.transport === 'voice-sdk' && state.isJoined) return;
    if (!listenOnly) {
      if (state.transport === 'voice-sdk' && (state.connectionState === 'connecting' || state.isJoined)) return;
      var tok = null;
      try {
        tok = await API.json('GET', '/api/agent/voice-token');
      } catch (err) {
        if (err && err.status === 401) {
          alert('Session expired, please log in again.');
          return;
        }
        tok = null;
      }
      if (tok && tok.token) {
        closeWs();
        cleanupMic();
        var started = await startVoiceSdkJoin(tok);
        if (started !== false) return;
      }
    }
    var s = state;
    if (!s.callSid || !s.relayUrl || !s.token) {
      alert('Softphone not ready for this call.');
      return;
    }
    var existing = internals.ws;

    // Reuse existing open connection (single WebSocket per active callSid)
    if (existing && existing.readyState === WebSocket.OPEN) {
      var ctxExisting = ensureAudioContext();
      if (!ctxExisting) {
        alert('Your browser does not support the audio APIs required for the softphone.');
        return;
      }
      setState({ connectionState: 'connected' });

      if (listenOnly) {
        sendListenStart();
      } else {
        sendHumanTakeover();
      }
      return;
    }

    // If we're already connecting, just record the desired mode and let onopen handle it
    if (existing && existing.readyState === WebSocket.CONNECTING) {
      internals.pendingListenOnOpen = internals.pendingListenOnOpen || listenOnly;
      internals.pendingJoinOnOpen = internals.pendingJoinOnOpen || !listenOnly;
      return;
    }

    var ctx = ensureAudioContext();
    if (!ctx) {
      alert('Your browser does not support the audio APIs required for the softphone.');
      return;
    }

    var wsUrl =
      s.relayUrl +
      '?token=' + encodeURIComponent(s.token) +
      '&callSid=' + encodeURIComponent(s.callSid) +
      '&role=browser_agent';

    setState({ connectionState: 'connecting', transport: 'websocket' });
    internals.pendingListenOnOpen = listenOnly;
    internals.pendingJoinOnOpen = !listenOnly;

    try {
      var ws = new WebSocket(wsUrl);
      internals.ws = ws;
      ws.binaryType = 'arraybuffer';

      ws.onopen = function () {
        console.log('[Softphone] WebSocket connected');
        setState({ connectionState: 'connected' });

        if (internals.pendingListenOnOpen) {
          sendListenStart();
        }
        if (internals.pendingJoinOnOpen) {
          sendHumanTakeover();
        }
        internals.pendingListenOnOpen = false;
        internals.pendingJoinOnOpen = false;
      };

      ws.onmessage = function (event) {
        if (!event) return;
        var data = event.data;

        // Binary audio: ArrayBuffer or Blob
        if (data instanceof ArrayBuffer) {
          handleRemoteAudio(data);
          return;
        }

        if (typeof Blob !== 'undefined' && data instanceof Blob) {
          data.arrayBuffer()
            .then(function (buf) {
              handleRemoteAudio(buf);
            })
            .catch(function (err) {
              console.error('Blob to ArrayBuffer failed', err);
            });
          return;
        }

        if (data && data.buffer instanceof ArrayBuffer) {
          handleRemoteAudio(data.buffer);
          return;
        }

        // Optional: text/control messages (e.g. live transcript)
        if (typeof data === 'string') {
          try {
            var msg = JSON.parse(data);
            if (msg && (msg.type === 'transcript' || msg.role || msg.content || msg.text)) {
              appendTranscriptLine(msg);
            }
          } catch (e) {
            // Non-JSON control text; ignore safely.
          }
        }
      };

      ws.onerror = function (err) {
        console.error('[Softphone] WebSocket error', err);
      };

      ws.onclose = function () {
        console.log('[Softphone] WebSocket closed');
        cleanupAllMedia();
        internals.ws = null;
        internals.remoteFrames = 0;
        internals.pendingListenOnOpen = false;
        internals.pendingJoinOnOpen = false;
        setState({
          connectionState: 'disconnected',
          micEnabled: false,
          isMuted: true,
          isListening: false,
          isJoined: false
        });
      };
    } catch (err) {
      console.error('Softphone join error', err);
      setState({ connectionState: 'disconnected', isListening: false, isJoined: false });
      alert('Failed to connect softphone: ' + err.message);
    }
  }

  // LEAVE (stop talking)
  // - Stop mic capture
  // - Send agent_disconnect if we were in takeover
  // - Optionally keep WebSocket open (for future Listen/Join)
  function leave() {
    if (state.transport === 'voice-sdk' && internals.voiceDevice) {
      internals.voiceDevice.hangup();
      cleanupMic();
      setState({
        isJoined: false,
        isMuted: true,
        micEnabled: false,
        isListening: false,
        connectionState: 'disconnected'
      });
      return;
    }
    // If we're currently in takeover mode, restore AI
    if (state.isJoined) {
      // Stop microphone capture
      cleanupMic();
      setState({
        micEnabled: false,
        isMuted: true
      });

      if (internals.ws && internals.ws.readyState === WebSocket.OPEN && state.callSid) {
        sendAgentDisconnect();
        // If we were also actively listening, stop that as well
        if (state.isListening) {
          sendListenStop();
        }
      }

      setState({
        isJoined: false,
        isListening: false
      });
      return;
    }

    // If we were only listening, this acts as STOP LISTEN (but keeps overlay)
    if (state.isListening) {
      if (internals.ws && internals.ws.readyState === WebSocket.OPEN && state.callSid) {
        sendListenStop();
      }
      closeWs();
      setState({
        connectionState: 'disconnected',
        micEnabled: false,
        isMuted: true,
        isListening: false,
        isJoined: false
      });
      return;
    }

    // Nothing to do: not joined, not listening.
  }

  async function setMuted(nextMuted) {
    nextMuted = !!nextMuted;

    if (state.transport === 'voice-sdk' && internals.voiceDevice) {
      internals.voiceDevice.setMuted(nextMuted);
      setState({ isMuted: nextMuted });
      return;
    }

    // If unmuting and mic is not yet initialized, start the mic stream now.
    if (!nextMuted && !state.micEnabled) {
      if (!internals.ws || internals.ws.readyState !== WebSocket.OPEN) {
        alert('Connect to the call before enabling your microphone.');
        return;
      }
      if (!state.isJoined) {
        alert('Click Join before enabling your microphone.');
        return;
      }
      var ctx = ensureAudioContext();
      if (!ctx) {
        alert('Your browser does not support audio capture.');
        return;
      }
      try {
        var mic = await startMicStream(internals.ws, ctx, function () {
          return state.isMuted;
        });
        internals.micStream = mic.stream;
        internals.micSource = mic.source;
        internals.micProcessor = mic.processor;
        setState({ micEnabled: true });
        if (getSOFTPHONE_DEBUG()) {
          internals.softphoneDebugInterval = setInterval(function () {
            var ctx = internals.audioContext;
            var sr = ctx && ctx.sampleRate ? ctx.sampleRate : 0;
            console.log(
              '[Softphone] SOFTPHONE_DEBUG callSid=%s jitter_fill_ms=%.1f underruns=%d mic_rms=%.6f outgoing_level=%.6f sampleRate=%d',
              state.callSid || '(none)',
              softphoneDebugStats.jitterFillMs,
              softphoneDebugStats.underrunCount,
              softphoneDebugStats.micRms,
              softphoneDebugStats.outgoingLevel,
              sr
            );
          }, 1000);
        }
      } catch (err) {
        console.error('Mic start failed', err);
        alert('Could not access microphone: ' + err.message);
        return;
      }
    }

    setState({ isMuted: nextMuted });
  }

  function setVolume(volume) {
    var v = volume;
    if (isNaN(v)) v = 1;
    if (v < 0) v = 0;
    if (v > 2) v = 2;
    if (v > 0) {
      internals.lastVolume = v;
    }
    if (internals.remoteGain) {
      try {
        internals.remoteGain.gain.value = v;
      } catch (e) {
        console.error('Failed to adjust volume', e);
      }
    }
    applySdkSpeaker(v, state.speakerMuted || v === 0);
    setState({ volume: v });
  }

  function toggleSpeakerMute() {
    if (!state.speakerMuted) {
      var currentVol = typeof state.volume === 'number' ? state.volume : 1;
      if (currentVol > 0) {
        internals.lastVolume = currentVol;
      }
      setVolume(0);
      setState({ speakerMuted: true });
      applySdkSpeaker(0, true);
    } else {
      var restore = internals.lastVolume || 1;
      setVolume(restore);
      setState({ speakerMuted: false });
      applySdkSpeaker(state.volume, false);
    }
  }

  function close() {
    // Full teardown + hide panel
    if (internals.ws && internals.ws.readyState === WebSocket.OPEN && state.callSid) {
      if (state.isJoined) {
        sendAgentDisconnect();
      }
      if (state.isListening) {
        sendListenStop();
      }
    }
    destroyVoiceDevice();
    cleanupAllMedia();
    closeWs();
    internals.remoteFrames = 0;
    setState({
      active: false,
      callSid: null,
      relayUrl: null,
      token: null,
      connectionState: 'disconnected',
      isMuted: true,
      isListening: false,
      micEnabled: false,
      speakerMuted: false,
      volume: 1,
      isJoined: false,
      transcript: [],
      transport: null,
      reason: null,
      voice: null
    });
  }

  return {
    getState: getState,
    setState: setState,
    subscribe: subscribe,
    openForCall: openForCall,
    join: join,
    leave: leave,
    setMuted: setMuted,
    setVolume: setVolume,
    toggleSpeakerMute: toggleSpeakerMute,
    close: close,
    acceptInBrowser: acceptInBrowser,
    callLeadInBrowser: callLeadInBrowser,
    sendDigits: function (digits) {
      if (internals.voiceDevice) internals.voiceDevice.sendDigits(digits);
    },
    listAudioDevices: function () {
      return internals.voiceDevice
        ? internals.voiceDevice.listDevices()
        : { inputs: [], outputs: [], inputId: null, outputId: null };
    },
    setInputDevice: function (id) {
      if (!internals.voiceDevice) return Promise.resolve();
      return internals.voiceDevice.setInputDevice(id);
    },
    setOutputDevice: function (id) {
      if (!internals.voiceDevice) return Promise.resolve();
      return internals.voiceDevice.setOutputDevice(id);
    }
  };
})();

// ---------------------------------------------------------------------
// Floating softphone dock (presentational panel in softphone-panel.js)
// ---------------------------------------------------------------------
function softphoneTranscriptRole(role) {
  if (role === 'agent') return 'agent';
  if (role === 'ai' || role === 'bot' || role === 'assistant') return 'assistant';
  return 'user';
}

function SoftphoneOverlay({ user, timezone }) {
  const [softphoneState, setSoftphoneState] = useState(SoftphoneController.getState());
  const [minimized, setMinimized] = useState(false);
  const [callInfo, setCallInfo] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [everConnected, setEverConnected] = useState(false);

  useEffect(() => SoftphoneController.subscribe(setSoftphoneState), []);

  const callSid = softphoneState.active ? softphoneState.callSid : null;
  useEffect(() => {
    setCallInfo(null);
    setStartedAt(null);
    setEverConnected(false);
    setMinimized(false);
    if (!callSid) return undefined;
    let cancelled = false;
    API.json('GET', '/api/calls/' + encodeURIComponent(callSid))
      .then(function (res) {
        if (!cancelled && res && res.call) setCallInfo(res.call);
      })
      .catch(function () { /* caller details are optional */ });
    return function () { cancelled = true; };
  }, [callSid]);

  const connected = softphoneState.connectionState === 'connected';
  useEffect(() => {
    if (!connected) return;
    setEverConnected(true);
    setStartedAt(function (t) { return t || Date.now(); });
  }, [connected]);

  if (!callSid) return null;

  const voice = softphoneState.voice || null;
  const sdk = softphoneState.transport === 'voice-sdk';
  const joined = connected && softphoneState.isJoined;
  const listenOnly = connected && softphoneState.isListening && !softphoneState.isJoined;
  const canListen = !!(softphoneState.relayUrl && softphoneState.token);
  const canJoin = sdk || canListen;
  const voiceError = sdk && voice && voice.status === 'error' ? (voice.error || 'Browser audio failed.') : '';

  let status = 'incoming';
  if (voiceError) status = 'error';
  else if (softphoneState.connectionState === 'connecting') status = 'connecting';
  else if (connected) status = 'connected';
  else if (everConnected) status = 'ended';

  const joinCall = function () { SoftphoneController.join({ listenOnly: false }); };
  const lead = callInfo && callInfo.lead ? callInfo.lead : null;
  const partyPhone = (lead && lead.phone) || (callInfo && (callInfo.direction === 'inbound' ? callInfo.from_e164 : (callInfo.to_e164 || callInfo.from_e164)));
  const leadLocal = (partyPhone || (lead && lead.local_timezone))
    ? leadLocalTimeLabel({
      phone: partyPhone,
      state: lead && lead.state,
      local_timezone: lead && lead.local_timezone
    })
    : '';
  const transcript = (softphoneState.transcript || []).map(function (line) {
    return { id: line.id, role: softphoneTranscriptRole(line.role), content: line.content, ts: line.timestamp };
  });

  return (
    <SoftphonePanel
      api={API.json}
      toast={showToast}
      user={user}
      callSid={callSid}
      timezone={timezone}
      status={status}
      mode={listenOnly ? 'listen' : 'browser'}
      caller={{
        name: lead ? lead.name : null,
        company: lead ? lead.company : null,
        phone: lead ? lead.phone : null,
        leadId: lead ? lead.id : null
      }}
      context={{
        interest: callInfo ? callInfo.interest_level : null,
        summary: callInfo ? callInfo.summary : null,
        reason: [softphoneState.reason || null, leadLocal || null].filter(Boolean).join(' · ') || null
      }}
      startedAt={(voice && voice.startedAt) || startedAt}
      muted={softphoneState.isMuted}
      minimized={minimized}
      inputLevel={sdk && voice ? voice.inputLevel : 0}
      outputLevel={sdk && voice ? voice.outputLevel : 0}
      transcript={transcript}
      error={voiceError}
      devices={sdk && voice ? voice.devices : null}
      onAccept={(status === 'incoming' && canJoin) || listenOnly || status === 'error' ? joinCall : undefined}
      onListen={status === 'incoming' && canListen ? function () { SoftphoneController.join({ listenOnly: true }); } : undefined}
      onDecline={status === 'incoming' ? function () { SoftphoneController.close(); } : undefined}
      onToggleMute={joined ? function () { SoftphoneController.setMuted(!softphoneState.isMuted); } : undefined}
      onHangup={connected || status === 'connecting' ? function () { SoftphoneController.leave(); } : undefined}
      onDigit={sdk && joined ? function (d) { SoftphoneController.sendDigits(d); } : undefined}
      onSelectInput={sdk && joined ? function (id) { SoftphoneController.setInputDevice(id); } : undefined}
      onSelectOutput={sdk && joined ? function (id) { SoftphoneController.setOutputDevice(id); } : undefined}
      onMinimize={function () { setMinimized(true); }}
      onRestore={function () { setMinimized(false); }}
      onClose={function () { SoftphoneController.close(); }}
    />
  );
}

function AuditLogsView({ user, tenantMe }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ tenant_id: '', action: '', entity_type: '', date_from: '', date_to: '' });
  const limit = 50;
  const isDemo = tenantMe && tenantMe.is_demo;

  useEffect(function () {
    var cancelled = false;
    setLoading(true);
    var q = '?limit=' + limit + '&offset=' + (page - 1) * limit;
    if (user && user.role === 'super_admin' && filters.tenant_id) q += '&tenant_id=' + encodeURIComponent(filters.tenant_id);
    if (filters.action) q += '&action=' + encodeURIComponent(filters.action);
    if (filters.entity_type) q += '&entity_type=' + encodeURIComponent(filters.entity_type);
    if (filters.date_from) q += '&date_from=' + encodeURIComponent(filters.date_from);
    if (filters.date_to) q += '&date_to=' + encodeURIComponent(filters.date_to);
    q += '&tz=' + encodeURIComponent(getWorkspaceTimeZone());
    API.json('GET', '/api/admin/audit-logs' + q)
      .then(function (res) {
        if (!cancelled && res) {
          setItems(res.items || []);
          setTotal(res.total != null ? res.total : (res.items || []).length);
        }
      })
      .catch(function () {
        if (!cancelled) setItems([]);
      })
      .finally(function () {
        if (!cancelled) setLoading(false);
      });
    return function () { cancelled = true; };
  }, [page, user, filters.action, filters.entity_type, filters.date_from, filters.date_to, filters.tenant_id]);

  async function exportCsv() {
    if (isDemo) return;
    var q = '?limit=5000&tz=' + encodeURIComponent(getWorkspaceTimeZone());
    if (filters.date_from) q += '&date_from=' + encodeURIComponent(filters.date_from);
    if (filters.date_to) q += '&date_to=' + encodeURIComponent(filters.date_to);
    try {
      var token = window.localStorage.getItem('authToken');
      var headers = token ? { Authorization: 'Bearer ' + token } : {};
      if (typeof window !== 'undefined' && (window.DEBUG_API === true || /[?&]debug=1/.test(window.location.search || ''))) headers['X-Debug-UI'] = '1';
      var res = await fetch(window.API_BASE + '/api/admin/audit-logs/export' + q, { headers: headers, credentials: 'include' });
      if (!res.ok) throw new Error(res.statusText || 'Export failed');
      var blob = await res.blob();
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'audit-logs.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e.message || 'Export failed');
    }
  }

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Audit logs</div>
          <div className="card-subtitle">Who did what, when. Append-only; no deletes. Client IPs are stored masked (first two IPv4 octets) for privacy. <TimesShown /></div>
        </div>
        <div>
          <button className="button outline small" onClick={exportCsv} disabled={isDemo} title={isDemo ? 'Export disabled for demo tenants' : 'Download CSV'}>
            Export CSV
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="stack gap-sm" style={{ marginBottom: 16, flexWrap: 'wrap', flexDirection: 'row', gap: 8 }}>
          {user && user.role === 'super_admin' && (
            <input type="number" className="input" aria-label="Tenant ID" placeholder="Tenant ID" value={filters.tenant_id || ''} onChange={function (e) { setFilters(function (f) { return { ...f, tenant_id: e.target.value || null }; }); }} style={{ width: 100 }} />
          )}
          <input type="text" className="input" aria-label="Filter by action" placeholder="Action" value={filters.action || ''} onChange={function (e) { setFilters(function (f) { return { ...f, action: e.target.value || '' }; }); }} style={{ width: 140 }} />
          <input type="text" className="input" aria-label="Filter by entity type" placeholder="Entity type" value={filters.entity_type || ''} onChange={function (e) { setFilters(function (f) { return { ...f, entity_type: e.target.value || '' }; }); }} style={{ width: 120 }} />
          <input type="date" className="input" aria-label="From date" value={filters.date_from || ''} onChange={function (e) { setFilters(function (f) { return { ...f, date_from: e.target.value || '' }; }); }} />
          <input type="date" className="input" aria-label="To date" value={filters.date_to || ''} onChange={function (e) { setFilters(function (f) { return { ...f, date_to: e.target.value || '' }; }); }} />
        </div>
        {loading ? <div className="muted">Loading…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Tenant</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th title="IPv4 addresses are masked at ingest (e.g. 104.167.*.*)">IP</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map(function (row) {
                  return (
                    <tr key={row.id}>
                      <td><WhenText value={row.created_at} /></td>
                      <td>{row.tenant_id}</td>
                      <td>{row.user_id || '—'}</td>
                      <td>{row.actor_role || '—'}</td>
                      <td>{row.action || '—'}</td>
                      <td>{(row.entity_type || '') + (row.entity_id ? '#' + row.entity_id : '')}</td>
                      <td style={{ fontSize: '0.75rem' }}>{row.ip_address || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="muted">Total: {total}</span>
          <div>
            <button className="button ghost small" disabled={page <= 1} onClick={function () { setPage(function (p) { return p - 1; }); }}>Previous</button>
            <span style={{ margin: '0 8px' }}>Page {page}</span>
            <button className="button ghost small" disabled={items.length < limit} onClick={function () { setPage(function (p) { return p + 1; }); }}>Next</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestLabView() {
  const [healthResult, setHealthResult] = useState(null);
  const [dbResult, setDbResult] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [integrationsResult, setIntegrationsResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(null);
  const [costSyncDryRun, setCostSyncDryRun] = useState(true);
  const [costSyncResult, setCostSyncResult] = useState(null);
  const [costSyncLoading, setCostSyncLoading] = useState(false);
  const [loadTarget, setLoadTarget] = useState('health');
  const [loadConcurrency, setLoadConcurrency] = useState(10);
  const [loadDuration, setLoadDuration] = useState(10);
  const [loadResult, setLoadResult] = useState(null);
  const [loadLoading, setLoadLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  function runValidation(suite) {
    setValidationLoading(true);
    setValidationResult(null);
    API.json('POST', '/api/admin/tests/run', { suite: suite }).then(function (data) {
      setValidationResult(data);
    }).catch(function (e) {
      setValidationResult({ ok: false, error: e.message, passed: 0, failed: 0, results: [] });
    }).finally(function () { setValidationLoading(false); });
  }

  useEffect(function () {
    API.json('GET', '/api/admin/tests/last').then(function (r) {
      if (r && r.last) setLastRun(r.last);
    }).catch(function () {});
  }, []);

  function runCheck(name, url) {
    setCheckLoading(name);
    setHealthResult(null);
    setDbResult(null);
    setAuditResult(null);
    setIntegrationsResult(null);
    if (name === 'health') setHealthResult(null);
    if (name === 'db') setDbResult(null);
    if (name === 'audit') setAuditResult(null);
    if (name === 'integrations') setIntegrationsResult(null);
    API.json('GET', url).then(function (data) {
      if (name === 'health') setHealthResult(data);
      if (name === 'db') setDbResult(data);
      if (name === 'audit') setAuditResult(data);
      if (name === 'integrations') setIntegrationsResult(data);
    }).catch(function (e) {
      var err = { error: e.message || 'Request failed', status: e.status, data: e.data };
      if (name === 'health') setHealthResult(err);
      if (name === 'db') setDbResult(err);
      if (name === 'audit') setAuditResult(err);
      if (name === 'integrations') setIntegrationsResult(err);
    }).finally(function () { setCheckLoading(null); });
  }

  function runCostSync() {
    setCostSyncLoading(true);
    setCostSyncResult(null);
    API.json('POST', '/api/admin/testlab/cost-sync', { dryRun: costSyncDryRun, lastNDays: 7 }).then(function (data) {
      setCostSyncResult(data);
    }).catch(function (e) {
      setCostSyncResult({ error: e.message || 'Sync failed', data: e.data });
    }).finally(function () { setCostSyncLoading(false); });
  }

  function runLoadTest() {
    setLoadLoading(true);
    setLoadResult(null);
    var concurrency = loadConcurrency;
    var duration = loadDuration;
    if (concurrency > 50) concurrency = 50;
    if (duration > 30) duration = 30;
    API.json('POST', '/api/admin/testlab/loadtest', { target: loadTarget, concurrency: concurrency, durationSeconds: duration }).then(function (data) {
      setLoadResult(data);
    }).catch(function (e) {
      setLoadResult({ error: e.message || 'Load test failed', data: e.data });
    }).finally(function () { setLoadLoading(false); });
  }

  var currentResult = healthResult || dbResult || auditResult || integrationsResult;
  return (
    <React.Fragment>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Quick Checks</div>
          <div className="card-subtitle">Read-only health, DB, audit summary, and integrations status.</div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="button outline small" disabled={checkLoading !== null} onClick={function () { runCheck('health', '/api/admin/testlab/health'); }}>
            {checkLoading === 'health' ? 'Running…' : 'Health'}
          </button>
          <button type="button" className="button outline small" disabled={checkLoading !== null} onClick={function () { runCheck('db', '/api/admin/testlab/db'); }}>
            {checkLoading === 'db' ? 'Running…' : 'DB'}
          </button>
          <button type="button" className="button outline small" disabled={checkLoading !== null} onClick={function () { runCheck('audit', '/api/admin/testlab/audit-summary'); }}>
            {checkLoading === 'audit' ? 'Running…' : 'Audit Summary'}
          </button>
          <button type="button" className="button outline small" disabled={checkLoading !== null} onClick={function () { runCheck('integrations', '/api/admin/testlab/integrations'); }}>
            {checkLoading === 'integrations' ? 'Running…' : 'Integrations Status'}
          </button>
        </div>
        {currentResult && (
          <div className="card-body" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <pre className="mono-block" style={{ margin: 0, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, overflow: 'auto', fontSize: '0.8rem' }}>
              {JSON.stringify(currentResult, null, 2)}
            </pre>
          </div>
        )}
      </section>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Cost Sync</div>
          <div className="card-subtitle">Twilio cost sync (dry-run by default; turn off to write).</div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={costSyncDryRun} onChange={function (e) { setCostSyncDryRun(e.target.checked); }} />
            <span>Dry run (no writes)</span>
          </label>
          <button type="button" className="button outline small" disabled={costSyncLoading} onClick={runCostSync}>
            {costSyncLoading ? 'Running…' : 'Run'}
          </button>
        </div>
        {costSyncResult && (
          <div className="card-body" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <pre className="mono-block" style={{ margin: 0, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, overflow: 'auto', fontSize: '0.8rem' }}>
              {JSON.stringify(costSyncResult, null, 2)}
            </pre>
          </div>
        )}
      </section>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Load Test</div>
          <div className="card-subtitle">Target: health or audit-summary only. Concurrency cap 50, duration cap 30s. Single-flight.</div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <label className="field">
            <span className="field-label">Target</span>
            <select value={loadTarget} onChange={function (e) { setLoadTarget(e.target.value); }}>
              <option value="health">health</option>
              <option value="audit-summary">audit-summary</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Concurrency (max 50)</span>
            <input type="number" min={1} max={50} value={loadConcurrency} onChange={function (e) { setLoadConcurrency(parseInt(e.target.value, 10) || 10); }} />
          </label>
          <label className="field">
            <span className="field-label">Duration (s, max 30)</span>
            <input type="number" min={1} max={30} value={loadDuration} onChange={function (e) { setLoadDuration(parseInt(e.target.value, 10) || 10); }} />
          </label>
          <button type="button" className="button outline small" disabled={loadLoading} onClick={runLoadTest}>
            {loadLoading ? 'Running…' : 'Run'}
          </button>
        </div>
        {loadResult && (
          <div className="card-body" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <pre className="mono-block" style={{ margin: 0, padding: 12, background: 'var(--color-bg-elevated)', borderRadius: 8, overflow: 'auto', fontSize: '0.8rem' }}>
              {JSON.stringify(loadResult, null, 2)}
            </pre>
          </div>
        )}
      </section>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">Regression Validation</div>
          <div className="card-subtitle">Run quick or full validation suite. Results can be copied to clipboard.</div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button type="button" className="button primary small" disabled={validationLoading} onClick={function () { runValidation('quick'); }}>
            {validationLoading ? 'Running…' : 'Run Quick Validation'}
          </button>
          <button type="button" className="button outline small" disabled={validationLoading} onClick={function () { runValidation('full'); }}>
            {validationLoading ? 'Running…' : 'Run Full Validation'}
          </button>
          {lastRun && lastRun.at && (
            <span className="muted" style={{ fontSize: '0.875rem' }}>Last run: <WhenText value={lastRun.at} style="full" /> ({lastRun.suite})</span>
          )}
        </div>
        {validationResult && (
          <div className="card-body" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span className={validationResult.ok ? 'success' : 'error'}>
                {validationResult.ok ? 'Passed' : 'Failed'} — {validationResult.passed} passed, {validationResult.failed} failed
              </span>
              <button type="button" className="button small outline" onClick={function () {
                var text = JSON.stringify(validationResult.results, null, 2);
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text).then(function () { alert('Copied to clipboard'); }).catch(function () {});
                } else {
                  alert('Copy not available');
                }
              }}>Copy results</button>
            </div>
            <table className="table" style={{ width: '100%', fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Result</th>
                  <th>Details</th>
                  <th>ms</th>
                </tr>
              </thead>
              <tbody>
                {(validationResult.results || []).map(function (r, i) {
                  return (
                    <tr key={i}>
                      <td>{r.name}</td>
                      <td className={r.ok ? 'success' : 'error'}>{r.ok ? 'OK' : 'FAIL'}</td>
                      <td>{r.details || '—'}</td>
                      <td>{r.duration_ms != null ? r.duration_ms : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </React.Fragment>
  );
}

function MonitoringView() {
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [oauthLogs, setOAuthLogs] = useState([]);
  const [jobRuns, setJobRuns] = useState([]);
  const [retryResult, setRetryResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([
      API.json('GET', '/api/admin/monitoring/delivery-logs?limit=50').then(function (r) { return r.logs || []; }).catch(function () { return []; }),
      API.json('GET', '/api/admin/monitoring/oauth-refresh-logs?limit=50').then(function (r) { return r.logs || []; }).catch(function () { return []; }),
      API.json('GET', '/api/admin/monitoring/job-runs?limit=50').then(function (r) { return r.runs || []; }).catch(function () { return []; })
    ]).then(function (a) {
      setDeliveryLogs(a[0]);
      setOAuthLogs(a[1]);
      setJobRuns(a[2]);
    }).catch(function () {}).finally(function () { setLoading(false); });
  }

  useEffect(function () { load(); }, []);

  function retryWebhooks() {
    setRetrying(true);
    setRetryResult(null);
    API.json('POST', '/api/admin/monitoring/retry-webhooks').then(function (r) {
      setRetryResult(r);
      load();
    }).catch(function (e) {
      setRetryResult({ error: e.message });
    }).finally(function () { setRetrying(false); });
  }

  if (loading) {
    return (
      <section className="card">
        <div className="card-body"><div className="muted">Loading…</div></div>
      </section>
    );
  }
  return (
    <React.Fragment>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="card-title">Integration Failures</div>
            <div className="card-subtitle">Delivery logs (webhooks and HubSpot push). Filter by status in URL: ?status=failed. <TimesShown /></div>
          </div>
          <button type="button" className="button primary small" disabled={retrying} onClick={retryWebhooks}>
            {retrying ? 'Retrying…' : 'Retry failed webhooks now'}
          </button>
        </div>
        {retryResult && (
          <div className="card-body" style={{ borderBottom: '1px solid var(--border)' }}>
            <span className="success">{retryResult.processed != null ? 'Processed: ' + retryResult.processed + ', succeeded: ' + retryResult.succeeded : (retryResult.error || '')}</span>
          </div>
        )}
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>id</th>
                <th>tenant_id</th>
                <th>provider</th>
                <th>event_type</th>
                <th>status</th>
                <th>attempt_count</th>
                <th>last_error</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {deliveryLogs.slice(0, 50).map(function (row) {
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.tenant_id}</td>
                    <td>{row.provider}</td>
                    <td>{row.event_type}</td>
                    <td className={row.status === 'success' ? 'success' : (row.status === 'failed' ? 'error' : '')}>{row.status}</td>
                    <td>{row.attempt_count}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.last_error}>{row.last_error || '—'}</td>
                    <td><WhenText value={row.created_at} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {deliveryLogs.length === 0 && <div className="muted" style={{ padding: 12 }}>No delivery logs.</div>}
        </div>
      </section>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title">OAuth refresh failures</div>
        </div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>id</th>
                <th>tenant_id</th>
                <th>provider</th>
                <th>status</th>
                <th>last_error</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {oauthLogs.slice(0, 50).map(function (row) {
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.tenant_id}</td>
                    <td>{row.provider}</td>
                    <td>{row.status}</td>
                    <td>{row.last_error || '—'}</td>
                    <td><WhenText value={row.created_at} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {oauthLogs.length === 0 && <div className="muted" style={{ padding: 12 }}>No OAuth refresh logs.</div>}
        </div>
      </section>
      <section className="card">
        <div className="card-header">
          <div className="card-title">Job runs summary</div>
        </div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>id</th>
                <th>job_name</th>
                <th>status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {jobRuns.slice(0, 50).map(function (row) {
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.job_name}</td>
                    <td>{row.status}</td>
                    <td><WhenText value={row.created_at} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {jobRuns.length === 0 && <div className="muted" style={{ padding: 12 }}>No job runs.</div>}
        </div>
      </section>
    </React.Fragment>
  );
}

function DemoControlsView({ tenants, refreshTenants, user }) {
  const [resetting, setResetting] = useState(null);

  async function setDemo(tenantId, isDemo) {
    try {
      const t = tenants.find(function (x) { return x.id === tenantId; });
      if (!t) return;
      await API.json('PUT', '/api/admin/tenants/' + tenantId, {
        name: t.name,
        timezone: t.timezone,
        plan_name: t.plan_name,
        is_demo: !!isDemo,
        demo_expires_at: isDemo ? (t.demo_expires_at || null) : null,
        demo_max_calls_per_day: isDemo ? (t.demo_max_calls_per_day != null ? t.demo_max_calls_per_day : 50) : 50,
        demo_max_call_duration_seconds: isDemo ? (t.demo_max_call_duration_seconds != null ? t.demo_max_call_duration_seconds : 180) : 180
      });
      refreshTenants && refreshTenants();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to update');
    }
  }

  async function resetDemo(tenantId) {
    if (!confirm('Reset all demo data (leads, calls, metrics) for this tenant? Flows and configuration are preserved.')) return;
    setResetting(tenantId);
    try {
      await API.json('POST', '/api/admin/tenants/' + tenantId + '/demo-reset');
      refreshTenants && refreshTenants();
    } catch (e) {
      console.error(e);
      alert(e.data && e.data.detail ? e.data.detail : (e.message || 'Reset failed'));
    } finally {
      setResetting(null);
    }
  }

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Demo tenant controls</div>
          <div className="card-subtitle">Mark tenants as demo (sales/PoC). Reset demo data without touching flows or config.</div>
        </div>
      </div>
      <div className="card-body">
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Demo</th>
                <th>Max calls/day</th>
                <th>Max duration (s)</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(tenants || []).map(function (t) {
                var isDemo = !!(t.is_demo === 1 || t.is_demo === true);
                return (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.name}</td>
                    <td>
                      <label className="checkbox-inline">
                        <input type="checkbox" checked={isDemo} onChange={function (e) { setDemo(t.id, e.target.checked); }} />
                        <span>Demo</span>
                      </label>
                    </td>
                    <td>{t.demo_max_calls_per_day != null ? t.demo_max_calls_per_day : 50}</td>
                    <td>{t.demo_max_call_duration_seconds != null ? t.demo_max_call_duration_seconds : 180}</td>
                    <td>{t.demo_expires_at || '—'}</td>
                    <td>
                      {isDemo ? (
                        <button className="button outline small" onClick={function () { resetDemo(t.id); }} disabled={resetting === t.id}>
                          {resetting === t.id ? 'Resetting…' : 'Reset demo data'}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function AppShell({ user, onLogout }) {
  // Keep session context in sync for getSessionContext() and tenant-context banner.
  useEffect(function () { setSessionUser(user || null); }, [user]);

  // ===========================
  // Inbound Instructions UI hook
  // ===========================
  const tenantId =
    (user && (user.tenant_id || user.tenantId || (user.tenant && user.tenant.id))) || null;

  // ============================================================
  // Live Call Monitor (Admin) - helper to normalize call objects
  // ============================================================
  function normalizeLiveCall(raw, existing) {
    const src = raw || {};
    const prev = existing || {};

    const callSid =
      src.callSid ||
      src.call_sid ||
      src.sid ||
      prev.callSid ||
      null;

    const status =
      src.status ||
      src.state ||          // <-- SSE uses "state"
      src.callStatus ||
      prev.status ||
      'in-progress';

    const phone =
      src.phone ||
      src.lead_phone ||
      src.to ||
      src.from ||
      prev.phone ||
      null;

    const direction =
      src.direction ||
      src.call_direction ||
      prev.direction ||
      null;

    const premiumFlag =
      typeof src.premium === 'boolean'
        ? src.premium
        : typeof src.is_premium === 'boolean'
        ? src.is_premium
        : typeof src.premium_flag === 'boolean'
        ? src.premium_flag
        : src.tier === 'premium'
        ? true
        : prev.premium || false;

    const startedAt =
      src.started_at ||
      src.startedAt ||
      src.start_time ||
      src.timestamp ||
      src.created_at ||
      prev.startedAt ||
      null;

    const leadName =
      src.leadName ||
      src.lead_name ||
      src.lead ||
      src.lead_full_name ||
      prev.leadName ||
      null;

    const tenantName =
      src.tenantName ||
      src.tenant_name ||
      prev.tenantName ||
      null;

    return {
      callSid,
      phone,
      status,
      direction,
      premium: !!premiumFlag,
      startedAt,
      leadName,
      tenantName
    };
  }

  const [active, setActive] = useState(function () {
    if (typeof window !== 'undefined' && window.location.hash.startsWith('#integrations')) return 'integrations';
    return 'dashboard';
  });
  const [setupOpen, setSetupOpen] = useState(false);
  useEffect(function () {
    function open() { setSetupOpen(true); }
    window.addEventListener('open-setup-wizard', open);
    return function () { window.removeEventListener('open-setup-wizard', open); };
  }, []);
  useEffect(function () {
    var role = user && user.role;
    var impersonating = !!(user && user.is_impersonation);
    var show = role === 'tenant_admin' || (role === 'super_admin' && impersonating);
    if (!show || user.tenant_id == null || user.tenant_id === '') return undefined;
    var cancel = false;
    API.json('GET', '/api/onboarding/status').then(function (row) {
      if (!cancel && row && row.show_wizard) setSetupOpen(true);
    }).catch(function () {});
    return function () { cancel = true; };
  }, [user && user.id, user && user.tenant_id, user && user.role]);
  useEffect(function () {
    if (user && user.role === 'super_admin' && (user.tenant_id == null) && active === 'dashboard') {
      setActive('admin-dashboard');
    }
  }, [user]);
  // Integrations sub-route: 'hub' = External Integrations tiles, 'manual' = REST/Webhook list (same as #integrations vs #integrations/manual)
  const [integrationsSubRoute, setIntegrationsSubRoute] = useState(function () {
    if (typeof window !== 'undefined' && window.location.hash === '#integrations/manual') return 'manual';
    return 'hub';
  });
  useEffect(function () {
    function syncFromHash() {
      if (window.location.hash === '#integrations/manual') setIntegrationsSubRoute('manual');
      else if (window.location.hash.startsWith('#integrations')) setIntegrationsSubRoute('hub');
    }
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return function () { window.removeEventListener('hashchange', syncFromHash); };
  }, []);
  function navigateToIntegrationsManual() {
    setActive('integrations');
    setIntegrationsSubRoute('manual');
    window.location.hash = '#integrations/manual';
  }
  function navigateToIntegrationsHub() {
    setIntegrationsSubRoute('hub');
    window.location.hash = '#integrations';
  }
  const [stats, setStats] = useState({
    totalLeads: 0,
    interestedLeads: 0,
    dncLeads: 0,
    callsToday: 0,
    avgDuration: 0,
    hotRate: 0
  });
  const [usage, setUsage] = useState(null);
  const [metricsSummary, setMetricsSummary] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsError, setLeadsError] = useState('');
  const [callsPage, setCallsPage] = useState(1);
  const [callsFilters, setCallsFilters] = useState(EMPTY_CALL_FILTERS);
  const [calls, setCalls] = useState([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [callsError, setCallsError] = useState('');
  const [callsReload, setCallsReload] = useState(0);
  const [openLead, setOpenLead] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [callbacksError, setCallbacksError] = useState('');
  const [settings, setSettings] = useState({ pollyVoice: 'Matthew' });
  const [pendingLeadId, setPendingLeadId] = useState(null);
  const [pendingCallSid, setPendingCallSid] = useState(null);
  function openLeadById(leadId) {
    if (!leadId) return;
    setPendingLeadId(leadId);
    setActive('leads');
  }
  function openCallBySid(callSid) {
    if (!callSid) return;
    setPendingCallSid(callSid);
    setActive('calls');
  }
  if (settings && settings.timezone) setWorkspaceTimeZone(settings.timezone);
  const [tenants, setTenants] = useState([]);
  const [superAdminUserId, setSuperAdminUserId] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [demoInbound, setDemoInbound] = useState(false);
  const [demoSetupLoading, setDemoSetupLoading] = useState(false);
  const [softphoneConsole, setSoftphoneConsole] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // ============================================================
  // Live Call Monitor (Admin) - in-memory active calls map
  // ============================================================
  const [liveCallsBySid, setLiveCallsBySid] = useState({});

  // ============================================================
  // Agent request notifications (caller asked for agent)
  // ============================================================
  const [pendingAgentRequest, setPendingAgentRequest] = useState(null);

  useEffect(() => {
    if (!pendingAgentRequest) return;
    try {
      console.log('[agent-handoff-debug] popup_state_opened', {
        callSid: pendingAgentRequest.callSid,
        tenantId: pendingAgentRequest.tenantId
      });
    } catch (e) {
      /* ignore */
    }
  }, [pendingAgentRequest]);

  const reloadLeads = React.useCallback(async () => {
    try {
      setLeadsError('');
      const data = await API.json('GET', `/api/leads?page=${leadsPage}`);
      var items = (data && data.items) ? data.items : [];
      setLeads(Array.isArray(items) ? items : []);
      var dataTotal = asCount(data && data.total);
      setLeadsTotal(dataTotal);
      const total = dataTotal;
      const interested = items.filter((l) => l.status === 'interested').length;
      const dnc = items.filter((l) => l.dnc_flag).length;
      setStats((s) => ({
        ...s,
        totalLeads: total,
        interestedLeads: interested,
        dncLeads: dnc
      }));
    } catch (e) {
      console.error(e);
      setLeadsError('Failed to load leads');
      setLeads([]);
    }
  }, [leadsPage]);

  useEffect(() => {
    if (!isTenantContext(user)) return;
    reloadLeads();
  }, [reloadLeads, user]);
  useEffect(() => {
    if (active === 'leads' && isTenantContext(user)) reloadLeads();
  }, [active]);
  useEffect(() => {
    if (!isTenantContext(user)) return;
    async function loadUsage() {
      try {
        const data = await API.json('GET', '/api/usage');
        setUsage(data);
      } catch (e) {
        console.error(e);
      }
    }
    loadUsage();
  }, [user]);

  var refreshMetrics = React.useCallback(function () {
    var tid = tenantId;
    if (!tid && user && user.role === 'super_admin') return;
    if (!tid) return;
    API.json('GET', '/api/metrics/summary' + (user && user.role === 'super_admin' ? '?tenant_id=' + tid : '')).then(function (d) {
      setMetricsSummary(d);
    }).catch(function (e) {
      console.error(e);
    });
  }, [tenantId, user]);

  useEffect(function () {
    if (active !== 'dashboard') return;
    refreshMetrics();
  }, [active, refreshMetrics]);

  useEffect(function () {
    fetch(window.API_BASE + '/api/public-config').then(function (r) { return r.json(); }).then(function (c) {
      setDemoInbound(c && (c.demoMode === true || c.demoInbound === true));
      setSoftphoneConsole(c && c.softphoneConsole === true);
    }).catch(function () {});
  }, []);

  useEffect(() => {
    if (!isTenantContext(user)) return;
    async function fetchCalls() {
      try {
        const data = await API.json('GET', '/api/calls?' + callsQueryString(callsPage, callsFilters));
        var items = (data && data.items) ? data.items : [];
        setCalls(Array.isArray(items) ? items : []);
        setCallsTotal(asCount(data && data.total));
        setCallsError('');
        if (callFiltersActive(callsFilters)) return;
        const today = todayYmd();
        const todays = items.filter((c) => zonedYmd(c.created_at) === today);
        const avg =
          items.length > 0
            ? Math.round(
                items.reduce((sum, c) => sum + (c.duration || 0), 0) / items.length
              )
            : 0;
        const hotCount = items.filter((c) => c.interest_level === 'hot').length;
        const completed = items.filter((c) => c.status === 'completed').length;
        const hotRate = completed ? Math.round((hotCount / completed) * 100) : 0;
        setStats((s) => ({
          ...s,
          callsToday: todays.length,
          avgDuration: avg,
          hotRate
        }));
      } catch (e) {
        console.error(e);
        setCalls([]);
        setCallsError((e && e.data && (e.data.message || e.data.error)) || 'Could not load calls. Refresh the page. If this keeps happening, sign in again.');
      }
    }
    fetchCalls();
  }, [callsPage, callsFilters, user, callsReload]);

  useEffect(function () {
    if (active !== 'calls' || !isTenantContext(user)) return;
    var cancelled = false;
    var inFlight = false;
    var sawSuccess = false;
    function loadOpenCallbacks() {
      if (cancelled || inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      API.json('GET', '/api/callbacks?status=open')
        .then(function (cb) {
          if (cancelled) return;
          sawSuccess = true;
          setCallbacks((cb && cb.items) || []);
          setCallbacksError('');
        })
        .catch(function (e) {
          if (cancelled) return;
          console.error(e);
          if (!sawSuccess) {
            setCallbacksError((e && e.data && (e.data.message || e.data.error)) || 'Could not load callback requests. Refresh the page.');
          } else {
            setCallbacksError('Couldn\'t refresh callbacks');
          }
        })
        .finally(function () { inFlight = false; });
    }
    loadOpenCallbacks();
    var timer = setInterval(loadOpenCallbacks, 10000);
    function onVisible() {
      if (!document.hidden) loadOpenCallbacks();
    }
    document.addEventListener('visibilitychange', onVisible);
    return function () {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, user]);

  useEffect(() => {
    if (!isTenantContext(user)) return;
    async function fetchSettings() {
      try {
        const [core, sales] = await Promise.all([
          API.json('GET', '/api/settings'),
          API.json('GET', '/api/settings/sales')
        ]);
        setSettings({ ...core, sales });
      } catch (e) {
        console.error(e);
      }
    }
    fetchSettings();
  }, [user]);

  async function refreshTenants() {
    try {
      const data = await API.json('GET', '/api/admin/tenants');
      setTenants(data.items || []);
      setSuperAdminUserId(data.superAdminUserId || null);
    } catch (e) {
      console.error(e);
    }
  }

  const [tenantMe, setTenantMe] = useState(null);
  useEffect(function () {
    if (!user || !user.tenant_id) {
      setTenantMe(null);
      return;
    }
    API.json('GET', '/api/tenant/me')
      .then(function (res) {
        if (res && res.ok && res.tenant) setTenantMe(res.tenant);
        else setTenantMe(null);
      })
      .catch(function () { setTenantMe(null); });
  }, [user && user.id, user && user.tenant_id]);

  useEffect(function () {
    if (active === 'billing' || active === 'demo-controls') {
      if (user && user.role === 'super_admin') {
        refreshTenants();
      }
    }
    if (active === 'billing' && user && user.tenant_id) {
      (async function () {
        try {
          const sumRes = await API.json('GET', '/api/billing/summary');
          setBillingSummary(sumRes && (sumRes.ok === true || sumRes.plan != null || sumRes.invoices) ? sumRes : { ok: true, message: sumRes && sumRes.message ? sumRes.message : 'billing_not_configured', invoices: [] });
          setBillingInvoices(sumRes && sumRes.invoices ? sumRes.invoices : []);
        } catch (e) {
          var msg = (e.status === 403) ? 'Billing disabled by plan or you lack permission to view billing.' : (e.status === 400 && e.data && e.data.error === 'no_tenant') ? 'No tenant context. Impersonate a tenant.' : (e.data && e.data.error) ? e.data.error : (e.message || 'Unable to load billing');
          setBillingSummary({ message: 'billing_load_failed', error: msg });
          setBillingInvoices([]);
          showToast(msg, 'error');
        }
      })();
    }
  }, [active, user]);

  useEffect(function () {
    async function fetchAdminStats() {
      if (!user || user.role !== 'super_admin') return;
      try {
        setAdminStatsLoading(true);
        const data = await API.json('GET', '/api/admin/stats/system');
        setAdminStats(data);
      } catch (e) {
        console.error(e);
      } finally {
        setAdminStatsLoading(false);
      }
    }
    if (active === 'admin-dashboard' && user && user.role === 'super_admin') {
      fetchAdminStats();
    }
  }, [active, user]);

  // ============================================================
  // Live Call Monitor (Admin) - initial load + periodic poll of active calls (tenant context only)
  // Periodic poll ensures list updates even if SSE was missed (e.g. multi-instance).
  // ============================================================
  useEffect(() => {
    if (!isTenantContext(user)) return;
    let cancelled = false;

    async function fetchActiveCalls() {
      try {
        const data = await API.json('GET', '/api/calls/active');
        const items =
          (data && (data.items || data.calls || data.activeCalls)) || [];
        if (DEBUG_LIVE) console.log('[live] poll /api/calls/active count=%s tenantId=%s', items.length, user?.tenant_id ?? '(none)');
        const map = {};
        items.forEach((item) => {
          const normalized = normalizeLiveCall(item, null);
          if (normalized.callSid) {
            map[normalized.callSid] = normalized;
          }
        });
        if (!cancelled) {
          setLiveCallsBySid(map);
        }
      } catch (e) {
        console.error(e);
      }
    }

    fetchActiveCalls();
    const pollInterval = setInterval(fetchActiveCalls, 5000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [user]);

// ============================================================
// Live Call Monitor (Admin) - SSE subscription (supports "call" + "call_update")
// ============================================================
const handleSseEvent = React.useCallback((evt) => {
  if (!evt) return;

  // We support BOTH:
  // - evt.type === "call"        (payload.state)
  // - evt.type === "call_update" (payload.status)
  const payload = evt.payload && typeof evt.payload === 'object' ? evt.payload : evt;

  const callSid = payload.callSid || payload.call_sid || payload.sid || null;
  if (!callSid) return;

  const rawStatus = payload.status || payload.state || '';
  const statusLower = String(rawStatus).toLowerCase();
  const TERMINAL_STATUSES = ['completed', 'failed', 'canceled', 'cancelled', 'busy', 'no-answer'];

  setLiveCallsBySid((prev) => {
    // remove on any terminal status so call disappears from Live Monitor
    if (TERMINAL_STATUSES.includes(statusLower)) {
      if (!prev[callSid]) return prev;
      const next = { ...prev };
      delete next[callSid];
      return next;
    }

    const existing = prev[callSid] || null;

    // Create OR update row
    const next = { ...prev };
    next[callSid] = normalizeLiveCall(
      {
        callSid,
        status: rawStatus,
        phone: payload.phone,
        direction: payload.direction,
        premium: payload.premium
      },
      existing
    );

    return next;
  });
}, []);
useSse(handleSseEvent);

  const [agentEventsConnected, setAgentEventsConnected] = useState(false);
  const [agentPresenceState, setAgentPresenceState] = useState('offline');
  const handleAgentEvent = React.useCallback((evt) => {
    if (evt && evt.type === 'agent_request' && evt.payload) {
      const { callSid, tenantId, callerHint, timestamp, assignedUserId, queueTotal } = evt.payload;
      try {
        console.log('[agent-handoff-debug] popup_event_received_client', {
          callSid,
          tenantId,
          userId: user && user.id != null ? user.id : null,
          userRole: user && user.role != null ? user.role : null,
          userTenantId: user && (user.tenant_id != null ? user.tenant_id : user.tenantId)
        });
      } catch (e) {
        /* ignore */
      }
      const userTid = user && (user.tenant_id || user.tenantId);
      if (userTid && String(userTid) !== String(tenantId) && user.role !== 'super_admin') {
        try {
          console.log('[agent-handoff-debug] popup_state_blocked_reason', {
            reason: 'tenant_mismatch',
            payloadTenantId: tenantId,
            userTenantId: userTid
          });
        } catch (e) {
          /* ignore */
        }
        return;
      }
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification('Incoming agent request', {
            body: 'Call ' + String(callSid || '').slice(-8) + ' — ' + (callerHint || 'Caller requested an agent'),
            tag: 'agent-handoff-' + String(callSid || '')
          });
        }
      } catch (e) {
        /* ignore */
      }
      setPendingAgentRequest({ callSid, tenantId, callerHint, timestamp, assignedUserId, queueTotal });
    }
  }, [user]);
  useAgentEvents(handleAgentEvent, setAgentEventsConnected, setAgentPresenceState);

  const handleAcceptAgentRequest = React.useCallback(async () => {
    if (!pendingAgentRequest) return;
    const { callSid, callerHint } = pendingAgentRequest;
    try {
      const started = await SoftphoneController.acceptInBrowser(callSid, { reason: callerHint });
      if (started) {
        setPendingAgentRequest(null);
        return;
      }
      const res = await API.json('POST', '/api/agent/calls/' + encodeURIComponent(callSid) + '/accept');
      if (res.ok && res.relayUrl && res.token) {
        setPendingAgentRequest(null);
        SoftphoneController.openForCall({ callSid, relayUrl: res.relayUrl, token: res.token, reason: callerHint });
        SoftphoneController.join({ listenOnly: false });
      }
    } catch (e) {
      console.error('Accept agent request failed', e);
    }
  }, [pendingAgentRequest]);

  const handleDeclineAgentRequest = React.useCallback(async () => {
    if (!pendingAgentRequest) return;
    const { callSid } = pendingAgentRequest;
    try {
      await API.json('POST', '/api/agent/calls/' + encodeURIComponent(callSid) + '/decline');
      setPendingAgentRequest(null);
    } catch (e) {
      console.error('Decline agent request failed', e);
    }
  }, [pendingAgentRequest]);

  const handlePhoneAnswerAgentRequest = React.useCallback(async function () {
    if (!pendingAgentRequest) return;
    const callSid = pendingAgentRequest.callSid;
    try {
      const res = await API.json('POST', '/api/agent/calls/' + encodeURIComponent(callSid) + '/join-phone');
      if (isSoftphoneDebug()) {
        console.log('[handlePhoneAnswerAgentRequest] response', res);
      }
      if (res && res.ok) {
        setPendingAgentRequest(null);
        alert('Calling your profile phone number now — answer when it rings. (Not the tenant handoff line.)');
      } else {
        const msg = JOIN_PHONE_ERRORS[res && res.error] || (res && res.error) || 'Phone join failed.';
        alert(msg);
      }
    } catch (e) {
      if (e && e.status === 401) {
        alert('Session expired, please log in again.');
        return;
      }
      if (isSoftphoneDebug() && e && e.data) {
        console.log('[handlePhoneAnswerAgentRequest] error response', e.data);
      }
      const msg = (e && e.data && e.data.error) ? (JOIN_PHONE_ERRORS[e.data.error] || e.data.error) : (e && e.message ? e.message : 'Network error');
      alert('Phone answer failed: ' + msg);
    }
  }, [pendingAgentRequest]);

  // ============================================================
  // Live Call Monitor (Admin) - derived list for UI
  // ============================================================
  const liveCallsList = Object.values(liveCallsBySid)
    .filter((c) => {
      const status = String(c.status || '').toLowerCase();
      return status !== 'completed';
    })
    .sort((a, b) => {
      const at = parseTimestampMs(a.startedAt);
      const bt = parseTimestampMs(b.startedAt);
      return bt - at;
    });

  let mainContent = null;
  if (active === 'dashboard') {
    if (user && user.role === 'super_admin' && (user.tenant_id == null)) {
      mainContent = <AdminDashboard adminStats={adminStats} loading={adminStatsLoading} />;
    } else {
      mainContent = <Dashboard stats={stats} usage={usage} metricsSummary={metricsSummary} user={user} onRefresh={refreshMetrics} />;
    }
  } else if (active === 'leads') {
    mainContent = (
      <LeadsView
        leads={leads}
        page={leadsPage}
        total={leadsTotal}
        onPageChange={setLeadsPage}
        refresh={reloadLeads}
        loadError={leadsError}
        onLeadContext={setOpenLead}
        openLeadId={pendingLeadId}
        onOpenLeadHandled={function () { setPendingLeadId(null); }}
        user={user}
        timezone={settings.timezone || 'America/Los_Angeles'}
      />
    );
  } else if (active === 'calls') {
    mainContent = (
      <CallsView
        calls={calls}
        page={callsPage}
        total={callsTotal}
        onPageChange={setCallsPage}
        filters={callsFilters}
        onFiltersChange={function (next) { setCallsFilters(next); setCallsPage(1); }}
        callsFiltered={callFiltersActive(callsFilters)}
        timezone={settings.timezone}
        user={user}
        openCallSid={pendingCallSid}
        onOpenCallHandled={function () { setPendingCallSid(null); }}
        callsError={callsError}
        onRetryCalls={function () { setCallsReload(function (n) { return n + 1; }); }}
        callbacks={callbacks}
        callbacksError={callbacksError}
        onMarkCallbackDone={async function (id) {
          try {
            await API.json('POST', '/api/callbacks/' + encodeURIComponent(id) + '/done', {});
            setCallbacks(function (rows) { return (rows || []).filter(function (row) { return String(row.id) !== String(id); }); });
          } catch (e) {
            setCallbacksError((e && e.data && (e.data.message || e.data.error)) || 'Could not mark this callback done. Try again.');
          }
        }}
      />
    );
  } else if (active === 'campaigns') {
    mainContent = (
      <CampaignsView
        api={API.json}
        toast={showToast}
        user={user}
        timezone={settings.timezone || 'America/Los_Angeles'}
        onOpenLead={openLeadById}
      />
    );
  } else if (active === 'admin-dashboard') {
    mainContent = <AdminDashboard adminStats={adminStats} loading={adminStatsLoading} />;
  } else if (active === 'compliance-admin' && user && user.role === 'super_admin') {
    mainContent = <ComplianceAdminView api={API.json} toast={showToast} user={user} timezone={settings.timezone || 'America/Los_Angeles'} onOpenTenant={function () { setActive('billing'); }} />;
  } else if (active === 'oauth-providers' && user && user.role === 'super_admin') {
    mainContent = <OAuthProvidersPage />;
  } else if (active === 'plans' && user && user.role === 'super_admin') {
    mainContent = <AdminPlansView />;
  } else if (active === 'integrations') {
    if (user && user.role === 'super_admin' && (user.tenant_id == null || user.tenant_id === '') && !user.is_impersonation) {
      mainContent = (
        <SuperAdminIntegrationsLanding
          onTenantsBilling={function () { setActive('billing'); }}
          onAdminDashboard={function () { setActive('admin-dashboard'); }}
          onOAuthProviders={function () { setActive('oauth-providers'); }}
        />
      );
    } else {
    mainContent = integrationsSubRoute === 'manual'
      ? <ExternalIntegrationsView onBackToHub={navigateToIntegrationsHub} />
      : <IntegrationsHubView onNavigateToManual={navigateToIntegrationsManual} user={user} />;
    }
  } else if (active === 'flows') {
    if (user && user.role === 'super_admin' && (user.tenant_id == null)) {
      mainContent = (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
            <p className="muted">Switch to a tenant to edit flows.</p>
            <p style={{ fontSize: '0.8rem', marginTop: 8 }}>Use &quot;View as&quot; on a tenant in Tenants &amp; Billing to access flows.</p>
          </div>
        </div>
      );
    } else {
      mainContent = typeof FlowsView !== 'undefined' ? <FlowsView timezone={settings.timezone || getWorkspaceTimeZone()} onNavigate={function (tab) { setActive(tab); }} /> : <div className="card"><div className="card-body">Flows loading…</div></div>;
    }
  } else if (active === 'billing') {
    if (user && user.role === 'super_admin') {
      mainContent = <BillingView tenants={tenants} superAdminUserId={superAdminUserId} refreshTenants={refreshTenants} user={user} />;
    } else {
      mainContent = (
        <div>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-body">
              <a href="#" onClick={function (e) { e.preventDefault(); setActive('go-live'); }} className="button outline">View Go Live checklist</a>
            </div>
          </div>
          <TenantBillingView
            summary={billingSummary}
            invoices={billingInvoices}
            onRefresh={async () => {
              try {
                const sumRes = await API.json('GET', '/api/billing/summary');
                setBillingSummary(sumRes && (sumRes.ok === true || sumRes.plan != null || sumRes.invoices) ? sumRes : { ok: true, message: sumRes && sumRes.message ? sumRes.message : 'billing_not_configured', invoices: [] });
                setBillingInvoices(sumRes && sumRes.invoices ? sumRes.invoices : []);
              } catch (e) {
                var msg = (e.status === 403) ? 'Billing disabled by plan or you lack permission.' : (e.data && e.data.error) ? e.data.error : (e.message || 'Unable to load billing');
                setBillingSummary({ message: 'billing_load_failed', error: msg });
                setBillingInvoices([]);
                showToast(msg, 'error');
              }
            }}
          />
        </div>
      );
    }
  } else if (active === 'cost-usage') {
    mainContent = <CostUsageView />;
  } else if (active === 'monitor') {

    // ==========================================================
    // Live Monitor View: single deterministic panel
    // ==========================================================
    mainContent = <LiveMonitor calls={liveCallsList} />;
}

  else if (active === 'go-live') {
    mainContent = <GoLiveView onNavigate={function (page) { setActive(page || 'settings'); }} />;
  }
  else if (active === 'dnc') {
    mainContent = <DncManager api={API.json} toast={showToast} user={user} timezone={settings.timezone || 'America/Los_Angeles'} />;
  }
  else if (active === 'settings') {
    mainContent = <div>
      <FinishSetupCard api={API.json} user={user} />
      <SettingsView settings={settings} tenantId={tenantId} user={user} openLead={openLead} refresh={async () => {
      try {
        const [core, sales] = await Promise.all([
          API.json('GET', '/api/settings'),
          API.json('GET', '/api/settings/sales')
        ]);
        setSettings({ ...core, sales });
      } catch (e) {
        console.error(e);
      }
    }} />
    </div>;
  } else if (active === 'team') {
    mainContent = <TeamView user={user} />;
  } else if (active === 'lead-finder') {
    mainContent = <LeadFinderView api={API.json} toast={showToast} onOpenLeads={function () { setActive('leads'); reloadLeads(); }} />;
  } else if (active === 'follow-ups') {
    mainContent = (
      <FollowUpsView
        api={API.json}
        toast={showToast}
        user={user}
        timezone={settings.timezone || 'America/Los_Angeles'}
        onHumanCallLead={function (item) {
          if (!item || !item.lead_id) {
            showToast('This follow-up has no lead to call.', 'error');
            return;
          }
          var who = item.lead_name || item.caller_name || item.lead_company || 'lead';
          showToast('Ringing your browser. Answer it and we will dial ' + who + '.');
          SoftphoneController.callLeadInBrowser(item.lead_id, { reason: 'Calling ' + who })
            .then(function (r) {
              if (r && r.ok) return;
              var err = r && r.error;
              var code = err && err.data && err.data.error;
              if (code === 'voice_sdk_not_configured') {
                showToast('Browser calling is not set up for this workspace. Use Have AI call, or dial the number yourself.', 'error');
              } else if (!code) {
                showToast('Your browser phone could not connect. Allow microphone access and check your internet, then try again.', 'error');
              } else {
                showToast(callStartErrorMessage(err), 'error');
              }
            });
        }}
        onCallLead={function (leadId) {
          outboundSetupBlock().then(function (blocked) {
            if (blocked) {
              showToast(setupBlockMessage(blocked), 'error');
              openSetupWizard();
              return;
            }
            API.json('POST', '/api/call/start', { leadId: leadId })
              .then(function () { showToast('AI call started'); })
              .catch(function (e) { showToast(callStartErrorMessage(e), 'error'); });
          });
        }}
      />
    );
  } else if (active === 'quality') {
    mainContent = (
      <QualityView
        api={API.json}
        toast={showToast}
        user={user}
        timezone={settings.timezone || 'America/Los_Angeles'}
        onOpenCall={function (call) { openCallBySid(call && call.call_sid); }}
        onOpenLead={openLeadById}
      />
    );
  } else if (active === 'insights') {
    mainContent = <InsightsView user={user} tenants={tenants} tenantMe={tenantMe} />;
  } else if (active === 'audit-logs') {
    mainContent = <AuditLogsView user={user} tenantMe={tenantMe} />;
  } else if (active === 'demo-controls' && user && user.role === 'super_admin') {
    mainContent = <DemoControlsView tenants={tenants} refreshTenants={refreshTenants} user={user} />;
  } else if (active === 'testlab' && user && user.role === 'super_admin') {
    mainContent = <TestLabView />;
  } else if (active === 'monitoring' && user && user.role === 'super_admin') {
    mainContent = <MonitoringView />;
  }

  var userRole = user && user.role ? user.role : null;
  if (mainContent && userRole && !canAccessNav(userRole, active, user)) {
    mainContent = <NotAuthorized />;
  }

  function handleNavChange(newActive) {
    setMobileNavOpen(false);
    setActive(newActive);
    if (newActive === 'integrations') window.location.hash = '#integrations';
  }

  return (
    <div className="app-shell">
      {mobileNavOpen ? (
        <div
          className="sidebar-backdrop"
          role="presentation"
          onClick={function () { setMobileNavOpen(false); }}
        />
      ) : null}
      <Sidebar active={active} onChange={handleNavChange} user={user} mobileOpen={mobileNavOpen} />
      <main className="main">
        {user && user.is_impersonation && tenantMe ? (
          <div className="card" style={{ marginBottom: 12, padding: '10px 16px', background: 'rgba(239, 68, 68, 0.12)', borderLeft: '4px solid #dc2626' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-fg)' }}>IMPERSONATING TENANT: {tenantMe.name || ('ID ' + user.tenant_id)}</span>
            <button
              type="button"
              className="button small outline"
              style={{ marginLeft: 12 }}
              onClick={async function () {
                try {
                  await API.json('POST', '/api/admin/impersonate/stop', {});
                } catch (e) {}
                var orig = window.localStorage.getItem('superAdminToken');
                window.localStorage.removeItem('superAdminToken');
                if (orig) window.localStorage.setItem('authToken', orig);
                window.location.reload();
              }}
            >
              Stop impersonating
            </button>
          </div>
        ) : null}
        {user && user.role === 'super_admin' && (user.tenant_id == null) && ['dashboard', 'leads', 'lead-finder', 'calls', 'follow-ups', 'campaigns', 'monitor', 'flows', 'team', 'quality', 'insights', 'audit-logs', 'settings', 'go-live', 'billing'].indexOf(active) >= 0 ? (
          <div className="card" style={{ marginBottom: 12, padding: '10px 16px', background: 'rgba(59, 130, 246, 0.12)', borderLeft: '4px solid #3b82f6' }} role="alert">
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-fg)' }}>Tenant context required</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--muted)', marginLeft: 8 }}>Impersonate a tenant from Tenants &amp; billing to edit tenant settings, team, or view tenant data. Per-tenant CRM connections live in <strong>Integrations hub</strong> after you impersonate. Do not spam API calls until a tenant is selected.</span>
          </div>
        ) : null}
        {tenantMe && tenantMe.is_demo && !user.is_impersonation ? (
          <div className="card" style={{ marginBottom: 12, padding: '8px 16px', background: 'rgba(234, 179, 8, 0.12)', borderColor: 'rgba(234, 179, 8, 0.4)' }} title="Demo tenant: max calls/day and max duration apply; no billing; export disabled.">
            <span style={{ fontSize: '0.8rem', color: 'var(--warning)', fontWeight: 600 }}>Demo Mode</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginLeft: 8 }}>Limited calls per day, no billing, no export. Data resettable by admin.</span>
          </div>
        ) : null}
        <header className="main-header">
          <div className="main-header-inner">
            <div className="main-title-block">
              <div className="main-title">
                <button
                  type="button"
                  className="mobile-menu-toggle"
                  aria-label="Open navigation menu"
                  onClick={function () { setMobileNavOpen(true); }}
                >
                  ☰
                </button>
                <span>
                  {active === 'dashboard'
                    ? 'Revenue cockpit'
                    : active === 'leads'
                    ? 'Leads workspace'
                    : active === 'calls'
                    ? 'Call intelligence'
                    : active === 'admin-dashboard'
                    ? 'Admin Dashboard'
                    : active === 'compliance-admin'
                    ? 'Calling compliance'
                    : active === 'oauth-providers'
                    ? 'OAuth Providers'
                    : active === 'integrations'
                    ? 'Integrations hub'
                    : active === 'flows'
                    ? 'Flows'
                    : active === 'billing'
                    ? (user && user.role === 'super_admin' && user.tenant_id == null ? 'Tenants & billing' : 'Plan & billing')
                    : active === 'cost-usage'
                    ? 'Cost & Usage'
                    : active === 'monitor'
                    ? 'Live call stream'
                    : active === 'settings'
                    ? 'Workspace settings'
                    : active === 'campaigns'
                    ? 'Campaigns'
                    : active === 'team'
                    ? 'Team'
                    : active === 'lead-finder'
                    ? 'Lead Finder'
                    : active === 'follow-ups'
                    ? 'Follow-ups'
                    : active === 'quality'
                    ? 'Quality'
                    : active === 'insights'
                    ? 'Insights'
                    : active === 'audit-logs'
                    ? 'Audit logs'
                    : active === 'demo-controls'
                    ? 'Demo controls'
                    : active === 'testlab'
                    ? 'Test & Diagnostics'
                    : active === 'monitoring'
                    ? 'Monitoring'
                    : active === 'go-live'
                    ? 'Go Live'
                    : active === 'dnc'
                    ? 'Do not call'
                    : 'Admin'}
                </span>
                {tenantMe && tenantMe.is_demo ? (
                  <span className="main-title-pill" style={{ background: 'rgba(234, 179, 8, 0.2)', color: 'var(--warning)', marginLeft: 8 }} title="Demo tenant: limited calls, no billing, no export">
                    Demo Mode
                  </span>
                ) : null}
                <span className="main-title-pill">Real-time AI voice · Nightcoders</span>
              </div>
              <div className="main-subtitle">
                AI voice agent that qualifies your leads and books follow-up calls.
              </div>
            </div>
            <div className="main-header-actions">
              {user ? (
                <AgentStatusToggle
                  api={API.json}
                  toast={showToast}
                  user={user}
                  liveState={agentPresenceState}
                  connected={agentEventsConnected}
                />
              ) : null}
              <ThemeToggle />
              <button className="button outline small" onClick={onLogout}>
                ⎋ Logout
              </button>
            </div>
          </div>
        </header>
        {demoInbound ? (
          <div className="card" style={{ marginBottom: 16, background: 'var(--color-bg-elevated)', borderLeft: '4px solid #f59e0b' }}>
            <div className="card-body" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <span style={{ fontWeight: 600 }}>Demo mode is enabled (DEMO_MODE=1) — realtime stream + Billing Lookup flow</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="button outline small"
                    disabled={demoSetupLoading || (user && user.role === 'super_admin')}
                    onClick={async function () {
                      setDemoSetupLoading(true);
                      try {
                        await API.json('POST', '/api/demo/setup-integration');
                        alert('Demo integration created.');
                      } catch (e) {
                        alert(e.message || 'Failed');
                      } finally {
                        setDemoSetupLoading(false);
                      }
                    }}
                  >
                    Setup Httpbin demo integration
                  </button>
                  <button
                    type="button"
                    className="button outline small"
                    disabled={demoSetupLoading || (user && user.role === 'super_admin')}
                    onClick={async function () {
                      setDemoSetupLoading(true);
                      try {
                        await API.json('POST', '/api/demo/setup-flow');
                        alert('Demo flow template created.');
                      } catch (e) {
                        alert(e.message || 'Failed');
                      } finally {
                        setDemoSetupLoading(false);
                      }
                    }}
                  >
                    Create demo flow template
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {user && !(user.role === 'super_admin' && (user.tenant_id == null || user.tenant_id === '') && !user.is_impersonation) ? (
          <React.Fragment>
            <ComplianceStatusBanner api={API.json} user={user} />
            <LegalAcceptanceGate api={API.json} toast={showToast} user={user} />
            <SetupWizard api={API.json} toast={showToast} user={user} open={setupOpen} onClose={function () { setSetupOpen(false); }} />
          </React.Fragment>
        ) : null}
        {mainContent}
        {/* Incoming agent request (caller asked for agent) */}
        {pendingAgentRequest ? (
          <div
            style={{
              position: 'fixed',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1100,
              minWidth: 320,
              maxWidth: '90vw',
              background: 'rgba(15,23,42,0.98)',
              border: '2px solid #22c55e',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--success)' }}>Incoming agent request</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--fg-muted)', marginBottom: 12 }}>
              Call {String(pendingAgentRequest.callSid || '').slice(-8)} — {pendingAgentRequest.callerHint || 'Caller requested agent'}
              {(pendingAgentRequest.queueTotal != null && pendingAgentRequest.queueTotal > 1) ? (
                <span style={{ marginLeft: 8, color: 'var(--fg-subtle)', fontWeight: 600 }}>Queue ({pendingAgentRequest.queueTotal})</span>
              ) : null}
            </div>
            {pendingAgentRequest.assignedUserId != null ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--fg-subtle)', marginBottom: 8 }}>Assigned to: User #{pendingAgentRequest.assignedUserId}</div>
            ) : (
              <div style={{ fontSize: '0.8rem', color: 'var(--fg-subtle)', marginBottom: 8 }}>Unassigned (tenant pickup)</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              {typeof Notification !== 'undefined' && Notification.permission !== 'granted' ? (
                <button
                  type="button"
                  className="button small ghost"
                  style={{ marginRight: 'auto' }}
                  onClick={function () {
                    if (Notification && Notification.requestPermission) {
                      Notification.requestPermission().catch(function () {});
                    }
                  }}
                >
                  Enable desktop alerts
                </button>
              ) : null}
              <button type="button" className="button outline" onClick={handleDeclineAgentRequest}>Decline</button>
              <button type="button" className="button outline" onClick={handlePhoneAnswerAgentRequest}>Phone Answer</button>
              <button type="button" className="button primary" onClick={handleAcceptAgentRequest}>Browser Answer</button>
            </div>
          </div>
        ) : null}
        {/* Floating browser softphone; only rendered when a call session is active */}
        <SoftphoneOverlay user={user} timezone={settings.timezone || 'America/Los_Angeles'} />
      </main>
    </div>
  );
}



function readResetToken() {
  if (typeof window === 'undefined') return '';
  try {
    var params = new URLSearchParams(window.location.search || '');
    return params.get('reset_token') || '';
  } catch (e) {
    return '';
  }
}

function LoginView({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState(readResetToken() ? 'reset' : 'login');
  const [resetToken, setResetToken] = useState(readResetToken);

  const [mfaStep, setMfaStep] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [phoneHint, setPhoneHint] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setMfaError(null);
    setLoading(true);
    try {
      const res = await API.json('POST', '/api/auth/login', { email, password });
      if (res && res.mfa_required) {
        setPendingEmail(email);
        setPhoneHint(res.phone_hint || null);
        setMfaStep(true);
        setShowMfaModal(true);
        try {
          const startRes = await API.json('POST', '/api/auth/mfa/start', { email });
          if (startRes && startRes.phone_hint) setPhoneHint(startRes.phone_hint);
          setResendCooldown(60);
        } catch (err) {
          console.error('mfa/start error', err);
          setMfaError(authErrorText(err, 'Could not send code. Try again, or sign in with your password again.'));
        }
      } else if (res && res.user && res.token) {
        onLoggedIn && onLoggedIn(res.user, res.token);
      } else {
        setError('Unexpected login response.');
      }
    } catch (e) {
      console.error(e);
      var code = e && e.data && e.data.error;
      if (code === 'account_disabled') {
        setError('This account is turned off. Ask a workspace admin to reactivate it, then sign in again.');
      } else if (code === 'too_many_requests' || e.status === 429) {
        setError(authErrorText(e, 'Too many attempts. Wait about 15 minutes, then try again.'));
      } else if (code === 'email_and_password_required') {
        setError('Enter both your email and password, then try again.');
      } else if (code === 'invalid_credentials' || e.status === 401) {
        setError('That email and password do not match. Check both, or ask an admin to reset the password.');
      } else if (!e.status) {
        setError('Could not reach the server. Check your connection and try again.');
      } else {
        setError('Sign-in failed. Try again, or ask an admin if this keeps happening.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyMfa(e) {
    e.preventDefault();
    setMfaError(null);
    setMfaLoading(true);
    try {
      const res = await API.json('POST', '/api/auth/mfa/verify', {
        email: pendingEmail || email,
        code: mfaCode
      });
      if (res && res.user && res.token) {
        setShowMfaModal(false);
        setMfaStep(false);
        onLoggedIn && onLoggedIn(res.user, res.token);
      } else {
        setMfaError('Unexpected MFA response.');
      }
    } catch (e) {
      console.error(e);
      var code = e && e.data && e.data.error;
      var fallback = (code === 'code_invalid' || code === 'code_expired')
        ? 'That code does not match. Check the text message and try again, or resend a new code.'
        : 'Could not verify that code. Check the text message, or resend a new code.';
      setMfaError(authErrorText(e, fallback));
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleResendCode() {
    if (resendCooldown > 0) return;
    setMfaError(null);
    try {
      const r = await API.json('POST', '/api/auth/mfa/start', { email: pendingEmail || email });
      if (r && r.phone_hint) setPhoneHint(r.phone_hint);
      setResendCooldown(60);
    } catch (e) {
      console.error('mfa resend error', e);
      setMfaError(authErrorText(e, 'Could not resend code. Try again.'));
    }
  }

  useEffect(function () {
    if (resendCooldown <= 0) return;
    var t = setInterval(function () {
      setResendCooldown(function (c) { return Math.max(0, c - 1); });
    }, 1000);
    return function () { clearInterval(t); };
  }, [resendCooldown]);

  function closeModal() {
    setShowMfaModal(false);
  }

  return (
    <main className="login-shell" role="main" aria-busy={loading || mfaLoading}>
      <div className="login-card card">
        <div className="login-card-body">
          <div className="login-brand">
            <div className="logo-row login-logo-row">
              <div className="logo-orb">
                <div className="logo-orb-inner" />
              </div>
              <div className="logo-text">
                <div className="logo-title">AI Call Leads Pro</div>
                <div className="logo-subtitle">Nightcoders Workspace</div>
              </div>
            </div>
          </div>
          <h1 className="login-heading">{authMode === 'login' ? 'Sign in' : 'Account access'}</h1>
          {authMode === 'login' ? (
          <p className="login-lede">
            Enter your admin or tenant credentials to access live calls, leads, and workspace settings.
          </p>
          ) : null}
          {authMode === 'forgot' ? (
            <ForgotPasswordForm api={API.json} onBack={function () { setAuthMode('login'); }} />
          ) : null}
          {authMode === 'reset' ? (
            <ResetPasswordForm api={API.json} token={resetToken} onDone={function () { setAuthMode('login'); setResetToken(''); }} />
          ) : null}
          {authMode === 'login' ? (
          <form onSubmit={handleSubmit} className="login-form stack gap-md">
            <label className="field">
              <span className="field-label">Email</span>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                disabled={loading}
                autoComplete="username"
              />
            </label>
            <label className="field">
              <span className="field-label">Password</span>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </label>
            {error && (
              <div className="form-error" role="alert">
                {error}
              </div>
            )}
            <button className="button primary login-submit" type="submit" disabled={loading} aria-busy={loading}>
              {loading ? 'Signing in…' : 'Continue'}
            </button>
            <button className="acct-link" type="button" onClick={function () { setAuthMode('forgot'); setError(null); }}>
              Forgot password?
            </button>
          </form>
          ) : null}
        </div>
      </div>

      {showMfaModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="card-header">
              <div>
                <div className="card-title">Verify your sign-in</div>
                <div className="card-subtitle">
                  {phoneHint ? ('We sent a code to ' + phoneHint + '. Enter it below.') : 'We sent a 6-digit code to your registered phone. Enter it below.'}
                </div>
              </div>
              <button className="button ghost" type="button" onClick={closeModal} aria-label="Close verification">
                ×
              </button>
            </div>
            <div className="card-body">
              <form onSubmit={handleVerifyMfa} className="stack gap-sm">
                <label className="field">
                  <div className="field-label">Verification code</div>
                  <input
                    type="text"
                    className="input"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                </label>
                {mfaError && <div className="form-error">{mfaError}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="button primary" type="submit" disabled={mfaLoading}>
                    {mfaLoading ? 'Verifying…' : 'Verify code'}
                  </button>
                  <button
                    className="button ghost"
                    type="button"
                    onClick={handleResendCode}
                    disabled={mfaLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0 ? 'Resend in ' + resendCooldown + 's' : 'Resend code'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
function RootApp() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(function onAuthExpired() {
    function handler() {
      setUser(null);
    }
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('auth:expired', handler);
      return function () { window.removeEventListener('auth:expired', handler); };
    }
  }, []);

  useEffect(function boot() {
    const token = window.localStorage.getItem('authToken');
    if (!token) {
      setBooting(false);
      return;
    }
    API.json('GET', '/api/auth/me')
      .then(function (res) {
        setUser(res.user);
      })
      .catch(function () {
        window.localStorage.removeItem('authToken');
      })
      .finally(function () {
        setBooting(false);
      });
  }, []);

  const handleLoggedIn = (user, token) => {
    window.localStorage.setItem('authToken', token);
    setUser(user);
  };

  const handleLogout = () => {
    API.json('POST', '/api/auth/logout')
      .catch(function () {})
      .finally(function () {
        window.localStorage.removeItem('authToken');
        setUser(null);
      });
  };

  if (booting) {
    return (
      <main className="login-shell" role="main" aria-busy="true">
        <div className="login-card card">
          <div className="login-card-body login-card-body--center">
            <div className="login-brand">
              <div className="logo-row login-logo-row">
                <div className="logo-orb">
                  <div className="logo-orb-inner" />
                </div>
                <div className="logo-text">
                  <div className="logo-title">AI Call Leads Pro</div>
                  <div className="logo-subtitle">Nightcoders Workspace</div>
                </div>
              </div>
            </div>
            <h1 className="login-heading login-heading--muted">Loading workspace</h1>
            <p className="login-boot-message">Restoring your session…</p>
          </div>
        </div>
      </main>
    );
  }

  if (!user) {
    return <LoginView onLoggedIn={handleLoggedIn} />;
  }

  if (userMustChangePassword(user)) {
    return (
      <ForceChangePassword
        user={user}
        api={API.json}
        toast={showToast}
        onChanged={function (nextUser, token) {
          if (token) window.localStorage.setItem('authToken', token);
          setUser(nextUser || user);
        }}
        onSignOut={handleLogout}
      />
    );
  }

  if (user.phone_setup_required) {
    return (
      <ForcePhoneSetup
        user={user}
        api={API.json}
        toast={showToast}
        onDone={function (nextUser) { setUser(nextUser); }}
        onSignOut={handleLogout}
      />
    );
  }

  return <AppShell user={user} onLogout={handleLogout} />;
}

/** UI QA runner: call from browser console with window.runUiQa(). Option: window.runUiQa({ executeWrites: true }) to run mutations. */
function runUiQa(opts) {
  opts = opts || {};
  var executeWrites = opts.executeWrites === true;
  var base = (typeof window !== 'undefined' && window.API_BASE) ? window.API_BASE : API_BASE;
  var getEndpoints = [
    '/api/auth/me',
    '/api/users',
    '/api/dashboard/config',
    '/api/tenant/capabilities',
    '/api/tenant/plan-features',
    '/api/tenant/settings',
    '/api/tenant/handoff',
    '/api/billing/summary',
    '/api/integrations/status',
    '/api/integrations/hub',
    '/api/integrations/event-settings',
    '/api/calls/active',
    '/api/settings',
    '/api/leads?page=1&limit=5',
    '/api/campaigns'
  ];
  var results = [];
  function shapeOk(endpoint, data) {
    if (!data || typeof data !== 'object') return true;
    if (endpoint.indexOf('/users') >= 0) return Array.isArray(data.users) || Array.isArray(data.items) || Array.isArray(data);
    if (endpoint.indexOf('/dashboard') >= 0) return (data.presets || data.widgets || data.preset) != null || (data.ok && data.preset);
    if (endpoint.indexOf('/billing') >= 0) return data.ok === true || data.plan != null || data.message != null;
    if (endpoint.indexOf('/integrations/hub') >= 0) return Array.isArray(data.providers);
    if (endpoint.indexOf('/campaigns') >= 0) return Array.isArray(data.campaigns) || Array.isArray(data.items) || Array.isArray(data);
    return true;
  }
  return Promise.all(getEndpoints.map(function (path) {
    var url = path.startsWith('http') ? path : base + path;
    return apiClientRequest('GET', path).then(function (r) {
      results.push({
        endpoint: path,
        ok: r.ok,
        status: r.status,
        shapeOk: shapeOk(path, r.data),
        notes: r.errorText || (r.ok ? 'OK' : '')
      });
      return r;
    }).catch(function (e) {
      results.push({ endpoint: path, ok: false, status: e.status || 0, shapeOk: false, notes: (e.message || e.errorText || 'Network error') });
    });
  })).then(function () {
    console.table(results);
    console.log('runUiQa: GET checks done. executeWrites=', executeWrites, '- set window.runUiQa({ executeWrites: true }) to run mutation tests.');
    return results;
  });
}
if (typeof window !== 'undefined') {
  window.runUiQa = runUiQa;
  window.apiClientRequest = apiClientRequest;
  window.getSessionContext = getSessionContext;
  window.showToast = showToast;
}

ReactDOM.createRoot(document.getElementById('root')).render(<RootApp />);